import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, lazy, Suspense } from "react";
import { toast } from "sonner";
import { useResourceMeta } from './useResourceMeta';
// Lazy-loaded: only shown once during onboarding
const SplashAnimation = lazyRetry(() => import('./SplashAnimation'));
const ValuePropCarousel = lazyRetry(() => import('./ValuePropCarousel'));
const AppTutorial = lazyRetry(() => import('./AppTutorial'));
/** Must match the value exported from AppTutorial.tsx */
const TOUR_VERSION = 'v3';
import ModuleIntroBanner from './ModuleIntroBanner';
import { useModuleIntro } from './useModuleIntro';
import CheckinIntroCard from './CheckinIntroCard';
import EDCheckinIntroModal from './EDCheckinIntroModal';
import { useXP, computeLevel } from './hooks/useXP';
import { useAppStats } from './hooks/useAppStats';
import { useAccessibility } from './hooks/useAccessibility';
import { useCareTeam } from './hooks/useCareTeam';
import { useSupportNetwork } from './hooks/useSupportNetwork';
import { useCircle, CIRCLE_MEDIA_CONSENT_KEY } from './hooks/useCircle';
import { useWidgetLayout, DEFAULT_WIDGET_ORDER, WIDGET_LABELS } from './hooks/useWidgetLayout';
import { useEDMode } from './hooks/useEDMode';
import { useCheckin } from './hooks/useCheckin';
import { useStigmaTracker } from './hooks/useStigmaTracker';
import { useEssentials } from './hooks/useEssentials';
import { useOnlineStatus } from './hooks/useOnlineStatus';
import OfflineBanner from './components/OfflineBanner';
import OfflineGate from './components/OfflineGate';
import {
  IconStreak, IconCheckinNote, IconHospitalPrep, IconMedications, IconCrisisPlan, IconGrowthSprout,
  IconCheck, IconClose, IconWarning, IconChecklist, IconLightbulb, IconShield, IconPhone, IconLock,
  IconBook, IconChart, IconAlertSiren, IconThoughtBubble, IconSpeechBubble, IconHandshake,
  IconBrain, IconFire, IconGrounding, IconSolidarity, IconAdvocacy,
  IconHeart, IconSparkle, IconJourneys, IconJourneyAdultTransition, IconJourneyEmploymentRights,
  IconLogEvent, IconEmotionsBodyMap, IconWeatherDrizzle,
  IconWeatherWind, IconWeatherLocation,
  CRYSTAL_ICONS, type CrystalIconKey,
} from './components/CrystalIcons';
// Lazy-loaded: only needed when user exports PDF
const loadHealthSummaryPDF = () => import('./generateHealthSummaryPDF').then(m => m.generateHealthSummaryPDF);

// Lazy-loaded: only shown in check-in / patterns screens
const BodyMap = lazyRetry(() => import('./BodyMap'));
const CareWeaveBodyMap = lazyRetry(() => import('./CareWeaveBodyMap'));
const CareWeaveEmotionMap = lazyRetry(() => import('./CareWeaveEmotionMap'));

// Lazy-loaded: only shown in articles / growth sections
const ArticleCard = lazyRetry(() => import('./ArticleCard'));
/** Tiny inline copy — avoids pulling ArticleCard into main chunk just for XP calc */
function xpForItem(item: { intensity?: number }): number {
  return item.intensity === 3 ? 15 : item.intensity === 2 ? 10 : 5;
}

// Lazy-loaded: only shown inside specific ED content items
const DecisionTreeDiagram = lazyRetry(() => import('./ContentEnhancements').then(m => ({ default: m.DecisionTreeDiagram })));
const TriageTimeline = lazyRetry(() => import('./ContentEnhancements').then(m => ({ default: m.TriageTimeline })));
const EmergencyProfileCard = lazyRetry(() => import('./ContentEnhancements').then(m => ({ default: m.EmergencyProfileCard })));
const HeavinessSlider = lazyRetry(() => import('./ContentEnhancements').then(m => ({ default: m.HeavinessSlider })));
const SafetyAssessment = lazyRetry(() => import('./ContentEnhancements').then(m => ({ default: m.SafetyAssessment })));
const RightsGrid = lazyRetry(() => import('./ContentEnhancements').then(m => ({ default: m.RightsGrid })));
const ThreeSentenceBuilder = lazyRetry(() => import('./ContentEnhancements').then(m => ({ default: m.ThreeSentenceBuilder })));
const PainSpectrum = lazyRetry(() => import('./ContentEnhancements').then(m => ({ default: m.PainSpectrum })));
const ScriptPracticeMode = lazyRetry(() => import('./ContentEnhancements').then(m => ({ default: m.ScriptPracticeMode })));
const StoryQuoteHeader = lazyRetry(() => import('./ContentEnhancements').then(m => ({ default: m.StoryQuoteHeader })));
const Grounding54321 = lazyRetry(() => import('./ContentEnhancements').then(m => ({ default: m.Grounding54321 })));
const SystemVsYouVisual = lazyRetry(() => import('./ContentEnhancements').then(m => ({ default: m.SystemVsYouVisual })));
const NewcomerAffirmation = lazyRetry(() => import('./ContentEnhancements').then(m => ({ default: m.NewcomerAffirmation })));
import {
  ED_CONTENT as STATIC_ED_CONTENT, ED_STAGES, getContentByPhase,
  INTENSITY_ICONS, DELIVERY_ICONS,
  type EDContentItem, type EDStageId, type ContentPhase,
} from './edContent';
import { type PainIntensity, INTENSITY_COLORS, INTENSITY_LABELS, SCALE_EMOJIS } from './constants/painConstants';
import { useResourceProgress } from './useResourceProgress';
import { useGoals } from './useGoals';
import {
  GOAL_TEMPLATES, GOAL_THEMES, THEME_META,
  type GoalTemplate, type GoalTheme,
} from './goalTemplates';
import { getToken, clearToken, fetchMe, updateProfile, warmBackend, startTokenRefresh, stopTokenRefresh, type UserProfile } from './auth';
import type { JourneyResourceItem } from './journeyResources';
import { useAllJourneyResources, useArticles, type ContentJourneyResource, type ContentArticle } from './hooks/useContent';
const JourneyScene = lazyRetry(() => import('./components/JourneyScene'));
import { getCrystalGuide } from './components/crystalGlobeData';
import GuideBubble from './components/GuideBubble';
import FeatureTutorial, { isFeatureTutorialSeen } from './components/FeatureTutorial';

// --- Feature tutorial step definitions ---

const PATTERNS_TUTORIAL = [
  { icon: '\u{1F4C8}', title: 'Your health patterns', body: 'Every check-in feeds into Patterns. Over time, you\u2019ll see peak days, what\u2019s been helping, and what\u2019s been showing up across your body.' },
  { icon: '\u{1F50D}', title: 'Spot what matters', body: 'Filter by week or month, tap any day to drill into the details, and compare trends across different time periods. The more you check in, the clearer the picture.' },
  { icon: '\u{1F4CB}', title: 'Hold what you\u2019ve noticed', body: 'Your check-ins build up a story you can come back to. When something feels off, scroll back and see what was happening \u2014 that\u2019s information for you, not just a clinical record.' },
];

const GROWTH_TUTORIAL = [
  { icon: '\u{1F331}', title: 'Your growth journey', body: 'Set personal care goals, track your progress, and earn XP for every step you take. Growth is about building habits that matter to you.' },
  { icon: '\u{1F3C5}', title: 'Earn and unlock', body: 'Check-ins earn 10 XP. Completing goals earns more. Level up to unlock new Crystal Guide auras, comfort items, and achievements.' },
  { icon: '\u{1F5FA}\uFE0F', title: 'Guided journeys', body: 'Journeys are step-by-step paths through real care situations \u2014 your first ER visit, starting a new medication, navigating school. Each one has articles, reflections, and actions.' },
];

const TOOLS_TUTORIAL = [
  { icon: '\u{1F6E0}\uFE0F', title: 'Your care toolkit', body: 'Everything you need for self-advocacy, organized in one place. Medications, Stigma Tracker, hospital prep, crisis planning, and more.' },
  { icon: '\u{1F4DD}', title: 'Keep a record for yourself', body: 'The Stigma Tracker and medication tracker build a record you keep over time. When you need to come back to what you went through \u2014 for your own meaning-making or to share with someone you trust \u2014 it\u2019s here.' },
  { icon: '\u{1F198}', title: 'Ready for crisis', body: 'Your Crisis Plan, Hospital Prep Kit, and grounding exercises are always one tap away \u2014 even when you\u2019re in pain.' },
];

// Shown once per device. The storage key has a version suffix so people who
// saw the earlier intro (before the scrapbook) see this one once.
const JOURNAL_TUTORIAL = [
  { icon: '\u{1F4D3}', title: 'Your private scrapbook', body: 'Write in your own words, or tap Speak to type and say it out loud. Tap Give me a prompt if you want a starting point.' },
  { icon: '\u{1F3A4}', title: 'Speaking instead of typing', body: 'Your words show up as you talk. Tap Stop speaking when you are done. If you say the same words twice in a row, the second time may not show up, so type it in or say it a little differently. Your voice is turned into text on this device. Voice typing shows only in browsers that can do this on the device, such as recent versions of Chrome.' },
  { icon: '\u{1F48E}', title: 'Decorate with stickers', body: 'Add your Crystal Guide or emoji stickers, then drag them anywhere on the page. Tap a sticker to turn it, resize it, or remove it. More guide moods unlock as your bond grows, and more emoji unlock as you earn XP.' },
  { icon: '\u{1F4F8}', title: 'Keep it on your phone', body: 'Pages stay on this device and are not shared with anyone. Tap Save as photo to keep a page in your photos.' },
];

const CIRCLE_TUTORIAL = [
  { icon: '\u{1FAC2}', title: 'Welcome to The Circle', body: 'A safe, moderated community exclusively for youth with SCD. You post under a nickname \u2014 your real name is never shown.' },
  { icon: '\u{1F4AC}', title: 'Share and connect', body: 'Post tips, questions, stories, or just solidarity. React to posts, reply to threads, and find people who truly get it.' },
  { icon: '\u{1F6E1}\uFE0F', title: 'Your safety matters', body: 'Every post is moderated. You can mute words, block members, and report anything that doesn\u2019t feel right. This is your space.' },
];

const EVENTLOG_TUTORIAL = [
  { icon: '\u{1F4DD}', title: 'Log what happened', body: 'When you experience stigma, dismissal, or mistreatment, this is where you document it. Name the setting, who was involved, and what it felt like.' },
  { icon: '\u{1F52C}', title: 'Understand the layers', body: 'Rate the impact, identify which layers of stigma were at play, and describe your emotional and coping responses. This isn\u2019t just logging \u2014 it\u2019s making sense of it.' },
  { icon: '\u{1F4CA}', title: 'What you noticed', body: 'Over time, your stigma log reveals patterns of how you\u2019ve been treated across different systems. That\u2019s information you hold \u2014 for your own meaning-making, and for organizing alongside others when you choose to.' },
];

const MEDICATIONS_TUTORIAL = [
  { icon: '\u{1F48A}', title: 'Your medication list', body: 'Keep your full medication list here \u2014 hydroxyurea, pain meds, supplements, everything. An up-to-date list helps your care team and speeds up crisis visits.' },
  { icon: '\u23F0', title: 'Track daily doses', body: 'Log when you\u2019ve taken each medication. Tracking adherence helps you spot patterns and gives your care team better information.' },
  { icon: '\u{1F4C4}', title: 'Ready for the ER', body: 'Your medication list feeds into your Hospital Prep Kit and ED reports. When you\u2019re in crisis, everything is already documented.' },
];

const HOSPITALPREP_TUTORIAL = [
  { icon: '\u{1F3E5}', title: 'Be ready before you go', body: 'Hospital Prep puts your medical snapshot, advocacy scripts, packing checklist, and emergency contacts in one place \u2014 ready before the crisis hits.' },
  { icon: '\u{1F4CB}', title: 'Your medical snapshot', body: 'Your diagnoses, medications, allergies, and care preferences auto-populate from your profile. Review and update them here so they\u2019re always current.' },
  { icon: '\u{1F5E3}\uFE0F', title: 'Advocate for yourself', body: 'Practice scripts help you communicate with ER staff. Know your rights, describe your pain clearly, and request the care you deserve.' },
];

// Lazy-loaded components: these screens/panels are only shown on specific
// routes or behind user interactions so they don't need to be in the initial bundle.

/** Wrap lazy() with automatic retry — handles stale chunk hashes after deploys
 *  and transient network failures that would otherwise trigger ErrorBoundary.
 *  On second failure, forces a full page reload to pick up the new index.html
 *  (guarded by sessionStorage so we only auto-reload once per session). */
function lazyRetry<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(() =>
    importFn().catch(() =>
      new Promise<{ default: T }>((resolve, reject) => {
        setTimeout(() => {
          importFn().then(resolve).catch(() => {
            // Chunk permanently gone (new deploy) — reload to get fresh index.html
            const reloaded = sessionStorage.getItem('careweave-chunk-reload');
            if (!reloaded) {
              sessionStorage.setItem('careweave-chunk-reload', '1');
              window.location.reload();
              return;
            }
            reject(new Error('Chunk not found after deploy — please reload'));
          });
        }, 1500);
      }),
    ),
  );
}

const ResearcherDashboard = lazyRetry(() => import('./components/ResearcherDashboard'));
const ModerationDashboard = lazyRetry(() => import('./components/ModerationDashboard'));
const ModeratorDashboard = lazyRetry(() => import('./components/ModeratorDashboard'));
const SignUpScreen = lazyRetry(() => import('./components/SignUpScreen'));
const LoginScreen = lazyRetry(() => import('./components/LoginScreen'));
const JournalScreen = lazyRetry(() => import('./components/JournalScreen'));
const ForgotPasswordScreen = lazyRetry(() => import('./components/ForgotPasswordScreen'));
const PrivacyPolicyScreen = lazyRetry(() => import('./components/PrivacyPolicyScreen'));
const CrisisResourcesScreen = lazyRetry(() => import('./components/CrisisResourcesScreen'));
const ProfileSetupWizard = lazyRetry(() => import('./components/ProfileSetupWizard'));
const GuideCustomizer = lazyRetry(() => import('./components/GuideCustomizer'));
import { AVATARS } from './components/avatars';
import { CRYSTAL_CHARACTERS } from './components/guideCatalogue';
import { useGuideBond } from './hooks/useGuideBond';
import { useNotifications } from './useNotifications';
const NotificationPanel = lazyRetry(() => import('./NotificationPanel'));
const PatternsScreenInline = lazyRetry(() => import('./components/PatternsScreenInline'));
const EventLogScreenInline = lazyRetry(() => import('./components/EventLogScreenInline'));
const GrowthScreenInline = lazyRetry(() => import('./components/GrowthScreenInline'));
import { type AccessibilityPrefs } from './NotificationPanel';
import AddCustomTag from './components/AddCustomTag';
import CircleAvatarPicker from './components/CircleAvatarPicker';
import {
  COPING_STRATEGIES, EXPERIENCE_TAGS,
  WATERS_CHECK_OPTIONS, FATIGUE_TYPE_CHIPS, SLEEP_QUALITY_CHIPS,
  WORRY_CHIPS, FEELING_BLUE_CHIPS,
  STRESS_DESCRIPTOR_CHIPS, FEELING_CHIPS,
  WHAT_HELPED_CATEGORIES,
  STIGMA_ATTRIBUTION_CHIPS, STIGMA_EMOTIONAL_CHIPS,
  STIGMA_EXPANDED_PROTECTIVE_FACTORS, STIGMA_COPING_CHIPS,
  STIGMA_LAYER_CHIPS, STIGMA_EXPERIENCE_TYPE_CHIPS,
  EXPERIENCE_TAG_GROUPS,
} from './constants/tagConstants';
import { CHECKIN_STEP_ICONS, IconSleep, IconStress, IconFatigue, IconPain, IconWorry, IconBlue, IconBodyMap, IconWhatHelped, IconTreatment, IconCrisis, IconTrend, IconStar, IconCalmWaters, IconChoppyWaters, IconRoughSeas, IconSOS } from './components/CheckinIcons';
import { STIGMA_STEP_ICONS } from './components/StigmaIcons';
const GroundingScreen = lazyRetry(() => import('./components/GroundingScreen'));
const CircleWelcomeScreen = lazyRetry(() => import('./components/CircleWelcomeScreen'));
import { SCD_TYPES_FILTER, SCD_TYPES_CIRCLE } from './constants/scdTypes';
const FAQPanel = lazyRetry(() => import('./components/FAQPanel'));
const ContactSupportPanel = lazyRetry(() => import('./components/ContactSupportPanel'));
const SettingsPanel = lazyRetry(() => import('./components/SettingsPanel'));
const MedicationsScreen = lazyRetry(() => import('./components/MedicationsScreen'));
const CrisisPlanScreen = lazyRetry(() => import('./components/CrisisPlanScreen'));
const HistoryScreen = lazyRetry(() => import('./components/HistoryScreen'));
const HospitalPrepScreen = lazyRetry(() => import('./components/HospitalPrepScreen'));
const WeatherReportScreen = lazyRetry(() => import('./components/WeatherReportScreen'));
const CareTeamScreen = lazyRetry(() => import('./components/CareTeamScreen'));
const SupportNetworkScreen = lazyRetry(() => import('./components/SupportNetworkScreen'));
const ContentManager = lazyRetry(() => import('./components/ContentManager'));
const YouthFeatureMockups = lazyRetry(() => import('./components/YouthFeatureMockups'));
import {
  SK_PREP_PROFILE, SK_PREP_CONTACTS, SK_PREP_PACK,
  SK_CRISIS_CHECKED, SK_CRISIS_PLAN_STAGES, SK_CRISIS_NOTE_DISMISSED,
  SK_STARRED_TOOLS, SK_TOOL_ACCESS_COUNTS,
  SK_ACTIVE_JOURNEYS, SK_COMPLETED_JOURNEY_RESOURCES,
  SK_CHECKINS,
  SK_SEEN_CHECKIN_INTRO, SK_SEEN_ED_CHECKIN,
  SK_HIDE_PATTERNS,
  SK_DEMO_PROFILES,
  SK_ENABLED_CHECKIN_QUESTIONS,
  SK_XP, SK_XP_REWARDED, SK_EVENT_LOGS,
  SK_MEDICATIONS, SK_MED_LOGS, SK_FLARE_LOCATION,
} from './constants/storageKeys';
import { isDemoProfilesEnabled, setDemoProfilesEnabled, getDemoPosts, getDemoReactions } from './demoProfiles';
import { isDemoVisit, exitDemo } from './AccessGate';
import {
  getDemoMedications, getDemoGoals,
  getDemoJourneys, getDemoCompletedResources, getDemoXP,
  getDemoRewardedEvents, getDemoPrepProfile, getDemoPrepContacts,
  getDemoCrisisPlan, getDemoEventLogs,
} from './demoData';
import InstallPrompt from './components/InstallPrompt';
import { XpFlashBubble, XpLevelUpCelebration, LazyFallback } from './components/XpComponents';
import SliderField from './components/SliderField';
import {
  getLocalDate, localDateFromTimestamp,
  avgOfField, apiPost, apiPut, apiGet, API_BASE, fetchWithRetry,
} from './appUtils';
import { readResetLink, clearResetLink } from './resetLink';
import { fetchRegistrationOpen, markIntroSeen } from './entryFlow';
import {
  type Screen, type ModalId,
  type Medication, type MedicationLog, frequencyToDoseCount,
  type PainArea,
  type CareTeamMember, type SupportNetworkMember,
  type AuthFlow,
  type StigmaTrackerEntry,
} from './appTypes';
import {
  MEMBER_COLORS, MEMBER_EMOJIS_CARE, MEMBER_EMOJIS_NETWORK,
  screenSubtitles, edModeConfig, BODY_AREA_NAMES, SAFETY_FLAGS,
  COMMON_MEDICATIONS, VOC_OPTIONS, LEVEL_SYSTEM, ACHIEVEMENTS,
  SPLASH_SEEN_KEY, CAROUSEL_SEEN_KEY,
  getTutorialDoneKey, getTutorialVersionKey, getTutorialXpKey,
} from './appConstants';

// Timing constants (ms)
// AFFIRMATION_DISPLAY_MS moved to hooks/useCheckin.ts
// XP timing constants moved to hooks/useXP.ts
const SCROLL_DELAY_MS = 150;
const PDF_TOAST_MS = 3000;
const CHECKIN_DELAY_MS = 2000;

/**
 * Prunes old entries from localStorage arrays that grow unbounded over time.
 * Only runs if an array has > 500 entries, and removes entries older than 365 days.
 * Defensive: wrapped in try/catch so it never crashes the app.
 */
function pruneOldLocalStorageData() {
  const PRUNE_THRESHOLD = 500;
  const MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;
  const cutoff = Date.now() - MAX_AGE_MS;

  const targets: Array<{
    key: string;
    label: string;
    getTime: (entry: Record<string, unknown>) => number;
  }> = [
    {
      key: SK_CHECKINS,
      label: 'checkins',
      getTime: (e) => {
        const ts = e.timestamp;
        if (typeof ts === 'string') return new Date(ts).getTime();
        return 0;
      },
    },
    {
      key: SK_EVENT_LOGS,
      label: 'stigma logs',
      getTime: (e) => {
        const ts = e.timestamp;
        if (typeof ts === 'string') return new Date(ts).getTime();
        return 0;
      },
    },
    {
      key: SK_MED_LOGS,
      label: 'medication logs',
      getTime: (e) => {
        const d = e.date;
        if (typeof d === 'string') return new Date(d).getTime();
        return 0;
      },
    },
  ];

  for (const target of targets) {
    try {
      const raw = localStorage.getItem(target.key);
      if (!raw) continue;

      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed) || parsed.length <= PRUNE_THRESHOLD) continue;

      const kept = parsed.filter((entry) => {
        if (!entry || typeof entry !== 'object') return true; // keep malformed entries
        const t = target.getTime(entry);
        if (!t || isNaN(t)) return true; // keep entries with unparseable dates
        return t >= cutoff;
      });

      if (kept.length < parsed.length) {
        localStorage.setItem(target.key, JSON.stringify(kept));
      }
    } catch {
      /* prune is best-effort */
    }
  }
}

function AdminHeader({ title, email, onSwitchView, onSignOut }: { title: string; email: string; onSwitchView: () => void; onSignOut: () => void }) {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', position: 'sticky', top: 0, zIndex: 'var(--z-sticky)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <img src="/careweave-net-icon.svg" alt="" width="28" height="28" />
        <span style={{ fontWeight: 700, fontSize: 'var(--fs-base)', color: 'var(--color-text-primary)', fontFamily: "'Source Serif 4', serif" }}>{title}</span>
      </div>
      <div style={{ position: 'relative' }}>
        {/* eslint-disable-next-line cw/no-inline-button-style -- admin avatar dropdown trigger; composite (avatar circle + email + caret) with active-state bg; not a chamfered .btn variant */}
        <button onClick={() => setMenuOpen(s => !s)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 'var(--radius-sm)', background: menuOpen ? 'var(--color-accent-light)' : 'transparent', border: '1px solid var(--color-border)', cursor: 'pointer', color: 'var(--color-text-primary)', fontSize: 'var(--fs-sm)', fontWeight: 600 }}>
          <span style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--color-accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--color-accent)' }}>{(email || 'A').charAt(0).toUpperCase()}</span>
          {email} <span style={{ fontSize: 'var(--fs-xs)', opacity: 0.5 }}>▾</span>
        </button>
        {menuOpen && (
          <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 'var(--z-dropdown)', background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-overlay)', overflow: 'hidden', minWidth: 160 }}>
            {/* eslint-disable-next-line cw/no-inline-button-style -- dropdown menu row; full-width left-aligned with divider, not a chamfered .btn variant */}
            <button onClick={() => { setMenuOpen(false); onSwitchView(); }} style={{ width: '100%', padding: '11px 16px', border: 'none', background: 'none', borderBottom: '1px solid var(--color-border)', color: 'var(--color-accent)', fontSize: 'var(--fs-sm)', fontWeight: 600, textAlign: 'left', cursor: 'pointer' }}>Switch view</button>
            {/* eslint-disable-next-line cw/no-inline-button-style -- dropdown menu row; full-width left-aligned, danger variant, not a chamfered .btn variant */}
            <button onClick={onSignOut} style={{ width: '100%', padding: '11px 16px', border: 'none', background: 'none', color: 'var(--color-danger)', fontSize: 'var(--fs-sm)', fontWeight: 600, textAlign: 'left', cursor: 'pointer' }}>Sign out</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function App() {
  const [authFlow, setAuthFlow] = useState<AuthFlow>('loading');
  // Details from a password reset email link (#reset?email=...&code=...), read once.
  // While set, the reset screen is shown in place of the splash, welcome
  // slides or landing screen (their timers would otherwise replace it).
  const [resetLink, setResetLink] = useState(readResetLink);
  // Whether public sign-up is open; decides if the sign-in page offers "Create an account".
  const [registrationOpen, setRegistrationOpen] = useState(false);
  useEffect(() => { fetchRegistrationOpen().then(setRegistrationOpen); }, []);
  // Reaching the sign-in page counts as having seen the first-visit intro.
  useEffect(() => { if (authFlow === 'login') markIntroSeen(); }, [authFlow]);
  useEffect(() => {
    if (resetLink && authFlow === 'app') {
      // Already signed in on this device: no reset needed.
      setResetLink(null);
      clearResetLink();
    }
  }, [resetLink, authFlow]);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [isDemoUser, setIsDemoUser] = useState(false);
  const [isDevMode, setIsDevMode] = useState(false);
  // Posts a public-demo visitor writes; shown only on their screen, never sent.
  const [demoLocalPosts, setDemoLocalPosts] = useState<{ id: number; type: string; text: string; createdAt: string }[]>([]);
  const [showDevPanel, setShowDevPanel] = useState(false);
  const [showDemoXpAdjuster, setShowDemoXpAdjuster] = useState(false);
  const [demoXpInput, setDemoXpInput] = useState('');
  const [devXpInput, setDevXpInput] = useState('');
  const [showTutorial, setShowTutorial] = useState(false);
  const [, forceUpdate] = useState(0);
  const [showCrisisBanner, setShowCrisisBanner] = useState(false);

  // Lazy-load the static journeyResources module as a fallback, then overlay API data
  const [staticPhaseResources, setStaticPhaseResources] = useState<Record<string, Record<string, JourneyResourceItem[]>>>({});
  useEffect(() => {
    import('./journeyResources').then(mod => setStaticPhaseResources(mod.PHASE_RESOURCES));
  }, []);
  const { data: apiJourneyResources } = useAllJourneyResources();
  const { data: apiArticles } = useArticles();

  // ED_CONTENT: prefer API articles, fall back to static edContent.ts
  const ED_CONTENT = useMemo<EDContentItem[]>(() => {
    if (!apiArticles || apiArticles.length === 0) return STATIC_ED_CONTENT;
    return apiArticles.map((item: ContentArticle) => ({
      id: item.slug,
      type: item.content_type as EDContentItem['type'],
      title: item.title,
      summary: item.summary,
      body: item.body,
      script_lines: item.script_lines,
      journey: (item.journey as 'ED'),
      phase: item.phase.length === 1 ? (item.phase[0] as ContentPhase) : (item.phase as ContentPhase[]),
      module: item.module,
      stigma_vital_signs: item.stigma_vital_signs as EDContentItem['stigma_vital_signs'],
      tags: item.tags,
      intensity: item.intensity as EDContentItem['intensity'],
      delivery_mode: item.delivery_mode as EDContentItem['delivery_mode'],
      estimated_time_minutes: item.estimated_time_minutes,
      notes_for_youth: item.notes_for_youth ?? undefined,
      age_at_event: item.age_at_event ?? undefined,
      self_described_identities: item.self_described_identities,
      content_warnings: item.content_warnings,
      gif: item.gif ?? undefined,
    }));
  }, [apiArticles]);

  // Merge: API data takes precedence; static data fills gaps (offline/not-yet-edited)
  const phaseResourcesData = useMemo<Record<string, Record<string, JourneyResourceItem[]>>>(() => {
    if (!apiJourneyResources || Object.keys(apiJourneyResources).length === 0) {
      return staticPhaseResources;
    }
    const merged: Record<string, Record<string, JourneyResourceItem[]>> = { ...staticPhaseResources };
    for (const [journeySlug, phases] of Object.entries(apiJourneyResources)) {
      merged[journeySlug] = merged[journeySlug] ? { ...merged[journeySlug] } : {};
      for (const [phaseKey, resources] of Object.entries(phases)) {
        merged[journeySlug][phaseKey] = (resources as ContentJourneyResource[]).map(r => ({
          id: r.resource_id,
          icon: r.icon,
          title: r.title,
          desc: r.description,
          type: r.resource_type,
          items: r.items,
        }));
      }
    }
    return merged;
  }, [apiJourneyResources, staticPhaseResources]);

  const [moduleIntroSeenCheckin, dismissModuleIntroCheckin] = useModuleIntro('checkin');
  const [moduleIntroSeenPatterns, dismissModuleIntroPatterns] = useModuleIntro('patterns');
  const [moduleIntroSeenGrowth, dismissModuleIntroGrowth] = useModuleIntro('growth');
  const [moduleIntroSeenMedications, dismissModuleIntroMedications] = useModuleIntro('medications');
  const [moduleIntroSeenEventLog, dismissModuleIntroEventLog] = useModuleIntro('event-log');
  const [moduleIntroSeenJourneys, dismissModuleIntroJourneys] = useModuleIntro('journeys');
  const [moduleIntroSeenCircle, dismissModuleIntroCircle] = useModuleIntro('peer-connect');
  const [moduleIntroSeenTools, dismissModuleIntroTools] = useModuleIntro('tools');
  const [crisisNoteDismissed, setCrisisNoteDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem(SK_CRISIS_NOTE_DISMISSED) === '1'; } catch { return false; }
  });
  const dismissCrisisNote = () => {
    setCrisisNoteDismissed(true);
    try { localStorage.setItem(SK_CRISIS_NOTE_DISMISSED, '1'); } catch { /* ignore */ }
  };

  // showCheckinIntro, showEDCheckinIntro, checkinDraftActive, checkinReturnScreen are now in useCheckin hook
  // Install prompt is now handled by the <InstallPrompt /> component

  // Crisis banner is now user-triggered from the hamburger menu — no auto-show.

  // Circle / Peer Connect state (extracted to useCircle hook)
  const {
    circleProfile, setCircleProfile,
    circleDisclosure, setCircleDisclosure,
    circleSetupModal, setCircleSetupModal,
    showCircleWelcome, setShowCircleWelcome,
    circleSetupNickname, setCircleSetupNickname,
    circleSetupBio, setCircleSetupBio,
    circleSetupNicknameError, setCircleSetupNicknameError,
    circleSetupBioError, setCircleSetupBioError,
    circleSetupSaving, setCircleSetupSaving,
    circleEditNickname, setCircleEditNickname,
    circleEditBio, setCircleEditBio,
    circleEditNicknameError, setCircleEditNicknameError,
    circleEditBioError, setCircleEditBioError,
    circleEditSaving, setCircleEditSaving,
    circleEditSaved, setCircleEditSaved,
    circleDisclosureEditAgeBracket, setCircleDisclosureEditAgeBracket,
    circleDisclosureEditScdType, setCircleDisclosureEditScdType,
    circleDisclosureEditScdOther, setCircleDisclosureEditScdOther,
    circleDisclosureEditScdShowOther, setCircleDisclosureEditScdShowOther,
    circleDisclosureEditTags, setCircleDisclosureEditTags,
    circleDisclosureEditTagInput, setCircleDisclosureEditTagInput,
    circleDisclosureEditTagError, setCircleDisclosureEditTagError,
    circleDisclosureEditSaving, setCircleDisclosureEditSaving,
    circleDisclosureEditSaved, setCircleDisclosureEditSaved,
    circleLivePosts, setCircleLivePosts,
    circleAbortRef,
    circleThreadPost, setCircleThreadPost,
    circleThreadReplies, setCircleThreadReplies,
    circleThreadLoading, setCircleThreadLoading,
    circleThreadReplyText, setCircleThreadReplyText,
    circleThreadReplyLoading, setCircleThreadReplyLoading,
    circleThreadReplyError, setCircleThreadReplyError,
    circleThreadReplyCounts, setCircleThreadReplyCounts,
    circleThreadRemovedReplies, setCircleThreadRemovedReplies,
    circleThreadReportedReplies, setCircleThreadReportedReplies,
    peerFilter, setPeerFilter,
    peerAgeFilter, setPeerAgeFilter,
    peerScdFilter, setPeerScdFilter,
    peerView, setPeerView,
    peerSelectedTopic, setPeerSelectedTopic,
    peerReactions, setPeerReactions,
    peerReacted, setPeerReacted,
    peerEmojiPickerPost, setPeerEmojiPickerPost,
    peerPostModal, setPeerPostModal,
    peerPostType, setPeerPostType,
    peerPostText, setPeerPostText,
    peerRemovedPosts, setPeerRemovedPosts,
    peerRemovalModal, setPeerRemovalModal,
    peerRemovalReason, setPeerRemovalReason,
    peerRemovalConfirmed, setPeerRemovalConfirmed,
    peerPostError, setPeerPostError,
    peerPostNudge, setPeerPostNudge,
    peerPostLoading, setPeerPostLoading,
    showCircleEmojiPicker, setShowCircleEmojiPicker,
    circleEmojiDraft, setCircleEmojiDraft,
    peerRevealedPosts, setPeerRevealedPosts,
    safetyMutedWords, setSafetyMutedWords,
    safetyMutedMembers, setSafetyMutedMembers,
    safetyBlockedMembers, setSafetyBlockedMembers,
    safetyLoaded, setSafetyLoaded,
    safetyNewWord, setSafetyNewWord,
    safetyWordError, setSafetyWordError,
    circleMediaConsentModal, setCircleMediaConsentModal,
    circleMediaConsentPendingAction, setCircleMediaConsentPendingAction,
    peerPostImage, setPeerPostImage,
    peerPostImageName, setPeerPostImageName,
    peerPostAltText, setPeerPostAltText,
    peerPostGif, setPeerPostGif,
    peerPostImageUploading, setPeerPostImageUploading,
    peerPostImageError, setPeerPostImageError,
    peerPostImageContentNote, setPeerPostImageContentNote,
    showGifPicker, setShowGifPicker,
    gifCategories, setGifCategories,
    gifItems, setGifItems,
    gifFilter, setGifFilter,
    // adminGifCategories/adminModerationQueue moved to ResearcherDashboard
    loadSafetySettings,
    addMutedWord,
    removeMutedWord,
    muteMember,
    unmuteMember,
    blockMember,
    unblockMember,
    loadCirclePosts,
    openCircleThread,
    submitCircleReply,
    loadCircleReactions,
    circleTimeAgo,
  } = useCircle(authToken);

  useEffect(() => {
    // Dark mode initialization is handled by useAccessibility hook

    // Public demo visit: open the fictional demo profile with no account.
    if (isDemoVisit()) {
      toggleDemoProfiles(true);
      setPeerReactions(r => ({ ...getDemoReactions(), ...r }));
      handlePasscodeDevMode();
      // Show the app tour on a visitor's first demo visit, as a new member would see it.
      if (shouldShowTutorial(0)) setShowTutorial(true);
      return;
    }

    // Check for an existing session. If there's no token at all, skip the
    // DB warm-up and show the splash/landing immediately — no point making
    // the user wait 5-7s for Neon to wake when there's nothing to restore.
    const memToken = getToken();

    function showUnauthenticated() {
      clearToken();
      const splashSeen = localStorage.getItem(SPLASH_SEEN_KEY);
      const carouselSeen = localStorage.getItem(CAROUSEL_SEEN_KEY);
      if (!splashSeen) { setAuthFlow('splash'); return; }
      if (!carouselSeen) { setAuthFlow('carousel'); return; }
      setAuthFlow('login');
    }

    // No in-memory token AND first-time visitor → show splash immediately.
    // warmBackend() fires in the background so the DB is waking while
    // the user reads the landing page.
    const splashSeen = localStorage.getItem(SPLASH_SEEN_KEY);
    if (!memToken && !splashSeen) {
      warmBackend(); // fire-and-forget
      showUnauthenticated();
      return;
    }

    // Restore session via in-memory token OR HttpOnly cookie.
    // After a page reload the in-memory token is gone, but the HttpOnly
    // cookie survives — so we attempt fetchMe() which sends credentials.
    warmBackend().then(() => fetchMe(memToken)).then(user => {
      if (!user) {
        showUnauthenticated();
        return;
      }
      if (memToken) setAuthToken(memToken);
      setCurrentUser(user);
      if (user.customChips && Object.keys(user.customChips).length > 0) {
        setCustomCheckinChips(user.customChips);
      }
      const xpEarned = getTutorialXpEarned(user.id);
      setTutorialCompleted(xpEarned);
      if (!user.completedOnboarding) { setAuthFlow('onboarding'); return; }
      setAuthFlow('mode-chooser');
      if (!getTutorialDone(user.id) && !user.isAdmin && !user.isModerator) {
        setShowTutorial(true);
      }
    }).catch(() => {
      showUnauthenticated();
    });
  }, []);

  // Prune old localStorage data on app load to prevent unbounded growth
  useEffect(() => {
    pruneOldLocalStorageData();
  }, []);

  useEffect(() => {
    if (!authToken) { stopTokenRefresh(); return; }
    // Keep the session alive by silently refreshing the JWT before it expires
    startTokenRefresh((newToken) => setAuthToken(newToken));
    // Hydrate Circle profile from localStorage cache first so a flaky
    // network or transient auth blip doesn't drop the user back to the
    // Community Agreement gate. We always refetch from the API below;
    // a successful response overwrites the cache, but a failure keeps
    // the cached profile in place.
    const CIRCLE_PROFILE_CACHE_KEY = 'cw_circle_profile_cache';
    try {
      const cachedRaw = localStorage.getItem(CIRCLE_PROFILE_CACHE_KEY);
      if (cachedRaw) {
        const cachedProfile = JSON.parse(cachedRaw);
        if (cachedProfile && typeof cachedProfile.nickname === 'string') {
          setCircleProfile(cachedProfile);
        }
      }
    } catch { /* corrupt cache — ignore */ }
    apiGet<{ profile: { nickname: string; bio: string | null } | null }>('/circle/profile', authToken).then(res => {
      const fresh = res?.profile ?? null;
      setCircleProfile(fresh);
      try {
        if (fresh) localStorage.setItem(CIRCLE_PROFILE_CACHE_KEY, JSON.stringify(fresh));
        else localStorage.removeItem(CIRCLE_PROFILE_CACHE_KEY);
      } catch { /* localStorage may be full or unavailable */ }
    }).catch(() => { /* keep cached profile in state — do not wipe on transient failure */ });
    apiGet<{ disclosure: { ageBracket: string | null; scdType: string | null; customTags: string[] } | null }>('/circle/disclosure', authToken).then(res => {
      setCircleDisclosure(res?.disclosure ?? null);
    }).catch(() => { /* disclosure fetch — non-critical */ });

    // Sync XP from API — merge with localStorage (take the higher value)
    apiGet<{ totalXp: number; rewardedEvents: string[] }>('/xp', authToken).then(res => {
      if (!res) return;
      if (res.rewardedEvents && res.rewardedEvents.length > 0) {
        for (const e of res.rewardedEvents) rewardedEventsRef.current.add(e);
        try { localStorage.setItem(SK_XP_REWARDED, JSON.stringify([...rewardedEventsRef.current])); } catch { /* localStorage may be unavailable */ }
      }
      // Read local XP before the async setState so we compare against a consistent value
      const localXp = parseInt(localStorage.getItem(SK_XP) ?? '0', 10) || 0;
      const merged = Math.max(localXp, res.totalXp);
      try { localStorage.setItem(SK_XP, String(merged)); } catch { /* localStorage may be unavailable */ }
      setTotalXP(merged);
      if (localXp > res.totalXp) {
        fetch(`${API_BASE}/xp/sync`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
          credentials: 'include',
          body: JSON.stringify({ totalXp: localXp, rewardedEvents: [...rewardedEventsRef.current] }),
        }).catch(() => { /* XP sync — best effort */ });
      }
    }).catch(() => { /* XP fetch — best effort */ });

    // Sync journeys from API
    apiGet<Array<{ journeyId: string; currentPhaseId: string | null; completedResources: string[] }>>('/journeys', authToken).then(res => {
      if (!res || res.length === 0) return;
      // Merge server journeys into localStorage
      const serverActiveJourneys: Record<string, string> = {};
      const serverCompletedResources = new Set<string>();
      for (const j of res) {
        if (j.currentPhaseId) serverActiveJourneys[j.journeyId] = j.currentPhaseId;
        for (const r of (j.completedResources ?? [])) serverCompletedResources.add(r);
      }
      if (Object.keys(serverActiveJourneys).length > 0) {
        setActiveJourneys(prev => {
          const merged = { ...serverActiveJourneys, ...prev };
          localStorage.setItem(SK_ACTIVE_JOURNEYS, JSON.stringify(merged));
          return merged;
        });
      }
      if (serverCompletedResources.size > 0) {
        setCompletedJourneyResources(prev => {
          const merged = new Set([...serverCompletedResources, ...prev]);
          localStorage.setItem(SK_COMPLETED_JOURNEY_RESOURCES, JSON.stringify([...merged]));
          return merged;
        });
      }
    }).catch(() => { /* journey sync — best effort */ });
  }, [authToken]);

  const handleSplashComplete = useCallback(() => {
    localStorage.setItem(SPLASH_SEEN_KEY, 'true');
    const carouselSeen = localStorage.getItem(CAROUSEL_SEEN_KEY);
    setAuthFlow(carouselSeen ? 'login' : 'carousel');
  }, []);

  const handleCarouselComplete = useCallback(() => {
    localStorage.setItem(CAROUSEL_SEEN_KEY, 'true');
    setAuthFlow('login');
  }, []);

  function handleSignUpSuccess() {
    const token = getToken();
    if (!token) { setAuthFlow('login'); return; }
    setAuthToken(token);
    // Ensure DB is warm before fetching the new user profile.
    // Always navigate to onboarding even if fetchMe fails — the user
    // just verified their email, so the account exists.
    warmBackend()
      .then(() => fetchMe(token))
      .then(user => { if (user) setCurrentUser(user); })
      .catch(() => { /* fetchMe failed — proceed without profile */ })
      .finally(() => { setAuthFlow('onboarding'); });
  }

  function handleLoginSuccess() {
    const token = getToken();
    if (!token) { setAuthFlow('login'); return; }
    setAuthToken(token);
    // Ensure DB is warm before fetching the user profile
    warmBackend().then(() => fetchMe(token)).then(user => {
      setCurrentUser(user);
      if (!user) { setAuthFlow('login'); return; }
      const xpEarned = getTutorialXpEarned(user.id);
      setTutorialCompleted(xpEarned);
      if (!user.completedOnboarding && !user.isAdmin && !user.isModerator) { setAuthFlow('onboarding'); return; }
      setAuthFlow('mode-chooser');
      if (!getTutorialDone(user.id) && !user.isAdmin && !user.isModerator) {
        setShowTutorial(true);
      }
    }).catch(() => {
      // If fetchMe or post-login logic fails unexpectedly, send user to
      // landing rather than leaving them stuck on the login screen.
      setAuthFlow('login');
    });
  }

  function handleCodeLoginSuccess() {
    const token = getToken();
    if (!token) { setAuthFlow('login'); return; }
    setAuthToken(token);
    warmBackend().then(() => fetchMe(token)).then(user => {
      if (!user) { setAuthFlow('login'); return; }
      setCurrentUser(user);
      if (user.isAdmin || user.isModerator) {
        const xpEarned = getTutorialXpEarned(user.id);
        setTutorialCompleted(xpEarned);
        if (!user.completedOnboarding) { setAuthFlow('onboarding'); return; }
        setAuthFlow('mode-chooser');
        return;
      }
      setIsDemoUser(true);
      setAuthFlow('code-login-choice');
    });
  }

  function handlePasscodeDevMode() {
    setIsDevMode(true);
    setIsDemoUser(true);
    // Create a minimal dev user profile so the app works without a real login
    if (!currentUser) {
      setCurrentUser({
        id: 0,
        email: 'dev@careweave.local',
        displayName: isDemoVisit() ? 'Demo User' : 'Dev User',
        pronouns: null,
        ageRange: null,
        birthYear: null,
        province: null,
        scdType: null,
        diagnosisYear: null,
        careCenter: null,
        carePreferences: null,
        avatarId: null,
        avatarEmoji: null,
        avatarCustomisation: null,
        guideBondProgress: null,
        customChips: null,
        hobbies: null,
        specialInterests: null,
        healthTeamNote: null,
        completedOnboarding: true,
        identities: [],
      });
    }
    setAuthFlow('app');
  }

  const [tutorialCompleted, setTutorialCompleted] = useState(false);

  function getTutorialDone(userId?: string | number | null) {
    const key = getTutorialDoneKey(userId);
    return localStorage.getItem(key) === 'true';
  }

  function getTutorialXpEarned(userId?: string | number | null) {
    return localStorage.getItem(getTutorialXpKey(userId)) === 'true';
  }

  function shouldShowTutorial(userId?: string | number | null) {
    return !getTutorialDone(userId);
  }

  function markTutorialSeen(userId?: string | number | null) {
    const key = getTutorialDoneKey(userId);
    const versionKey = getTutorialVersionKey(userId);
    localStorage.setItem(key, 'true');
    localStorage.setItem(versionKey, TOUR_VERSION);
  }

  function markTutorialXpEarned(userId?: string | number | null) {
    localStorage.setItem(getTutorialXpKey(userId), 'true');
    setTutorialCompleted(true);
  }

  function handleOnboardingComplete(profile: Partial<UserProfile>) {
    setCurrentUser(prev => prev ? { ...prev, ...profile } : null);
    if (shouldShowTutorial(currentUser?.id)) {
      setShowTutorial(true);
    }
    // If currentUser was never set (e.g. fetchMe failed after signup 2FA),
    // fetch the full user before entering the app to avoid null crashes.
    if (!currentUser) {
      const token = authToken || getToken();
      if (token) {
        warmBackend().then(() => fetchMe(token)).then(user => {
          if (user) {
            setCurrentUser({ ...user, ...profile });
          }
          setAuthFlow('app');
        });
        return;
      }
    }
    setAuthFlow('app');
  }

  function handleTutorialComplete(xpAwarded: number) {
    markTutorialSeen(currentUser?.id);
    setShowTutorial(false);
    if (!tutorialCompleted && xpAwarded > 0) {
      markTutorialXpEarned(currentUser?.id);
      earnXP(xpAwarded);
    }
  }

  function handleTutorialSkip() {
    markTutorialSeen(currentUser?.id);
    setShowTutorial(false);
  }

  function handleReplayTour() {
    setShowTutorial(true);
  }

  const [screen, setScreen] = useState<Screen>('home');
  const [articlesBackTarget, setArticlesBackTarget] = useState<Screen>('home');
  const [weatherBackTarget, setWeatherBackTarget] = useState<Screen>('home');

  useEffect(() => {
    function handleModeratorRoute() {
      const path = window.location.pathname;
      const isModeratorPath = path.endsWith('/moderator') ||
        window.location.hash === '#/moderator';
      if (isModeratorPath) {
        if (authFlow === 'loading') return;
        if (authFlow !== 'app' || !currentUser) {
          window.history.replaceState(null, '', '/home');
          return;
        }
        if (currentUser.isAdmin || currentUser.isModerator) {
          window.history.replaceState(null, '', '/moderator');
          setScreen('moderator');
        } else {
          window.history.replaceState(null, '', '/home');
          setScreen('home');
        }
        return;
      }
      // Canonicalize app home to /home only once the user is in the app.
      // Pre-app states (splash, access gate, auth) keep their own URLs.
      if (authFlow !== 'app' || !currentUser) return;
      // Once in-app, leftover /auth/* URLs (e.g. /auth/dev-code) should
      // canonicalise to /home too. /admin and /api stay reserved.
      const isReservedPath = path.startsWith('/admin') ||
        path.startsWith('/api');
      if (!isReservedPath && path !== '/home') {
        window.history.replaceState(null, '', '/home');
      }
    }
    handleModeratorRoute();
    window.addEventListener('popstate', handleModeratorRoute);
    window.addEventListener('hashchange', handleModeratorRoute);
    return () => {
      window.removeEventListener('popstate', handleModeratorRoute);
      window.removeEventListener('hashchange', handleModeratorRoute);
    };
  }, [authFlow, currentUser]);

  useEffect(() => {
    if (screen === 'peer-connect') {
      loadCirclePosts();
      loadCircleReactions();
      if (circleProfile === null) {
        setShowCircleWelcome(true);
      }
    }
  }, [screen, circleProfile]);

  const [activeModal, setActiveModal] = useState<ModalId>(null);
  const [activeAchievement, setActiveAchievement] = useState<typeof ACHIEVEMENTS[number] | null>(null);
  const [identityPickerDraft, setIdentityPickerDraft] = useState<string[]>([]);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editProfileName, setEditProfileName] = useState('');
  const [editProfilePronouns, setEditProfilePronouns] = useState('');
  const [editProfileCareCenter, setEditProfileCareCenter] = useState('');
  const [editProfileSaving, setEditProfileSaving] = useState(false);

  const { goals: _rawGoals, activeGoals: _rawActiveGoals, completedGoals: _rawCompletedGoals, adoptGoal, addCustomGoal, toggleStep, markXPAwarded, totalAdopted: _rawTotalAdopted, totalCompleted: _rawTotalCompleted } = useGoals(authToken);
  const [exploreTheme, setExploreTheme] = useState<GoalTheme | null>(null);
  const [growthExploreOpen, setGrowthExploreOpen] = useState(false);
  const [writeOwnTitle, setWriteOwnTitle] = useState('');
  const [writeOwnDescription, setWriteOwnDescription] = useState('');
  const [writeOwnOpen, setWriteOwnOpen] = useState(false);

  const [customGoalTitle, setCustomGoalTitle] = useState('');
  const [customGoalDescription, setCustomGoalDescription] = useState('');
  const [customGoalSteps, setCustomGoalSteps] = useState<string[]>(['', '', '']);
  const [smartGuideStep, setSmartGuideStep] = useState<number>(0); // 0=overview, 1=title, 2=steps, 3=review
  const [goalOfMomentDismissed, setGoalOfMomentDismissed] = useState(false);
  const [patternsPeriod, setPatternsPeriod] = useState<number>(7);
  const [patternsOffset, setPatternsOffset] = useState<number>(0);
  const [patternsSelectedDay, setPatternsSelectedDay] = useState<string | null>(null);
  const [patternsSelectedArea, setPatternsSelectedArea] = useState<string | null>(null);
  const [patternsChartMode, setPatternsChartMode] = useState<'summary' | 'graph'>('graph');
  const [patternsHoveredPoint, setPatternsHoveredPoint] = useState<{ key: string; dateStr: string; value: number; x: number; y: number } | null>(null);

  // Hospital Prep Kit state
  type PrepTab = 'profile' | 'scripts' | 'admission' | 'rights' | 'contacts';
  const [prepTab, setPrepTab] = useState<PrepTab>('profile');
  const [prepEditingProfile, setPrepEditingProfile] = useState(false);
  const [_rawPrepProfile, setPrepProfile] = useState<{
    genotype: string; bloodType: string; healthCard: string;
    allergies: string; triggers: string; baselinePain: string;
    preferredHospital: string; specialInstructions: string;
    physicianName: string; physicianPhone: string;
  }>(() => {
    try { return JSON.parse(localStorage.getItem(SK_PREP_PROFILE) || 'null') || { genotype: '', bloodType: '', healthCard: '', allergies: '', triggers: '', baselinePain: '', preferredHospital: 'SickKids', specialInstructions: '', physicianName: '', physicianPhone: '' }; } catch { /* malformed data */ return { genotype: '', bloodType: '', healthCard: '', allergies: '', triggers: '', baselinePain: '', preferredHospital: 'SickKids', specialInstructions: '', physicianName: '', physicianPhone: '' }; }
  });
  const [_rawPrepContacts, setPrepContacts] = useState<Array<{ id: string; name: string; role: string; phone: string }>>(() => {
    try { return JSON.parse(localStorage.getItem(SK_PREP_CONTACTS) || '[]'); } catch { /* malformed data */ return []; }
  });
  const [prepContactForm, setPrepContactForm] = useState<{ name: string; role: string; phone: string } | null>(null);
  const [prepPackChecked, setPrepPackChecked] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(SK_PREP_PACK) || '[]')); } catch { /* malformed data */ return new Set(); }
  });
  const [prepQuestionsExpanded, setPrepQuestionsExpanded] = useState<Set<number>>(new Set());
  const [prepScriptCopied, setPrepScriptCopied] = useState<string | null>(null);

  // Crisis Plan Builder state
  const [crisisPlanExpanded, setCrisisPlanExpanded] = useState<string | null>('moderate');
  const [crisisPlanChecked, setCrisisPlanChecked] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem(SK_CRISIS_CHECKED) || '{}'); } catch { /* malformed data */ return {}; }
  });
  const [crisisPlanEditMode, setCrisisPlanEditMode] = useState(false);

  interface CrisisPlanStageData { id: string; actions: string[]; medications: string; contact: string; }

  const SCD_CRISIS_DEFAULTS: CrisisPlanStageData[] = [
    { id: 'early', actions: ['Take Ibuprofen 400mg + Tylenol 500mg', 'Apply heat pack to affected area', 'Drink 500ml water immediately', 'Log the start time in CareWeave'], medications: 'Ibuprofen 400mg · Tylenol 500mg', contact: '' },
    { id: 'moderate', actions: ['Take Hydromorphone 2mg if prescribed', 'Call your primary support — ask them to come', 'Prepare your bag (ID, phone charger, hospital prep kit)', 'Monitor pain every 30 min'], medications: 'Hydromorphone 2mg oral · Continue heat', contact: 'My primary support person' },
    { id: 'severe', actions: ['Do NOT drive yourself — call someone or Uber', "Tell the driver you're going to the hospital — not optional", 'Show your hospital prep profile at triage', 'Say: "I have sickle cell. This is a vaso-occlusive crisis."'], medications: 'IV pain meds at hospital — do not delay going', contact: 'Your hematologist or on-call line' },
    { id: 'emergency', actions: ['Call 911 immediately', 'Tell dispatcher: "Sickle cell disease — possible ACS or stroke"', 'Unlock front door if home alone', 'Send your location to your emergency contact'], medications: 'Emergency responders will manage — do not delay', contact: '911 — then your emergency contact' },
  ];

  const [_rawCrisisPlanStages, setCrisisPlanStages] = useState<CrisisPlanStageData[]>(() => {
    try {
      const saved = localStorage.getItem(SK_CRISIS_PLAN_STAGES);
      if (saved) return JSON.parse(saved) as CrisisPlanStageData[];
    } catch { /* localStorage parse failure — use defaults */ }
    return SCD_CRISIS_DEFAULTS;
  });

  // Tool starred & access tracking state
  const [starredTools, setStarredTools] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(SK_STARRED_TOOLS) || '[]'); } catch { /* malformed data */ return []; }
  });
  const [toolAccessCounts, setToolAccessCounts] = useState<Record<string, number>>(() => {
    try { return JSON.parse(localStorage.getItem(SK_TOOL_ACCESS_COUNTS) || '{}'); } catch { /* malformed data */ return {}; }
  });

  function toggleStarredTool(label: string) {
    setStarredTools(prev => {
      const next = prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label];
      try { localStorage.setItem(SK_STARRED_TOOLS, JSON.stringify(next)); } catch { /* localStorage may be unavailable */ }
      return next;
    });
  }

  function recordToolAccess(label: string) {
    setToolAccessCounts(prev => {
      const next = { ...prev, [label]: (prev[label] ?? 0) + 1 };
      try { localStorage.setItem(SK_TOOL_ACCESS_COUNTS, JSON.stringify(next)); } catch { /* localStorage may be unavailable */ }
      return next;
    });
  }

  // Care Team Hub state
  const [careTeamView, setCareTeamView] = useState<'team' | 'summary'>('team');
  const {
    careTeamExpanded, setCareTeamExpanded,
    careTeamDownloading, setCareTeamDownloading,
    careTeamMembers, setCareTeamMembers,
    careTeamLoading,
    showCareTeamForm, setShowCareTeamForm,
    careTeamFormMode,
    careTeamEditTarget,
    careTeamFormName, setCareTeamFormName,
    careTeamFormRole, setCareTeamFormRole,
    careTeamFormOrg, setCareTeamFormOrg,
    careTeamFormPhone, setCareTeamFormPhone,
    careTeamFormNotes, setCareTeamFormNotes,
    careTeamFormAppt, setCareTeamFormAppt,
    careTeamFormEmoji, setCareTeamFormEmoji,
    careTeamFormColor, setCareTeamFormColor,
    careTeamFormMemberType, setCareTeamFormMemberType,
    careTeamFormEmail, setCareTeamFormEmail,
    careTeamFormSaving,
    loadCareTeam,
    saveCareTeamMember,
    deleteCareTeamMember,
    resetCareTeamForm,
    openCareTeamEditForm,
  } = useCareTeam(authToken);

  // Support Network state (extracted to useSupportNetwork hook)
  const {
    networkView, setNetworkView,
    networkSelectedId, setNetworkSelectedId,
    networkMembers, setNetworkMembers,
    networkLoading,
    showNetworkForm, setShowNetworkForm,
    networkFormMode, setNetworkFormMode,
    networkEditTarget,
    networkFormName, setNetworkFormName,
    networkFormRelationship, setNetworkFormRelationship,
    networkFormRing, setNetworkFormRing,
    networkFormPhone, setNetworkFormPhone,
    networkFormNotes, setNetworkFormNotes,
    networkFormEmoji, setNetworkFormEmoji,
    networkFormColor, setNetworkFormColor,
    networkFormSaving,
    loadSupportNetwork,
    saveSupportNetworkMember,
    deleteSupportNetworkMember,
    resetNetworkForm,
    openNetworkEditForm,
  } = useSupportNetwork(authToken);

  // Journey state
  const [_rawActiveJourneys, setActiveJourneys] = useState<Record<string, string>>(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(SK_ACTIVE_JOURNEYS) || '{}');
      if (raw['hospital-episode']) {
        const oldPhase = raw['hospital-episode'];
        delete raw['hospital-episode'];
        const phaseMap: Record<string, [string, string]> = {
          before: ['emergency-room', 'before'],
          ed: ['emergency-room', 'triage'],
          ward: ['hospital-admission', 'admitted'],
          discharge: ['hospital-admission', 'preparing-to-leave'],
          recovery: ['emergency-room', 'recovery'],
        };
        const mapped = phaseMap[oldPhase];
        if (mapped) raw[mapped[0]] = mapped[1];
        localStorage.setItem(SK_ACTIVE_JOURNEYS, JSON.stringify(raw));
      }
      return raw;
    } catch { /* malformed data */ return {}; }
  });
  const [journeyActivatePrompt, setJourneyActivatePrompt] = useState<{ id: string; title: string; firstPhase: string } | null>(null);
  const [journeyGroupExpanded, setJourneyGroupExpanded] = useState<string | null>(null);
  const [expandedJourneyPhaseResources, setExpandedJourneyPhaseResources] = useState<string | null>(null);
  const [_rawCompletedJourneyResources, setCompletedJourneyResources] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(SK_COMPLETED_JOURNEY_RESOURCES) || '[]')); } catch { /* malformed data */ return new Set(); }
  });
  const toggleJourneyResource = (id: string) => {
    setCompletedJourneyResources(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); earnXP(10); }
      const arr = [...next];
      localStorage.setItem(SK_COMPLETED_JOURNEY_RESOURCES, JSON.stringify(arr));
      // Persist to API — find which journey this resource belongs to and sync
      if (authToken) {
        for (const [journeyId] of Object.entries(activeJourneys)) {
          fetch(`${API_BASE}/journeys/${encodeURIComponent(journeyId)}/resources`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
            body: JSON.stringify({ completedResources: arr }),
          }).catch(() => { toast.error("Couldn't save your journey progress. We'll try again next time."); });
          break; // sync once — resources are stored globally
        }
      }
      return next;
    });
  };
  const [growthTab, setGrowthTab] = useState<'goals' | 'achievements' | 'community' | 'journeys'>('goals');
  const [activeScene, setActiveScene] = useState<{ journeyId: string; phaseId: string; phaseLabel: string; journeyColor: string } | null>(null);

  useEffect(() => {
    if (screen === 'growth' && growthTab === 'journeys') {
      const timer = setTimeout(() => {
        journeysSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, SCROLL_DELAY_MS);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [screen, growthTab]);

  const activateJourney = (id: string, firstPhase: string) => {
    const next = { ...activeJourneys, [id]: firstPhase };
    setActiveJourneys(next);
    localStorage.setItem(SK_ACTIVE_JOURNEYS, JSON.stringify(next));
    setJourneyActivatePrompt(null);
    earnXP(30);
    // Persist to API
    if (authToken) {
      fetch(`${API_BASE}/journeys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ journeyId: id, currentPhaseId: firstPhase }),
      }).catch(() => { toast.error("Couldn't activate the journey just now. Try again."); });
    }
  };
  const advanceJourneyPhase = (id: string, phaseId: string, isLast: boolean) => {
    const next = { ...activeJourneys, [id]: phaseId };
    setActiveJourneys(next);
    localStorage.setItem(SK_ACTIVE_JOURNEYS, JSON.stringify(next));
    earnXP(isLast ? 150 : 50);
    // Persist to API
    if (authToken) {
      fetch(`${API_BASE}/journeys/${encodeURIComponent(id)}/phase`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ currentPhaseId: phaseId, completedAt: isLast ? new Date().toISOString() : null }),
      }).catch(() => { toast.error("Couldn't save your journey progress. We'll try again next time."); });
    }
  };
  const deactivateJourney = (id: string) => {
    const next = { ...activeJourneys };
    delete next[id];
    setActiveJourneys(next);
    localStorage.setItem(SK_ACTIVE_JOURNEYS, JSON.stringify(next));
    if (id === 'emergency-room') {
      stopEDTimer();
      endEDVisit();
      clearEDCheckins();
      setEdModeState('off');
    }
    // Persist to API
    if (authToken) {
      fetch(`${API_BASE}/journeys/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` },
      }).catch(() => { toast.error("Couldn't remove the journey. Try again."); });
    }
  };

  // Weather Report state
  const [flareLocation, setFlareLocation] = useState<{ lat: number; lon: number; name: string } | null>(() => {
    try {
      const saved = localStorage.getItem(SK_FLARE_LOCATION);
      if (saved) return JSON.parse(saved);
    } catch { /* localStorage may be unavailable */ }
    return null;
  });
  const [flareLocationStatus, setFlareLocationStatus] = useState<'idle' | 'requesting' | 'granted' | 'denied'>(() => {
    try {
      const saved = localStorage.getItem(SK_FLARE_LOCATION);
      if (saved) return 'granted';
    } catch { /* localStorage may be unavailable */ }
    return 'idle';
  });
  const [flareWeather, setFlareWeather] = useState<{
    daily: {
      time: string[];
      temperature_2m_max: number[];
      temperature_2m_min: number[];
      precipitation_probability_max: number[];
      windspeed_10m_max: number[];
      weathercode: number[];
    };
  } | null>(null);
  const [flareWeatherLoading, setFlareWeatherLoading] = useState(false);
  const [flareTab, setFlareTab] = useState<'calendar' | 'insights'>('calendar');
  const [flareSelectedDay, setFlareSelectedDay] = useState<number | null>(null);
  const [flareBackTarget, setFlareBackTarget] = useState<string>('home');
  const [goalNudgeDismissed, setGoalNudgeDismissed] = useState(false);
  const [goalNudgeTemplate, setGoalNudgeTemplate] = useState<GoalTemplate | null>(null);

  // ED mode state is now in useEDMode hook (initialized below after medications/earnXP)
  // Daily check-in state is now in useCheckin hook (initialized below after earnXP)

  // Legacy event-log step state removed 2026-05-14 — replaced by useStigmaTracker hook (Phase 3 redesign).

  const containerRef = useRef<HTMLElement>(null);
  // Ref-stable wrapper so hooks called before showScreen is defined can still navigate.
  // The ref is assigned once showScreen is available (below); event handlers always see the latest.
  const showScreenRef = useRef<(s: Screen) => void>(s => setScreen(s));
  const journeysSectionRef = useRef<HTMLDivElement>(null);

  // ED checkin flow state is now in useEDMode hook

  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const { darkMode, toggleDarkMode, accessibilityPrefs, setAccessibilityPrefs, handleAccessibilityChange } = useAccessibility();
  const { isOnline, pendingCount } = useOnlineStatus();

  const { notifications, badgeCount, reminderTime, fetchHistory, markAllSeen, saveReminderTime } = useNotifications();

  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [faqPanelOpen, setFaqPanelOpen] = useState(false);
  const [contactSupportPanelOpen, setContactSupportPanelOpen] = useState(false);
  const [settingsPanelOpen, setSettingsPanelOpen] = useState(false);
  const {
    dashboardEditMode, setDashboardEditMode,
    widgetOrder, widgetVisible, setWidgetVisible,
    dragWidgetId, dragOverWidgetId, widgetRefs,
    saveWidgetLayout, toggleWidgetVisible,
    handleDragStart, handleDragOver, handleDrop, handleDragEnd,
    handleTouchStart, handleTouchMove, handleTouchEnd,
  } = useWidgetLayout();

  // Global Escape key handler for all modals and overlays
  useEffect(() => {
    function handleEscapeKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      // Close modals in priority order (highest z-index first)
      if (showGifPicker) { setShowGifPicker(false); return; }
      if (circleMediaConsentModal) { setCircleMediaConsentModal(false); setCircleMediaConsentPendingAction(null); return; }
      if (showCircleEmojiPicker) { setShowCircleEmojiPicker(false); return; }
      if (circleThreadPost) { setCircleThreadPost(null); return; }
      if (peerRemovalModal) { setPeerRemovalModal(null); setPeerRemovalReason(null); setPeerRemovalConfirmed(false); return; }
      if (peerPostModal) { setPeerPostModal(false); setShowGifPicker(false); return; }
      if (circleSetupModal) { setCircleSetupModal(false); return; }
      if (journeyActivatePrompt) { setJourneyActivatePrompt(null); return; }
      if (activeModal) { closeModal(); return; }
      if (menuOpen) { setMenuOpen(false); return; }
    }
    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [activeModal, menuOpen, circleSetupModal, circleMediaConsentModal, showGifPicker, peerPostModal, peerRemovalModal, circleThreadPost, showCircleEmojiPicker, journeyActivatePrompt]);

  const [fabTucked, setFabTucked] = useState(false);
  const fabTuckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Legacy event-log measurement sliders + per-step state removed 2026-05-14 —
  // replaced by useStigmaTracker hook (Phase 3 redesign).
  const [youthSubmissionText, setYouthSubmissionText] = useState('');
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  // copySuccess and pdfLoading are now in useEDMode hook

  // edStage and checkinRecommendations are now in useEDMode hook
  const [activeContentItem, setActiveContentItem] = useState<EDContentItem | null>(null);
  const [expandedArticleSections, setExpandedArticleSections] = useState<Set<string>>(
    () => new Set<string>()
  );
  const [expandedArticleTopic, setExpandedArticleTopic] = useState<string | null>(null);
  const [articleScrollY, setArticleScrollY] = useState(0);
  const [historyFilter, setHistoryFilter] = useState<'all' | 'checkin' | 'ed-checkin' | 'event' | 'med-log'>('all');
  const [historyCalCollapsed, setHistoryCalCollapsed] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState<Set<string>>(new Set());
  const [historyMonthView, setHistoryMonthView] = useState<[number, number]>(() => {
    const n = new Date(); return [n.getFullYear(), n.getMonth()];
  });
  const [selectedHistoryDay, setSelectedHistoryDay] = useState<string | null>(null);
  const [hidePatterns, setHidePatterns] = useState(() => localStorage.getItem(SK_HIDE_PATTERNS) === 'true');
  function toggleHidePatterns() {
    const newVal = !hidePatterns;
    setHidePatterns(newVal);
    localStorage.setItem(SK_HIDE_PATTERNS, String(newVal));
  }

  // darkMode and toggleDarkMode now provided by useAccessibility hook

  // ── Enabled check-in questions (lifted here to break useEssentials ↔ useCheckin circular dep) ──
  const [enabledCheckinQuestions, setEnabledCheckinQuestions] = useState<Set<number>>(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(SK_ENABLED_CHECKIN_QUESTIONS) || '[1,2,3,4,5,6,7,8,9,10]');
      const s = new Set<number>(stored);
      for (let i = 1; i <= 10; i++) if (!s.has(i)) s.add(i);
      for (const v of s) if (v > 10) s.delete(v);
      return s;
    } catch { return new Set([1,2,3,4,5,6,7,8,9,10]); }
  });

  // ── Essentials Mode (ADHD / neurodivergent-friendly) ──
  const { essentialsMode, essentialsTools, toggleEssentialsMode, updateEssentialsTools } = useEssentials({
    setWidgetVisible,
    saveWidgetLayout,
    widgetOrder,
    setEnabledCheckinQuestions,
    accessibilityPrefs,
    handleAccessibilityChange,
  });

  const [demoProfilesOn, setDemoProfilesOn] = useState(isDemoProfilesEnabled);
  const demoActive = isDevMode && demoProfilesOn;
  function toggleDemoProfiles(on?: boolean) {
    const next = on ?? !demoProfilesOn;
    setDemoProfilesOn(next);
    setDemoProfilesEnabled(next);
  }

  const { getStatus, markViewed, cycleStatus, markCompleted, progress } = useResourceProgress();
  const { getMeta, toggleStar, recordOpen } = useResourceMeta();

  const {
    rawTotalXP: _rawTotalXP, setTotalXP,
    xpFlash, setXpFlash,
    xpLevelUpCelebration, setXpLevelUpCelebration,
    rewardedEventsRef,
    earnXP, earnXPOnce,
  } = useXP(authToken);

  // ── Check-in state (extracted to useCheckin hook) ─────────────────
  const {
    checkinStep, setCheckinStep,
    checkinDone, setCheckinDone,
    watersCheck, setWatersCheck,
    selectedBodySensations, setSelectedBodySensations,
    stressValue, setStressValue,
    selectedStressDescriptors, setSelectedStressDescriptors,
    selectedFeelings, setSelectedFeelings,
    fatigueValue, setFatigueValue,
    selectedFatigueType, setSelectedFatigueType,
    worryValue, setWorryValue,
    selectedWorryChips, setSelectedWorryChips,
    feelingBlueValue, setFeelingBlueValue,
    selectedBlueChips, setSelectedBlueChips,
    selectedSleepChips, setSelectedSleepChips,
    selectedMoodChip, setSelectedMoodChip,
    customCheckinChips, setCustomCheckinChips,
    skippedCheckinSteps, setSkippedCheckinSteps,
    showCrisisReturnNudge, setShowCrisisReturnNudge,
    showAnchorNudge, setShowAnchorNudge,
    checkinSummary, setCheckinSummary,
    guideAffirmation, setGuideAffirmation,
    painValue, setPainValue,
    moodValue, setMoodValue,
    energyValue, setEnergyValue,
    checkinPainAreas, setCheckinPainAreas,
    bodySpotWords, setBodySpotWords,
    bodySpotFreeText, setBodySpotFreeText,
    activeBodySpot, setActiveBodySpot,
    painMapResult, setPainMapResult,
    emotionMapResult, setEmotionMapResult,
    mindBodyStressValue, setMindBodyStressValue,
    groundingShown, setGroundingShown,
    mindBodyChips, setMindBodyChips,
    sleepFatigueValue, setSleepFatigueValue,
    selectedCopingStrategies, setSelectedCopingStrategies,
    customCopingStrategies, setCustomCopingStrategies,
    customCopingInput, setCustomCopingInput,
    showCustomCopingInput, setShowCustomCopingInput,
    selectedTreatment, setSelectedTreatment,
    selectedProtectiveFactors, setSelectedProtectiveFactors,
    checkinIsVOC, setCheckinIsVOC,
    checkinEntries, setCheckinEntries,
    savedCheckins, setSavedCheckins,
    lastCrisisDate, setLastCrisisDate,
    totalCheckins, setTotalCheckins,
    streak,
    showCheckinIntro, setShowCheckinIntro,
    showEDCheckinIntro, setShowEDCheckinIntro,
    checkinDraftActive, setCheckinDraftActive,
    checkinReturnScreen, setCheckinReturnScreen,
    checkinScrollTop, setCheckinScrollTop,
    checkinScrollBottom, setCheckinScrollBottom,
    checkinScreenRef,
    toggleCheckinQuestion,
    bodyMapMetrics,
    loadCheckins,
    saveCheckinDraft,
    clearCheckinDraft,
    exitCheckinWithSave,
    skipCheckinStep,
    nextCheckinStep,
    prevCheckinStep,
    toggleCopingStrategy,
    toggleProtectiveFactor,
    doStartCheckin,
    startCheckin,
    submitCheckin,
    handleOpenCheckin,
  } = useCheckin({
    authToken,
    currentUser,
    setCurrentUser,
    earnXP,
    earnXPOnce,
    showScreen: showScreenRef.current,
    screen,
    essentialsMode,
    demoActive,
    containerRef,
    enabledCheckinQuestions,
    setEnabledCheckinQuestions,
  });

  const [_rawEventLogEntries, setEventLogEntries] = useState<Array<{ timestamp: string; location: string; personInvolved: string[]; experienceTags: string[]; protectiveFactors: string[] }>>(() => {
    try { return JSON.parse(localStorage.getItem(SK_EVENT_LOGS) || '[]'); } catch { /* malformed data */ return []; }
  });
  const [articlesVisited, setArticlesVisited] = useState(false);

  const [_rawMedications, setMedications] = useState<Medication[]>(() => {
    try { return JSON.parse(localStorage.getItem(SK_MEDICATIONS) || '[]'); } catch { /* malformed data */ return []; }
  });
  const [_rawMedicationLogs, setMedicationLogs] = useState<MedicationLog[]>(() => {
    try {
      const raw: MedicationLog[] = JSON.parse(localStorage.getItem(SK_MED_LOGS) || '[]');
      return raw.map(l => ({ ...l, slotIndex: l.slotIndex ?? 0 }));
    } catch { /* malformed data */ return []; }
  });
  // ── Demo data merge layer ──────────────────────────────────────────
  // When demo toggle is on, merge generated data with real data in memory.
  // checkinEntries demo merge is now in useCheckin hook
  const medications = useMemo(() => {
    if (!demoActive) return _rawMedications;
    const demo = getDemoMedications();
    return [..._rawMedications, ...demo.medications];
  }, [_rawMedications, demoActive]);
  const medicationLogs = useMemo(() => {
    if (!demoActive) return _rawMedicationLogs;
    const demo = getDemoMedications();
    return [..._rawMedicationLogs, ...demo.logs];
  }, [_rawMedicationLogs, demoActive]);

  // ED (Emergency Department) mode hook
  const {
    edModeState, setEdModeState,
    edCheckins, setEdCheckins,
    currentEDVisitId,
    edVisitHistory, setEdVisitHistory,
    edScript, setEdScript,
    edReportNote, setEdReportNote,
    copySuccess,
    pdfLoading,
    edCheckinStep, setEdCheckinStep,
    edVocStatus, setEdVocStatus,
    edPainLevel, setEdPainLevel,
    edBodyAreas, setEdBodyAreas,
    edSafetyFlags, setEdSafetyFlags,
    edCheckinVisible, setEdCheckinVisible,
    edCheckinComplete, setEdCheckinComplete,
    edCheckinSummary, setEdCheckinSummary,
    edStage, setEdStage,
    checkinRecommendations, setCheckinRecommendations,
    loadEDHistory,
    clearEDCheckins,
    endEDVisit,
    stopEDTimer,
    cycleEDMode,
    toggleEdBodyArea,
    toggleSafetyFlag,
    submitEDCheckin,
    copyEDReport,
    downloadEDReportPDF,
    copyScript,
  } = useEDMode({ authToken, medications, earnXP });

  const stigmaTracker = useStigmaTracker(demoActive);

  const eventLogEntries = useMemo(
    () => demoActive ? [..._rawEventLogEntries, ...getDemoEventLogs()] : _rawEventLogEntries,
    [_rawEventLogEntries, demoActive],
  );
  const totalXP = useMemo(
    () => demoActive ? _rawTotalXP + getDemoXP() : _rawTotalXP,
    [_rawTotalXP, demoActive],
  );
  const activeJourneys = useMemo(
    () => demoActive ? { ...getDemoJourneys(), ..._rawActiveJourneys } : _rawActiveJourneys,
    [_rawActiveJourneys, demoActive],
  );
  const completedJourneyResources = useMemo(() => {
    if (!demoActive) return _rawCompletedJourneyResources;
    const merged = new Set(_rawCompletedJourneyResources);
    for (const r of getDemoCompletedResources()) merged.add(r);
    return merged;
  }, [_rawCompletedJourneyResources, demoActive]);
  const prepProfile = useMemo(() => {
    if (!demoActive) return _rawPrepProfile;
    const hasRealData = _rawPrepProfile.genotype || _rawPrepProfile.allergies || _rawPrepProfile.triggers;
    return hasRealData ? _rawPrepProfile : getDemoPrepProfile();
  }, [_rawPrepProfile, demoActive]);
  const prepContacts = useMemo(
    () => demoActive && _rawPrepContacts.length === 0 ? getDemoPrepContacts() : _rawPrepContacts,
    [_rawPrepContacts, demoActive],
  );
  const crisisPlanStages = useMemo(() => {
    if (!demoActive) return _rawCrisisPlanStages;
    // Only use demo plan if user hasn't customised theirs
    const isDefault = _rawCrisisPlanStages[0]?.actions[1] === 'Apply heat pack to affected area';
    return isDefault ? getDemoCrisisPlan() : _rawCrisisPlanStages;
  }, [_rawCrisisPlanStages, demoActive]);
  const goals = useMemo(
    () => demoActive ? [..._rawGoals, ...getDemoGoals()] : _rawGoals,
    [_rawGoals, demoActive],
  );
  const activeGoals = useMemo(() => goals.filter(g => !g.completedAt), [goals]);
  const completedGoals = useMemo(() => goals.filter(g => !!g.completedAt), [goals]);
  const totalAdopted = goals.length;
  const totalCompleted = completedGoals.length;
  // ── End demo merge layer ──────────────────────────────────────────
  const eventsLogged = eventLogEntries.length;
  // streak is now provided by useCheckin hook

  const [medFormName, setMedFormName] = useState('');
  const [medFormDose, setMedFormDose] = useState('');
  const [medFormUnit, setMedFormUnit] = useState('mg');
  const [medFormFrequency, setMedFormFrequency] = useState('');
  const [medFormNotes, setMedFormNotes] = useState('');
  const [editingMed, setEditingMed] = useState<Medication | null>(null);
  const [medNameDropdownOpen, setMedNameDropdownOpen] = useState(false);

  // bodyMapMetrics is now provided by useCheckin hook

  const dataLoadToken = useRef(0);
  useEffect(() => {
    if (!authToken) return;
    // Increment token so stale loads from a previous authToken cycle are ignored
    const token = ++dataLoadToken.current;
    const guard = () => dataLoadToken.current === token;
    loadEDHistory();
    loadMedications();
    loadMedicationLogs();
    if (guard()) loadCheckins();
    if (guard()) loadCareTeam();
    if (guard()) loadSupportNetwork();
    if (guard()) loadCirclePosts();
    if (guard()) loadCircleReactions();
    if (guard()) loadCrisisPlan();
  }, [authToken]);

  // Check-in scroll effect is now in useCheckin hook

  useEffect(() => {
    if (flareLocation && !flareWeather) {
      fetchFlareWeather(flareLocation.lat, flareLocation.lon);
    }
  }, []);

  async function fetchFlareWeather(lat: number, lon: number) {
    setFlareWeatherLoading(true);
    try {
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat.toFixed(4)}&longitude=${lon.toFixed(4)}&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max,windspeed_10m_max,weathercode&timezone=auto&forecast_days=5`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setFlareWeather(data);
        setFlareSelectedDay(0);
      }
    } catch { /* error handled silently */ }
    setFlareWeatherLoading(false);
  }

  function saveFlareLocation(loc: { lat: number; lon: number; name: string }) {
    try { localStorage.setItem(SK_FLARE_LOCATION, JSON.stringify(loc)); } catch { /* localStorage may be unavailable */ }
    setFlareLocation(loc);
  }

  function clearFlareLocation() {
    try { localStorage.removeItem(SK_FLARE_LOCATION); } catch { /* localStorage may be unavailable */ }
    setFlareLocation(null);
    setFlareLocationStatus('idle');
    setFlareWeather(null);
  }

  function requestFlareLocation() {
    setFlareLocationStatus('requesting');
    const fallback = () => {
      const loc = { lat: 43.6532, lon: -79.3832, name: 'Toronto, ON (default)' };
      saveFlareLocation(loc);
      setFlareLocationStatus('denied');
      fetchFlareWeather(43.6532, -79.3832);
    };
    if (!navigator.geolocation) { fallback(); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        const loc = { lat, lon, name: 'Your location' };
        saveFlareLocation(loc);
        setFlareLocationStatus('granted');
        fetchFlareWeather(lat, lon);
      },
      fallback,
      { timeout: 10000 }
    );
  }

  // loadCheckins is now in useCheckin hook

  async function loadMedications() {
    const remote = await apiGet<Medication[]>('/medications', authToken);
    if (remote) {
      setMedications(remote);
      try { localStorage.setItem(SK_MEDICATIONS, JSON.stringify(remote)); } catch { /* localStorage may be unavailable */ }
    }
  }

  async function loadMedicationLogs() {
    if (!authToken) return;
    const remote = await apiGet<MedicationLog[]>('/medication-logs', authToken);
    if (remote) {
      const normalized = remote.map(l => ({ ...l, slotIndex: l.slotIndex ?? 0 }));
      setMedicationLogs(normalized);
      try { localStorage.setItem(SK_MED_LOGS, JSON.stringify(normalized)); } catch { /* localStorage may be unavailable */ }
    }
  }

  async function loadCrisisPlan() {
    if (!authToken) return;
    try {
      const remote = await apiGet<{ stages: CrisisPlanStageData[] }>('/crisis-plan', authToken);
      if (remote?.stages && Array.isArray(remote.stages)) {
        setCrisisPlanStages(remote.stages);
        localStorage.setItem(SK_CRISIS_PLAN_STAGES, JSON.stringify(remote.stages));
      }
    } catch { /* error handled silently */ }
  }

  async function saveCrisisPlanToBackend(stages: CrisisPlanStageData[]) {
    if (!authToken) return;
    await apiPut('/crisis-plan', { stages }, authToken);
  }

  async function downloadHealthSummary(member: CareTeamMember) {
    setCareTeamDownloading(prev => ({ ...prev, [member.id]: true }));
    try {
      const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      const recentCheckins = checkinEntries.filter(e => new Date(e.timestamp).getTime() >= oneWeekAgo);
      const avgPain = avgOfField(recentCheckins, e => e.painLevel);
      const avgMood = avgOfField(recentCheckins, e => e.moodLevel);
      const avgEnergy = avgOfField(recentCheckins, e => e.energyLevel);

      const generateHealthSummaryPDF = await loadHealthSummaryPDF();
      await generateHealthSummaryPDF({
        targetMember: member,
        userName: userDisplayName,
        prepProfile,
        activeMedications: medications.filter(m => m.active),
        checkinStats: {
          avgPain,
          avgMood,
          avgEnergy,
          totalCheckins: recentCheckins.length,
        },
        allCareTeamMembers: careTeamMembers.filter(m => m.memberType !== 'administrator'),
      });
    } finally {
      setCareTeamDownloading(prev => ({ ...prev, [member.id]: false }));
    }
  }

  async function saveMedication(name: string, dose: string, frequency: string, notes: string) {
    const tempMed: Medication = { id: Date.now(), name, dose, frequency, notes: notes || null, active: true };
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
    const posted = await fetch(`${API_BASE}/medications`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ name, dose, frequency, notes: notes || undefined }),
    });
    let savedMed = tempMed;
    if (posted.ok) {
      try { savedMed = await posted.json(); } catch { /* error handled silently */ }
    }
    setMedications(prev => {
      const updated = [...prev, savedMed];
      try { localStorage.setItem(SK_MEDICATIONS, JSON.stringify(updated)); } catch { /* localStorage may be unavailable */ }
      return updated;
    });
  }

  async function updateMedication(id: number, updates: Partial<Medication>) {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
    await fetch(`${API_BASE}/medications/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(updates),
    });
    setMedications(prev => {
      const updated = prev.map(m => m.id === id ? { ...m, ...updates } : m);
      try { localStorage.setItem(SK_MEDICATIONS, JSON.stringify(updated)); } catch { /* localStorage may be unavailable */ }
      return updated;
    });
  }

  async function deleteMedication(id: number) {
    const headers: HeadersInit = {};
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
    await fetch(`${API_BASE}/medications/${id}`, { method: 'DELETE', headers });
    setMedications(prev => {
      const updated = prev.filter(m => m.id !== id);
      try { localStorage.setItem(SK_MEDICATIONS, JSON.stringify(updated)); } catch { /* localStorage may be unavailable */ }
      return updated;
    });
  }

  async function saveMedicationLog(medicationId: number, date: string, taken: boolean, slotIndex = 0) {
    await apiPost('/medication-logs', { medicationId, date, taken, slotIndex }, authToken);
    setMedicationLogs(prev => {
      const existing = prev.find(l => l.medicationId === medicationId && l.date === date && l.slotIndex === slotIndex);
      let updated: MedicationLog[];
      if (existing) {
        updated = prev.map(l => l.medicationId === medicationId && l.date === date && l.slotIndex === slotIndex ? { ...l, taken } : l);
      } else {
        updated = [...prev, { id: Date.now(), medicationId, date, taken, slotIndex }];
      }
      try { localStorage.setItem(SK_MED_LOGS, JSON.stringify(updated)); } catch { /* localStorage may be unavailable */ }
      return updated;
    });
  }

  // Accessibility class toggling is now handled by useAccessibility hook

  // ED mode functions (loadEDHistory, startEDVisit, endEDVisit, logEDCheckin,
  // clearEDCheckins, cycleEDMode, startEDTimer, stopEDTimer) are now in useEDMode hook

  const resetTuckTimer = useCallback(() => {
    setFabTucked(false);
    if (fabTuckTimer.current) clearTimeout(fabTuckTimer.current);
    fabTuckTimer.current = setTimeout(() => {
      setFabTucked(true);
    }, 4000);
  }, []);

  useEffect(() => {
    resetTuckTimer();
    return () => { if (fabTuckTimer.current) clearTimeout(fabTuckTimer.current); };
  }, []);

  // ED mode effect (clear edCheckinSummary when leaving active) is now in useEDMode hook

  function showScreen(s: Screen, opts?: { libraryTopicId?: string | null }) {
    setScreen(s);
    setQuickActionsOpen(false);
    setMenuOpen(false);
    setActiveModal(null);
    setJourneyActivatePrompt(null);
    setActiveScene(null);
    document.body.style.overflow = '';
    resetTuckTimer();
    if (s === 'circle-safety' || (s === 'peer-connect' && !safetyLoaded)) {
      if (s === 'circle-safety') setSafetyLoaded(false);
      loadSafetySettings();
    }
    if (s === 'articles') {
      setArticlesVisited(true);
      if (opts?.libraryTopicId) {
        setExpandedArticleTopic(opts.libraryTopicId);
        setExpandedArticleSections(new Set(['all-topics']));
      } else {
        setExpandedArticleTopic(null);
        const hasStarred = ED_CONTENT.some(i => getMeta(i.id).starred);
        const hasFrequent = ED_CONTENT.some(i => { const m = getMeta(i.id); return m.openCount > 0 && !m.starred; });
        const initial = new Set<string>();
        if (hasStarred) initial.add('my-tools');
        if (hasFrequent) initial.add('frequent');
        setExpandedArticleSections(initial);
      }
    }
  }
  showScreenRef.current = showScreen;

  useLayoutEffect(() => {
    containerRef.current?.scrollTo({ top: 0, behavior: 'instant' });
  }, [screen]);

  // saveCheckinDraft, clearCheckinDraft, exitCheckinWithSave, and related
  // effects (draft detection, auto-save, guide affirmation) are now in useCheckin hook

  useEffect(() => {
    if (screen !== 'articles') {
      setArticleScrollY(0);
    }
  }, [screen]);

  // computeLevel, earnXP, earnXPOnce now provided by useXP hook

  function saveCustomChipsToProfile(newChips: Record<string, string[]>) {
    if (!authToken) return;
    const merged = { ...(currentUser?.customChips ?? {}), ...newChips };
    updateProfile(authToken, { customChips: merged }).catch(() => { toast.error("Couldn't save your tags. Try again."); });
    setCurrentUser(prev => prev ? { ...prev, customChips: merged } : null);
  }

  function openModal(id: ModalId) {
    setActiveModal(id);
    document.body.style.overflow = 'hidden';
  }

  const IDENTITY_OPTIONS = [
    'Black', 'Indigenous', 'Latino/a/x', 'Asian', 'South Asian', 'Middle Eastern', 'Mixed race', 'White',
    'Woman', 'Man', 'Non-binary', 'Transgender', 'Gender non-conforming',
    'Disabled', 'Chronically ill', 'Neurodivergent', 'Deaf / Hard of hearing', 'Blind / Low vision',
    'Adolescent', 'Teen', 'Youth', 'Young adult', 'Adult',
    'LGBTQ+', 'Queer', 'Gay', 'Lesbian', 'Bisexual',
    'Immigrant', 'First-generation', 'Refugee',
    'Low income', 'Rural', 'Urban',
    'Caregiver', 'Parent', 'Single parent', 'Sibling', 'Sister', 'Brother',
    'Student', 'Healthcare worker', 'Educator', 'Service worker', 'Business professional', 'Artist', 'Athlete',
    'Muslim', 'Jewish', 'Christian', 'Hindu', 'Buddhist', 'Jain', 'Sikh', 'Atheist / Agnostic', 'Spiritual (non-religious)', 'Other faith',
  ];

  const IDENTITY_DICTIONARY: { category: string; entries: { term: string; definition: string }[] }[] = [
    {
      category: 'Race & Ethnicity',
      entries: [
        { term: 'Black', definition: 'A person of African, Afro-Caribbean, or African-American descent. In sickle cell care, Black patients often face racial bias in pain management and treatment.' },
        { term: 'Indigenous', definition: 'A person belonging to a First Nations, Native American, Aboriginal, or other Indigenous community. Indigenous patients may navigate both traditional and Western care systems.' },
        { term: 'Latino/a/x', definition: 'A person of Latin American heritage. Sickle cell disease also affects Latino/a/x communities, though awareness and resources may be limited.' },
        { term: 'Asian', definition: 'A person of East or Southeast Asian descent. Sickle cell trait and disease can appear in Asian communities, sometimes leading to delayed diagnosis.' },
        { term: 'South Asian', definition: 'A person from the Indian subcontinent (India, Pakistan, Bangladesh, Sri Lanka, etc.). Sickle cell is present in certain South Asian populations.' },
        { term: 'Middle Eastern', definition: 'A person from the Middle East or North Africa. Sickle cell disease is found in this region, particularly in Saudi Arabia and parts of the Gulf.' },
        { term: 'Mixed race', definition: 'A person who identifies with two or more racial backgrounds. Mixed-race individuals may face unique challenges in how their care needs are understood.' },
        { term: 'White', definition: 'A person of European descent. Though less common, sickle cell disease can affect White individuals, who may face disbelief or delayed diagnosis.' },
      ],
    },
    {
      category: 'Gender Identity',
      entries: [
        { term: 'Woman', definition: 'A person who identifies as female. Women with sickle cell face unique challenges around menstrual health, pregnancy, and hormonal pain patterns.' },
        { term: 'Man', definition: 'A person who identifies as male. Men with sickle cell may face stigma around showing pain and seeking help.' },
        { term: 'Non-binary', definition: 'A person whose gender identity falls outside the male/female binary. Non-binary patients may need to advocate for inclusive language in healthcare settings.' },
        { term: 'Transgender', definition: 'A person whose gender identity differs from the sex assigned at birth. Trans patients may navigate additional medical needs and potential bias in care.' },
        { term: 'Gender non-conforming', definition: 'A person whose gender expression doesn\'t align with traditional expectations. GNC individuals may face misgendering or discomfort in clinical environments.' },
      ],
    },
    {
      category: 'Disability & Health',
      entries: [
        { term: 'Disabled', definition: 'A person with a physical, sensory, intellectual, or mental health disability. Sickle cell disease itself is a disability that can be invisible to others.' },
        { term: 'Chronically ill', definition: 'A person living with a long-term health condition. Chronic illness shapes daily life, energy levels, and relationships with the healthcare system.' },
        { term: 'Neurodivergent', definition: 'A person whose brain functions differently from what is considered typical — includes ADHD, autism, dyslexia, and more. Neurodivergent patients may need different communication and support approaches.' },
        { term: 'Deaf / Hard of hearing', definition: 'A person with hearing loss or who is culturally Deaf. Access to sign language interpreters and visual communication in healthcare is essential.' },
        { term: 'Blind / Low vision', definition: 'A person with significant vision loss. Accessible formats for medical information and technology are important.' },
      ],
    },
    {
      category: 'Age Group',
      entries: [
        { term: 'Adolescent', definition: 'A young person between early teen years and adulthood, typically 13-17. Adolescents with sickle cell are developing independence and navigating school, peer relationships, and identity.' },
        { term: 'Teen', definition: 'A young person between 13-19 years old. Teens with sickle cell may be managing school absences due to pain and transitioning toward adult independence.' },
        { term: 'Youth', definition: 'A young person between 18-24 years old. Youth with sickle cell are navigating the transition from pediatric to adult care, a critical and often difficult period.' },
        { term: 'Young adult', definition: 'A person roughly 25–40 years old. Young adults with sickle cell establish themselves in adult care systems and navigate work, relationships, and long-term disease management.' },
        { term: 'Adult', definition: 'A person in their middle to later years. Adults with sickle cell navigate the long-term effects of the disease and often face gaps in provider knowledge.' },
      ],
    },
    {
      category: 'Sexual Orientation',
      entries: [
        { term: 'LGBTQ+', definition: 'An umbrella term for people who identify as Lesbian, Gay, Bisexual, Transgender, Queer, and other non-heteronormative identities. LGBTQ+ patients may face compounded discrimination in healthcare.' },
        { term: 'Queer', definition: 'A reclaimed umbrella term for sexual and gender minorities. Some people prefer "queer" as a broad, inclusive identity.' },
        { term: 'Gay', definition: 'A person who is attracted to people of the same gender, typically used for men. Gay patients may face assumptions about their health needs and sexual history.' },
        { term: 'Lesbian', definition: 'A woman who is attracted to women. Lesbian patients may face erasure in healthcare settings where providers assume heterosexuality.' },
        { term: 'Bisexual', definition: 'A person attracted to more than one gender. Bisexual individuals often face erasure in both healthcare and social settings.' },
      ],
    },
    {
      category: 'Immigration & Origin',
      entries: [
        { term: 'Immigrant', definition: 'A person who has moved to a new country. Immigrant patients may face language barriers, unfamiliarity with the healthcare system, and fear around documentation status.' },
        { term: 'First-generation', definition: 'A person who is the first in their family born in or to immigrate to a country. First-gen individuals often bridge cultural differences in healthcare.' },
        { term: 'Refugee', definition: 'A person displaced from their home country due to conflict, persecution, or disaster. Refugees face significant barriers to consistent healthcare.' },
      ],
    },
    {
      category: 'Socioeconomic & Geographic',
      entries: [
        { term: 'Low income', definition: 'A person or family with limited financial resources. Low income affects access to medications, transportation to appointments, and ability to miss work for care.' },
        { term: 'Rural', definition: 'A person living in a rural area. Rural patients often travel long distances to reach sickle cell specialists and may have limited local support.' },
        { term: 'Urban', definition: 'A person living in a city. Urban patients may have more provider options but still face overcrowded ERs and systemic barriers.' },
      ],
    },
    {
      category: 'Family & Caregiving',
      entries: [
        { term: 'Caregiver', definition: 'A person who provides care for someone with a health condition. Caregivers of sickle cell patients carry significant emotional, physical, and financial burdens.' },
        { term: 'Parent', definition: 'A person raising children. Parents with sickle cell balance their own health needs with the demands of parenting.' },
        { term: 'Single parent', definition: 'A person raising children without a partner. Single parents with chronic illness face compounded challenges managing care alone.' },
        { term: 'Sibling', definition: 'A brother or sister. Siblings of sickle cell patients may have complex roles as emotional support, caregivers, or may themselves have sickle cell disease.' },
        { term: 'Sister', definition: 'A female sibling. Sisters may play important support roles in the health journey of their family members with sickle cell disease.' },
        { term: 'Brother', definition: 'A male sibling. Brothers may provide practical support, transportation to appointments, or emotional encouragement during health crises.' },
      ],
    },
    {
      category: 'Profession & Work',
      entries: [
        { term: 'Student', definition: 'A person pursuing education. Students with sickle cell navigate managing their health while attending school, managing absences, and building their future.' },
        { term: 'High school student', definition: 'A student in high school (grades 9-12). High school students with sickle cell balance academics, extracurriculars, and health management during critical developmental years.' },
        { term: 'Undergraduate student', definition: 'A student pursuing a bachelor\'s degree. Undergrads with sickle cell navigate college life, independence, and health management away from home.' },
        { term: 'College student', definition: 'A student enrolled in college or university. College students with sickle cell balance academic demands, social life, and access to campus health services.' },
        { term: 'Graduate student', definition: 'A student pursuing a master\'s, PhD, or other advanced degree. Graduate students with sickle cell manage intensive studies, research, and health needs.' },
        { term: 'Healthcare worker', definition: 'A person who works in healthcare, including doctors, nurses, social workers, and other professionals. Healthcare workers with sickle cell bring lived experience to their care work.' },
        { term: 'Educator', definition: 'A person who teaches or works in education. Educators with sickle cell advocate for understanding their health needs in school and workplace settings.' },
        { term: 'Service worker', definition: 'A person in customer service, food service, hospitality, or other frontline roles. Service workers may face challenges accessing care during shifts or taking time off for health needs.' },
        { term: 'Business professional', definition: 'A person working in business, corporate, or management roles. Business professionals with sickle cell navigate workplace disclosure and accommodation needs.' },
        { term: 'Artist', definition: 'A person working in creative fields including music, visual art, performance, or design. Artists with sickle cell use creativity to process their health experiences and contribute to culture.' },
        { term: 'Athlete', definition: 'A person involved in sports or athletic pursuits. Athletes with sickle cell must balance training and competition with their health needs and medical advice.' },
      ],
    },
    {
      category: 'Faith & Spirituality',
      entries: [
        { term: 'Muslim', definition: 'A follower of Islam. Religious practices like fasting during Ramadan may require special health considerations for sickle cell patients.' },
        { term: 'Jewish', definition: 'A person of Jewish faith or heritage. Faith communities can be an important source of support during health challenges.' },
        { term: 'Christian', definition: 'A follower of Christianity. Many sickle cell patients draw strength from their faith, which can be an important part of their care approach.' },
        { term: 'Hindu', definition: 'A follower of Hinduism. Practices such as fasting or dietary restrictions may have implications for sickle cell management and should be discussed with care teams.' },
        { term: 'Buddhist', definition: 'A follower of Buddhism. Mindfulness and meditation practices common in Buddhism may complement pain management strategies for sickle cell patients.' },
        { term: 'Jain', definition: 'A follower of Jainism. Strict dietary practices, including veganism, may require nutritional attention in the context of sickle cell care.' },
        { term: 'Sikh', definition: 'A follower of Sikhism. The Sikh value of seva (selfless service) and community support can be a meaningful resource for those managing sickle cell disease.' },
        { term: 'Atheist / Agnostic', definition: 'A person who does not hold religious beliefs or is uncertain about the existence of a higher power. Healthcare teams should respect non-religious perspectives on illness, suffering, and care decisions.' },
        { term: 'Spiritual (non-religious)', definition: 'A person who identifies as spiritual but not affiliated with an organized religion. Spiritual well-being can be an important dimension of whole-person sickle cell care.' },
        { term: 'Other faith', definition: 'A person who follows a spiritual or religious tradition not listed here. All faith backgrounds deserve recognition and respect in healthcare.' },
      ],
    },
  ];

  const [expandedDictCategory, setExpandedDictCategory] = useState<string | null>(null);

  async function saveIdentities(selected: string[]) {
    setCurrentUser(prev => prev ? { ...prev, identities: selected } : null);
    if (authToken) {
      updateProfile(authToken, { identities: selected });
    }
    if (selected.length > 0) {
      earnXPOnce('profile_identities_set', 25);
    }
  }

  function openContentItem(item: EDContentItem) {
    setActiveContentItem(item);
    openModal('content-item');
    markViewed(item.id);
    recordOpen(item.id);
    if (screen === 'articles') {
      setExpandedArticleSections(prev => { const s = new Set(prev); s.add('frequent'); return s; });
    }
  }

  function handleResourceCycleStatus(e: React.MouseEvent, item: EDContentItem) {
    e.stopPropagation();
    const { isFirstCompletion } = cycleStatus(item.id);
    if (isFirstCompletion) {
      earnXP(xpForItem(item));
    }
  }

  function closeModal() {
    setActiveModal(null);
    setMedNameDropdownOpen(false);
    document.body.style.overflow = '';
  }

  // skipCheckinStep, nextCheckinStep, prevCheckinStep, toggleCopingStrategy,
  // toggleProtectiveFactor are now in useCheckin hook

  // Legacy toggleEventLogProtectiveFactor / toggleEventLogExperienceTag /
  // nextEventLogStep / prevEventLogStep / saveEventLog removed 2026-05-14.
  // Replaced by useStigmaTracker hook + onTrackerEntrySaved below.

  // toggleEdBodyArea and toggleSafetyFlag are now in useEDMode hook

  // doStartCheckin, startCheckin, submitCheckin are now in useCheckin hook

  // submitEDCheckin is now in useEDMode hook

  /**
   * Called by the Stigma Tracker when a youth taps "Save this" or "Add more
   * later" on the review screen, or "Save" on the quick-note door. Posts the
   * entry to /api/events (new tracker shape + legacy adapter for backward
   * compatibility), updates the local entries list used by patterns / growth /
   * history surfaces, and runs the goal-nudge logic that previously lived
   * inline in saveEventLog. Logging a stigma experience does not award XP:
   * youth are not rewarded for experiencing or reporting stigma.
   */
  async function onTrackerEntrySaved(entry: StigmaTrackerEntry) {
    const legacyEntry = {
      timestamp: entry.submittedAt ?? new Date().toISOString(),
      location: entry.step3.setting.join(', '),
      personInvolved: entry.step2.people,
      experienceTags: entry.step2.practiceChips,
      protectiveFactors: entry.step4.carriedChips,
    };

    try {
      await apiPost('/events', {
        status: entry.status,
        trackerEntry: entry,
        location: legacyEntry.location || null,
        personInvolved: legacyEntry.personInvolved.length > 0 ? legacyEntry.personInvolved : null,
        whenHappened: entry.step2.when ?? null,
        disbeliefScore: entry.depth?.disbeliefValue ?? 0,
        differentialScore: entry.depth?.identityTiedValue ?? 0,
        safetyScore: entry.depth?.safetyThreatValue ?? 0,
        internalizedScore: 0,
        experienceTags: entry.step2.practiceChips,
        protectiveFactors: entry.step4.carriedChips,
        attribution: entry.depth?.attributionChips ?? null,
        emotionalResponse: entry.depth?.emotionalResponseChips ?? null,
        copingResponse: [
          ...(entry.depth?.copingInMomentChips ?? []),
          ...(entry.depth?.copingAfterwardsChips ?? []),
        ],
        customDescriptors: null,
        stigmaLayer: null,
        experienceType: entry.step2.earnshawType ? [entry.step2.earnshawType] : null,
      }, authToken);
    } catch {
      // Offline / network failure — entry stays in localStorage draft;
      // the offline queue resyncs when connectivity returns.
    }

    if (entry.status === 'complete') {
      setEventLogEntries(prev => {
        const updated = [...prev, legacyEntry];
        localStorage.setItem(SK_EVENT_LOGS, JSON.stringify(updated));
        return updated;
      });

      // Goal-nudge logic preserved from the legacy saveEventLog
      const hasInternalized = entry.step2.earnshawType === 'internalized';
      const hasSystemic =
        entry.step3.namedForces.some(f => f === 'the-system') ||
        entry.step3.setting.some(s => s === 'healthcare' || s === 'school' || s === 'workplace');
      const isHighPain = painValue !== null && painValue >= 7;
      const isLowEnergy = energyValue !== null && energyValue <= 3;
      if (!isHighPain && !isLowEnergy && !goalNudgeDismissed) {
        if (hasInternalized) {
          const suggested = GOAL_TEMPLATES.find(t => t.theme === 'Bias Resilience');
          if (suggested) setGoalNudgeTemplate(suggested);
        } else if (hasSystemic) {
          const suggested = GOAL_TEMPLATES.find(t => t.theme === 'Advocacy in Care');
          if (suggested) setGoalNudgeTemplate(suggested);
        }
      }
    }
  }

  // copyEDReport, downloadEDReportPDF, copyScript are now in useEDMode hook

  function submitYouthStory() {
    if (!youthSubmissionText.trim()) return;
    setSubmissionSuccess(true);
    setTimeout(() => {
      setYouthSubmissionText('');
      setSubmissionSuccess(false);
      closeModal();
    }, 2000);
  }

  const edConf = edModeConfig[edModeState];

  const levelInfo = computeLevel(totalXP);

  const appStats = useAppStats({
    checkinEntries,
    eventLogEntries,
    goals,
    completedGoals,
    edCheckins,
    edVisitHistory,
    streak,
    articlesVisited,
  });
  const gamification = { xp: totalXP, levelInfo, stats: appStats };
  const unlockedAchievements = ACHIEVEMENTS.filter(a => a.check(appStats));
  const lockedAchievements = ACHIEVEMENTS.filter(a => !a.check(appStats));
  const nextAchievement = lockedAchievements[0];

  // useGuideBond MUST stay above the conditional `return <Splash/SignUp/...>`
  // branches further down — otherwise its hook count differs between renders
  // on the auth flow vs the app surface, tripping React error #310
  // ("Rendered more hooks than during the previous render"). The destructured
  // bond fields below the early returns are plain consts; only the hook call
  // itself has to live up here.
  const _guideBond = useGuideBond(currentUser?.avatarId ?? null, currentUser?.guideBondProgress ?? null);

  function openNotificationPanel() {
    setNotificationPanelOpen(true);
    markAllSeen();
    fetchHistory();
  }

  // handleOpenCheckin is now in useCheckin hook

  // handleAccessibilityChange now provided by useAccessibility hook

  if (resetLink && authFlow !== 'loading' && authFlow !== 'app') {
    const leaveReset = () => { setResetLink(null); setAuthFlow('login'); };
    return <Suspense fallback={<LazyFallback />}><ForgotPasswordScreen onBack={leaveReset} onSuccess={leaveReset} initialEmail={resetLink.email} initialCode={resetLink.code} /></Suspense>;
  }
  if (authFlow === 'loading') {
    return (
      <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--gradient-page)' }}>
        <img src="/careweave-net-icon.svg" alt="" width="64" height="64" style={{ display: 'block' }} />
      </div>
    );
  }
  if (authFlow === 'splash') {
    return <SplashAnimation onComplete={handleSplashComplete} />;
  }
  if (authFlow === 'carousel') {
    return (
      <ValuePropCarousel
        onComplete={handleCarouselComplete}
        onSkip={handleCarouselComplete}
      />
    );
  }
  if (authFlow === 'landing' || authFlow === 'login') {
    return (
      <Suspense fallback={<LazyFallback />}>
        <LoginScreen
          asPage
          showCreateAccount={registrationOpen}
          onSuccess={handleLoginSuccess}
          onCodeLoginSuccess={handleCodeLoginSuccess}
          onPasscodeDevMode={handlePasscodeDevMode}
          onForgotPassword={() => setAuthFlow('forgot-password')}
          onCreateAccount={() => setAuthFlow('signup')}
        />
      </Suspense>
    );
  }
  if (authFlow === 'signup') {
    return <Suspense fallback={<LazyFallback />}><SignUpScreen onSuccess={handleSignUpSuccess} onBack={() => setAuthFlow('login')} onPrivacy={() => setAuthFlow('privacy')} /></Suspense>;
  }
  if (authFlow === 'forgot-password') {
    return <Suspense fallback={<LazyFallback />}><ForgotPasswordScreen onBack={() => setAuthFlow('login')} onSuccess={() => setAuthFlow('login')} /></Suspense>;
  }
  if (authFlow === 'privacy') {
    return <Suspense fallback={<LazyFallback />}><PrivacyPolicyScreen onBack={() => setAuthFlow('signup')} /></Suspense>;
  }
  if (authFlow === 'privacy-landing') {
    return <Suspense fallback={<LazyFallback />}><PrivacyPolicyScreen onBack={() => setAuthFlow('login')} /></Suspense>;
  }
  if (authFlow === 'code-login-choice') {
    return (
      <div style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--gradient-page)',
        padding: '24px',
      }}>
        <div style={{
          width: '100%',
          maxWidth: 420,
          background: 'linear-gradient(160deg, var(--color-bg-surface) 0%, var(--color-bg-elevated) 50%, var(--color-bg-primary) 100%)',
          border: '1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)',
          borderRadius: 'var(--radius-xl)',
          padding: '40px 32px',
          boxShadow: 'var(--shadow-overlay), 0 0 0 1px var(--color-accent-light)',
          textAlign: 'center',
        }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%', margin: '0 auto 24px',
            background: 'var(--color-accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <IconLock size={32} ariaLabel="" />
          </div>
          <h2 style={{ color: 'var(--color-text-primary)', fontWeight: 800, fontSize: 'var(--fs-xl-plus)', marginBottom: 10, marginTop: 0 }}>
            Welcome{currentUser?.displayName ? `, ${currentUser.displayName}` : ''}!
          </h2>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm-plus)', lineHeight: 1.6, marginBottom: 36, marginTop: 0 }}>
            You're signed in. Would you like to set up your profile, or jump straight into the app?
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* eslint-disable-next-line cw/no-inline-button-style -- post-auth landing CTA; older brand gradient family pre-dating chamfer, parked design decision pre-pilot polish */}
            <button
              onClick={() => setAuthFlow('onboarding')}
              style={{
                padding: '16px 24px', borderRadius: 'var(--radius-lg)', border: 'none',
                background: 'var(--color-accent)',
                color: '#fff', fontWeight: 700, fontSize: 'var(--fs-base)',
                cursor: 'pointer',
                boxShadow: '0 8px 24px color-mix(in srgb, var(--color-accent) 35%, transparent)',
                width: '100%',
              }}
            >
              Review onboarding
            </button>
            {/* eslint-disable-next-line cw/no-inline-button-style -- post-auth landing secondary CTA; older brand gradient family pre-dating chamfer, parked design decision pre-pilot polish */}
            <button
              onClick={() => setAuthFlow('app')}
              style={{
                padding: '15px 24px', borderRadius: 'var(--radius-lg)',
                border: '1.5px solid color-mix(in srgb, var(--color-accent) 35%, transparent)',
                background: 'color-mix(in srgb, var(--color-accent) 8%, transparent)',
                color: 'var(--color-accent)', fontWeight: 600, fontSize: 'var(--fs-base)',
                cursor: 'pointer',
                width: '100%',
              }}
            >
              Go to app
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (authFlow === 'onboarding' && authToken) {
    return (
      <Suspense fallback={<LazyFallback />}>
        <ProfileSetupWizard
          token={authToken}
          displayName={currentUser?.displayName ?? null}
          onComplete={handleOnboardingComplete}
        />
      </Suspense>
    );
  }

  if (authFlow === 'mode-chooser') {
    const chooserBtnBase: React.CSSProperties = {
      padding: '1rem 1.25rem', borderRadius: 'var(--radius-lg)', cursor: 'pointer',
      fontSize: 'var(--fs-sm-plus)', fontWeight: 600, textAlign: 'left',
      display: 'flex', alignItems: 'center', gap: 14, transition: 'all 0.15s',
    };
    return (
      <div style={{
        minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--gradient-page)',
        fontFamily: "'Atkinson Hyperlegible', sans-serif", padding: '1rem',
      }}>
        <div style={{
          background: 'var(--color-bg-card)', borderRadius: 'var(--radius-xl)', padding: '2.5rem 2rem 2rem',
          maxWidth: 400, width: '100%', textAlign: 'center',
          boxShadow: 'var(--shadow-overlay), 0 0 0 1px var(--color-border)',
        }}>
          <img src="/careweave-net-icon.svg" alt="" width="52" height="52" style={{ display: 'inline-block', marginBottom: 10 }} />
          <h1 style={{
            color: 'var(--color-text-primary)', fontSize: 'var(--fs-xl)', fontWeight: 800,
            margin: '0 0 0.25rem', fontFamily: "'Source Serif 4', serif",
          }}>
            Welcome, {currentUser?.displayName || 'Friend'}
          </h1>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)', margin: '0 0 1.75rem' }}>
            Choose how you want to use CareWeave
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* eslint-disable-next-line cw/no-inline-button-style -- mode-chooser row: composite icon + 2-line label, left-aligned grid; solid accent fill matches .btn-primary colour */}
            <button onClick={() => { setAuthFlow('app'); setScreen('home'); }} style={{
              ...chooserBtnBase, border: 'none',
              background: 'var(--color-accent)', color: 'var(--color-on-accent)',
            }}>
              <span style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'color-mix(in srgb, var(--color-text-primary) 10%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><IconPhone size={20} ariaLabel="" /></span>
              <span>
                <span style={{ display: 'block' }}>App Preview</span>
                <span style={{ display: 'block', fontSize: 'var(--fs-xs)', fontWeight: 400, opacity: 0.85, marginTop: 2 }}>Check-ins, Circle, journeys, and tools</span>
              </span>
            </button>
            {currentUser?.isAdmin && (
              // eslint-disable-next-line cw/no-inline-button-style -- mode-chooser row: composite icon + 2-line label, left-aligned grid, not a chamfered .btn variant
              <button onClick={() => { setAuthFlow('app'); setScreen('researcher'); }} style={{
                ...chooserBtnBase,
                border: '1px solid var(--color-border)', background: 'var(--color-bg-surface)', color: 'var(--color-text-primary)',
              }}>
                <span style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'var(--color-accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><IconChart size={20} ariaLabel="" /></span>
                <span>
                  <span style={{ display: 'block' }}>Researcher Dashboard</span>
                  <span style={{ display: 'block', fontSize: 'var(--fs-xs)', fontWeight: 400, color: 'var(--color-text-secondary)', marginTop: 2 }}>Participants, analytics, and data exports</span>
                </span>
              </button>
            )}
            {(currentUser?.isAdmin || currentUser?.isModerator) && (
              // eslint-disable-next-line cw/no-inline-button-style -- mode-chooser row: composite icon + 2-line label, left-aligned grid, not a chamfered .btn variant
              <button onClick={() => { setAuthFlow('app'); setScreen('moderator'); }} style={{
                ...chooserBtnBase,
                border: '1px solid var(--color-border)', background: 'var(--color-bg-surface)', color: 'var(--color-text-primary)',
              }}>
                <span style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'var(--color-accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><IconShield size={20} ariaLabel="" /></span>
                <span>
                  <span style={{ display: 'block' }}>Moderator Dashboard</span>
                  <span style={{ display: 'block', fontSize: 'var(--fs-xs)', fontWeight: 400, color: 'var(--color-text-secondary)', marginTop: 2 }}>Queue, keywords, community health</span>
                </span>
              </button>
            )}
          </div>
          {/* eslint-disable-next-line cw/no-inline-button-style -- sub-action text-link "Sign out"; ghost-only style, no border or background */}
          <button onClick={() => { clearToken(); setAuthToken(null); setCurrentUser(null); setAuthFlow('login'); }} style={{
            marginTop: 20, background: 'none', border: 'none', color: 'var(--color-text-secondary)',
            fontSize: 'var(--fs-xs)', cursor: 'pointer', fontWeight: 600,
          }}>
            Sign out
          </button>
        </div>
      </div>
    );
  }

  const userDisplayName = currentUser?.displayName || 'Friend';
  const userAvatarId = currentUser?.avatarId || null;
  const userAvatar = AVATARS.find(a => a.id === userAvatarId);
  const userCustomisation = currentUser?.avatarCustomisation ?? null;

  const userAvatarEmoji = currentUser?.avatarEmoji || null;

  const activeGuideChar = userAvatarId ? CRYSTAL_CHARACTERS.find(c => c.id === userAvatarId) : null;
  // useGuideBond itself is called above the conditional auth-flow returns
  // (see comment up near useAppStats). The destructure here is plain
  // const reads from the result, safe to live below the returns.
  const guideBondDays = _guideBond.checkInDays;
  const guideBondLevel = _guideBond.level;
  const guideBondProgress = _guideBond.progress;
  const guideBondGreeting = activeGuideChar ? _guideBond.greeting : null;

  if (screen === 'privacy-policy') {
    return <PrivacyPolicyScreen onBack={() => showScreen('profile')} />;
  }

  if (screen === 'crisis-resources') {
    return <CrisisResourcesScreen onBack={() => showScreen('profile')} />;
  }

  const devBtnStyle: React.CSSProperties = {
    padding: '7px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-accent-light)',
    background: 'var(--color-accent-light)', color: 'var(--color-accent)', fontSize: 'var(--fs-xs)',
    cursor: 'pointer', fontWeight: 600, textAlign: 'left' as const, width: '100%',
  };

  return (
    <Suspense fallback={<LazyFallback />}>
    <div className="app-root">
      <OfflineBanner isOnline={isOnline} pendingCount={pendingCount} />
      {isDemoVisit() && (
        <div role="note" style={{ background: 'var(--color-bg-surface)', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-xs)', textAlign: 'center', padding: '6px 12px', borderBottom: '1px solid var(--color-border, color-mix(in srgb, var(--color-text-primary) 10%, transparent))' }}>
          Demo with fictional data. Not for clinical use. Please do not enter real personal or health information.
        </div>
      )}
      <a href="#main-content" className="skip-nav">Skip to main content</a>
      {(isDemoUser || isDevMode || currentUser?.isAdmin) && !isDemoVisit() && screen !== 'researcher' && screen !== 'moderator' && screen !== 'moderation' && screen !== 'content-manager' && (
        <div style={{ position: 'fixed', bottom: 80, right: 16, zIndex: 'var(--z-modal-top)' }}>
          {(showDemoXpAdjuster || showDevPanel) ? (
            <div style={{
              background: 'var(--color-bg-card)',
              border: '1.5px solid var(--color-accent)',
              borderRadius: 'var(--radius-lg)',
              padding: '14px 16px',
              boxShadow: 'var(--color-card-shadow)',
              width: isDevMode ? 280 : 240,
              maxHeight: '70vh',
              overflowY: 'auto',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--color-accent)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                  {isDevMode ? 'Dev Mode' : 'Demo: Set XP'}
                </span>
                {/* eslint-disable-next-line cw/no-inline-button-style -- dev/demo panel close X glyph; absolute-positioned icon button, no .btn-icon-only primitive */}
                <button
                  onClick={() => { setShowDemoXpAdjuster(false); setShowDevPanel(false); }}
                  aria-label="Close XP adjuster"
                  style={{ background: 'none', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', fontSize: 'var(--fs-base)', padding: 0, lineHeight: 1 }}
                >×</button>
              </div>

              {/* XP Controls */}
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                <strong style={{ color: 'var(--color-accent)' }}>{totalXP} XP</strong> · <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, verticalAlign: 'middle' }}>{(() => { const LB = CRYSTAL_ICONS[`level-${Math.min(7, Math.max(1, levelInfo.level))}` as CrystalIconKey]; return LB ? <LB size={18} ariaLabel="" /> : null; })()} Level {levelInfo.level} — {levelInfo.name}</span>
              </div>
              <input
                type="range"
                min={0}
                max={3200}
                step={50}
                value={totalXP}
                onChange={e => {
                  const val = parseInt(e.target.value, 10);
                  setTotalXP(val);
                  try { localStorage.setItem(SK_XP, String(val)); } catch { /* localStorage may be unavailable */ }
                }}
                aria-label="XP slider"
                style={{ width: '100%', accentColor: 'var(--color-accent)', marginBottom: 8 }}
              />
              <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                <input
                  type="number"
                  min={0}
                  max={9999}
                  value={isDevMode ? devXpInput : demoXpInput}
                  onChange={e => isDevMode ? setDevXpInput(e.target.value) : setDemoXpInput(e.target.value)}
                  placeholder="Custom XP…"
                  aria-label="Custom XP value"
                  style={{
                    flex: 1, padding: '6px 10px', borderRadius: 'var(--radius-sm)',
                    border: '1px solid color-mix(in srgb, var(--color-accent) 40%, transparent)', background: 'color-mix(in srgb, var(--color-text-primary) 4%, transparent)',
                    color: '#fff', fontSize: 'var(--fs-sm)', outline: 'none', minWidth: 0,
                  }}
                />
                {/* eslint-disable-next-line cw/no-inline-button-style -- dev/demo panel inline "Set" button paired with number input; sub-component sized to input row, not a chamfered .btn variant */}
                <button
                  onClick={() => {
                    const input = isDevMode ? devXpInput : demoXpInput;
                    const val = parseInt(input, 10);
                    if (!isNaN(val) && val >= 0) {
                      setTotalXP(val);
                      try { localStorage.setItem(SK_XP, String(val)); } catch { /* localStorage may be unavailable */ }
                      isDevMode ? setDevXpInput('') : setDemoXpInput('');
                    }
                  }}
                  style={{
                    padding: '6px 10px', borderRadius: 'var(--radius-sm)', border: 'none',
                    background: 'var(--color-accent)',
                    color: '#fff', fontWeight: 700, fontSize: 'var(--fs-sm)', cursor: 'pointer',
                  }}
                >Set</button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: isDevMode ? 14 : 0 }}>
                {LEVEL_SYSTEM.map(lvl => (
                  // eslint-disable-next-line cw/no-inline-button-style -- dev/demo level-jump pill (L1..L7); compact 2-char chip in flex-wrap row, not a chamfered .btn variant
                  <button
                    key={lvl.level}
                    onClick={() => { setTotalXP(lvl.minXP); try { localStorage.setItem(SK_XP, String(lvl.minXP)); } catch { /* localStorage may be unavailable */ } }}
                    style={{
                      padding: '3px 8px', borderRadius: 'var(--radius-sm)', border: '1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)',
                      background: computeLevel(totalXP).level === lvl.level ? 'color-mix(in srgb, var(--color-accent) 40%, transparent)' : 'color-mix(in srgb, var(--color-accent) 10%, transparent)',
                      color: 'var(--color-accent)', fontSize: 'var(--fs-xs)', cursor: 'pointer', fontWeight: 600,
                    }}
                  >L{lvl.level}</button>
                ))}
              </div>

              {/* Dev Mode extras */}
              {isDevMode && (
                <>
                  {/* Tutorial Controls */}
                  <div style={{ borderTop: '1px solid color-mix(in srgb, var(--color-accent) 20%, transparent)', paddingTop: 12, marginBottom: 12 }}>
                    <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--color-accent)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                      Tutorials
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {/* eslint-disable-next-line cw/no-inline-button-style -- dev panel action row; shared devBtnStyle, admin chrome not on the chamfered .btn track */}
                      <button
                        onClick={() => setShowTutorial(true)}
                        style={devBtnStyle}
                      >
                        Replay App Tutorial
                      </button>
                      {/* eslint-disable-next-line cw/no-inline-button-style -- dev panel action row; shared devBtnStyle, admin chrome not on the chamfered .btn track */}
                      <button
                        onClick={() => {
                          markTutorialSeen(currentUser?.id);
                          setShowTutorial(false);
                          setTutorialCompleted(true);
                        }}
                        style={devBtnStyle}
                      >
                        Skip / Turn Off Tutorial
                      </button>
                      {/* eslint-disable-next-line cw/no-inline-button-style -- dev panel action row; shared devBtnStyle, admin chrome not on the chamfered .btn track */}
                      <button
                        onClick={() => {
                          // Reset tutorial so it shows again
                          const key = getTutorialDoneKey(currentUser?.id);
                          localStorage.removeItem(key);
                          setTutorialCompleted(false);
                        }}
                        style={devBtnStyle}
                      >
                        Reset Tutorial (Show Again)
                      </button>
                    </div>
                  </div>

                  {/* Module Intros */}
                  <div style={{ borderTop: '1px solid color-mix(in srgb, var(--color-accent) 20%, transparent)', paddingTop: 12, marginBottom: 12 }}>
                    <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--color-accent)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                      Module Intros
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {/* eslint-disable-next-line cw/no-inline-button-style -- dev panel action row; shared devBtnStyle, admin chrome not on the chamfered .btn track */}
                      <button
                        onClick={() => {
                          // Dismiss all module intros
                          dismissModuleIntroCheckin();
                          dismissModuleIntroPatterns();
                          dismissModuleIntroGrowth();
                          dismissModuleIntroMedications();
                          dismissModuleIntroEventLog();
                          dismissModuleIntroJourneys();
                          dismissModuleIntroCircle();
                          dismissModuleIntroTools();
                        }}
                        style={devBtnStyle}
                      >
                        Dismiss All Module Intros
                      </button>
                      {/* eslint-disable-next-line cw/no-inline-button-style -- dev panel action row; shared devBtnStyle, admin chrome not on the chamfered .btn track */}
                      <button
                        onClick={() => {
                          // Reset module intros by clearing localStorage keys
                          ['checkin','patterns','growth','medications','event-log','journeys','peer-connect','tools'].forEach(m => {
                            localStorage.removeItem(`cw_module_intro_${m}`);
                          });
                          window.location.reload();
                        }}
                        style={devBtnStyle}
                      >
                        Reset All Module Intros
                      </button>
                    </div>
                  </div>

                  {/* Quick Navigation */}
                  <div style={{ borderTop: '1px solid color-mix(in srgb, var(--color-accent) 20%, transparent)', paddingTop: 12, marginBottom: 12 }}>
                    <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--color-accent)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                      Go to Screen
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {([
                        ['home', 'Home'], ['checkin', 'Check-in'], ['event-log', 'Stigma Tracker'],
                        ['journeys', 'Journeys'], ['patterns', 'Patterns'], ['articles', 'Articles'],
                        ['growth', 'Growth'], ['medications', 'Meds'], ['tools', 'Tools'],
                        ['peer-connect', 'Circle'], ['grounding', 'Grounding'], ['crisis-resources', 'Crisis'],
                        ['profile', 'Profile'], ['hospital-prep', 'Hospital'], ['crisis-plan', 'Crisis Plan'],
                        ['weather-report', 'Weather'], ['care-team', 'Care Team'], ['support-network', 'Support'],
                      ] as const).map(([s, label]) => (
                        // eslint-disable-next-line cw/no-inline-button-style -- dev panel screen-jump pill; compact chip in flex-wrap row, not a chamfered .btn variant
                        <button
                          key={s}
                          onClick={() => { showScreen(s); setShowDevPanel(false); }}
                          style={{
                            padding: '3px 7px', borderRadius: 'var(--radius-sm)',
                            border: '1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)',
                            background: screen === s ? 'color-mix(in srgb, var(--color-accent) 40%, transparent)' : 'color-mix(in srgb, var(--color-accent) 10%, transparent)',
                            color: 'var(--color-accent)', fontSize: 'var(--fs-xs)', cursor: 'pointer', fontWeight: 600,
                          }}
                        >{label}</button>
                      ))}
                    </div>
                  </div>

                  {/* Misc Actions */}
                  <div style={{ borderTop: '1px solid color-mix(in srgb, var(--color-accent) 20%, transparent)', paddingTop: 12 }}>
                    <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--color-accent)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                      Actions
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {/* eslint-disable-next-line cw/no-inline-button-style -- dev panel action row; shared devBtnStyle, admin chrome not on the chamfered .btn track */}
                      <button
                        onClick={() => earnXP(50)}
                        style={devBtnStyle}
                      >
                        +50 XP (with animation)
                      </button>
                      {/* eslint-disable-next-line cw/no-inline-button-style -- dev panel action row; shared devBtnStyle, admin chrome not on the chamfered .btn track */}
                      <button
                        onClick={() => earnXP(200)}
                        style={devBtnStyle}
                      >
                        +200 XP (with animation)
                      </button>
                      {/* eslint-disable-next-line cw/no-inline-button-style -- dev panel danger-tinted exit row; devBtnStyle override with danger border, not a chamfered .btn variant */}
                      <button
                        onClick={() => {
                          setIsDevMode(false);
                          setIsDemoUser(false);
                          setShowDevPanel(false);
                          setAuthFlow('login');
                          setCurrentUser(null);
                          setAuthToken(null);
                        }}
                        style={{ ...devBtnStyle, borderColor: 'color-mix(in srgb, var(--color-danger) 40%, transparent)', color: 'var(--color-danger)' }}
                      >
                        Exit Dev Mode
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            // eslint-disable-next-line cw/no-inline-button-style -- dev/demo floating launcher; corner-anchored composite control with dev-only purple gradient theme, not a chamfered .btn variant
            <button
              onClick={() => isDevMode ? setShowDevPanel(true) : setShowDemoXpAdjuster(true)}
              title={isDevMode ? 'Dev Mode Panel' : 'Demo: Adjust XP'}
              style={{
                background: isDevMode
                  // eslint-disable-next-line cw/no-raw-style-values -- dev-mode-only panel; never shown to participants or demo visitors
                  ? 'linear-gradient(135deg, #2d1065, #581c87)'
                  : 'linear-gradient(135deg, var(--color-bg-primary), var(--color-bg-primary))',
                border: `1.5px solid ${isDevMode ? 'color-mix(in srgb, var(--color-accent) 60%, transparent)' : 'color-mix(in srgb, var(--color-accent) 50%, transparent)'}`,
                borderRadius: 'var(--radius-md)',
                padding: '8px 12px',
                color: isDevMode ? 'var(--color-accent)' : 'var(--color-accent)',
                fontSize: 'var(--fs-xs)',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: 'var(--shadow-raised)',
                letterSpacing: '0.02em',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {isDevMode ? 'Dev Mode' : 'Demo XP'}
            </button>
          )}
        </div>
      )}

      {showTutorial && (
        <AppTutorial
          onComplete={handleTutorialComplete}
          onSkip={handleTutorialSkip}
          isReplay={tutorialCompleted}
          onJumpToScreen={(s) => showScreen(s as Screen)}
        />
      )}

      {showCheckinIntro && (
        <CheckinIntroCard
          onStart={() => {
            localStorage.setItem(SK_SEEN_CHECKIN_INTRO, 'true');
            setShowCheckinIntro(false);
            doStartCheckin();
          }}
          onSkip={() => {
            localStorage.setItem(SK_SEEN_CHECKIN_INTRO, 'true');
            setShowCheckinIntro(false);
            doStartCheckin();
          }}
          onClose={() => {
            setShowCheckinIntro(false);
            showScreen('home');
          }}
        />
      )}

      {showEDCheckinIntro && (
        <EDCheckinIntroModal
          onDismiss={() => {
            localStorage.setItem(SK_SEEN_ED_CHECKIN, 'true');
            setShowEDCheckinIntro(false);
            setEdCheckinVisible(true);
            setEdCheckinStep(1);
          }}
          onClose={() => {
            setShowEDCheckinIntro(false);
          }}
        />
      )}

      {showEditProfile && (
        <div className="modal-overlay" onClick={() => setShowEditProfile(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <h3>Edit profile</h3>
              <button className="modal-close" onClick={() => setShowEditProfile(false)} aria-label="Close dialog">&times;</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Crystal Guide quick-access */}
              <div style={{ display: 'flex', gap: 8 }}>
                {/* eslint-disable-next-line cw/no-inline-button-style -- edit-profile modal quick-access row; 2-col flex pair with min-height tap target, not a chamfered .btn variant */}
                <button
                  onClick={() => { setShowEditProfile(false); showScreen('crystal-guide'); }}
                  style={{ flex: 1, padding: '10px 12px', borderRadius: 'var(--radius-md)', border: '1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)', background: 'var(--color-accent-light)', color: 'var(--color-text-primary)', fontSize: 'var(--fs-sm)', cursor: 'pointer', minHeight: 44 }}
                >
                  Change Crystal Guide
                </button>
                {/* eslint-disable-next-line cw/no-inline-button-style -- edit-profile modal quick-access row; 2-col flex pair with min-height tap target, not a chamfered .btn variant */}
                <button
                  onClick={() => { setShowEditProfile(false); showScreen('home'); }}
                  style={{ flex: 1, padding: '10px 12px', borderRadius: 'var(--radius-md)', border: '1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)', background: 'var(--color-accent-light)', color: 'var(--color-text-primary)', fontSize: 'var(--fs-sm)', cursor: 'pointer', minHeight: 44 }}
                >
                  View Globe
                </button>
              </div>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)' }}>Display name</span>
                <input
                  type="text"
                  value={editProfileName}
                  onChange={e => setEditProfileName(e.target.value)}
                  maxLength={100}
                  style={{ padding: '10px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)', fontSize: 'var(--fs-sm-plus)' }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)' }}>Pronouns</span>
                <input
                  type="text"
                  value={editProfilePronouns}
                  onChange={e => setEditProfilePronouns(e.target.value)}
                  placeholder="e.g. She/Her, They/Them"
                  style={{ padding: '10px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)', fontSize: 'var(--fs-sm-plus)' }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)' }}>Care centre</span>
                <input
                  type="text"
                  value={editProfileCareCenter}
                  onChange={e => setEditProfileCareCenter(e.target.value)}
                  placeholder="e.g. SickKids"
                  style={{ padding: '10px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)', fontSize: 'var(--fs-sm-plus)' }}
                />
              </label>
              <button
                className="btn btn-primary"
                disabled={editProfileSaving}
                onClick={async () => {
                  setEditProfileSaving(true);
                  const ok = await updateProfile(authToken, {
                    displayName: editProfileName.trim() || null,
                    pronouns: editProfilePronouns.trim() || null,
                    careCenter: editProfileCareCenter.trim() || null,
                  });
                  setEditProfileSaving(false);
                  if (ok) {
                    setCurrentUser(prev => prev ? {
                      ...prev,
                      displayName: editProfileName.trim() || null,
                      pronouns: editProfilePronouns.trim() || null,
                      careCenter: editProfileCareCenter.trim() || null,
                    } : null);
                    setShowEditProfile(false);
                  }
                }}
              >
                <span>{editProfileSaving ? 'Saving…' : 'Save changes'}</span>
              </button>
            </div>
          </div>
        </div>
      )}


      <NotificationPanel
        open={notificationPanelOpen}
        onClose={() => setNotificationPanelOpen(false)}
        notifications={notifications}
        reminderTime={reminderTime}
        saveReminderTime={saveReminderTime}
        onOpenCheckin={handleOpenCheckin}
        accessibilityPrefs={accessibilityPrefs}
        onAccessibilityChange={handleAccessibilityChange}
      />

      {xpFlash.visible && (
        <XpFlashBubble key={xpFlash.key} amount={xpFlash.amount} />
      )}

      {activeScene && (
        <JourneyScene
          journeyId={activeScene.journeyId}
          phaseId={activeScene.phaseId}
          phaseLabel={activeScene.phaseLabel}
          journeyColor={activeScene.journeyColor}
          completedResources={completedJourneyResources}
          onToggleResource={toggleJourneyResource}
          onClose={() => setActiveScene(null)}
          onSceneComplete={(_xp) => {}}
          earnXP={earnXP}
          guide={userAvatarId ? getCrystalGuide(userAvatarId) ?? null : null}
          phaseResourcesData={phaseResourcesData}
        />
      )}

      {xpLevelUpCelebration?.visible && (
        <XpLevelUpCelebration
          level={xpLevelUpCelebration.level}
          name={xpLevelUpCelebration.name}
          onDismiss={() => setXpLevelUpCelebration(null)}
        />
      )}

      <main
        id="main-content"
        tabIndex={-1}
        className="container"
        ref={containerRef}
        onScroll={() => {
          const el = containerRef.current;
          if (!el) return;
          if (screen === 'checkin') {
            setCheckinScrollTop(el.scrollTop >= 40);
            setCheckinScrollBottom(el.scrollTop + el.clientHeight < el.scrollHeight - 40);
          }
          if (screen === 'articles') {
            setArticleScrollY(el.scrollTop);
          }
        }}
      >
        {/* Page header card — sits at the top of every screen */}
        <header className="page-header-card" role="banner">
          <button
            onClick={() => setMenuOpen(o => !o)}
            aria-label="Open menu"
            className="page-header-icon-btn"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
          <button
            type="button"
            onClick={() => showScreen('profile')}
            aria-label="Edit your profile"
            title="Edit your profile"
            className="page-header-avatar"
          >
            <GuideBubble
              avatarId={userAvatarId || 'citrine'}
              size={36}
              showGlow={false}
              customisation={userCustomisation}
              ariaLabel="Your Crystal Guide — tap to edit your profile"
            />
            <span aria-hidden="true" className="page-header-avatar-edit">
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
              </svg>
            </span>
          </button>
          <div className="page-header-text">
            <h1 className="page-header-title">{
              screen === 'home' ? `Hi, ${userDisplayName}`
              : screen === 'tools' ? 'Tools'
              : screen === 'patterns' ? 'Patterns'
              : screen === 'growth' ? 'Growth'
              : screen === 'peer-connect' ? 'Circle'
              : screenSubtitles[screen]
            }</h1>
            <p className="page-header-date">{new Date().toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
          </div>
          <button
            onClick={openNotificationPanel}
            aria-label={badgeCount > 0 ? `Notifications, ${badgeCount} unread` : 'Notifications'}
            className="page-header-icon-btn"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/>
              <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/>
            </svg>
            {badgeCount > 0 && (
              <span className="page-header-bell-badge">{badgeCount > 9 ? '9+' : badgeCount}</span>
            )}
          </button>
        </header>
        {checkinDraftActive && screen !== 'checkin' && !checkinSummary && (
          <div style={{ position: 'sticky', top: 0, zIndex: 'var(--z-sticky)', background: 'var(--color-accent)', color: 'var(--color-on-accent)', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center' }}><IconCheck size={16} ariaLabel="" /></span>
              <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--color-on-accent)' }}>Check-in in progress</span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {/* eslint-disable-next-line cw/no-inline-button-style -- sticky check-in resume banner pill; rounded-20 chip in gradient-tinted toolbar, not a chamfered .btn variant */}
              <button
                onClick={() => startCheckin(true)}
                style={{ background: '#fff', border: 'none', borderRadius: 'var(--radius-xl)', color: 'var(--color-accent)', fontSize: 'var(--fs-xs)', fontWeight: 700, padding: '5px 14px', cursor: 'pointer' }}
              >Resume</button>
              {/* eslint-disable-next-line cw/no-inline-button-style -- sticky check-in discard banner pill; rounded-20 chip in gradient-tinted toolbar, not a chamfered .btn variant */}
              <button
                onClick={() => clearCheckinDraft()}
                style={{ background: 'color-mix(in srgb, var(--color-text-primary) 10%, transparent)', border: 'none', borderRadius: 'var(--radius-xl)', color: 'var(--color-on-accent)', fontSize: 'var(--fs-xs)', fontWeight: 600, padding: '5px 12px', cursor: 'pointer' }}
              >Discard</button>
            </div>
          </div>
        )}
        {screen === 'home' && (
          <div className="screen">
            <div className="card">
              {guideBondGreeting && activeGuideChar && (
                <div style={{
                  padding: '12px 14px',
                  background: `color-mix(in srgb, ${activeGuideChar.bg} 9%, transparent)`,
                  border: `1px solid color-mix(in srgb, ${activeGuideChar.bg} 27%, transparent)`,
                  borderRadius: 'var(--radius-md)',
                  marginBottom: 14,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                }}>
                  <span style={{ flexShrink: 0, marginTop: 1, display: 'inline-flex' }}><IconSparkle size={18} ariaLabel="" /></span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: activeGuideChar.bg, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>
                      {activeGuideChar.crystalName} · Bond Level {guideBondLevel}
                    </div>
                    <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.5, fontStyle: 'italic' }}>
                      "{guideBondGreeting}"
                    </p>
                    {/* eslint-disable-next-line cw/no-inline-button-style -- inline text-link inside guide bond greeting; color bound to active guide's bg, ghost-only style, not a chamfered .btn variant */}
                    <button
                      type="button"
                      onClick={() => showScreen('crystal-guide')}
                      style={{
                        marginTop: 8,
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        color: activeGuideChar.bg,
                        fontSize: 'var(--fs-xs)',
                        fontWeight: 700,
                        letterSpacing: '0.02em',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                      aria-label={`Tend your bond with ${activeGuideChar.crystalName} — opens the Crystal Globe`}
                    >
                      Tend the bond ›
                    </button>
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                <div className="stat-mini">
                  <div style={{ marginBottom: 4, display: 'flex', justifyContent: 'center' }}>
                    <IconStreak size={28} ariaLabel="Day streak" />
                  </div>
                  <div className="stat-val">{streak}</div>
                  <div className="stat-lbl">Day Streak</div>
                </div>
                <div className="stat-mini">
                  <div style={{ marginBottom: 4, display: 'flex', justifyContent: 'center' }}>
                    <IconCheckinNote size={28} ariaLabel="Check-ins" />
                  </div>
                  <div className="stat-val" style={{ color: 'var(--color-secondary)' }}>{checkinEntries.length}</div>
                  <div className="stat-lbl">Check-ins</div>
                </div>
              </div>

              <div
                style={{ padding: '14px 16px', background: 'color-mix(in srgb, var(--color-accent) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--color-accent) 18%, transparent)', borderRadius: 'var(--radius-lg)', marginBottom: 14, cursor: 'pointer' }}
                onClick={() => showScreen('growth')}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm-plus)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }}>{(() => { const LB = CRYSTAL_ICONS[`level-${Math.min(7, Math.max(1, levelInfo.level))}` as CrystalIconKey]; return LB ? <LB size={20} ariaLabel="" /> : null; })()}Level {levelInfo.level} — {levelInfo.name}</div>
                  <div style={{ fontSize: 'var(--fs-xs-plus)', color: 'var(--color-accent)', fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap' }}>{levelInfo.xpInLevel}/{levelInfo.xpForLevel} XP</div>
                </div>
                <div className="progress-bar" style={{ height: 6, marginBottom: 8 }}>
                  <div className="progress-fill" style={{ width: `${levelInfo.pct}%`, background: 'var(--color-accent)' }} />
                </div>
                {nextAchievement ? (
                  <div style={{ fontSize: 'var(--fs-xs-plus)', color: 'var(--color-text-secondary)', lineHeight: 1.4 }}>
                    {levelInfo.toNext > 0 ? `${levelInfo.toNext} XP to ${levelInfo.nextLevelName} · ` : ''}
                    Next:{' '}
                    <span
                      style={{ color: 'var(--color-accent)', cursor: 'pointer', textDecoration: 'underline dotted' }}
                      onClick={(e) => { e.stopPropagation(); setActiveAchievement(nextAchievement); openModal('achievement-detail'); }}
                    >
                      {nextAchievement.iconKey ? (() => { const Icon = CRYSTAL_ICONS[nextAchievement.iconKey as CrystalIconKey]; return Icon ? <Icon size={16} ariaLabel="" /> : null; })() : nextAchievement.icon} {nextAchievement.label}
                    </span>
                  </div>
                ) : (
                  <div style={{ fontSize: 'var(--fs-xs-plus)', color: 'var(--color-text-secondary)' }}>
                    {levelInfo.toNext > 0 ? `${levelInfo.toNext} XP to ${levelInfo.nextLevelName}` : 'All achievements unlocked'}
                  </div>
                )}
              </div>

              {unlockedAchievements.length > 0 && (
                <div style={{ overflowX: 'auto', display: 'flex', gap: 8, paddingBottom: 4, marginBottom: 14, scrollbarWidth: 'none' }}>
                  {unlockedAchievements.map(a => (
                    // eslint-disable-next-line cw/no-inline-button-style -- achievement tile in horizontal-scroll carousel; vertical icon + label stack, fixed width, not a chamfered .btn variant
                    <button key={a.id} title={a.desc}
                      onClick={() => { setActiveAchievement(a); openModal('achievement-detail'); }}
                      style={{
                        flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
                        width: 68, padding: '10px 6px',
                        background: 'color-mix(in srgb, var(--color-accent) 10%, transparent)',
                        border: '1px solid color-mix(in srgb, var(--color-accent) 20%, transparent)',
                        borderRadius: 'var(--radius-lg)', textAlign: 'center', cursor: 'pointer',
                      }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: '50%',
                        background: 'color-mix(in srgb, var(--color-accent) 15%, transparent)',
                        border: '1.5px solid color-mix(in srgb, var(--color-accent) 35%, transparent)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 'var(--fs-md)',
                      }}>{a.icon}</div>
                      <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', lineHeight: 1.25, width: '100%', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{a.label}</div>
                    </button>
                  ))}
                </div>
              )}

              {/* Customise Dashboard button */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
                {/* eslint-disable-next-line cw/no-inline-button-style -- dashboard customize toggle pill; right-aligned tertiary toggle with dynamic on/off state, rounded-20, not a chamfered .btn variant */}
                <button
                  onClick={() => setDashboardEditMode(e => !e)}
                  style={{
                    padding: '8px 16px',
                    background: dashboardEditMode ? 'var(--color-accent)' : 'transparent',
                    border: `1px solid ${dashboardEditMode ? 'var(--color-accent)' : 'color-mix(in srgb, var(--color-accent) 20%, transparent)'}`,
                    borderRadius: 'var(--radius-xl)',
                    color: dashboardEditMode ? '#fff' : 'var(--color-text-secondary)',
                    fontSize: 'var(--fs-xs-plus)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    minHeight: 36,
                  }}
                >
                  {dashboardEditMode ? 'Done' : 'Customize'}
                </button>
              </div>
            </div>

            {dashboardEditMode && (
              <div style={{ margin: '8px 0 4px', padding: '10px 14px', background: 'color-mix(in srgb, var(--color-accent) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--color-accent) 20%, transparent)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)' }}>Drag widgets to reorder · tap the eye to show or hide</span>
              </div>
            )}

            {/* ── Reorderable Widget Section ─────────── */}
            {widgetOrder.map(widgetId => {
              const isVisible = widgetVisible[widgetId] !== false;
              const isDragging = dragWidgetId === widgetId;
              const isDragTarget = dragOverWidgetId === widgetId;

              const wrapWidget = (content: React.ReactNode) => (
                <div
                  key={widgetId}
                  ref={(el) => { widgetRefs.current[widgetId] = el; }}
                  draggable={dashboardEditMode}
                  onDragStart={dashboardEditMode ? () => handleDragStart(widgetId) : undefined}
                  onDragOver={dashboardEditMode ? (e) => handleDragOver(e, widgetId) : undefined}
                  onDrop={dashboardEditMode ? (e) => handleDrop(e, widgetId) : undefined}
                  onDragEnd={dashboardEditMode ? handleDragEnd : undefined}
                  onTouchStart={dashboardEditMode ? () => handleTouchStart(widgetId) : undefined}
                  onTouchMove={dashboardEditMode ? handleTouchMove : undefined}
                  onTouchEnd={dashboardEditMode ? handleTouchEnd : undefined}
                  style={{
                    opacity: isDragging ? 0.5 : 1,
                    outline: isDragTarget ? '2px solid var(--color-accent)' : 'none',
                    borderRadius: 'var(--radius-lg)',
                    transition: 'opacity 0.15s',
                    position: 'relative',
                    touchAction: dashboardEditMode ? 'none' : 'auto',
                  }}
                >
                  {dashboardEditMode && (
                    <div style={{
                      position: 'absolute',
                      top: 0, left: 0, right: 0, bottom: 0,
                      zIndex: 'var(--z-edit-overlay)',
                      borderRadius: 'var(--radius-lg)',
                      background: 'rgba(0,0,0,0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0 14px',
                      pointerEvents: 'none',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, pointerEvents: 'none' }}>
                        <span style={{ fontSize: 'var(--fs-md)', opacity: 0.7 }}>☰</span>
                        <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: '#fff', opacity: 0.9 }}>{WIDGET_LABELS[widgetId] ?? widgetId}</span>
                      </div>
                      {/* eslint-disable-next-line cw/no-inline-button-style -- widget visibility toggle inside dashboard edit-mode overlay; absolute-positioned icon-only with pointer-events override, not a chamfered .btn variant */}
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleWidgetVisible(widgetId); }}
                        style={{
                          pointerEvents: 'auto',
                          background: 'color-mix(in srgb, var(--color-text-primary) 8%, transparent)',
                          border: '1px solid color-mix(in srgb, var(--color-text-primary) 25%, transparent)',
                          borderRadius: 'var(--radius-sm)',
                          color: '#fff',
                          fontSize: 'var(--fs-md)',
                          width: 36, height: 36,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          cursor: 'pointer',
                        }}
                        aria-label={isVisible ? 'Hide widget' : 'Show widget'}
                        title={isVisible ? 'Hide widget' : 'Show widget'}
                      >
                        {isVisible ? '👁' : '🙈'}
                      </button>
                    </div>
                  )}
                  {(!dashboardEditMode && !isVisible) ? null : (
                    <div style={{ opacity: !isVisible && dashboardEditMode ? 0.35 : 1 }}>
                      {content}
                    </div>
                  )}
                </div>
              );

              if (widgetId === 'checkin') return wrapWidget(<>
              {/* Daily Check-In CTA */}
              {!checkinDone && (
                <div style={{ marginBottom: 14 }}>
                  <button
                    onClick={() => startCheckin()}
                    className="cw-hero-cta"
                  >
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontWeight: 800, fontSize: 'var(--fs-base-plus)', color: 'var(--color-morph-ink)', letterSpacing: '-0.01em' }}>Start today's check-in</div>
                      <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-morph-ink-soft)', marginTop: 4 }}>About 2 min — pain, mood, energy, coping</div>
                    </div>
                    <div style={{ fontSize: 'var(--fs-xl)', opacity: 0.8 }}>→</div>
                  </button>
                </div>
              )}
              {checkinDone && (
                <div style={{ marginBottom: 14, padding: '16px 18px', background: 'linear-gradient(135deg, color-mix(in srgb, var(--color-success) 10%, transparent), color-mix(in srgb, var(--color-success) 6%, transparent))', border: '1.5px solid color-mix(in srgb, var(--color-success) 30%, transparent)', borderRadius: 'var(--radius-lg)', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}><IconCheck size={22} ariaLabel="" /></span>
                    <div style={{ fontWeight: 700, fontSize: 'var(--fs-base)', color: 'var(--color-success)' }}>Check-in complete for today</div>
                  </div>
                  <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
                    You can check in again any time from the Tools screen.
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {/* eslint-disable-next-line cw/no-inline-button-style -- inline .btn-primary style override (flex/padding/font-size) on completed-check-in pair; rule flags any style on button regardless of className */}
                    <button className="btn btn-primary" style={{ flex: 1, padding: '11px 14px', fontSize: 'var(--fs-sm)' }} onClick={() => showScreen('patterns')}>View patterns</button>
                    {/* eslint-disable-next-line cw/no-inline-button-style -- inline .btn-secondary style override (flex/padding/font-size) on completed-check-in pair; rule flags any style on button regardless of className */}
                    <button className="btn btn-secondary" style={{ flex: 1, padding: '11px 12px', fontSize: 'var(--fs-sm)' }} onClick={() => showScreen('event-log')}>+ Log stigma</button>
                  </div>
                </div>
              )}

              </>);

              if (widgetId === 'weather') return wrapWidget((() => {
                const WX_ICON_KEY: Record<number, CrystalIconKey> = {
                  0: 'weather-clear', 1: 'weather-partly', 2: 'weather-partly', 3: 'weather-cloudy',
                  45: 'weather-fog', 48: 'weather-fog',
                  51: 'weather-drizzle', 53: 'weather-drizzle',
                  55: 'weather-rain', 61: 'weather-rain', 63: 'weather-rain', 65: 'weather-rain',
                  71: 'weather-snow', 73: 'weather-snow', 75: 'weather-snow', 77: 'weather-snow',
                  80: 'weather-drizzle', 81: 'weather-drizzle',
                  82: 'weather-storm',
                  85: 'weather-snow', 86: 'weather-snow',
                  95: 'weather-storm', 96: 'weather-storm', 99: 'weather-storm',
                };
                const wxIcon = (code: number) => {
                  const Icon = CRYSTAL_ICONS[WX_ICON_KEY[code] ?? 'weather-cloudy'];
                  return <Icon size={36} ariaLabel="Today's weather" />;
                };
                if (flareWeather) {
                  const tMin = flareWeather.daily.temperature_2m_min[0];
                  const tMax = flareWeather.daily.temperature_2m_max[0];
                  const wind = flareWeather.daily.windspeed_10m_max[0];
                  const rain = flareWeather.daily.precipitation_probability_max[0];
                  const code = flareWeather.daily.weathercode[0];
                  let wScore = 0;
                  if (tMin < 0) wScore += 2; else if (tMin < 5) wScore += 1;
                  if (tMax > 32) wScore += 1;
                  if (wind > 40 && tMin < 10) wScore += 1;
                  const rLevel = wScore >= 3 ? 'high' : wScore >= 1 ? 'moderate' : 'low';
                  const rColor = { low:'var(--color-success)', moderate:'var(--color-warning)', high:'var(--color-crisis)' }[rLevel];
                  const rBg = { low:'color-mix(in srgb, var(--color-success) 8%, transparent)', moderate:'color-mix(in srgb, var(--color-warning) 8%, transparent)', high:'color-mix(in srgb, var(--color-crisis) 8%, transparent)' }[rLevel];
                  const rBorder = { low:'color-mix(in srgb, var(--color-success) 25%, transparent)', moderate:'color-mix(in srgb, var(--color-warning) 25%, transparent)', high:'color-mix(in srgb, var(--color-crisis) 30%, transparent)' }[rLevel];
                  return (
                    <div
                      onClick={() => { setWeatherBackTarget('home'); showScreen('weather-report'); }}
                      style={{ marginBottom:12, padding:'16px 18px', background:rBg, border: `1px solid ${rBorder}`, borderRadius: 'var(--radius-lg)', cursor:'pointer', display:'flex', alignItems:'center', gap:14 }}
                    >
                      <div style={{ lineHeight:1, flexShrink:0, display:'flex', alignItems:'center' }}>{wxIcon(code)}</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                          <span style={{ fontWeight:700, fontSize: 'var(--fs-sm)', color:'var(--color-text-primary)', display:'inline-flex', alignItems:'center', gap:6 }}><IconWeatherDrizzle size={16} ariaLabel="" /> Today's Flare Risk</span>
                          <span style={{ fontSize: 'var(--fs-xs)', fontWeight:700, padding:'2px 8px', borderRadius: 'var(--radius-xl)', background: `color-mix(in srgb, ${rColor} 13%, transparent)`, color:rColor, border: `1px solid color-mix(in srgb, ${rColor} 33%, transparent)` }}>{rLevel.charAt(0).toUpperCase()+rLevel.slice(1)}</span>
                        </div>
                        <div style={{ fontSize: 'var(--fs-xs)', color:'var(--color-text-secondary)', display:'flex', flexWrap:'wrap', alignItems:'center', gap:6 }}>
                          <span>{Math.round(tMin)}°–{Math.round(tMax)}°C · {rain}% rain</span>
                          {wind > 30 && (
                            <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                              · <IconWeatherWind size={16} ariaLabel="" /> {Math.round(wind)} km/h
                            </span>
                          )}
                          {flareLocation && (
                            <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                              · <IconWeatherLocation size={16} ariaLabel="" /> {flareLocation.name.split(',')[0]}
                            </span>
                          )}
                        </div>
                      </div>
                      <span style={{ fontSize: 'var(--fs-xs)', color:'var(--color-text-secondary)', flexShrink:0 }}>›</span>
                    </div>
                  );
                }
                if (flareLocationStatus === 'idle') {
                  return (
                    <div
                      onClick={() => { setWeatherBackTarget('home'); showScreen('weather-report'); }}
                      style={{ marginBottom:12, padding:'16px 18px', background:'color-mix(in srgb, var(--color-accent) 7%, transparent)', border:'1px solid color-mix(in srgb, var(--color-accent) 20%, transparent)', borderRadius: 'var(--radius-lg)', cursor:'pointer', display:'flex', alignItems:'center', gap:12 }}
                    >
                      <span style={{ flexShrink:0, display:'inline-flex' }}><IconWeatherDrizzle size={28} ariaLabel="" /></span>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:700, fontSize: 'var(--fs-sm)', color:'var(--color-text-primary)' }}>Set up Weather Report</div>
                        <div style={{ fontSize: 'var(--fs-xs)', color:'var(--color-text-secondary)', marginTop:2 }}>See weather-based flare risk tailored to your patterns</div>
                      </div>
                      <span style={{ fontSize: 'var(--fs-xs)', color:'var(--color-text-secondary)', flexShrink:0 }}>›</span>
                    </div>
                  );
                }
                return null;
              })());

              if (widgetId === 'journeys') return wrapWidget(<>
              {/* Journey context strip */}
              {Object.keys(activeJourneys).length === 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <h3 style={{ fontSize: 'var(--fs-base)', margin: 0, color: 'var(--color-text-primary)', fontWeight: 700, display:'inline-flex', alignItems:'center', gap:6 }}><IconJourneys size={18} ariaLabel="" /> Journeys</h3>
                    <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--color-accent)', background: 'color-mix(in srgb, var(--color-accent) 12%, transparent)', borderRadius: 'var(--radius-sm)', padding: '2px 7px' }}>In Growth</span>
                  </div>
                  <div
                    onClick={() => { setGrowthTab('journeys'); showScreen('growth'); }}
                    style={{
                      padding: '16px 18px',
                      background: 'color-mix(in srgb, var(--color-accent) 6%, transparent)',
                      border: '1px solid color-mix(in srgb, var(--color-accent) 18%, transparent)',
                      borderRadius: 'var(--radius-lg)',
                      cursor: 'pointer',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ marginBottom: 8, display:'flex', justifyContent:'center' }}><IconJourneys size={36} ariaLabel="" /></div>
                    <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm-plus)', color: 'var(--color-text-primary)', marginBottom: 4 }}>
                      Start a journey when you're ready
                    </div>
                    <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.4, marginBottom: 12 }}>
                      Journeys give you context-specific support — hospital visits, school, mental health, and more.
                    </div>
                    <span style={{ display: 'inline-block', padding: '8px 20px', background: 'var(--color-accent)', color: '#fff', borderRadius: 'var(--radius-md)', fontSize: 'var(--fs-sm)', fontWeight: 600 }}>
                      Browse Journeys in Growth →
                    </span>
                  </div>
                </div>
              )}
              {Object.keys(activeJourneys).length > 0 && (() => {
                const JOURNEY_MAP: Record<string, { icon?: string; iconKey: CrystalIconKey; title: string; color: string; phases: { id: string; label: string; desc: string }[] }> = {
                  'clinic-visit': { icon: '🩺', iconKey: 'journey-clinic-visit', title: 'Clinic Visit', color: 'var(--color-crisis)', phases: [{ id: 'before', label: 'Getting Ready', desc: 'Prep questions, documents, and mindset the night before' }, { id: 'clinic', label: 'In the Room', desc: 'Advocate, ask, and document during your appointment' }, { id: 'follow-up', label: 'What Comes Next', desc: 'Action items, tracking, and follow-up between visits' }] },
                  'emergency-room': { icon: '🚨', iconKey: 'journey-er-prep', title: 'Emergency Room Visit', color: 'var(--color-crisis)', phases: [{ id: 'before', label: 'Before I Go', desc: 'Pack and prepare your advocacy tools' }, { id: 'triage', label: 'In Triage', desc: 'Get your pain recorded accurately' }, { id: 'waiting', label: 'Waiting & Admitted', desc: 'Managing the wait and advocating for care' }, { id: 'going-home', label: 'Going Home', desc: 'Pre-discharge checklist' }, { id: 'recovery', label: 'Recovery & Reflection', desc: 'Rest, reflect, update your plan' }] },
                  'hospital-admission': { icon: '🏥', iconKey: 'journey-hospital-admission', title: 'Hospital Admission', color: 'var(--color-crisis)', phases: [{ id: 'admitted', label: 'Admitted', desc: 'Setting up your care on the ward' }, { id: 'daily', label: 'Daily in Hospital', desc: 'Daily tracking, questions, and rights' }, { id: 'preparing-to-leave', label: 'Preparing to Leave', desc: 'Pre-discharge checklist' }, { id: 'recovery-at-home', label: 'Recovery at Home', desc: 'Rest, reflect, update your plan' }] },
                  'school': { icon: '🏫', iconKey: 'journey-school', title: 'School', color: 'var(--color-accent)', phases: [{ id: 'ready', label: 'Getting Ready', desc: 'Set up accommodations early' }, { id: 'managing', label: 'Managing', desc: 'Absences, flares, disclosure' }, { id: 'moving', label: 'Moving Up', desc: 'Transitioning to next level' }] },
                  'independence': { icon: '🏠', iconKey: 'journey-moving-out', title: 'Moving Out', color: 'var(--color-info)', phases: [{ id: 'preparing', label: 'Preparing', desc: 'Build your plan before moving out' }, { id: 'first-months', label: 'First Months', desc: 'SCD on your own for the first time' }, { id: 'established', label: 'Established', desc: 'Routines and building your network' }] },
                  'post-secondary': { icon: '🎓', iconKey: 'journey-post-secondary', title: 'Post-Secondary', color: 'var(--color-accent)', phases: [{ id: 'getting-in', label: 'Getting In', desc: 'Applications, disclosure, and choosing the right fit' }, { id: 'first-semester', label: 'First Semester', desc: 'Accessibility, course load, and finding your rhythm' }, { id: 'staying-in', label: 'Staying In', desc: 'Managing long-term through flares, absences, and burnout' }] },
                  'adult-transition': { icon: '🔄', iconKey: 'journey-adult-transition', title: 'Adult Transition', color: 'var(--color-success)', phases: [{ id: 'foundation', label: 'Building My Foundation', desc: 'Know your history, find your voice' }, { id: 'wheel', label: 'Taking the Wheel', desc: 'First adult appointments' }, { id: 'own', label: 'In My Own Care', desc: 'Settled and thriving' }] },
                  'mental-health-journey': { icon: '🧠', iconKey: 'journey-mental-health', title: 'Mental Health', color: 'var(--color-accent)', phases: [{ id: 'noticing', label: 'Noticing', desc: 'Recognizing I\'m struggling' }, { id: 'support', label: 'Getting Support', desc: 'Finding care and what helps' }, { id: 'living', label: 'Living With Both', desc: 'Long-term management' }] },
                  'substance-use-journey': { icon: '🌿', iconKey: 'journey-substance-use', title: 'SCD & Substance Use', color: 'var(--color-success)', phases: [{ id: 'whats-real', label: "What's Real", desc: 'Honest facts by substance — alcohol, cannabis, nicotine, other' }, { id: 'knowing-risks', label: 'Knowing My Risks', desc: 'Connecting the dots between use and SCD' }, { id: 'making-choices', label: 'Making My Choices', desc: 'Agency, harm reduction, and support' }] },
                  'navigating-stigma': { icon: '🛡️', iconKey: 'journey-navigating-stigma', title: 'Navigating Stigma', color: 'var(--color-accent)', phases: [{ id: 'medical-stigma', label: 'Medical Stigma', desc: 'Drug-seeking labels, ED dismissal' }, { id: 'school-social-stigma', label: 'School & Social Stigma', desc: 'Disbelief in school and social settings' }, { id: 'community-family-stigma', label: 'Community & Family Stigma', desc: 'Cultural silence and family minimization' }, { id: 'internalized-stigma', label: 'Internalized Stigma', desc: 'Self-doubt and rebuilding your narrative' }] },
                  'trauma-healing': { icon: '🌱', iconKey: 'journey-trauma-healing', title: 'Trauma & Healing', color: 'var(--color-accent)', phases: [{ id: 'recognising-trauma', label: 'Recognizing Trauma', desc: 'Hypervigilance and avoidance patterns' }, { id: 'processing-grounding', label: 'Processing & Grounding', desc: 'Breathwork, body check-ins, journalling' }, { id: 'trauma-informed-support', label: 'Trauma-Informed Support', desc: 'Building a team that understands' }] },
                  'migration-scd': { icon: '🌍', iconKey: 'journey-migration-scd', title: 'Migration & SCD Culture', color: 'var(--color-accent)', phases: [{ id: 'new-health-system', label: 'New Health System', desc: 'OHIP, hematologists, ED culture' }, { id: 'cultural-language-barriers', label: 'Cultural & Language Barriers', desc: 'Interpreters and communication' }, { id: 'community-diaspora', label: 'Community & Diaspora', desc: 'Finding SCD community in Canada' }] },
                  'familial-stigma': { icon: '💜', iconKey: 'journey-familial-stigma', title: 'Familial Stigma & Denial', color: 'var(--color-accent)', phases: [{ id: 'family-disbelief', label: 'Family Disbelief', desc: 'When family doesn\'t believe you' }, { id: 'having-conversation', label: 'Having the Conversation', desc: 'Scripts for family conversations' }, { id: 'protecting-yourself', label: 'Protecting Yourself', desc: 'Limits, chosen family, coping' }] },
                  'relationships-disclosure': { icon: '💛', iconKey: 'journey-relationships', title: 'Relationships & Disclosure', color: 'var(--color-crisis)', phases: [{ id: 'deciding', label: 'Deciding to Tell', desc: 'Thinking through disclosure' }, { id: 'conversation', label: 'Having the Conversation', desc: 'Scripts and preparation' }, { id: 'after-sharing', label: 'After You\'ve Shared', desc: 'Navigating responses and outcomes' }] },
                  'travel-flying': { icon: '✈️', iconKey: 'journey-travel-flying', title: 'Travel & SCD', color: 'var(--color-crisis)', phases: [{ id: 'before-i-go', label: 'Before I Go', desc: 'Packing, documentation, insurance, research' }, { id: 'on-the-way', label: 'On the Way', desc: 'In-transit hydration, movement, and management' }, { id: 'while-there', label: "While I'm There", desc: 'Managing away from home' }, { id: 'getting-home', label: 'Getting Home', desc: 'Return, recovery, and lessons learned' }] },
                  'career-workplace': { icon: '💼', iconKey: 'journey-career-workplace', title: 'Work & Accommodation', color: 'var(--color-info)', phases: [{ id: 'landing-job', label: 'Landing the Job', desc: 'Resume, interviews, and sustainable work' }, { id: 'disclosure', label: 'Do I Tell Them', desc: 'Disclosure, privacy, and legal rights' }, { id: 'on-the-clock', label: 'On the Clock', desc: 'Shift survival, flares at work, and calling in' }, { id: 'knowing-rights', label: 'Knowing My Rights', desc: 'Accommodation requests and Ontario Human Rights Code' }] },
                  'financial-insurance': { icon: '💰', iconKey: 'journey-financial-insurance', title: 'Financial & Insurance', color: 'var(--color-info)', phases: [{ id: 'understanding-coverage', label: 'Understanding Coverage', desc: 'OHIP, ODB, Trillium, private insurance' }, { id: 'denied-claims', label: 'Denied Claims', desc: 'Appeals, assistance programs' }, { id: 'planning-gaps', label: 'Planning for Gaps', desc: 'RDSP, DTC, long-term planning' }] },
                  'sexual-health': { icon: '❤️‍🔥', iconKey: 'journey-sexual-health', title: 'Sexual Health & SCD', color: 'var(--color-secondary)', phases: [{ id: 'what-to-know', label: 'What I Need to Know', desc: 'How SCD affects sexual health, priapism, energy' }, { id: 'having-conversation', label: 'Having the Conversation', desc: 'Disclosure, boundaries, partner scripts' }, { id: 'getting-care', label: 'Getting the Care I Need', desc: 'Provider scripts, referrals, advocacy' }] },
                  'menstrual-health': { icon: '🩸', iconKey: 'journey-menstrual-health', title: 'Menstrual Health & SCD', color: 'var(--color-secondary)', phases: [{ id: 'basics', label: 'The Basics', desc: 'How SCD affects menstruation, tracking, and pain' }, { id: 'patterns-pain', label: 'Patterns & Pain', desc: 'Reading your data, management options' }, { id: 'talking-about-it', label: 'Talking About It', desc: 'Provider scripts, advocacy, referrals' }] },
                  'family-planning': { icon: '👶', iconKey: 'journey-family-planning', title: 'Family Planning & SCD', color: 'var(--color-secondary)', phases: [{ id: 'before-anything', label: 'Before Anything Else', desc: 'Genetics, autonomy, and bigger questions' }, { id: 'my-options', label: 'My Options', desc: 'Contraception, fertility, provider scripts' }, { id: 'making-decisions', label: 'Making Decisions', desc: 'Your choice, navigating pressure, next steps' }] },
                  'full-picture': { icon: '🌈', iconKey: 'journey-full-picture', title: 'The Full Picture', color: 'var(--color-accent)', phases: [{ id: 'thinking', label: 'Thinking About It', desc: 'Open space to consider adding something new' }, { id: 'setting-up', label: 'Setting It Up', desc: 'Research, referrals, first steps' }, { id: 'showing-up', label: 'Showing Up', desc: 'Early experiences, first sessions' }, { id: 'staying-with-it', label: 'Staying With It', desc: 'Maintenance, evaluation, barriers' }, { id: 'changing-course', label: 'Changing Course', desc: 'Stopping, pivoting, or coming back' }] },
                  'body-image': { icon: '🪞', iconKey: 'journey-body-image', title: 'Body Image & SCD', color: 'var(--color-accent)', phases: [{ id: 'whose-standards', label: 'Whose Standards', desc: 'Naming the sources of body messages' }, { id: 'the-noise', label: 'The Noise', desc: 'What gets said, and what it means' }, { id: 'unpacking-it', label: 'Unpacking It', desc: 'Separating your voice from the noise' }, { id: 'on-my-terms', label: 'On My Terms', desc: 'Reclaiming, expressing, and building your standard' }] },
                };
                const activeEntries = Object.entries(activeJourneys);
                const [firstJId, firstPhaseId] = activeEntries[0] ?? [];
                const firstMeta = firstJId ? JOURNEY_MAP[firstJId] : null;
                const firstPhase = firstMeta?.phases.find(p => p.id === firstPhaseId);
                const firstPhaseResources = firstJId && firstPhaseId ? (phaseResourcesData[firstJId] ?? {})[firstPhaseId] ?? [] : [];
                const firstTotal = firstPhaseResources.reduce((a: number, r: JourneyResourceItem) => a + (r.items?.length ?? 0), 0);
                const firstDone = firstPhaseResources.reduce((a: number, r: JourneyResourceItem) => a + (r.items?.filter((_x: string, i: number) => completedJourneyResources.has(`${r.id}-${i}`)).length ?? 0), 0);
                return (
                  <div style={{ marginBottom: 12 }}>
                    {firstMeta && firstPhase && firstTotal > 0 && (
                      <div
                        onClick={() => setActiveScene({ journeyId: firstJId, phaseId: firstPhaseId, phaseLabel: firstPhase.label, journeyColor: firstMeta.color })}
                        className="cw-journey-card"
                      >
                        <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--color-morph-ink-rose)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 5 }}>Continue your journey</div>
                        <div style={{ fontWeight: 800, fontSize: 'var(--fs-sm-plus)', color: 'var(--color-morph-ink)', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
                          {(() => { const Icon = CRYSTAL_ICONS[firstMeta.iconKey]; return <Icon size={20} ariaLabel="" />; })()} {firstMeta.title}
                        </div>
                        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-morph-ink-soft)', marginBottom: 10 }}>
                          {firstPhase.label} · {firstDone}/{firstTotal} explored
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--color-bg-surface)', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${firstTotal > 0 ? Math.round((firstDone / firstTotal) * 100) : 0}%`, background: 'var(--color-accent)', borderRadius: 3, transition: 'width 0.4s' }} />
                          </div>
                          <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-morph-ink-plum)', fontWeight: 700, flexShrink: 0 }}>Enter →</span>
                        </div>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <h3 style={{ fontSize: 'var(--fs-base)', margin: 0, color: 'var(--color-text-primary)', fontWeight: 700, display:'inline-flex', alignItems:'center', gap:6 }}><IconJourneys size={18} ariaLabel="" /> Active journeys</h3>
                        <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--color-accent)', background: 'color-mix(in srgb, var(--color-accent) 12%, transparent)', borderRadius: 'var(--radius-sm)', padding: '2px 7px' }}>In Growth</span>
                      </div>
                      {/* eslint-disable-next-line cw/no-inline-button-style -- inline section-header text-link "New Journey ›"; ghost-only with no border/bg, sits next to <h3>, not a chamfered .btn variant */}
                      <button onClick={() => { const firstGroup = Object.keys(activeJourneys)[0]; const groupId = firstGroup === 'clinic-visit' || firstGroup === 'emergency-room' || firstGroup === 'hospital-admission' ? 'hospital' : firstGroup === 'school' || firstGroup === 'independence' || firstGroup === 'post-secondary' ? 'life' : firstGroup === 'adult-transition' ? 'transition' : firstGroup === 'mental-health-journey' || firstGroup === 'full-picture' ? 'mental-health' : firstGroup === 'substance-use-journey' ? 'substance-use' : firstGroup === 'navigating-stigma' || firstGroup === 'trauma-healing' || firstGroup === 'migration-scd' || firstGroup === 'familial-stigma' || firstGroup === 'body-image' ? 'identity-wellbeing' : firstGroup === 'sexual-health' || firstGroup === 'menstrual-health' || firstGroup === 'family-planning' ? 'health-body' : firstGroup === 'relationships-disclosure' || firstGroup === 'travel-flying' ? 'relationships-life' : firstGroup === 'career-workplace' || firstGroup === 'financial-insurance' ? 'work-finances' : 'hospital'; setJourneyGroupExpanded(groupId); setGrowthTab('journeys'); showScreen('growth'); }} style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-accent)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>New journey ›</button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {activeEntries.map(([jId, phaseId]) => {
                        const meta = JOURNEY_MAP[jId];
                        if (!meta) return null;
                        const phase = meta.phases.find(p => p.id === phaseId);
                        const phaseIdx = meta.phases.findIndex(p => p.id === phaseId);
                        const nextPhase = meta.phases[phaseIdx + 1];
                        const phaseResources = (phaseResourcesData[jId] ?? {})[phaseId] ?? [];
                        const totalSceneItems = phaseResources.reduce((a: number, r: JourneyResourceItem) => a + (r.items?.length ?? 0), 0);
                        const doneSceneItems = phaseResources.reduce((a: number, r: JourneyResourceItem) => a + (r.items?.filter((_x: string, i: number) => completedJourneyResources.has(`${r.id}-${i}`)).length ?? 0), 0);
                        const hasScene = totalSceneItems > 0;
                        return (
                          <div key={jId} style={{ padding: '12px 14px', background: `color-mix(in srgb, ${meta.color} 7%, transparent)`, border: `1px solid color-mix(in srgb, ${meta.color} 21%, transparent)`, borderRadius: 'var(--radius-md)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {(() => { const Icon = CRYSTAL_ICONS[meta.iconKey]; return <Icon size={18} ariaLabel="" />; })()}
                                <div>
                                  <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700 }}>{meta.title}</div>
                                  <div style={{ fontSize: 'var(--fs-xs)', color: meta.color, fontWeight: 600, marginTop: 1 }}>
                                    Step {phaseIdx + 1} of {meta.phases.length}: {phase?.label}
                                  </div>
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                                {meta.phases.map((_, pIdx) => (
                                  <div key={pIdx} style={{ width: 6, height: 6, borderRadius: '50%', background: pIdx <= phaseIdx ? meta.color : 'color-mix(in srgb, var(--color-text-primary) 8%, transparent)' }} />
                                ))}
                              </div>
                            </div>
                            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginTop: 6 }}>{phase?.desc}</div>
                            {jId === 'emergency-room' && (phaseId === 'triage' || phaseId === 'waiting') && edModeState === 'off' && (
                              <div style={{ marginTop: 8, padding: '8px 10px', background: 'color-mix(in srgb, var(--color-crisis) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--color-crisis) 30%, transparent)', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-crisis)', fontWeight: 600, lineHeight: 1.3, display: 'flex', alignItems: 'center', gap: 6 }}><IconCrisisPlan size={14} ariaLabel="" /> You're in the crisis phase — use the Crisis button (bottom right) to activate Crisis Mode</span>
                              </div>
                            )}
                            {hasScene && (
                              // eslint-disable-next-line cw/no-inline-button-style -- inline active-journey scene-enter row; full-width with dynamic per-journey color tint + progress fraction on right, not a chamfered .btn variant
                              <button
                                onClick={() => setActiveScene({ journeyId: jId, phaseId, phaseLabel: phase?.label ?? phaseId, journeyColor: meta.color })}
                                style={{
                                  marginTop: 10,
                                  width: '100%',
                                  padding: '9px 12px',
                                  background: `color-mix(in srgb, ${meta.color} 13%, transparent)`,
                                  border: `1.5px solid color-mix(in srgb, ${meta.color} 33%, transparent)`,
                                  borderRadius: 'var(--radius-md)',
                                  color: meta.color,
                                  fontSize: 'var(--fs-xs)',
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                }}
                              >
                                <span>Enter →</span>
                                <span style={{ fontSize: 'var(--fs-xs)', opacity: 0.75 }}>{doneSceneItems}/{totalSceneItems}</span>
                              </button>
                            )}
                            {nextPhase && (
                              <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)' }}>Next: {nextPhase.label}</span>
                                {/* eslint-disable-next-line cw/no-inline-button-style -- inline next-phase advance chip; compact rounded-8 pill with per-journey color tint, sits in flex row next to "Next: <label>" text, not a chamfered .btn variant */}
                                <button onClick={() => { advanceJourneyPhase(jId, nextPhase.id, phaseIdx + 1 === meta.phases.length - 1); }} style={{ fontSize: 'var(--fs-xs)', padding: '3px 8px', background: `color-mix(in srgb, ${meta.color} 15%, transparent)`, border: `1px solid color-mix(in srgb, ${meta.color} 31%, transparent)`, borderRadius: 'var(--radius-sm)', color: meta.color, cursor: 'pointer', fontWeight: 600 }}>
                                  Move forward +50 XP
                                </button>
                              </div>
                            )}
                            {!nextPhase && (
                              <div style={{ marginTop: 8, fontSize: 'var(--fs-xs)', color: 'var(--color-success)', fontWeight: 600, display:'inline-flex', alignItems:'center', gap:6 }}><IconSparkle size={14} ariaLabel="" /> Final phase — journey almost complete!</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              </>);

              if (widgetId === 'medications') return wrapWidget(<>
              {edModeState !== 'off' && (
                <div style={{ marginBottom: 16 }}>
                  <div
                    className={`ed-mode-card${edModeState === 'active' ? ' ed-mode-card--active' : ''}`}
                    style={{ background: edConf.bg, border: edConf.border, padding: '16px 16px' }}
                  >
                    <div
                      className="ed-mode-card__status"
                      role="button"
                      tabIndex={0}
                      aria-label="View Crisis Report"
                      onClick={() => openModal('ed-report')}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openModal('ed-report'); } }}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="ed-mode-card__icon" style={{ fontSize: 'var(--fs-2xl)' }}>{edConf.icon}</span>
                      <span className="ed-mode-card__label" style={{ color: edConf.color, fontSize: 'var(--fs-base)', fontWeight: 700 }}>
                        {edConf.label}
                      </span>
                      <span className="ed-mode-card__report-hint" style={{ color: edConf.color, fontSize: 'var(--fs-sm)' }}>View Report ›</span>
                    </div>
                    <p className="ed-mode-card__subtext" style={{ color: edConf.color }}>{edConf.subtext}</p>
                    {/* eslint-disable-next-line cw/no-inline-button-style -- inline ed-mode advance button overrides on dedicated .ed-mode-card__advance-btn class; per-stage edConf color injection, rule flags any style on button regardless of className */}
                    <button
                      className="ed-mode-card__advance-btn"
                      style={{ borderColor: edConf.color, color: edConf.color, fontSize: 'var(--fs-sm-plus)', fontWeight: 700, padding: '12px 16px' }}
                      onClick={cycleEDMode}
                    >
                      {edModeState === 'plan' && 'Begin visit →'}
                      {edModeState === 'active' && 'End visit →'}
                      {edModeState === 'reflect' && 'Finish reflection →'}
                    </button>
                    {/* eslint-disable-next-line cw/no-inline-button-style -- ed-mode hospital-prep sub-action; full-width tinted secondary inside the ed-mode card with per-stage color tint, not a chamfered .btn variant */}
                    <button
                      onClick={() => showScreen('hospital-prep')}
                      style={{ marginTop: 8, width: '100%', padding: '10px 16px', background: 'var(--color-bg-surface)', border: `1px solid color-mix(in srgb, ${edConf.color} 33%, transparent)`, borderRadius: 'var(--radius-sm)', color: edConf.color, fontSize: 'var(--fs-sm)', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                    >
                      <IconHospitalPrep size={18} ariaLabel="" /> Hospital prep kit
                    </button>
                  </div>
                </div>
              )}

              {/* Medication Summary */}
              {medications.filter(m => m.active).length > 0 && (() => {
                const activeMeds = medications.filter(m => m.active);
                const todayLogs = medicationLogs.filter(l => l.date === getLocalDate() && l.taken);
                const totalSlotsDue = activeMeds.reduce((sum, m) => sum + frequencyToDoseCount(m.frequency), 0);
                const totalSlotsTaken = todayLogs.length;
                const takenMeds = activeMeds.filter(m => {
                  const count = frequencyToDoseCount(m.frequency);
                  return Array.from({ length: count }, (_, i) => i).every(i => todayLogs.some(l => l.medicationId === m.id && l.slotIndex === i));
                });
                const allDone = totalSlotsTaken >= totalSlotsDue;
                const remaining = totalSlotsDue - totalSlotsTaken;
                return (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <h3 style={{ fontSize: 'var(--fs-base)', margin: 0, color: 'var(--color-text-primary)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}><IconMedications size={20} ariaLabel="" /> Medications</h3>
                      {/* eslint-disable-next-line cw/no-inline-button-style -- inline section-header text-link "Manage ›"; ghost-only sibling to <h3>, not a chamfered .btn variant */}
                      <button onClick={() => showScreen('medications')} style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-accent)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Manage ›</button>
                    </div>
                    <div style={{ padding: '16px 18px', background: allDone ? 'color-mix(in srgb, var(--color-success) 6%, transparent)' : 'color-mix(in srgb, var(--color-warning) 7%, transparent)', border: `1px solid ${allDone ? 'color-mix(in srgb, var(--color-success) 25%, transparent)' : 'color-mix(in srgb, var(--color-warning) 30%, transparent)'}`, borderRadius: 'var(--radius-lg)' }}>
                      {allDone ? (
                        <div>
                          <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', marginBottom: 8 }}>All doses logged today:</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {takenMeds.map(med => (
                              <div key={med.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--color-success)' }}><IconCheck size={14} ariaLabel="" /></span>
                                <div>
                                  <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>{med.name}</div>
                                  <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)' }}>{med.dose}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div
                          onClick={() => showScreen('medications')}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 12,
                            cursor: 'pointer',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{
                              width: 38,
                              height: 38,
                              borderRadius: 'var(--radius-md)',
                              background: 'color-mix(in srgb, var(--color-warning) 18%, transparent)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}><IconMedications size={22} ariaLabel="" /></div>
                            <div>
                              <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 700, color: 'var(--color-warning)' }}>
                                {totalSlotsTaken === 0 ? 'No doses logged today' : `${remaining} dose${remaining !== 1 ? 's' : ''} still to log`}
                              </div>
                              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginTop: 2 }}>
                                {totalSlotsTaken === 0
                                  ? `${totalSlotsDue} dose${totalSlotsDue !== 1 ? 's' : ''} due today`
                                  : `${totalSlotsTaken} of ${totalSlotsDue} logged`
                                }
                              </div>
                            </div>
                          </div>
                          <div style={{
                            padding: '6px 14px',
                            background: 'color-mix(in srgb, var(--color-warning) 18%, transparent)',
                            border: '1px solid color-mix(in srgb, var(--color-warning) 40%, transparent)',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: 'var(--fs-xs)',
                            fontWeight: 700,
                            color: 'var(--color-warning)',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                          }}>
                            Log now →
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {edModeState === 'active' && (
                <div className="ed-active-panel">
                  <div style={{ fontWeight: 600, marginBottom: 8, color: 'var(--color-danger)', display: 'flex', alignItems: 'center', gap: 6 }}><IconCrisisPlan size={18} ariaLabel="" /> Active Crisis</div>

                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginBottom: 6 }}>Where are you in the visit?</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {ED_STAGES.map(stage => (
                        <div
                          key={stage.id}
                          className={`tag ${edStage === stage.id ? 'selected' : ''}`}
                          style={{ fontSize: 'var(--fs-xs)', cursor: 'pointer' }}
                          onClick={() => setEdStage(stage.id)}
                        >
                          {stage.label}
                        </div>
                      ))}
                    </div>
                  </div>

                  <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', marginBottom: 12 }}>
                    {edCheckins.length} check-in{edCheckins.length !== 1 ? 's' : ''} logged this visit
                  </p>
                  {edCheckinComplete && !edCheckinSummary && (
                    <div className="ed-checkin-success" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><IconCheck size={16} ariaLabel="" /> Saved — we've got you</div>
                  )}
                  {edCheckinSummary && (
                    <div style={{ marginTop: 8, background: 'var(--color-bg-surface)', border: '1px solid color-mix(in srgb, var(--color-text-primary) 9%, transparent)', borderRadius: 'var(--radius-md)', padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}><IconCheck size={16} ariaLabel="" /></span>
                        <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 700 }}>Check-In Saved</div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                        <div style={{ padding: '6px 10px', background: 'color-mix(in srgb, var(--color-crisis) 10%, transparent)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--fs-xs)' }}>
                          <span style={{ color: 'var(--color-crisis)', fontWeight: 700 }}>🩸 Pain: {edCheckinSummary.painLevel}/10</span>
                        </div>
                        {edCheckinSummary.vocStatus && (
                          <div style={{ padding: '6px 10px', background: 'color-mix(in srgb, var(--color-warning) 10%, transparent)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--fs-xs)' }}>
                            <span style={{ color: 'var(--color-warning)', fontWeight: 600 }}>VOC: {edCheckinSummary.vocStatus === 'yes' ? 'In crisis' : edCheckinSummary.vocStatus === 'unsure' ? 'Unsure' : 'Not in crisis'}</span>
                          </div>
                        )}
                      </div>
                      {edCheckinSummary.bodyAreas.length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginBottom: 4 }}>Pain areas</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {edCheckinSummary.bodyAreas.map(pa => (
                              <span key={pa.area} style={{ fontSize: 'var(--fs-xs)', padding: '3px 7px', borderRadius: 'var(--radius-md)', background: 'var(--color-bg-surface)', border: '1px solid color-mix(in srgb, var(--color-text-primary) 10%, transparent)' }}>
                                {BODY_AREA_NAMES[pa.area] ?? pa.area}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {edCheckinSummary.safetyFlags.length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-crisis)', marginBottom: 4 }}>Safety flags noted</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {edCheckinSummary.safetyFlags.map(f => (
                              <span key={f} style={{ fontSize: 'var(--fs-xs)', padding: '3px 7px', borderRadius: 'var(--radius-md)', background: 'color-mix(in srgb, var(--color-crisis) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--color-crisis) 25%, transparent)', color: 'var(--color-crisis)' }}>{f}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {edCheckinSummary.safetyFlags.length > 0 ? (
                        <div style={{ marginBottom: 12, padding: '10px 12px', background: 'color-mix(in srgb, var(--color-crisis) 7%, transparent)', border: '1px solid color-mix(in srgb, var(--color-crisis) 20%, transparent)', borderRadius: 'var(--radius-sm)' }}>
                          <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--color-crisis)', marginBottom: 4, display:'inline-flex', alignItems:'center', gap:6 }}><IconHeart size={14} ariaLabel="" heartTint="var(--color-crisis)" /> It sounds like something difficult is happening</div>
                          <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', margin: '0 0 8px 0' }}>These articles may help you make sense of what you're experiencing.</p>
                          {/* eslint-disable-next-line cw/no-inline-button-style -- ed-checkin safety-alert articles link; warm-toned outlined chip in narrow alert card, not a chamfered .btn variant */}
                          <button style={{ background: 'none', border: '1px solid color-mix(in srgb, var(--color-crisis) 40%, transparent)', borderRadius: 'var(--radius-sm)', color: 'var(--color-crisis)', fontSize: 'var(--fs-xs)', fontWeight: 600, padding: '5px 10px', cursor: 'pointer' }} onClick={() => { setArticlesBackTarget('home'); showScreen('articles'); }}>
                            Stigma and support articles ›
                          </button>
                        </div>
                      ) : (
                        <div style={{ marginBottom: 12 }}>
                          {/* eslint-disable-next-line cw/no-inline-button-style -- ed-checkin no-flags articles link; ghost text-link with leading icon, sub-action inside summary card, not a chamfered .btn variant */}
                          <button style={{ background: 'none', border: 'none', color: 'var(--color-accent)', fontSize: 'var(--fs-xs)', fontWeight: 600, cursor: 'pointer', padding: 0, display:'inline-flex', alignItems:'center', gap:6 }} onClick={() => { setArticlesBackTarget('home'); showScreen('articles'); }}>
                            <IconBook size={14} ariaLabel="" /> Open the articles for support ›
                          </button>
                        </div>
                      )}
                      {checkinRecommendations.length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginBottom: 6 }}>Suggested for right now:</div>
                          {checkinRecommendations.map(item => (
                            <div
                              key={item.id}
                              style={{ padding: '8px 10px', background: 'color-mix(in srgb, var(--color-accent) 10%, transparent)', borderLeft: '3px solid var(--color-accent)', borderRadius: 'var(--radius-sm)', marginBottom: 6, cursor: 'pointer' }}
                              onClick={() => { setActiveContentItem(item); openModal('content-item'); }}
                            >
                              <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600 }}>{item.title}</div>
                              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginTop: 2 }}>{item.summary}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {/* eslint-disable-next-line cw/no-inline-button-style -- inline .btn-primary style override (font-size, padding) on ed-checkin summary action pair; rule flags any style on button regardless of className */}
                        <button className="btn btn-primary" style={{ fontSize: 'var(--fs-xs)', padding: '8px 14px' }} onClick={() => { setEdCheckinSummary(null); showScreen('home'); }}>Done</button>
                        {/* eslint-disable-next-line cw/no-inline-button-style -- inline .btn-secondary style override (font-size, padding) on ed-checkin summary action pair; rule flags any style on button regardless of className */}
                        <button className="btn btn-secondary" style={{ fontSize: 'var(--fs-xs)', padding: '8px 14px' }} onClick={() => { setEdCheckinSummary(null); setEdCheckinVisible(true); setEdCheckinStep(1); }}>+ Add another check-in</button>
                      </div>
                    </div>
                  )}
                  {!edCheckinVisible && !edCheckinSummary && !edCheckinComplete && (
                    // eslint-disable-next-line cw/no-inline-button-style -- inline .btn-primary marginBottom override on ed-active-panel "Add to visit log" CTA; rule flags any style on button regardless of className
                    <button className="btn btn-primary" style={{ marginBottom: 8 }} onClick={() => {
                      const edIntroSeen = localStorage.getItem(SK_SEEN_ED_CHECKIN) === 'true';
                      if (!edIntroSeen) {
                        setShowEDCheckinIntro(true);
                      } else {
                        setEdCheckinVisible(true);
                        setEdCheckinStep(1);
                      }
                    }}>
                      + Add to visit log
                    </button>
                  )}
                  {edCheckinVisible && (
                    <div className="ed-checkin-flow">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>Step {edCheckinStep} of 3</span>
                        {/* eslint-disable-next-line cw/no-inline-button-style -- ed-checkin wizard close X glyph; absolute icon-only with 44px tap target, no .btn-icon-only primitive */}
                        <button onClick={() => setEdCheckinVisible(false)} aria-label="Close check-in" style={{ background: 'none', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', fontSize: 'var(--fs-lg)', minWidth: 44, minHeight: 44 }}>×</button>
                      </div>
                      <div className="progress-bar" style={{ marginBottom: 16 }}>
                        <div className="progress-fill" style={{ width: `${(edCheckinStep / 3) * 100}%`, background: 'var(--color-accent)' }} />
                      </div>

                      {edCheckinStep === 1 && (
                        <div>
                          <div className="form-group">
                            <label>Are you in a VOC (vaso-occlusive crisis)?</label>
                            <div className="radio-group" role="radiogroup" aria-label="VOC status">
                              {VOC_OPTIONS.map(opt => (
                                <div key={opt.value} className={`radio-option ${edVocStatus === opt.value ? 'selected' : ''}`} role="radio" aria-checked={edVocStatus === opt.value} tabIndex={0} onClick={() => setEdVocStatus(opt.value)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEdVocStatus(opt.value); } }}>
                                  {opt.label}
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="form-group">
                            <SliderField
                              label="Current pain level (0–10)"
                              value={edPainLevel}
                              setValue={setEdPainLevel}
                              leftLabel="No pain"
                              rightLabel="Worst pain"
                              color="var(--color-crisis)"
                              gradient="linear-gradient(to right, var(--color-info), var(--color-info), var(--color-success), var(--color-warning), var(--color-crisis))"
                            />
                          </div>
                          <button className="btn btn-primary" onClick={() => setEdCheckinStep(2)}>Next →</button>
                        </div>
                      )}

                      {edCheckinStep === 2 && (
                        <div>
                          <div className="form-group">
                            <label>Where is your pain? Tap area → tap again to increase intensity</label>
                            <div style={{ marginTop: 8, marginBottom: 8, display: 'flex', justifyContent: 'space-around', fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--color-risk-low)', display: 'inline-block' }} /> Mild</span>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--color-risk-moderate)', display: 'inline-block' }} /> Moderate</span>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--color-risk-high)', display: 'inline-block' }} /> Severe</span>
                            </div>
                            <BodyMap id="ed-body-map" areas={edBodyAreas} onToggle={toggleEdBodyArea} />
                          </div>
                          <div className="btn-group">
                            <button className="btn btn-secondary" onClick={() => setEdCheckinStep(1)}>← Back</button>
                            <button className="btn btn-primary" onClick={() => setEdCheckinStep(3)}>Next →</button>
                          </div>
                        </div>
                      )}

                      {edCheckinStep === 3 && (
                        <div>
                          <div className="form-group">
                            <label>Any safety concerns? Select all that apply.</label>
                            <div className="tag-group">
                              {edSafetyFlags.filter(f => !SAFETY_FLAGS.includes(f)).map(flag => (
                                <div
                                  key={flag}
                                  className="tag selected"
                                  onClick={() => toggleSafetyFlag(flag)}
                                >
                                  {flag}
                                </div>
                              ))}
                              {SAFETY_FLAGS.map(flag => (
                                <div
                                  key={flag}
                                  className={`tag ${edSafetyFlags.includes(flag) ? 'selected' : ''}`}
                                  onClick={() => toggleSafetyFlag(flag)}
                                >
                                  {flag}
                                </div>
                              ))}
                              <AddCustomTag onAdd={tag => { if (!edSafetyFlags.includes(tag)) setEdSafetyFlags(prev => [...prev, tag]); }} />
                            </div>
                          </div>
                          {edSafetyFlags.length > 0 && (
                            <div className="ed-safety-alert">
                              <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}><IconWarning size={16} ariaLabel="" /> Safety flags noted. Consider requesting a patient advocate.</span>
                              {/* eslint-disable-next-line cw/no-inline-button-style -- inline .btn-secondary style overrides (marginTop, font-size, inline-flex icon) on safety-alert advocacy chip; rule flags any style on button regardless of className */}
                              <button className="btn btn-secondary" style={{ marginTop: 8, fontSize: 'var(--fs-xs)', display:'inline-flex', alignItems:'center', gap:6 }} onClick={() => {
                                const text = `I need to speak with a patient advocate. I am documenting: ${edSafetyFlags.join(', ')}.`;
                                navigator.clipboard.writeText(text);
                              }}>
                                <IconChecklist size={14} ariaLabel="" /> Copy advocacy phrase
                              </button>
                            </div>
                          )}
                          <div className="btn-group" style={{ marginTop: 12 }}>
                            <button className="btn btn-secondary" onClick={() => setEdCheckinStep(2)}>← Back</button>
                            <button className="btn btn-primary" onClick={submitEDCheckin}>Save Check-In</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              </>);

              if (widgetId === 'goals') return wrapWidget((() => {
                const topGoal = activeGoals[0];
                const goalOfMomentEl = !goalOfMomentDismissed ? (() => {
                  if (!topGoal) return (
                    <div style={{ background: 'color-mix(in srgb, var(--color-success) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--color-success) 25%, transparent)', borderRadius: 'var(--radius-lg)', padding: '16px 18px', marginBottom: 12, position: 'relative' }}>
                      {/* eslint-disable-next-line cw/no-inline-button-style -- goal-of-moment empty-state dismiss X; absolute icon-only with 44px tap target, no .btn-icon-only primitive */}
                      <button onClick={() => setGoalOfMomentDismissed(true)} aria-label="Dismiss goal reminder" style={{ position: 'absolute', top: 10, right: 12, background: 'none', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', fontSize: 'var(--fs-md)', lineHeight: 1, minWidth: 44, minHeight: 44 }}>×</button>
                      <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 700, marginBottom: 4, color: 'var(--color-success)', display: 'flex', alignItems: 'center', gap: 6 }}><IconGrowthSprout size={16} ariaLabel="" /> Grow with intention</div>
                      <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', margin: '0 0 10px' }}>Set a growth goal to start building resilience one step at a time.</p>
                      {/* eslint-disable-next-line cw/no-inline-button-style -- inline .btn-secondary style override (padding, font-size) on goal-of-moment empty-state CTA; rule flags any style on button regardless of className */}
                      <button className="btn btn-secondary" style={{ padding: '7px 14px', fontSize: 'var(--fs-xs)' }} onClick={() => { setGrowthExploreOpen(true); setExploreTheme(null); showScreen('growth'); }}>Explore goals</button>
                    </div>
                  );
                  const tmplM = GOAL_TEMPLATES.find(t => t.id === topGoal.templateId);
                  if (!tmplM) return null;
                  const nextStep = tmplM.microSteps.find(ms => !topGoal.completedSteps.includes(ms.id));
                  const linkedItem = tmplM.linkedLibraryIds[0] ? ED_CONTENT.find(c => c.id === tmplM.linkedLibraryIds[0]) : null;
                  const themeColorM = THEME_META[tmplM.theme].color;
                  return (
                    <div style={{ background: 'color-mix(in srgb, var(--color-success) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--color-success) 25%, transparent)', borderRadius: 'var(--radius-lg)', padding: '16px 18px', marginBottom: 12, position: 'relative' }}>
                      {/* eslint-disable-next-line cw/no-inline-button-style -- goal-of-moment populated-state dismiss X; absolute icon-only with 44px tap target, no .btn-icon-only primitive */}
                      <button onClick={() => setGoalOfMomentDismissed(true)} aria-label="Dismiss goal reminder" style={{ position: 'absolute', top: 10, right: 12, background: 'none', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', fontSize: 'var(--fs-md)', lineHeight: 1, minWidth: 44, minHeight: 44 }}>×</button>
                      <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: themeColorM, marginBottom: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>{THEME_META[tmplM.theme].iconKey ? (() => { const Icon = CRYSTAL_ICONS[THEME_META[tmplM.theme].iconKey!]; return <Icon size={14} ariaLabel="" />; })() : <span>{THEME_META[tmplM.theme].icon}</span>} {tmplM.theme}</div>
                      <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 700, marginBottom: 6 }}>Goal of the moment</div>
                      {nextStep ? (
                        <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', marginBottom: 10 }}>
                          <span style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>Next step:</span> {nextStep.label}
                        </div>
                      ) : (
                        <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-success)', marginBottom: 10, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>All steps done! <IconCheck size={14} ariaLabel="" /></div>
                      )}
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {/* eslint-disable-next-line cw/no-inline-button-style -- inline .btn-secondary style override (padding, font-size) on goal-of-moment View action; rule flags any style on button regardless of className */}
                        <button className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: 'var(--fs-xs)' }} onClick={() => showScreen('growth')}>View in Growth</button>
                        {linkedItem && (
                          // eslint-disable-next-line cw/no-inline-button-style -- inline .btn-secondary style overrides (padding, font-size, inline-flex icon) on goal-of-moment Related article chip; rule flags any style on button regardless of className
                          <button className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: 'var(--fs-xs)', display:'inline-flex', alignItems:'center', gap:6 }} onClick={() => { setActiveContentItem(linkedItem); openModal('content-item'); }}><IconBook size={14} ariaLabel="" /> Related article</button>
                        )}
                      </div>
                    </div>
                  );
                })() : null;
                const tmplG = topGoal ? GOAL_TEMPLATES.find(t => t.id === topGoal.templateId) : null;
                const goalTitle = tmplG?.title ?? 'Custom Goal';
                const totalSteps = tmplG?.microSteps.length ?? 0;
                const doneSteps = topGoal?.completedSteps.length ?? 0;
                const pct = totalSteps > 0 ? Math.round((doneSteps / totalSteps) * 100) : 0;
                const themeColorG = tmplG ? THEME_META[tmplG.theme].color : 'var(--color-success)';
                const goalsCardEl = topGoal ? (
                  <div role="button" tabIndex={0} aria-label={`Goals: ${goalTitle}, ${doneSteps} of ${totalSteps} steps done`} onClick={() => { setGrowthTab('goals'); showScreen('growth'); }} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setGrowthTab('goals'); showScreen('growth'); } }} style={{ background: 'var(--color-bg-surface)', border: '1px solid color-mix(in srgb, var(--color-text-primary) 8%, transparent)', borderRadius: 'var(--radius-lg)', padding: '16px 18px', marginBottom: 12, cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: `color-mix(in srgb, ${themeColorG} 13%, transparent)`, border: `1px solid color-mix(in srgb, ${themeColorG} 27%, transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} aria-hidden="true"><IconAdvocacy size={20} ariaLabel="" /></div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm)' }}>Goals</div>
                          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{goalTitle}</div>
                        </div>
                      </div>
                      <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)', flexShrink: 0 }} aria-hidden="true">›</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'color-mix(in srgb, var(--color-text-primary) 4%, transparent)', overflow: 'hidden' }} role="progressbar" aria-label="Goal progress" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                        <div style={{ height: '100%', width: `${pct}%`, background: themeColorG, borderRadius: 3, transition: 'width 0.4s' }} />
                      </div>
                      <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: themeColorG, flexShrink: 0 }}>{doneSteps}/{totalSteps}</span>
                    </div>
                  </div>
                ) : (
                  <div role="button" tabIndex={0} aria-label="Goals: Add your first goal" onClick={() => { setGrowthTab('goals'); setGrowthExploreOpen(true); setExploreTheme(null); showScreen('growth'); }} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setGrowthTab('goals'); setGrowthExploreOpen(true); setExploreTheme(null); showScreen('growth'); } }} style={{ background: 'var(--color-bg-surface)', border: '1px solid color-mix(in srgb, var(--color-text-primary) 8%, transparent)', borderRadius: 'var(--radius-lg)', padding: '16px 18px', marginBottom: 12, cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'color-mix(in srgb, var(--color-success) 15%, transparent)', border: '1px solid color-mix(in srgb, var(--color-success) 30%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} aria-hidden="true"><IconAdvocacy size={20} ariaLabel="" /></div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm)' }}>Goals</div>
                          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-success)', marginTop: 1 }}>Add your first goal</div>
                        </div>
                      </div>
                      <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)', flexShrink: 0 }}>›</span>
                    </div>
                  </div>
                );
                return <>{goalOfMomentEl}{goalsCardEl}</>;
              })());

              if (widgetId === 'patterns') return wrapWidget((() => {
                const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
                const recentCI = checkinEntries.filter(e => new Date(e.timestamp).getTime() >= oneWeekAgo);
                const painEntriesP = recentCI.filter(e => e.painLevel != null);
                const moodEntriesP = recentCI.filter(e => e.moodLevel != null);
                const energyEntriesP = recentCI.filter(e => e.energyLevel != null);
                const avgPainP = painEntriesP.length > 0 ? Math.round(painEntriesP.reduce((s, e) => s + (e.painLevel ?? 0), 0) / painEntriesP.length * 10) / 10 : null;
                const avgMoodP = moodEntriesP.length > 0 ? Math.round(moodEntriesP.reduce((s, e) => s + (e.moodLevel ?? 0), 0) / moodEntriesP.length * 10) / 10 : null;
                const avgEnergyP = energyEntriesP.length > 0 ? Math.round(energyEntriesP.reduce((s, e) => s + (e.energyLevel ?? 0), 0) / energyEntriesP.length * 10) / 10 : null;
                const hasDataP = avgPainP !== null || avgMoodP !== null || avgEnergyP !== null;
                return (
                  <div role="button" tabIndex={0} aria-label="Patterns: 7-day snapshot" onClick={() => showScreen('patterns')} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); showScreen('patterns'); } }} style={{ background: 'var(--color-bg-surface)', border: '1px solid color-mix(in srgb, var(--color-text-primary) 8%, transparent)', borderRadius: 'var(--radius-lg)', padding: '16px 18px', marginBottom: 12, cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: hasDataP ? 12 : 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'color-mix(in srgb, var(--color-accent) 15%, transparent)', border: '1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} aria-hidden="true"><IconChart size={20} ariaLabel="" /></div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm)' }}>Patterns</div>
                          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginTop: 1 }}>7-day snapshot</div>
                        </div>
                      </div>
                      <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)', flexShrink: 0 }}>›</span>
                    </div>
                    {hasDataP ? (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                        {avgPainP !== null && <div style={{ textAlign: 'center', padding: '8px 4px', background: 'color-mix(in srgb, var(--color-crisis) 8%, transparent)', borderRadius: 'var(--radius-md)', border: '1px solid color-mix(in srgb, var(--color-crisis) 15%, transparent)' }}><div style={{ fontSize: 'var(--fs-md)', fontWeight: 800, color: 'var(--color-crisis)' }}>{avgPainP}</div><div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginTop: 2 }}>Avg Pain</div></div>}
                        {avgMoodP !== null && <div style={{ textAlign: 'center', padding: '8px 4px', background: 'color-mix(in srgb, var(--color-accent) 8%, transparent)', borderRadius: 'var(--radius-md)', border: '1px solid color-mix(in srgb, var(--color-accent) 15%, transparent)' }}><div style={{ fontSize: 'var(--fs-md)', fontWeight: 800, color: 'var(--color-accent)' }}>{avgMoodP}</div><div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginTop: 2 }}>Avg Mood</div></div>}
                        {avgEnergyP !== null && <div style={{ textAlign: 'center', padding: '8px 4px', background: 'color-mix(in srgb, var(--color-success) 8%, transparent)', borderRadius: 'var(--radius-md)', border: '1px solid color-mix(in srgb, var(--color-success) 15%, transparent)' }}><div style={{ fontSize: 'var(--fs-md)', fontWeight: 800, color: 'var(--color-success)' }}>{avgEnergyP}</div><div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginTop: 2 }}>Avg Energy</div></div>}
                      </div>
                    ) : (
                      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', fontStyle: 'italic', marginTop: 4 }}>Complete a check-in to see your patterns</div>
                    )}
                    {streak > 0 && <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 'var(--fs-xs)', color: 'var(--color-warning)' }}><IconStreak size={14} ariaLabel="" /><span style={{ fontWeight: 700 }}>{streak}-day streak</span></div>}
                  </div>
                );
              })());

              if (widgetId === 'quick-access') return wrapWidget((() => {
                type QTItem = { icon?: string; iconKey: CrystalIconKey; label: string; desc: string; color: string; screen: Screen; articlesFilter?: string; action?: () => void };
                const allQT: QTItem[] = [
                  { icon: '📋', iconKey: 'checkin-note', label: 'Daily Check-In', desc: "Log how you're feeling today", screen: 'home', color: 'var(--color-accent)', action: () => startCheckin() },
                  { icon: '💊', iconKey: 'medications', label: 'Medications', desc: 'Your medication list and history', screen: 'medications', color: 'var(--color-accent)' },
                  { icon: '📓', iconKey: 'growth-sprout', label: 'Journal', desc: 'Write or speak, then decorate with stickers', screen: 'journal', color: 'var(--color-secondary)' },
                  { icon: '📝', iconKey: 'log-event', label: 'Log stigma', desc: 'Document what happened today', screen: 'event-log', color: 'var(--color-warning)' },
                  { icon: '🏥', iconKey: 'hospital-prep', label: 'Hospital Prep Kit', desc: 'Scripts, rights, and admission prep', screen: 'hospital-prep', color: 'var(--color-crisis)' },
                  { icon: '🧭', iconKey: 'crisis-plan', label: 'Crisis Plan', desc: 'Your personalized crisis response', screen: 'crisis-plan', color: 'var(--color-secondary)' },
                  { icon: '🌦', iconKey: 'weather-drizzle', label: 'Weather Report', desc: 'Weather-based flare risk outlook', screen: 'weather-report', color: 'var(--color-info)' },
                  { icon: '🏥', iconKey: 'journey-clinic-visit', label: 'Crisis visits and the ED', desc: 'Guides for hospital visits and clinic care', screen: 'articles', color: 'var(--color-crisis)', articlesFilter: 'passive_read' },
                  { icon: '🎯', iconKey: 'advocacy', label: 'Advocacy scripts', desc: 'Language to assert your rights', screen: 'articles', color: 'var(--color-accent)', articlesFilter: 'script' },
                  { icon: '🧘', iconKey: 'grounding', label: 'Grounding practices', desc: 'Breathing, 5-4-3-2-1, and calming tools', screen: 'grounding', color: 'var(--color-success)' },
                  { icon: '📖', iconKey: 'new-moment', label: 'Community stories', desc: 'Real stories from youth with SCD', screen: 'articles', color: 'var(--color-info)', articlesFilter: 'story' },
                  { icon: '✊', iconKey: 'solidarity', label: 'Understanding stigma', desc: 'Racial justice and intersectional stigma', screen: 'articles', color: 'var(--color-crisis)', articlesFilter: 'stigma' },
                  { icon: '🏷️', iconKey: 'identity-tag', label: 'Identity dictionary', desc: 'Terms and identities used in CareWeave', screen: 'articles', color: 'var(--color-accent)', articlesFilter: 'identity' },
                  { icon: '📋', iconKey: 'care-team', label: 'Care Team Hub', desc: 'Doctors, nurses, and contacts', screen: 'care-team', color: 'var(--color-success)' },
                  { icon: '🤝', iconKey: 'journey-relationships', label: 'Support Network', desc: 'Family, friends, and community', screen: 'support-network', color: 'var(--color-crisis)' },
                ];
                const defaultQT: QTItem[] = [
                  { icon: '🎯', iconKey: 'advocacy', label: 'Crisis Advocacy Script', desc: 'Build and copy your personalized script for communicating clearly during a crisis visit.', screen: 'hospital-prep', color: 'var(--color-crisis)', action: () => openModal('ed-script') },
                  { icon: '🧘', iconKey: 'grounding', label: 'Grounding Exercise', desc: '5-4-3-2-1 technique to calm your nervous system during a flare or anxious moment.', screen: 'home', color: 'var(--color-success)', action: () => openModal('grounding-54321') },
                  { icon: '⚖️', iconKey: 'journey-employment-rights', label: 'Know Your Rights', desc: 'Patient rights around pain treatment, documentation, and escalation during a crisis.', screen: 'hospital-prep', color: 'var(--color-accent)', action: () => openModal('rights-script') },
                ];
                const starredQT = allQT.filter(t => starredTools.includes(t.label));
                let quickItems: QTItem[];
                if (starredQT.length > 0) {
                  quickItems = starredQT.slice(0, 4);
                } else {
                  const freq = allQT.filter(t => (toolAccessCounts[t.label] ?? 0) > 0).sort((a, b) => (toolAccessCounts[b.label] ?? 0) - (toolAccessCounts[a.label] ?? 0)).slice(0, 3);
                  quickItems = freq.length > 0 ? freq : defaultQT;
                }
                function handleQTap(item: QTItem) {
                  recordToolAccess(item.label);
                  if (item.action) { item.action(); } else if (item.articlesFilter) {
                    const topicMap: Record<string, string> = { 'passive_read': 'ed', 'script': 'rights-advocacy', 'grounding': 'pain-sleep', 'story': 'mental-health-stigma', 'stigma': 'mental-health-stigma', 'identity': 'school-work' };
                    if (item.screen === 'articles') setArticlesBackTarget('home');
                    showScreen(item.screen, topicMap[item.articlesFilter] ? { libraryTopicId: topicMap[item.articlesFilter] } : undefined);
                  } else {
                    if (item.screen === 'articles') setArticlesBackTarget('home');
                    if (item.screen === 'weather-report') setWeatherBackTarget('home');
                    showScreen(item.screen);
                  }
                }
                return (
                  <div style={{ background: 'var(--color-bg-surface)', border: '1px solid color-mix(in srgb, var(--color-text-primary) 8%, transparent)', borderRadius: 'var(--radius-lg)', padding: '16px 18px', marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <h3 style={{ margin: 0 }}>Quick access</h3>
                      {/* eslint-disable-next-line cw/no-inline-button-style -- inline section-header text-link "Customize"; ghost-only sibling to <h3>, not a chamfered .btn variant */}
                      <button onClick={() => showScreen('tools')} style={{ background: 'none', border: 'none', color: 'var(--color-accent)', fontSize: 'var(--fs-sm)', cursor: 'pointer', padding: '4px 0', fontWeight: 600 }}>Customize</button>
                    </div>
                    <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', marginBottom: 16 }}>{starredTools.length > 0 ? 'Your starred tools' : 'Tools ready when you need them'}</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {quickItems.map((item, i) => (
                        <div key={item.label} role="button" tabIndex={0} onClick={() => handleQTap(item)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleQTap(item); } }} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 4px', cursor: 'pointer', borderRadius: 'var(--radius-md)', transition: 'background 0.15s', borderBottom: i < quickItems.length - 1 ? '1px solid color-mix(in srgb, var(--color-accent) 6%, transparent)' : 'none' }}>
                          <div style={{ width: 42, height: 42, borderRadius: 'var(--radius-md)', background: `color-mix(in srgb, ${item.color} 9%, transparent)`, border: `1px solid color-mix(in srgb, ${item.color} 19%, transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{(() => { const Icon = CRYSTAL_ICONS[item.iconKey]; return <Icon size={24} ariaLabel="" />; })()}</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }}>{item.label}</div>
                            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginTop: 1 }}>{item.desc}</div>
                          </div>
                          <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--fs-md)', flexShrink: 0 }}>›</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })());

              if (widgetId === 'articles') return wrapWidget((() => {
                const featuredArticle = ED_CONTENT[0];
                if (!featuredArticle) return <div style={{ background: 'var(--color-bg-surface)', border: '1px solid color-mix(in srgb, var(--color-text-primary) 8%, transparent)', borderRadius: 'var(--radius-lg)', padding: '16px 18px', marginBottom: 12 }}><div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><div style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'color-mix(in srgb, var(--color-info) 15%, transparent)', border: '1px solid color-mix(in srgb, var(--color-info) 30%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><IconBook size={20} ariaLabel="" /></div><div><div style={{ fontWeight: 700, fontSize: 'var(--fs-sm)' }}>Articles</div><div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginTop: 1 }}>Articles are loading in — check back soon.</div></div></div></div>;
                const rawTag = (featuredArticle.module.length > 0 ? featuredArticle.module[0] : featuredArticle.tags[0]) ?? '';
                const categoryTag = rawTag.replace(/_/g, ' ');
                return (
                  <div role="button" tabIndex={0} aria-label={`Featured article: ${featuredArticle.title}`} onClick={() => { setArticlesBackTarget('home'); showScreen('articles'); setTimeout(() => { setActiveContentItem(featuredArticle); openModal('content-item'); }, 50); }} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setArticlesBackTarget('home'); showScreen('articles'); setTimeout(() => { setActiveContentItem(featuredArticle); openModal('content-item'); }, 50); } }} style={{ background: 'var(--color-bg-surface)', border: '1px solid color-mix(in srgb, var(--color-text-primary) 8%, transparent)', borderRadius: 'var(--radius-lg)', padding: '16px 18px', marginBottom: 12, cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'color-mix(in srgb, var(--color-info) 15%, transparent)', border: '1px solid color-mix(in srgb, var(--color-info) 30%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><IconBook size={20} ariaLabel="" /></div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm)' }}>Articles</div>
                          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{featuredArticle.title}</div>
                        </div>
                      </div>
                      <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)', flexShrink: 0 }}>›</span>
                    </div>
                    {categoryTag && <div style={{ marginTop: 8 }}><span style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, padding: '3px 8px', borderRadius: 'var(--radius-sm)', background: 'color-mix(in srgb, var(--color-info) 12%, transparent)', color: 'var(--color-info)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{categoryTag}</span></div>}
                  </div>
                );
              })());

              if (widgetId === 'circle') return wrapWidget((() => {
                const postCount = circleLivePosts.length;
                const latestPost = circleLivePosts[0];
                return (
                  <div role="button" tabIndex={0} aria-label="The Circle: community support" onClick={() => showScreen('peer-connect')} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); showScreen('peer-connect'); } }} style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-accent-light)', borderRadius: 'var(--radius-lg)', padding: '16px 18px', marginBottom: 12, cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'var(--color-accent-light)', border: '1px solid var(--color-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} aria-hidden="true">{(() => { const Icon = CRYSTAL_ICONS['handshake']; return <Icon size={20} ariaLabel="" />; })()}</div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm)' }}>The Circle</div>
                          {latestPost ? (
                            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{latestPost.avatarEmoji} {latestPost.handle}: {latestPost.text.slice(0, 60)}{latestPost.text.length > 60 ? '…' : ''}</div>
                          ) : (
                            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginTop: 1 }}>Join the community conversation</div>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        {postCount > 0 && <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, padding: '2px 7px', borderRadius: 'var(--radius-md)', background: 'var(--color-accent-light)', color: 'var(--color-accent)', border: '1px solid var(--color-accent)' }}>{postCount} post{postCount !== 1 ? 's' : ''}</span>}
                        <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)' }}>›</span>
                      </div>
                    </div>
                  </div>
                );
              })());

              if (widgetId === 'settings') return wrapWidget(
                <div style={{ background: 'var(--color-bg-surface)', border: '1px solid color-mix(in srgb, var(--color-text-primary) 8%, transparent)', borderRadius: 'var(--radius-lg)', padding: '16px 18px', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'color-mix(in srgb, var(--color-accent) 15%, transparent)', border: '1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--fs-md)', flexShrink: 0 }}>♿</div>
                      <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm)' }}>Accessibility shortcuts</div>
                    </div>
                    {/* eslint-disable-next-line cw/no-inline-button-style -- inline section-header text-link "Open ›"; ghost-only sibling to section heading, not a chamfered .btn variant */}
                    <button onClick={() => setSettingsPanelOpen(true)} style={{ background: 'none', border: 'none', color: 'var(--color-accent)', fontSize: 'var(--fs-sm)', cursor: 'pointer', fontWeight: 600 }}>Open ›</button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 'var(--fs-sm)' }}>Dark mode</span>
                      {/* eslint-disable-next-line cw/no-inline-button-style -- iOS-style switch toggle (44×24 with absolute knob); native-control look, not a chamfered .btn variant */}
                      <button
                        onClick={() => toggleDarkMode()}
                        role="switch" aria-checked={darkMode}
                        style={{ width: 44, height: 24, borderRadius: 'var(--radius-md)', background: darkMode ? 'var(--color-accent)' : 'color-mix(in srgb, var(--color-text-primary) 6%, transparent)', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}
                        aria-label="Toggle dark mode"
                      >
                        <div style={{ position: 'absolute', top: 2, left: darkMode ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: 'var(--shadow-card)' }} />
                      </button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 'var(--fs-sm)' }}>Large text</span>
                      {/* eslint-disable-next-line cw/no-inline-button-style -- iOS-style switch toggle (44×24 with absolute knob); native-control look, not a chamfered .btn variant */}
                      <button
                        onClick={() => handleAccessibilityChange({ ...accessibilityPrefs, fontSize: accessibilityPrefs.fontSize === 'large' ? 'default' : 'large' })}
                        role="switch" aria-checked={accessibilityPrefs.fontSize === 'large'}
                        style={{ width: 44, height: 24, borderRadius: 'var(--radius-md)', background: accessibilityPrefs.fontSize === 'large' ? 'var(--color-accent)' : 'color-mix(in srgb, var(--color-text-primary) 6%, transparent)', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}
                        aria-label="Toggle large text"
                      >
                        <div style={{ position: 'absolute', top: 2, left: accessibilityPrefs.fontSize === 'large' ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: 'var(--shadow-card)' }} />
                      </button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 'var(--fs-sm)' }}>High contrast</span>
                      {/* eslint-disable-next-line cw/no-inline-button-style -- iOS-style switch toggle (44×24 with absolute knob); native-control look, not a chamfered .btn variant */}
                      <button
                        onClick={() => handleAccessibilityChange({ ...accessibilityPrefs, highContrast: !accessibilityPrefs.highContrast })}
                        role="switch" aria-checked={accessibilityPrefs.highContrast}
                        style={{ width: 44, height: 24, borderRadius: 'var(--radius-md)', background: accessibilityPrefs.highContrast ? 'var(--color-accent)' : 'color-mix(in srgb, var(--color-text-primary) 6%, transparent)', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}
                        aria-label="Toggle high contrast"
                      >
                        <div style={{ position: 'absolute', top: 2, left: accessibilityPrefs.highContrast ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: 'var(--shadow-card)' }} />
                      </button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 'var(--fs-sm)' }}>Reduce motion</span>
                      {/* eslint-disable-next-line cw/no-inline-button-style -- iOS-style switch toggle (44×24 with absolute knob); native-control look, not a chamfered .btn variant */}
                      <button
                        onClick={() => handleAccessibilityChange({ ...accessibilityPrefs, reducedMotion: !accessibilityPrefs.reducedMotion })}
                        role="switch" aria-checked={accessibilityPrefs.reducedMotion}
                        style={{ width: 44, height: 24, borderRadius: 'var(--radius-md)', background: accessibilityPrefs.reducedMotion ? 'var(--color-accent)' : 'color-mix(in srgb, var(--color-text-primary) 6%, transparent)', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}
                        aria-label="Toggle reduce motion"
                      >
                        <div style={{ position: 'absolute', top: 2, left: accessibilityPrefs.reducedMotion ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: 'var(--shadow-card)' }} />
                      </button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 'var(--fs-sm)' }}>Reduce flashing</span>
                      {/* eslint-disable-next-line cw/no-inline-button-style -- iOS-style switch toggle (44×24 with absolute knob); native-control look, not a chamfered .btn variant */}
                      <button
                        onClick={() => handleAccessibilityChange({ ...accessibilityPrefs, reduceFlashing: !accessibilityPrefs.reduceFlashing })}
                        role="switch" aria-checked={accessibilityPrefs.reduceFlashing}
                        style={{ width: 44, height: 24, borderRadius: 'var(--radius-md)', background: accessibilityPrefs.reduceFlashing ? 'var(--color-accent)' : 'color-mix(in srgb, var(--color-text-primary) 6%, transparent)', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}
                        aria-label="Toggle reduce flashing"
                      >
                        <div style={{ position: 'absolute', top: 2, left: accessibilityPrefs.reduceFlashing ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: 'var(--shadow-card)' }} />
                      </button>
                    </div>
                  </div>
                </div>
              );

              return null;
            })}

            {/* Event-log goal nudge — always shown outside the map */}
            {goalNudgeTemplate && !goalNudgeDismissed && (
              <div style={{ background: 'color-mix(in srgb, var(--color-accent) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)', borderRadius: 'var(--radius-lg)', padding: '16px 18px', marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 700, color: 'var(--color-accent)', display: 'flex', alignItems: 'center', gap: 6 }}><IconGrowthSprout size={16} ariaLabel="" /> A goal that might help</div>
                  {/* eslint-disable-next-line cw/no-inline-button-style -- goal-nudge banner dismiss X; ghost icon-only in flex header next to title, no .btn-icon-only primitive */}
                  <button onClick={() => { setGoalNudgeDismissed(true); setGoalNudgeTemplate(null); }} aria-label="Dismiss goal suggestion" style={{ background: 'none', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', fontSize: 'var(--fs-md)', lineHeight: 1, padding: 0 }}>×</button>
                </div>
                <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: THEME_META[goalNudgeTemplate.theme].color, marginBottom: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>{THEME_META[goalNudgeTemplate.theme].iconKey ? (() => { const Icon = CRYSTAL_ICONS[THEME_META[goalNudgeTemplate.theme].iconKey!]; return <Icon size={14} ariaLabel="" />; })() : <span>{THEME_META[goalNudgeTemplate.theme].icon}</span>} {goalNudgeTemplate.theme}</div>
                <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, marginBottom: 6 }}>{goalNudgeTemplate.title}</div>
                <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginBottom: 12 }}>{goalNudgeTemplate.description}</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  {/* eslint-disable-next-line cw/no-inline-button-style -- inline .btn-primary style override (padding, font-size) on goal-nudge primary action; rule flags any style on button regardless of className */}
                  <button className="btn btn-primary" style={{ padding: '8px 14px', fontSize: 'var(--fs-xs)' }} onClick={() => {
                    const adopted = adoptGoal(goalNudgeTemplate);
                    if (adopted) { earnXP(20); earnXPOnce('first_goal_adopted', 30); }
                    setGoalNudgeDismissed(true);
                    setGoalNudgeTemplate(null);
                    showScreen('growth');
                  }}>Add this goal</button>
                  {/* eslint-disable-next-line cw/no-inline-button-style -- inline .btn-secondary style override (padding, font-size) on goal-nudge dismiss; rule flags any style on button regardless of className */}
                  <button className="btn btn-secondary" style={{ padding: '8px 14px', fontSize: 'var(--fs-xs)' }} onClick={() => { setGoalNudgeDismissed(true); setGoalNudgeTemplate(null); }}>Not now</button>
                </div>
              </div>
            )}

            {/* Body Map Snapshot — static section always at bottom of home */}
            <div style={{ background: 'var(--color-bg-surface)', border: '1px solid color-mix(in srgb, var(--color-text-primary) 8%, transparent)', borderRadius: 'var(--radius-lg)', padding: '16px 18px', marginBottom: 12 }}>
              <h3 style={{ margin: '0 0 4px 0' }}>Body map snapshot</h3>
              <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', marginBottom: 12 }}>Recent pain areas and common experiences</p>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <div style={{ flexShrink: 0, width: 100 }}>
                  <svg viewBox="0 0 200 280" style={{ width: '100%', height: 'auto' }}>
                    <ellipse cx="100" cy="40" rx="25" ry="30" fill="var(--color-bg-surface)" stroke="var(--color-accent)" strokeWidth="2" />
                    <rect x="75" y="65" width="50" height="80" rx="10" fill="var(--color-bg-surface)" stroke="var(--color-accent)" strokeWidth="2" />
                    <rect x="50" y="70" width="25" height="60" rx="8" fill="var(--color-bg-surface)" stroke="var(--color-accent)" strokeWidth="2" />
                    <rect x="125" y="70" width="25" height="60" rx="8" fill="var(--color-bg-surface)" stroke="var(--color-accent)" strokeWidth="2" />
                    <rect x="80" y="145" width="15" height="90" rx="6" fill="var(--color-bg-surface)" stroke="var(--color-accent)" strokeWidth="2" />
                    <rect x="105" y="145" width="15" height="90" rx="6" fill="var(--color-bg-surface)" stroke="var(--color-accent)" strokeWidth="2" />
                    {/* Dynamic pain dots from recent check-ins */}
                    {(() => {
                      const areaCoords: Record<string, [number, number]> = {
                        head: [100, 35], 'jaw-face': [100, 55], neck: [100, 62],
                        'left-shoulder': [65, 75], 'right-shoulder': [135, 75],
                        'left-chest': [87, 85], 'right-chest': [113, 85],
                        'upper-abdomen': [100, 100], 'lower-abdomen': [100, 120],
                        'pelvis-hips': [100, 140],
                        'left-upper-arm': [55, 90], 'right-upper-arm': [145, 90],
                        'left-thigh': [87, 170], 'right-thigh': [113, 170],
                        'left-knee': [87, 195], 'right-knee': [113, 195],
                        'upper-back': [100, 90], 'lower-back': [100, 130],
                      };
                      const recentAreas: Record<string, number> = {};
                      const oneWeek = Date.now() - 7 * 86400000;
                      for (const ci of checkinEntries) {
                        if (new Date(ci.timestamp).getTime() < oneWeek) continue;
                        for (const pa of ci.painAreas ?? []) {
                          recentAreas[pa.area] = (recentAreas[pa.area] || 0) + 1;
                        }
                      }
                      return Object.entries(recentAreas).map(([area, count]) => {
                        const coord = areaCoords[area];
                        if (!coord) return null;
                        const r = Math.min(6 + count * 2, 14);
                        const op = Math.min(0.4 + count * 0.1, 0.8);
                        return <circle key={area} cx={coord[0]} cy={coord[1]} r={r} fill={`rgba(190,120,90,${op})`} />;
                      });
                    })()}
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginBottom: 4 }}>Top coping this week</div>
                    {bodyMapMetrics.topStrategy ? (
                      <span className="tag selected" style={{ fontSize: 'var(--fs-xs)' }}>{bodyMapMetrics.topStrategy}</span>
                    ) : (
                      <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>Complete a check-in to see</span>
                    )}
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginBottom: 4 }}>Pain peaks</div>
                    {bodyMapMetrics.peakPeriod ? (
                      <span className="tag selected" style={{ fontSize: 'var(--fs-xs)' }}>{bodyMapMetrics.peakPeriod}</span>
                    ) : (
                      <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>Complete a check-in to see</span>
                    )}
                  </div>
                  {/* eslint-disable-next-line cw/no-inline-button-style -- inline .btn-secondary style override (font-size, width, padding) on body-map-snapshot patterns link; rule flags any style on button regardless of className */}
                  <button className="btn btn-secondary" style={{ fontSize: 'var(--fs-xs)', width: 'auto', padding: '6px 12px' }} onClick={() => showScreen('patterns')}>View all patterns</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {screen === 'checkin' && (
          <div
            className="screen"
            ref={checkinScreenRef}
            style={{ position: 'relative' }}
          >
            {checkinScrollTop && (
              // eslint-disable-next-line cw/no-inline-button-style -- floating scroll-to-top FAB; circular 40px position:fixed bottom-right, no .btn-fab primitive
              <button
                onClick={() => containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
                style={{ position: 'fixed', bottom: 120, right: 16, zIndex: 'var(--z-floating)', width: 40, height: 40, borderRadius: '50%', border: '1px solid color-mix(in srgb, var(--color-text-primary) 15%, transparent)', background: 'var(--color-bg-surface)', color: 'var(--color-text-primary)', fontSize: 'var(--fs-base)', cursor: 'pointer', boxShadow: 'var(--shadow-raised)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                aria-label="Scroll to top"
              >↑</button>
            )}
            {checkinScrollBottom && !checkinSummary && (
              // eslint-disable-next-line cw/no-inline-button-style -- floating scroll-to-bottom FAB; circular 40px position:fixed bottom-right, no .btn-fab primitive
              <button
                onClick={() => { const el = containerRef.current; if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' }); }}
                style={{ position: 'fixed', bottom: 72, right: 16, zIndex: 'var(--z-floating)', width: 40, height: 40, borderRadius: '50%', border: '1px solid color-mix(in srgb, var(--color-text-primary) 15%, transparent)', background: 'var(--color-bg-surface)', color: 'var(--color-text-primary)', fontSize: 'var(--fs-base)', cursor: 'pointer', boxShadow: 'var(--shadow-raised)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                aria-label="Scroll to bottom"
              >↓</button>
            )}
            {!moduleIntroSeenCheckin && !checkinSummary && (
              <ModuleIntroBanner
                icon={<IconCheck size={28} ariaLabel="" />}
                title="Your daily check-in"
                body="Log your pain, mood, energy, and body areas each day. Over time, your check-ins build the patterns that help you understand your health — and advocate for better care."
                onDismiss={dismissModuleIntroCheckin}
              />
            )}
            {checkinSummary ? (
              <>
                <div className="card">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: guideAffirmation ? 12 : 4 }}>
                    {userAvatarId && (
                      <GuideBubble avatarId={userAvatarId} size={36} showGlow={false} customisation={userCustomisation} />
                    )}
                    <div>
                      <h2 style={{ margin: 0 }}>Check-In Saved</h2>
                      <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)', margin: 0 }}>Here's what you logged today</p>
                    </div>
                  </div>
                  {guideAffirmation && (() => {
                    const character = userCustomisation?.characterId
                      ? (CRYSTAL_CHARACTERS.find(c => c.id === userCustomisation.characterId) ?? null)
                      : null;
                    const bubbleBg = character ? `color-mix(in srgb, ${character.bg} 13%, transparent)` : 'color-mix(in srgb, var(--color-accent) 12%, transparent)';
                    const bubbleBorder = character ? `color-mix(in srgb, ${character.bg} 33%, transparent)` : 'color-mix(in srgb, var(--color-accent) 30%, transparent)';
                    const bubbleColor = character ? character.bg : 'var(--color-accent)';
                    return (
                      <div
                        role="status"
                        aria-live="polite"
                        onClick={() => setGuideAffirmation(null)}
                        style={{
                          position: 'relative',
                          background: bubbleBg,
                          border: `1.5px solid ${bubbleBorder}`,
                          borderRadius: 'var(--radius-lg)',
                          padding: '12px 14px',
                          cursor: 'pointer',
                          animation: 'guideAffirmationIn 0.4s ease forwards',
                        }}
                      >
                        <div style={{ position: 'absolute', top: -9, left: 22, width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottom: `10px solid ${bubbleBorder}` }} />
                        <div style={{ position: 'absolute', top: -7, left: 23, width: 0, height: 0, borderLeft: '7px solid transparent', borderRight: '7px solid transparent', borderBottom: `9px solid ${bubbleBg}` }} />
                        {character && (
                          <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: bubbleColor, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4, opacity: 0.8 }}>
                            {character.crystalName} says
                          </div>
                        )}
                        <p style={{ margin: 0, fontSize: 'var(--fs-sm)', lineHeight: 1.5, color: 'var(--color-text-primary)', fontStyle: 'italic' }}>
                          "{guideAffirmation}"
                        </p>
                        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginTop: 6, textAlign: 'right', opacity: 0.6 }}>Tap to dismiss</div>
                      </div>
                    );
                  })()}
                </div>
                {watersCheck === 'rough' && (
                  <div className="card" style={{ background: 'color-mix(in srgb, var(--color-accent) 7%, transparent)', border: '1px solid color-mix(in srgb, var(--color-accent) 25%, transparent)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                      <IconRoughSeas size={28} />
                      <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm-plus)', color: 'var(--color-accent)' }}>Rough seas — glad you checked in</div>
                    </div>
                    <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', marginBottom: 14 }}>Want to take a moment to look at your care plan or support resources?</p>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {/* eslint-disable-next-line cw/no-inline-button-style -- rough-seas summary CTA pair: tinted primary; 2-col flex pair inside narrow comfort card, not a chamfered .btn variant */}
                      <button
                        onClick={() => showScreen('care-team')}
                        style={{ flex: 1, padding: '10px', borderRadius: 'var(--radius-md)', border: '2px solid color-mix(in srgb, var(--color-accent) 40%, transparent)', background: 'color-mix(in srgb, var(--color-accent) 10%, transparent)', color: 'var(--color-accent)', fontWeight: 700, fontSize: 'var(--fs-sm)', cursor: 'pointer' }}
                      >View care team</button>
                      {/* eslint-disable-next-line cw/no-inline-button-style -- rough-seas summary CTA pair: ghost secondary; 2-col flex pair inside narrow comfort card, not a chamfered .btn variant */}
                      <button
                        onClick={() => showScreen('crisis-plan')}
                        style={{ flex: 1, padding: '10px', borderRadius: 'var(--radius-md)', border: '1px solid color-mix(in srgb, var(--color-text-primary) 12%, transparent)', background: 'transparent', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)', cursor: 'pointer' }}
                      >Crisis plan</button>
                    </div>
                  </div>
                )}
                <div className="card">
                  <h3 style={{ marginBottom: 14 }}>Today's snapshot</h3>
                  {(() => {
                    const skipped = checkinSummary.skippedSteps ?? [];
                    const metricCard = (label: string, icon: React.ReactNode, value: number | null, color: string, stepNum: number) => {
                      const wasSkipped = skipped.includes(stepNum);
                      return (
                        <div style={{ flex: 1, minWidth: 88, background: wasSkipped ? 'var(--color-bg-surface)' : `color-mix(in srgb, ${color} 8%, transparent)`, border: `1px solid ${wasSkipped ? 'color-mix(in srgb, var(--color-text-primary) 8%, transparent)' : `color-mix(in srgb, ${color} 19%, transparent)`}`, borderRadius: 'var(--radius-lg)', padding: '12px 10px', textAlign: 'center', opacity: wasSkipped ? 0.45 : 1 }}>
                          <div style={{ fontSize: 'var(--fs-xs-plus)', color: wasSkipped ? 'var(--color-text-secondary)' : color, fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>{icon} {label}</div>
                          {wasSkipped ? (
                            <div style={{ fontSize: 'var(--fs-base)', color: 'var(--color-text-secondary)', fontStyle: 'italic', padding: '4px 0' }}>—</div>
                          ) : (
                            <>
                              <div style={{ fontSize: 'var(--fs-xl)', fontWeight: 800, color, letterSpacing: '-0.02em' }}>{value}</div>
                              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginTop: 2 }}>/ 10</div>
                            </>
                          )}
                        </div>
                      );
                    };
                    return (
                      <>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
                          {metricCard('Sleep', <IconSleep size={14} color="var(--color-accent)" />, checkinSummary.sleepQuality, 'var(--color-accent)', 2)}
                          {metricCard('Stress', <IconStress size={14} color="var(--color-accent)" />, checkinSummary.stressLevel, 'var(--color-accent)', 3)}
                          {metricCard('Fatigue', <IconFatigue size={14} color="var(--color-warning)" />, checkinSummary.fatigueLevel, 'var(--color-warning)', 6)}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 }}>
                          {metricCard('Pain', <IconPain size={14} color="var(--color-crisis)" />, checkinSummary.painLevel, 'var(--color-crisis)', 7)}
                          {metricCard('Worry', <IconWorry size={14} color="var(--color-crisis)" />, checkinSummary.worryLevel, 'var(--color-crisis)', 4)}
                          {metricCard('Blue', <IconBlue size={14} color="var(--color-info)" />, checkinSummary.feelingBlueLevel, 'var(--color-info)', 5)}
                        </div>
                      </>
                    );
                  })()}
                  {checkinSummary.painAreas.length > 0 && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 'var(--fs-xs-plus)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}><IconBodyMap size={14} color="var(--color-text-secondary)" /> Body areas affected</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                        {checkinSummary.painAreas.map(pa => (
                          <span key={pa.area} style={{ fontSize: 'var(--fs-xs-plus)', padding: '6px 11px', borderRadius: 'var(--radius-xl)', background: pa.intensity === 3 ? 'color-mix(in srgb, var(--color-crisis) 15%, transparent)' : pa.intensity === 2 ? 'color-mix(in srgb, var(--color-crisis) 15%, transparent)' : 'color-mix(in srgb, var(--color-warning) 15%, transparent)', color: pa.intensity === 3 ? 'var(--color-crisis)' : pa.intensity === 2 ? 'var(--color-crisis)' : 'var(--color-warning)', border: `1px solid ${pa.intensity === 3 ? 'color-mix(in srgb, var(--color-crisis) 30%, transparent)' : pa.intensity === 2 ? 'color-mix(in srgb, var(--color-crisis) 30%, transparent)' : 'color-mix(in srgb, var(--color-warning) 30%, transparent)'}` }}>
                            {BODY_AREA_NAMES[pa.area] ?? pa.area}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {checkinSummary.stressDescriptors && checkinSummary.stressDescriptors.length > 0 && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 'var(--fs-xs-plus)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}><IconStress size={14} color="var(--color-text-secondary)" /> Stress sources</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                        {checkinSummary.stressDescriptors.map(s => (
                          <span key={s} style={{ fontSize: 'var(--fs-xs-plus)', padding: '6px 11px', borderRadius: 'var(--radius-xl)', background: 'color-mix(in srgb, var(--color-accent) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--color-accent) 20%, transparent)', color: 'var(--color-accent)' }}>{s}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {checkinSummary.fatigueType && checkinSummary.fatigueType.length > 0 && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 'var(--fs-xs-plus)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}><IconFatigue size={14} color="var(--color-text-secondary)" /> Fatigue type</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                        {checkinSummary.fatigueType.map(t => (
                          <span key={t} style={{ fontSize: 'var(--fs-xs-plus)', padding: '6px 11px', borderRadius: 'var(--radius-xl)', background: 'color-mix(in srgb, var(--color-info) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--color-info) 20%, transparent)', color: 'var(--color-text-secondary)' }}>{t}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {checkinSummary.copingStrategies.length > 0 && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 'var(--fs-xs-plus)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}><IconWhatHelped size={14} color="var(--color-text-secondary)" /> Coping strategies used</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                        {checkinSummary.copingStrategies.map(s => (
                          <span key={s} style={{ fontSize: 'var(--fs-xs-plus)', padding: '6px 11px', borderRadius: 'var(--radius-xl)', background: 'color-mix(in srgb, var(--color-accent) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--color-accent) 20%, transparent)', color: 'var(--color-accent)' }}>{s}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {!(checkinSummary.skippedSteps ?? []).includes(8) && (
                  <div style={{ marginBottom: 4 }}>
                    <div style={{ fontSize: 'var(--fs-xs-plus)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}><IconTreatment size={14} color="var(--color-text-secondary)" /> Treatment experience</div>
                    <div style={{ fontSize: 'var(--fs-sm)', padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: checkinSummary.treatment === 'poor' ? 'color-mix(in srgb, var(--color-crisis) 8%, transparent)' : checkinSummary.treatment === 'great' || checkinSummary.treatment === 'good' ? 'color-mix(in srgb, var(--color-success) 8%, transparent)' : 'color-mix(in srgb, var(--color-text-primary) 2%, transparent)', border: `1px solid ${checkinSummary.treatment === 'poor' ? 'color-mix(in srgb, var(--color-crisis) 20%, transparent)' : checkinSummary.treatment === 'great' || checkinSummary.treatment === 'good' ? 'color-mix(in srgb, var(--color-success) 20%, transparent)' : 'color-mix(in srgb, var(--color-text-primary) 8%, transparent)'}` }}>
                      {checkinSummary.treatment === 'great' ? '🫶 Really well — felt cared for and respected' : checkinSummary.treatment === 'good' ? '👍 Okay — mostly positive' : checkinSummary.treatment === 'mixed' ? '🤔 Mixed — some things felt off' : checkinSummary.treatment === 'poor' ? '👎 Poorly — dismissed, harmed or disrespected' : '— Not answered'}
                    </div>
                  </div>
                  )}
                </div>
                {(checkinSummary.treatment === 'poor') && (
                  <div className="card" style={{ background: 'color-mix(in srgb, var(--color-crisis) 7%, transparent)', border: '1px solid color-mix(in srgb, var(--color-crisis) 25%, transparent)' }}>
                    <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 700, color: 'var(--color-crisis)', marginBottom: 6, display:'inline-flex', alignItems:'center', gap:6 }}><IconHeart size={16} ariaLabel="" heartTint="var(--color-crisis)" /> It sounds like something difficult happened</div>
                    <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', marginBottom: 12 }}>What you experienced matters. These articles may help you make sense of it and find support.</p>
                    {/* eslint-disable-next-line cw/no-inline-button-style -- inline .btn-primary marginBottom override on checkin-summary "Explore stigma and support" CTA; rule flags any style on button regardless of className */}
                    <button className="btn btn-primary" style={{ marginBottom: 0 }} onClick={() => { setCheckinSummary(null); setGuideAffirmation(null); setArticlesBackTarget('checkin'); showScreen('articles'); }}>
                      Explore stigma and support articles
                    </button>
                  </div>
                )}
                {(checkinSummary.treatment === 'mixed' || checkinSummary.treatment === 'good' || checkinSummary.treatment === 'great' || checkinSummary.treatment === null) && (
                  <div className="card" style={{ background: 'color-mix(in srgb, var(--color-accent) 6%, transparent)', border: '1px solid color-mix(in srgb, var(--color-accent) 15%, transparent)' }}>
                    <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--color-accent)', marginBottom: 4, display:'inline-flex', alignItems:'center', gap:6 }}><IconBook size={16} ariaLabel="" /> Want to learn more?</div>
                    <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginBottom: 10 }}>Articles covers stigma, advocacy, and navigating care as a racialized person with sickle cell.</p>
                    {/* eslint-disable-next-line cw/no-inline-button-style -- checkin-summary articles text-link; ghost-only sibling to copy block, not a chamfered .btn variant */}
                    <button style={{ background: 'none', border: 'none', color: 'var(--color-accent)', fontSize: 'var(--fs-sm)', fontWeight: 600, cursor: 'pointer', padding: 0 }} onClick={() => { setCheckinSummary(null); setGuideAffirmation(null); setArticlesBackTarget('checkin'); showScreen('articles'); }}>
                      Open articles ›
                    </button>
                  </div>
                )}
                <div className="card" style={{ paddingTop: 12, paddingBottom: 12 }}>
                  <button className="btn btn-primary" onClick={() => { setCheckinSummary(null); setGuideAffirmation(null); showScreen('home'); }}>Done — Back to Home</button>
                </div>
              </>
            ) : (
            <>
            <div className="card" style={{ paddingBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                {checkinStep > 1 ? (
                  // eslint-disable-next-line cw/no-inline-button-style -- checkin wizard header "← Back" text-link; ghost-only with 44px tap target in 3-col header (back / title / save&exit), not a chamfered .btn variant
                  <button
                    onClick={prevCheckinStep}
                    style={{ background: 'none', border: 'none', color: 'var(--color-accent)', fontSize: 'var(--fs-sm-plus)', fontWeight: 600, cursor: 'pointer', padding: '8px 12px 8px 0', display: 'flex', alignItems: 'center', gap: 4, minHeight: 44 }}
                  >← Back</button>
                ) : <span />}
                <h2 style={{ margin: 0, flex: 1, textAlign: 'center', fontSize: 'var(--fs-md)' }}>Daily check-in</h2>
                {/* eslint-disable-next-line cw/no-inline-button-style -- checkin wizard "Save & exit" small chip; outlined tertiary in 3-col header, not a chamfered .btn variant */}
                <button
                  onClick={exitCheckinWithSave}
                  style={{ background: 'none', border: '1px solid color-mix(in srgb, var(--color-accent) 20%, transparent)', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-xs-plus)', fontWeight: 600, cursor: 'pointer', padding: '6px 12px', minHeight: 36, textAlign: 'right', borderRadius: 'var(--radius-sm)' }}
                  title="Save progress and exit"
                >Save and exit</button>
              </div>
              {userAvatarId && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center', marginTop: 4, marginBottom: 6 }}>
                  <GuideBubble avatarId={userAvatarId} size={28} showGlow={false} customisation={userCustomisation} />
                  <p style={{ margin: 0, fontSize: 'var(--fs-xs-plus)', color: 'var(--color-text-secondary)' }}>Track how you're doing today</p>
                </div>
              )}
              {!userAvatarId && <p style={{ textAlign: 'center', fontSize: 'var(--fs-sm)' }}>Track how you're doing today</p>}
              <div style={{ marginTop: 12 }}>
                {(() => {
                  const totalEnabled = enabledCheckinQuestions.size;
                  const enabledArray = Array.from(enabledCheckinQuestions).sort((a, b) => a - b);
                  const currentIndex = enabledArray.indexOf(checkinStep);
                  const stepNum = currentIndex >= 0 ? currentIndex + 1 : 1;
                  return (
                    <>
                      <div className="progress-bar" style={{ height: 6 }}><div className="progress-fill" style={{ width: `${Math.round(stepNum / totalEnabled * 100)}%`, background: 'var(--color-accent)' }} /></div>
                      <p style={{ fontSize: 'var(--fs-xs-plus)', color: 'var(--color-text-secondary)', marginTop: 6, textAlign: 'center' }}>Step {stepNum} of {totalEnabled}</p>
                    </>
                  );
                })()}
              </div>
            </div>
            <div className="card" style={{ position: 'relative' }}>
              {/* === STEP 1: How are you today? + Crisis flag === */}
              {checkinStep === 1 && (
                <div>
                  {showCrisisReturnNudge ? (
                    <div style={{ padding: 20, textAlign: 'center' }}>
                      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}><IconSOS size={40} /></div>
                      <div style={{ fontWeight: 700, fontSize: 'var(--fs-base)', marginBottom: 8, color: 'var(--color-secondary)' }}>Support is here</div>
                      <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', marginBottom: 20 }}>Your safety matters. Please reach out to someone who can help right now.</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {/* eslint-disable-next-line cw/no-inline-button-style -- crisis-return-nudge primary CTA "Open crisis resources"; full-width chip with crisis-tinted background and inline icon, sits inside narrow safety panel, not a chamfered .btn variant */}
                        <button onClick={() => showScreen('crisis-resources')} style={{ padding: '14px 16px', borderRadius: 'var(--radius-md)', background: 'var(--color-crisis)', color: '#fff', border: 'none', fontWeight: 700, fontSize: 'var(--fs-sm)', cursor: 'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center', gap:6 }}><IconAlertSiren size={16} ariaLabel="" /> Open crisis resources</button>
                        {/* eslint-disable-next-line cw/no-inline-button-style -- crisis-return-nudge secondary CTA "View my crisis plan"; full-width warm-tinted outline inside narrow safety panel, not a chamfered .btn variant */}
                        <button onClick={() => { showScreen('crisis-plan'); }} style={{ padding: '12px 16px', borderRadius: 'var(--radius-md)', border: '2px solid color-mix(in srgb, var(--color-crisis) 50%, transparent)', background: 'color-mix(in srgb, var(--color-crisis) 10%, transparent)', color: 'var(--color-crisis)', fontWeight: 700, fontSize: 'var(--fs-sm)', cursor: 'pointer' }}>View my crisis plan</button>
                        {/* eslint-disable-next-line cw/no-inline-button-style -- crisis-return-nudge tertiary continue link; ghost text-link inside narrow safety panel, not a chamfered .btn variant */}
                        <button onClick={() => { setWatersCheck('sos'); setCheckinIsVOC(true); setShowCrisisReturnNudge(false); nextCheckinStep(); }} style={{ padding: '10px 16px', borderRadius: 'var(--radius-md)', border: 'none', background: 'none', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)', cursor: 'pointer' }}>Continue check-in anyway →</button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>{CHECKIN_STEP_ICONS[1]({ size: 20, color: 'var(--color-accent)' })} How are you today?</div>
                      <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', marginBottom: 16 }}>Pick what feels closest right now.</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {WATERS_CHECK_OPTIONS.map(opt => {
                          const isSelected = watersCheck === opt.code;
                          const isCrisis = opt.code === 'sos';
                          const color = isCrisis ? 'var(--color-secondary)' : 'var(--color-accent)';
                          const WatersIcon = { calm: IconCalmWaters, choppy: IconChoppyWaters, rough: IconRoughSeas, sos: IconSOS }[opt.code];
                          return (
                            // eslint-disable-next-line cw/no-inline-button-style -- waters-check option tile (Calm / Choppy / Rough / SOS); large 64-min-height tile with icon + 2-line label, dynamic color per state, not a chamfered .btn variant
                            <button
                              key={opt.code}
                              onClick={() => {
                                setWatersCheck(opt.code);
                                if (isCrisis) { setCheckinIsVOC(true); setShowCrisisReturnNudge(true); return; }
                                setTimeout(() => nextCheckinStep(), 300);
                              }}
                              style={{ padding: '16px 18px', borderRadius: 'var(--radius-lg)', border: isSelected ? `2px solid ${color}` : '2px solid color-mix(in srgb, var(--color-text-primary) 8%, transparent)', background: isSelected ? `color-mix(in srgb, ${color} 8%, transparent)` : 'color-mix(in srgb, var(--color-text-primary) 1%, transparent)', color: 'var(--color-text-primary)', fontSize: 'var(--fs-sm-plus)', fontWeight: isSelected ? 700 : 400, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 14, transition: 'border 0.15s ease, background 0.15s ease', minHeight: 64 }}
                            >
                              {WatersIcon && <WatersIcon size={34} />}
                              <div>
                                <div style={{ fontWeight: 600, fontSize: 'var(--fs-sm-plus)' }}>{opt.label}</div>
                                <div style={{ fontSize: 'var(--fs-xs-plus)', color: 'var(--color-text-secondary)', marginTop: 2 }}>{opt.desc}</div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      {/* "This is a crisis" toggle only appears under the SOS flow */}
                      {!essentialsMode && (
                        // eslint-disable-next-line cw/no-inline-button-style -- waters-check inline "skip" mini text-link; subdued ghost link below the option list, not a chamfered .btn variant
                        <button style={{ border: 'none', background: 'none', color: 'var(--color-text-secondary)', opacity: 0.5, fontSize: 'var(--fs-xs)', cursor: 'pointer', padding: '4px 8px', marginTop: 12 }} onClick={() => { setWatersCheck('skipped'); skipCheckinStep(); }}>skip</button>
                      )}
                    </div>
                  )}
                </div>
              )}
              {/* === STEP 2: Sleep (last night) === */}
              {checkinStep === 2 && (
                <div>
                  <SliderField
                    label={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconSleep size={18} color="var(--color-info)" /> Sleep quality last night</span>}
                    value={sleepFatigueValue}
                    setValue={setSleepFatigueValue}
                    leftLabel="Slept great"
                    rightLabel="Worst sleep"
                    color="var(--color-info)"
                    emojiMap={SCALE_EMOJIS}
                  />
                  {sleepFatigueValue !== null && sleepFatigueValue >= 3 && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, marginBottom: 8 }}>What affected your sleep?</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {SLEEP_QUALITY_CHIPS.map(chip => {
                          const isSelected = selectedSleepChips.includes(chip.code);
                          return (
                            <span key={chip.code} onClick={() => setSelectedSleepChips(prev => isSelected ? prev.filter(c => c !== chip.code) : [...prev, chip.code])} style={{ padding: '9px 14px', borderRadius: 'var(--radius-xl)', fontSize: 'var(--fs-sm-plus)', cursor: 'pointer', minHeight: 40, border: isSelected ? '2px solid var(--color-info)' : '1px solid color-mix(in srgb, var(--color-text-primary) 15%, transparent)', background: isSelected ? 'color-mix(in srgb, var(--color-info) 15%, transparent)' : 'color-mix(in srgb, var(--color-text-primary) 2%, transparent)', color: 'var(--color-text-primary)', userSelect: 'none' }}>{chip.label}</span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="btn-group" style={{ marginTop: 16, gridTemplateColumns: '1fr auto 1fr' }}>
                    <button className="btn btn-secondary" onClick={prevCheckinStep}>← Back</button>
                    {/* eslint-disable-next-line cw/no-inline-button-style -- checkin wizard nav-row "skip" text-link; ghost-only middle column inside 3-col .btn-group beside .btn back/next, sub-action consistent across all checkin steps, not a chamfered .btn variant */}
                    {!essentialsMode && <button style={{ border: 'none', background: 'none', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-xs-plus)', cursor: 'pointer', padding: '8px 14px', minHeight: 40, borderRadius: 'var(--radius-sm)' }} onClick={skipCheckinStep}>skip</button>}
                    <button className="btn btn-primary" onClick={nextCheckinStep}>Next</button>
                  </div>
                </div>
              )}
              {/* === STEP 3: Stress + descriptors === */}
              {checkinStep === 3 && (
                <div>
                  <SliderField
                    label={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconStress size={18} color="var(--color-accent)" /> Stress today</span>}
                    value={stressValue}
                    setValue={setStressValue}
                    leftLabel="No stress"
                    rightLabel="Overwhelming"
                    color="var(--color-accent)"
                    emojiMap={SCALE_EMOJIS}
                  />
                  {stressValue !== null && stressValue >= 3 && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, marginBottom: 8 }}>What's driving the stress?</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {STRESS_DESCRIPTOR_CHIPS.map(chip => {
                          const isSelected = selectedStressDescriptors.includes(chip.code);
                          return (
                            <span key={chip.code} onClick={() => setSelectedStressDescriptors(prev => isSelected ? prev.filter(c => c !== chip.code) : [...prev, chip.code])} style={{ padding: '9px 14px', borderRadius: 'var(--radius-xl)', fontSize: 'var(--fs-sm-plus)', cursor: 'pointer', minHeight: 40, border: isSelected ? '2px solid var(--color-accent)' : '1px solid color-mix(in srgb, var(--color-text-primary) 15%, transparent)', background: isSelected ? 'color-mix(in srgb, var(--color-accent) 15%, transparent)' : 'color-mix(in srgb, var(--color-text-primary) 2%, transparent)', color: 'var(--color-text-primary)', userSelect: 'none' }}>{chip.label}</span>
                          );
                        })}
                        {(customCheckinChips['stressDescriptors'] ?? []).map(tag => {
                          const isSelected = selectedStressDescriptors.includes(tag);
                          return (
                            <span key={tag} onClick={() => setSelectedStressDescriptors(prev => isSelected ? prev.filter(c => c !== tag) : [...prev, tag])} style={{ padding: '9px 14px', borderRadius: 'var(--radius-xl)', fontSize: 'var(--fs-sm-plus)', cursor: 'pointer', minHeight: 40, border: isSelected ? '2px solid var(--color-accent)' : '1px solid color-mix(in srgb, var(--color-text-primary) 15%, transparent)', background: isSelected ? 'color-mix(in srgb, var(--color-accent) 15%, transparent)' : 'color-mix(in srgb, var(--color-text-primary) 2%, transparent)', color: 'var(--color-text-primary)', userSelect: 'none' }}>{tag}</span>
                          );
                        })}
                      </div>
                      <AddCustomTag
                        usedEmojis={(customCheckinChips['stressDescriptors'] ?? []).map(c => c.match(/^\p{Emoji}/u)?.[0] ?? '').filter(Boolean)}
                        onAdd={tag => {
                          const updated = { ...customCheckinChips, stressDescriptors: [...(customCheckinChips['stressDescriptors'] ?? []), tag] };
                          setCustomCheckinChips(updated);
                          setSelectedStressDescriptors(prev => [...prev, tag]);
                          saveCustomChipsToProfile(updated);
                        }}
                      />
                    </div>
                  )}
                  <div className="btn-group" style={{ marginTop: 16, gridTemplateColumns: '1fr auto 1fr' }}>
                    <button className="btn btn-secondary" onClick={prevCheckinStep}>← Back</button>
                    {/* eslint-disable-next-line cw/no-inline-button-style -- checkin wizard nav-row "skip" text-link; ghost-only middle column inside 3-col .btn-group beside .btn back/next, sub-action consistent across all checkin steps, not a chamfered .btn variant */}
                    {!essentialsMode && <button style={{ border: 'none', background: 'none', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-xs-plus)', cursor: 'pointer', padding: '8px 14px', minHeight: 40, borderRadius: 'var(--radius-sm)' }} onClick={skipCheckinStep}>skip</button>}
                    <button className="btn btn-primary" onClick={nextCheckinStep}>Next</button>
                  </div>
                </div>
              )}
              {/* === STEP 6: Fatigue + type === */}
              {checkinStep === 6 && (
                <div>
                  <SliderField
                    label={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconFatigue size={18} color="var(--color-warning)" /> Fatigue today</span>}
                    value={fatigueValue}
                    setValue={setFatigueValue}
                    leftLabel="Not fatigued"
                    rightLabel="Completely drained"
                    color="var(--color-warning)"
                    emojiMap={SCALE_EMOJIS}
                  />
                  {fatigueValue !== null && fatigueValue >= 3 && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, marginBottom: 8 }}>What kind of fatigue?</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {FATIGUE_TYPE_CHIPS.map(chip => {
                          const isSelected = selectedFatigueType.includes(chip.code);
                          return (
                            <span key={chip.code} onClick={() => setSelectedFatigueType(prev => isSelected ? prev.filter(c => c !== chip.code) : [...prev, chip.code])} style={{ padding: '9px 14px', borderRadius: 'var(--radius-xl)', fontSize: 'var(--fs-sm-plus)', cursor: 'pointer', minHeight: 40, border: isSelected ? '2px solid var(--color-warning)' : '1px solid color-mix(in srgb, var(--color-text-primary) 15%, transparent)', background: isSelected ? 'color-mix(in srgb, var(--color-warning) 15%, transparent)' : 'color-mix(in srgb, var(--color-text-primary) 2%, transparent)', color: 'var(--color-text-primary)', userSelect: 'none' }}>{chip.label}</span>
                          );
                        })}
                        {(customCheckinChips['fatigueType'] ?? []).map(tag => {
                          const isSelected = selectedFatigueType.includes(tag);
                          return (
                            <span key={tag} onClick={() => setSelectedFatigueType(prev => isSelected ? prev.filter(c => c !== tag) : [...prev, tag])} style={{ padding: '9px 14px', borderRadius: 'var(--radius-xl)', fontSize: 'var(--fs-sm-plus)', cursor: 'pointer', minHeight: 40, border: isSelected ? '2px solid var(--color-warning)' : '1px solid color-mix(in srgb, var(--color-text-primary) 15%, transparent)', background: isSelected ? 'color-mix(in srgb, var(--color-warning) 15%, transparent)' : 'color-mix(in srgb, var(--color-text-primary) 2%, transparent)', color: 'var(--color-text-primary)', userSelect: 'none' }}>{tag}</span>
                          );
                        })}
                      </div>
                      <AddCustomTag
                        usedEmojis={(customCheckinChips['fatigueType'] ?? []).map(c => c.match(/^\p{Emoji}/u)?.[0] ?? '').filter(Boolean)}
                        onAdd={tag => {
                          const updated = { ...customCheckinChips, fatigueType: [...(customCheckinChips['fatigueType'] ?? []), tag] };
                          setCustomCheckinChips(updated);
                          setSelectedFatigueType(prev => [...prev, tag]);
                          saveCustomChipsToProfile(updated);
                        }}
                      />
                    </div>
                  )}
                  <div className="btn-group" style={{ marginTop: 16, gridTemplateColumns: '1fr auto 1fr' }}>
                    <button className="btn btn-secondary" onClick={prevCheckinStep}>← Back</button>
                    {/* eslint-disable-next-line cw/no-inline-button-style -- checkin wizard nav-row "skip" text-link; ghost-only middle column inside 3-col .btn-group beside .btn back/next, sub-action consistent across all checkin steps, not a chamfered .btn variant */}
                    {!essentialsMode && <button style={{ border: 'none', background: 'none', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-xs-plus)', cursor: 'pointer', padding: '8px 14px', minHeight: 40, borderRadius: 'var(--radius-sm)' }} onClick={skipCheckinStep}>skip</button>}
                    <button className="btn btn-primary" onClick={nextCheckinStep}>Next</button>
                  </div>
                </div>
              )}
              {/* === STEP 7: Pain level === */}
              {checkinStep === 7 && (
                <div>
                  <SliderField
                    label={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconPain size={18} color="var(--color-crisis)" /> Pain today</span>}
                    value={painValue}
                    setValue={setPainValue}
                    leftLabel="No pain"
                    rightLabel="Worst pain"
                    color="var(--color-crisis)"
                    emojiMap={SCALE_EMOJIS}
                  />
                  <div className="btn-group" style={{ marginTop: 8, gridTemplateColumns: '1fr auto 1fr' }}>
                    <button className="btn btn-secondary" onClick={prevCheckinStep}>← Back</button>
                    {/* eslint-disable-next-line cw/no-inline-button-style -- checkin wizard nav-row "skip" text-link; ghost-only middle column inside 3-col .btn-group beside .btn back/next, sub-action consistent across all checkin steps, not a chamfered .btn variant */}
                    {!essentialsMode && <button style={{ border: 'none', background: 'none', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-xs-plus)', cursor: 'pointer', padding: '8px 14px', minHeight: 40, borderRadius: 'var(--radius-sm)' }} onClick={skipCheckinStep}>skip</button>}
                    <button className="btn btn-primary" onClick={nextCheckinStep}>Next</button>
                  </div>
                </div>
              )}
              {/* === STEP 8: Body map (after pain question) === */}
              {checkinStep === 8 && (
                <CareWeaveBodyMap
                  onSave={(result) => { setPainMapResult(result); nextCheckinStep(); }}
                  onSkip={essentialsMode ? undefined : () => skipCheckinStep()}
                />
              )}
              {/* === STEP 4: Worry === */}
              {checkinStep === 4 && (
                <div>
                  <SliderField
                    label={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconWorry size={18} color="var(--color-crisis)" /> Worry today</span>}
                    value={worryValue}
                    setValue={setWorryValue}
                    leftLabel="Not worried"
                    rightLabel="Constant worry"
                    color="var(--color-crisis)"
                    emojiMap={SCALE_EMOJIS}
                  />
                  {worryValue !== null && worryValue >= 3 && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, marginBottom: 8 }}>What's on your mind?</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {WORRY_CHIPS.map(chip => {
                          const isSelected = selectedWorryChips.includes(chip.code);
                          return (
                            <span key={chip.code} onClick={() => setSelectedWorryChips(prev => isSelected ? prev.filter(c => c !== chip.code) : [...prev, chip.code])} style={{ padding: '9px 14px', borderRadius: 'var(--radius-xl)', fontSize: 'var(--fs-sm-plus)', cursor: 'pointer', minHeight: 40, border: isSelected ? '2px solid var(--color-crisis)' : '1px solid color-mix(in srgb, var(--color-text-primary) 15%, transparent)', background: isSelected ? 'color-mix(in srgb, var(--color-crisis) 15%, transparent)' : 'color-mix(in srgb, var(--color-text-primary) 2%, transparent)', color: 'var(--color-text-primary)', userSelect: 'none' }}>{chip.label}</span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="btn-group" style={{ marginTop: 8, gridTemplateColumns: '1fr auto 1fr' }}>
                    <button className="btn btn-secondary" onClick={prevCheckinStep}>← Back</button>
                    {/* eslint-disable-next-line cw/no-inline-button-style -- checkin wizard nav-row "skip" text-link; ghost-only middle column inside 3-col .btn-group beside .btn back/next, sub-action consistent across all checkin steps, not a chamfered .btn variant */}
                    {!essentialsMode && <button style={{ border: 'none', background: 'none', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-xs-plus)', cursor: 'pointer', padding: '8px 14px', minHeight: 40, borderRadius: 'var(--radius-sm)' }} onClick={skipCheckinStep}>skip</button>}
                    <button className="btn btn-primary" onClick={nextCheckinStep}>Next</button>
                  </div>
                </div>
              )}
              {/* === STEP 5: Feeling blue === */}
              {checkinStep === 5 && (
                <div>
                  <SliderField
                    label={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconBlue size={18} color="var(--color-info)" /> Feeling blue</span>}
                    value={feelingBlueValue}
                    setValue={setFeelingBlueValue}
                    leftLabel="Not at all"
                    rightLabel="Very low"
                    color="var(--color-info)"
                    emojiMap={SCALE_EMOJIS}
                  />
                  {feelingBlueValue !== null && feelingBlueValue >= 3 && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, marginBottom: 8 }}>What does it feel like?</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {FEELING_BLUE_CHIPS.map(chip => {
                          const isSelected = selectedBlueChips.includes(chip.code);
                          return (
                            <span key={chip.code} onClick={() => setSelectedBlueChips(prev => isSelected ? prev.filter(c => c !== chip.code) : [...prev, chip.code])} style={{ padding: '9px 14px', borderRadius: 'var(--radius-xl)', fontSize: 'var(--fs-sm-plus)', cursor: 'pointer', minHeight: 40, border: isSelected ? '2px solid var(--color-info)' : '1px solid color-mix(in srgb, var(--color-text-primary) 15%, transparent)', background: isSelected ? 'color-mix(in srgb, var(--color-info) 15%, transparent)' : 'color-mix(in srgb, var(--color-text-primary) 2%, transparent)', color: 'var(--color-text-primary)', userSelect: 'none' }}>{chip.label}</span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="btn-group" style={{ marginTop: 8, gridTemplateColumns: '1fr auto 1fr' }}>
                    <button className="btn btn-secondary" onClick={prevCheckinStep}>← Back</button>
                    {/* eslint-disable-next-line cw/no-inline-button-style -- checkin wizard nav-row "skip" text-link; ghost-only middle column inside 3-col .btn-group beside .btn back/next, sub-action consistent across all checkin steps, not a chamfered .btn variant */}
                    {!essentialsMode && <button style={{ border: 'none', background: 'none', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-xs-plus)', cursor: 'pointer', padding: '8px 14px', minHeight: 40, borderRadius: 'var(--radius-sm)' }} onClick={skipCheckinStep}>skip</button>}
                    <button className="btn btn-primary" onClick={nextCheckinStep}>Next</button>
                  </div>
                </div>
              )}
              {/* === STEP 9: Treatment === */}
              {checkinStep === 9 && (
                <div>
                  <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>{CHECKIN_STEP_ICONS[9]({ size: 20, color: 'var(--color-accent)' })} How were you treated today?</div>
                  <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', marginBottom: 14 }}>In care or in your life — anything worth noting?</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                    {([
                      ['great', 'treatment-great', 'Really well', 'Felt cared for and respected', 'var(--color-crisis-stage-1)'],
                      ['good',  'treatment-good',  'Generally okay', 'Mostly fine, nothing concerning', 'var(--color-crisis-stage-2)'],
                      ['mixed', 'treatment-mixed', 'Mixed', 'Some things felt off', 'var(--color-crisis-stage-3)'],
                      ['poor',  'treatment-poor',  'Poorly', 'Dismissed, harmed or disrespected', 'var(--color-crisis-stage-4)'],
                    ] as [string, string, string, string, string][]).map(([v, iconKey, label, desc, color]) => (
                      // eslint-disable-next-line cw/no-inline-button-style -- treatment-rating tile (Great / Good / Mixed / Poor); 2-col grid 100-min-height tile with stacked icon + label + desc, dynamic per-stage color, Phase 3 Track B locked-precedent treatment-rating system, not a chamfered .btn variant
                      <button
                        key={v}
                        onClick={() => { setSelectedTreatment(v); if (v !== 'poor') setTimeout(() => nextCheckinStep(), 350); }}
                        style={{ padding: '16px 12px', borderRadius: 'var(--radius-lg)', border: selectedTreatment === v ? `2px solid ${color}` : '2px solid color-mix(in srgb, var(--color-text-primary) 8%, transparent)', background: selectedTreatment === v ? `color-mix(in srgb, ${color} 12%, transparent)` : 'color-mix(in srgb, var(--color-text-primary) 1%, transparent)', color: 'var(--color-text-primary)', fontSize: 'var(--fs-sm-plus)', fontWeight: selectedTreatment === v ? 700 : 400, cursor: 'pointer', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minHeight: 100, justifyContent: 'center' }}
                      >
                        <span style={{ color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          {(() => {
                            const Icon = CRYSTAL_ICONS[iconKey as CrystalIconKey];
                            return Icon ? <Icon size={32} ariaLabel="" /> : null;
                          })()}
                        </span>
                        <div style={{ fontWeight: 600, fontSize: 'var(--fs-sm-plus)' }}>{label}</div>
                        <div style={{ fontSize: 'var(--fs-xs-plus)', color: 'var(--color-text-secondary)', fontWeight: 400, lineHeight: 1.35 }}>{desc}</div>
                      </button>
                    ))}
                  </div>
                  {selectedTreatment === 'poor' && (
                    <div style={{ padding: 14, background: 'color-mix(in srgb, var(--color-crisis) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--color-crisis) 25%, transparent)', borderRadius: 'var(--radius-md)', marginBottom: 14 }}>
                      <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--color-crisis)', display:'inline-flex', alignItems:'center', gap:6 }}><IconHeart size={16} ariaLabel="" heartTint="var(--color-crisis)" /> That matters — and you deserve better</div>
                      <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', marginBottom: 12 }}>You can log what happened in the Stigma Tracker — it builds a record over time and it's yours.</p>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {/* eslint-disable-next-line cw/no-inline-button-style -- treatment-poor escalation continue: ghost outline in 2-col flex pair inside warm-tinted comfort card, not a chamfered .btn variant */}
                        <button onClick={nextCheckinStep} style={{ flex: 1, padding: '10px', borderRadius: 'var(--radius-md)', border: '1px solid color-mix(in srgb, var(--color-text-primary) 15%, transparent)', background: 'transparent', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)', cursor: 'pointer' }}>Continue</button>
                        {/* eslint-disable-next-line cw/no-inline-button-style -- treatment-poor escalation primary: warm-toned filled CTA opening stigma tracker, 2-col flex pair inside comfort card, not a chamfered .btn variant */}
                        <button onClick={() => { submitCheckin(true).then(() => showScreen('event-log')); }} style={{ flex: 1, padding: '10px', borderRadius: 'var(--radius-md)', background: 'var(--color-crisis)', color: '#fff', border: 'none', fontWeight: 700, fontSize: 'var(--fs-sm)', cursor: 'pointer' }}>Open stigma tracker →</button>
                      </div>
                    </div>
                  )}
                  <div className="btn-group" style={{ marginTop: 16, gridTemplateColumns: '1fr auto 1fr' }}>
                    <button className="btn btn-secondary" onClick={prevCheckinStep}>← Back</button>
                    {/* eslint-disable-next-line cw/no-inline-button-style -- checkin wizard nav-row "skip" text-link; ghost-only middle column inside 3-col .btn-group beside .btn back/next, sub-action consistent across all checkin steps, not a chamfered .btn variant */}
                    {!essentialsMode && <button style={{ border: 'none', background: 'none', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-xs-plus)', cursor: 'pointer', padding: '8px 14px', minHeight: 40, borderRadius: 'var(--radius-sm)' }} onClick={skipCheckinStep}>skip</button>}
                    <button className="btn btn-primary" onClick={nextCheckinStep}>Next</button>
                  </div>
                </div>
              )}
              {/* === STEP 10: What helped === */}
              {checkinStep === 10 && (
                <div>
                  <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>{CHECKIN_STEP_ICONS[10]({ size: 20, color: 'var(--color-accent)' })} What helped today?</div>
                  <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', marginBottom: 14 }}>Select everything that helped, or skip if nothing yet.</p>
                  {(Object.entries(WHAT_HELPED_CATEGORIES) as [string, readonly {code: string; label: string}[]][]).map(([category, chips]) => (
                    <div key={category} style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>{category}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {chips.map(chip => {
                          const isSelected = selectedCopingStrategies.includes(chip.code);
                          return (
                            <span key={chip.code} onClick={() => toggleCopingStrategy(chip.code)} style={{ padding: '9px 14px', borderRadius: 'var(--radius-xl)', fontSize: 'var(--fs-sm-plus)', cursor: 'pointer', minHeight: 40, border: isSelected ? '2px solid var(--color-accent)' : '1px solid color-mix(in srgb, var(--color-text-primary) 15%, transparent)', background: isSelected ? 'color-mix(in srgb, var(--color-accent) 15%, transparent)' : 'color-mix(in srgb, var(--color-text-primary) 2%, transparent)', color: 'var(--color-text-primary)', userSelect: 'none' }}>{chip.label}</span>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {(customCheckinChips['whatHelped'] ?? []).map(strategy => (
                      <span key={strategy} onClick={() => toggleCopingStrategy(strategy)} style={{ padding: '9px 14px', borderRadius: 'var(--radius-xl)', fontSize: 'var(--fs-sm-plus)', cursor: 'pointer', minHeight: 40, border: selectedCopingStrategies.includes(strategy) ? '2px solid var(--color-accent)' : '1px solid color-mix(in srgb, var(--color-text-primary) 15%, transparent)', background: selectedCopingStrategies.includes(strategy) ? 'color-mix(in srgb, var(--color-accent) 15%, transparent)' : 'color-mix(in srgb, var(--color-text-primary) 2%, transparent)', color: 'var(--color-text-primary)', userSelect: 'none' }}>{strategy}</span>
                    ))}
                  </div>
                  <AddCustomTag
                    usedEmojis={(customCheckinChips['whatHelped'] ?? []).map(c => c.match(/^\p{Emoji}/u)?.[0] ?? '').filter(Boolean)}
                    onAdd={tag => {
                      const updated = { ...customCheckinChips, whatHelped: [...(customCheckinChips['whatHelped'] ?? []), tag] };
                      setCustomCheckinChips(updated);
                      toggleCopingStrategy(tag);
                      saveCustomChipsToProfile(updated);
                    }}
                  />
                  <div className="btn-group" style={{ marginTop: 16, gridTemplateColumns: '1fr auto 1fr' }}>
                    <button className="btn btn-secondary" onClick={prevCheckinStep}>← Back</button>
                    {/* eslint-disable-next-line cw/no-inline-button-style -- checkin wizard nav-row "skip" text-link; ghost-only middle column inside 3-col .btn-group beside .btn back/next, sub-action consistent across all checkin steps, not a chamfered .btn variant */}
                    {!essentialsMode && <button style={{ border: 'none', background: 'none', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-xs-plus)', cursor: 'pointer', padding: '8px 14px', minHeight: 40, borderRadius: 'var(--radius-sm)' }} onClick={skipCheckinStep}>skip</button>}
                    <button className="btn btn-primary" onClick={nextCheckinStep}>Next</button>
                  </div>
                </div>
              )}
            </div>
            </>
            )}
          </div>
        )}

        {screen === 'event-log' && (
          <Suspense fallback={<LazyFallback />}>
            <EventLogScreenInline
              showScreen={showScreen}
              userAvatarId={userAvatarId}
              userCustomisation={userCustomisation}
              moduleIntroSeenEventLog={moduleIntroSeenEventLog}
              dismissModuleIntroEventLog={dismissModuleIntroEventLog}
              forceUpdate={forceUpdate}
              stigmaTracker={stigmaTracker}
              onEntrySaved={onTrackerEntrySaved}
            />
          </Suspense>
        )}

        {screen === 'tools' && (
          !isFeatureTutorialSeen('cw_tutorial_tools') ? (
            <FeatureTutorial storageKey="cw_tutorial_tools" steps={TOOLS_TUTORIAL} onComplete={() => forceUpdate(n => n + 1)} xpReward={10} />
          ) :
          <div className="screen">
            {!moduleIntroSeenTools && (
              <ModuleIntroBanner
                icon="🛠️"
                title="Your care toolkit"
                body="Everything you need in one place — track medications, log events, prepare for hospital visits, plan for crises, and more. Explore any tool to get started."
                onDismiss={dismissModuleIntroTools}
              />
            )}
            {(() => {
              type ToolItem = { icon?: string; iconKey: CrystalIconKey; label: string; desc: string; screen: Screen; color: string; articlesFilter?: string; action?: () => void };
              type ToolGroup = { group: string; items: ToolItem[] };
              const allToolGroups: ToolGroup[] = [
              {
                group: 'Track & Log',
                items: [
                  { icon: '📋', iconKey: 'checkin-note', label: 'Daily Check-In', desc: 'Log how you\'re feeling today', screen: 'home', color: 'var(--color-accent)', action: () => startCheckin() },
                  { icon: '💊', iconKey: 'medications', label: 'Medications', desc: 'Your medication list and history', screen: 'medications', color: 'var(--color-accent)' },
                  { icon: '📓', iconKey: 'growth-sprout', label: 'Journal', desc: 'Write or speak, then decorate with stickers', screen: 'journal', color: 'var(--color-secondary)' },
                  { icon: '📝', iconKey: 'log-event', label: 'Log stigma', desc: 'Document what happened today', screen: 'event-log', color: 'var(--color-warning)' },
                  { icon: '🫀', iconKey: 'emotions-body-map', label: 'Emotions Body Map', desc: 'Map how emotions feel in your body', screen: 'emotions-body-map', color: 'var(--color-secondary)' },
                ],
              },
              {
                group: 'Plan & Prepare',
                items: [
                  { icon: '🏥', iconKey: 'hospital-prep', label: 'Hospital Prep Kit', desc: 'Scripts, rights, and admission prep', screen: 'hospital-prep', color: 'var(--color-crisis)' },
                  { icon: '🧭', iconKey: 'crisis-plan', label: 'Crisis Plan', desc: 'Your personalized crisis response', screen: 'crisis-plan', color: 'var(--color-secondary)' },
                  { icon: '🌦', iconKey: 'weather-drizzle', label: 'Weather Report', desc: 'Weather-based flare risk outlook', screen: 'weather-report', color: 'var(--color-info)' },
                ],
              },
              {
                group: 'Articles',
                items: [
                  { icon: '🏥', iconKey: 'journey-clinic-visit', label: 'Crisis visits and the ED', desc: 'Guides for hospital visits and clinic care', screen: 'articles', color: 'var(--color-crisis)', articlesFilter: 'passive_read' },
                  { icon: '🎯', iconKey: 'advocacy', label: 'Advocacy scripts', desc: 'Language to assert your rights', screen: 'articles', color: 'var(--color-accent)', articlesFilter: 'script' },
                  { icon: '🧘', iconKey: 'grounding', label: 'Grounding practices', desc: 'Breathing, 5-4-3-2-1, and calming tools', screen: 'grounding', color: 'var(--color-success)' },
                  { icon: '📖', iconKey: 'new-moment', label: 'Community stories', desc: 'Real stories from youth with SCD', screen: 'articles', color: 'var(--color-info)', articlesFilter: 'story' },
                  { icon: '✊', iconKey: 'solidarity', label: 'Understanding stigma', desc: 'Racial justice and intersectional stigma', screen: 'articles', color: 'var(--color-crisis)', articlesFilter: 'stigma' },
                  { icon: '🏷️', iconKey: 'identity-tag', label: 'Identity dictionary', desc: 'Terms and identities used in CareWeave', screen: 'articles', color: 'var(--color-accent)', articlesFilter: 'identity' },
                ],
              },
              {
                group: 'My People',
                items: [
                  { icon: '📋', iconKey: 'care-team', label: 'Care Team Hub', desc: 'Doctors, nurses, and contacts', screen: 'care-team', color: 'var(--color-success)' },
                  { icon: '🤝', iconKey: 'journey-relationships', label: 'Support Network', desc: 'Family, friends, and community', screen: 'support-network', color: 'var(--color-crisis)' },
                ],
              },
            ];
            const allToolItems = allToolGroups.flatMap(g => g.items);
            const favouriteItems = allToolItems.filter(i => starredTools.includes(i.label));

            function handleToolTap(item: ToolItem) {
              recordToolAccess(item.label);
              if (item.action) {
                item.action();
              } else if (item.articlesFilter) {
                const filterTopicMap: Record<string, string> = {
                  'passive_read': 'ed',
                  'script': 'rights-advocacy',
                  'grounding': 'pain-sleep',
                  'story': 'mental-health-stigma',
                  'stigma': 'mental-health-stigma',
                  'identity': 'school-work',
                };
                const topicId = filterTopicMap[item.articlesFilter] ?? null;
                if (item.screen === 'articles') setArticlesBackTarget('tools');
                if (item.screen === 'weather-report') setWeatherBackTarget('tools');
                showScreen(item.screen, topicId ? { libraryTopicId: topicId } : undefined);
              } else {
                if (item.screen === 'articles') setArticlesBackTarget('tools');
                if (item.screen === 'weather-report') setWeatherBackTarget('tools');
                showScreen(item.screen);
              }
            }

            function renderToolRow(item: ToolItem, index: number, total: number) {
              const isStarred = starredTools.includes(item.label);
              return (
                <div
                  key={`${item.screen}-${item.label}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 4px',
                    cursor: 'pointer',
                    borderRadius: 'var(--radius-md)',
                    transition: 'background 0.15s',
                    borderBottom: index < total - 1 ? '1px solid color-mix(in srgb, var(--color-accent) 6%, transparent)' : 'none',
                    minHeight: 56,
                  }}
                >
                  {/* eslint-disable-next-line cw/no-inline-button-style -- tools list row: full-width composite (icon + 2-line label + chevron) inside parent flex card row, reset button styling to "div-like" since favourites star is a separate adjacent button, not a chamfered .btn variant */}
                  <button
                    type="button"
                    onClick={() => handleToolTap(item)}
                    aria-label={`${item.label}: ${item.desc}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      flex: 1,
                      minWidth: 0,
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      margin: 0,
                      color: 'inherit',
                      font: 'inherit',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{
                      width: 46,
                      height: 46,
                      borderRadius: 'var(--radius-lg)',
                      background: `color-mix(in srgb, ${item.color} 8%, transparent)`,
                      border: `1px solid color-mix(in srgb, ${item.color} 15%, transparent)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      {(() => { const Icon = CRYSTAL_ICONS[item.iconKey]; return <Icon size={26} ariaLabel="" />; })()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 'var(--fs-sm-plus)' }}>{item.label}</div>
                      <div style={{ fontSize: 'var(--fs-xs-plus)', color: 'var(--color-text-secondary)', marginTop: 2 }}>{item.desc}</div>
                    </div>
                    <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--fs-lg)', flexShrink: 0, opacity: 0.5 }}>›</span>
                  </button>
                  {/* eslint-disable-next-line cw/no-inline-button-style -- tools-list favourite star toggle; sibling icon-only with 44×44 tap target inside the tool row, no .btn-icon-only primitive */}
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleStarredTool(item.label); }}
                    aria-label={isStarred ? 'Remove from favourites' : 'Add to favourites'}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 'var(--fs-lg)',
                      color: isStarred ? 'var(--color-warning)' : 'var(--color-text-secondary)',
                      padding: '8px 4px 8px 10px',
                      flexShrink: 0,
                      lineHeight: 1,
                      opacity: isStarred ? 1 : 0.4,
                      transition: 'color 0.15s, opacity 0.15s',
                      minHeight: 44,
                      minWidth: 44,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {(() => { const Icon = CRYSTAL_ICONS[isStarred ? 'star-filled' : 'star-outlined']; return <Icon size={20} ariaLabel={isStarred ? 'Unfavourite' : 'Favourite'} />; })()}
                  </button>
                </div>
              );
            }

            return (
              <>
                {favouriteItems.length > 0 && (
                  <div className="card" style={{ padding: '16px 16px 8px' }}>
                    <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--color-warning)', marginBottom: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {(() => { const Icon = CRYSTAL_ICONS['star-filled']; return <Icon size={14} ariaLabel="" />; })()}
                      My favourites
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {favouriteItems.map((item, i) => renderToolRow(item, i, favouriteItems.length))}
                    </div>
                  </div>
                )}
                {allToolGroups.map(group => (
                  <div key={group.group} className="card" style={{ padding: '14px 16px 8px' }}>
                    <div style={{ fontSize: 'var(--fs-xs-plus)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-secondary)', marginBottom: 10 }}>
                      {group.group}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {group.items.map((item, i) => renderToolRow(item, i, group.items.length))}
                    </div>
                  </div>
                ))}
              </>
            );
            })()}
          </div>
        )}

        {screen === 'journal' && (
          !isFeatureTutorialSeen('cw_tutorial_journal_v2') ? (
            <FeatureTutorial storageKey="cw_tutorial_journal_v2" steps={JOURNAL_TUTORIAL} onComplete={() => forceUpdate(n => n + 1)} xpReward={10} />
          ) : (
            <JournalScreen onBack={() => showScreen('tools')} totalXP={totalXP} guideId={userAvatarId} bondLevel={guideBondLevel} />
          )
        )}

        {screen === 'grounding' && (
          <GroundingScreen
            onBack={() => showScreen('tools')}
            reduceFlashing={accessibilityPrefs.reduceFlashing}
          />
        )}

        {screen === 'emotions-body-map' && (
          <div className="screen">
            <div className="card" style={{ background: 'var(--gradient-soft)', border: '1px solid color-mix(in srgb, var(--color-secondary) 25%, transparent)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                {/* eslint-disable-next-line cw/no-inline-button-style -- emotions-body-map screen "‹" back chevron; ghost icon-only inline with avatar + title row, no .btn-icon-only primitive */}
                <button onClick={() => showScreen('tools')} style={{ background: 'none', border: 'none', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-lg)', cursor: 'pointer', padding: '4px 6px 4px 0', lineHeight: 1, flexShrink: 0, alignSelf: 'center' }} aria-label="Back">‹</button>
                {userAvatarId ? <GuideBubble avatarId={userAvatarId} size={36} showGlow={false} customisation={userCustomisation} /> : <div style={{ flexShrink: 0, display:'inline-flex' }}><IconEmotionsBodyMap size={36} ariaLabel="" /></div>}
                <div>
                  <h2 style={{ margin: '0 0 4px' }}>Emotions body map</h2>
                  <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', margin: 0 }}>Explore how emotions show up in your body — anytime, outside of check-in.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <CareWeaveEmotionMap
                standalone
                onSave={(result) => {
                  setEmotionMapResult(result);
                  showScreen('tools');
                }}
                onSkip={() => showScreen('tools')}
              />
            </div>
          </div>
        )}

        {screen === 'patterns' && (
          <Suspense fallback={<LazyFallback />}>
            <PatternsScreenInline
              hidePatterns={hidePatterns}
              toggleHidePatterns={toggleHidePatterns}
              patternsPeriod={patternsPeriod}
              setPatternsPeriod={setPatternsPeriod}
              patternsOffset={patternsOffset}
              setPatternsOffset={setPatternsOffset}
              patternsSelectedDay={patternsSelectedDay}
              setPatternsSelectedDay={setPatternsSelectedDay}
              patternsSelectedArea={patternsSelectedArea}
              setPatternsSelectedArea={setPatternsSelectedArea}
              patternsChartMode={patternsChartMode}
              setPatternsChartMode={setPatternsChartMode}
              patternsHoveredPoint={patternsHoveredPoint}
              setPatternsHoveredPoint={setPatternsHoveredPoint}
              checkinEntries={checkinEntries}
              eventLogEntries={eventLogEntries}
              edVisitHistory={edVisitHistory}
              userAvatarId={userAvatarId}
              userCustomisation={userCustomisation}
              moduleIntroSeenPatterns={moduleIntroSeenPatterns}
              dismissModuleIntroPatterns={dismissModuleIntroPatterns}
              streak={streak}
              medications={medications}
              medicationLogs={medicationLogs}
              lastCrisisDate={lastCrisisDate}
              showScreen={showScreen}
              startCheckin={startCheckin}
              setWeatherBackTarget={setWeatherBackTarget}
              forceUpdate={forceUpdate}
            />
          </Suspense>
        )}

        {screen === 'articles' && (() => {
          type TI = { kind: 'content'; id: string } | { kind: 'modal'; id: string; title: string; desc: string; tag: string };
          type T = { id: string; label: string; icon?: string; iconKey: CrystalIconKey; color: string; items: TI[] };

          const ARTICLE_TOPICS: T[] = [
            {
              id: 'ed', label: 'Crisis visits and the ED', icon: '🏥', iconKey: 'hospital-prep', color: 'var(--color-crisis)',
              items: [
                { kind: 'content', id: 'ed_before_is_it_bad_enough_v1' },
                { kind: 'content', id: 'ed_before_emergency_profile_intro_v1' },
                { kind: 'content', id: 'ed_before_triage_overview_v1' },
                { kind: 'content', id: 'ed_during_waiting_grounding_v1' },
                { kind: 'content', id: 'ed_during_security_safety_check_v1' },
                { kind: 'content', id: 'ed_after_debrief_normalize_v1' },
                { kind: 'content', id: 'ed_after_one_slider_checkin_v1' },
                { kind: 'content', id: 'ed_after_what_helped_micro_reflection_v1' },
                { kind: 'content', id: 'newcomer_navigating_healthcare_levels_v1' },
                { kind: 'modal', id: 'ed-script', title: 'Crisis Advocacy Script', desc: 'Build and copy your personalized crisis communication script', tag: 'Interactive' },
                { kind: 'modal', id: 'story-maya', title: "Maya's Crisis Story", desc: 'How tracking helped Maya advocate during crisis visits', tag: '5 min' },
                { kind: 'modal', id: 'story-jamal', title: "Jamal's Transition", desc: 'Navigating the move to adult sickle cell care', tag: '7 min' },
              ],
            },
            {
              id: 'pain-sleep', label: 'Pain and sleep', icon: '🌙', iconKey: 'grounding', color: 'var(--color-accent)',
              items: [
                { kind: 'content', id: 'newcomer_your_pain_is_real_v1' },
                { kind: 'content', id: 'newcomer_pain_looks_different_v1' },
                { kind: 'content', id: 'newcomer_why_dont_they_believe_me_v1' },
                { kind: 'content', id: 'ed_during_waiting_grounding_v1' },
                { kind: 'content', id: 'newcomer_3p_approach_v1' },
                { kind: 'modal', id: 'grounding-54321', title: '5-4-3-2-1 Technique', desc: 'A quick sensory grounding practice for pain', tag: '3 min' },
                { kind: 'modal', id: 'breathing', title: 'Box Breathing', desc: 'Calm your nervous system with structured breathing', tag: '2 min' },
              ],
            },
            {
              id: 'school-work', label: 'School and work', icon: '🎓', iconKey: 'journey-post-secondary', color: 'var(--color-warning)',
              items: [
                { kind: 'content', id: 'newcomer_3sentence_health_summary_v1' },
                { kind: 'content', id: 'newcomer_rights_canadian_healthcare_v1' },
                { kind: 'content', id: 'newcomer_tests_and_scans_v1' },
                { kind: 'content', id: 'newcomer_glossary_v1' },
                { kind: 'modal', id: 'transition-script', title: 'Transition Conversation Starter', desc: 'Questions to ask when moving to adult care', tag: 'Customizable' },
                { kind: 'modal', id: 'rights-script', title: 'Know Your Rights', desc: 'Language to assert your rights as a patient', tag: 'Reference' },
              ],
            },
            {
              id: 'mental-health-stigma', label: 'Mental health and stigma', icon: '✊', iconKey: 'solidarity', color: 'var(--color-crisis)',
              items: [
                { kind: 'content', id: 'newcomer_stigma_discrimination_v1' },
                { kind: 'content', id: 'newcomer_when_system_hurts_you_v1' },
                { kind: 'content', id: 'newcomer_equity_vs_equality_v1' },
                { kind: 'content', id: 'story_ed_too_calm_v1' },
                { kind: 'content', id: 'story_ed_security_watch_v1' },
                { kind: 'content', id: 'story_ed_believed_and_documented_v1' },
                { kind: 'modal', id: 'racial-justice', title: 'Racial Justice & SCD', desc: 'The history of racism in sickle cell research', tag: 'Learn' },
                { kind: 'modal', id: 'stigma-101', title: 'Stigma 101', desc: 'What is intersectional stigma and why does it matter?', tag: 'Foundation' },
              ],
            },
            {
              id: 'rights-advocacy', label: 'Rights, advocacy, and safety', icon: '🛡️', iconKey: 'shield', color: 'var(--color-success)',
              items: [
                { kind: 'content', id: 'script_ed_drug_seeking_physician_v1' },
                { kind: 'content', id: 'script_ed_long_waits_triage_v1' },
                { kind: 'content', id: 'newcomer_rights_canadian_healthcare_v1' },
                { kind: 'content', id: 'ed_during_security_safety_check_v1' },
                { kind: 'content', id: 'newcomer_equity_vs_equality_v1' },
                { kind: 'modal', id: 'ed-script', title: 'Crisis Advocacy Script', desc: 'Build and copy your personalized crisis communication script', tag: 'Interactive' },
                { kind: 'modal', id: 'rights-script', title: 'Know Your Rights', desc: 'Language to assert your rights as a patient', tag: 'Reference' },
              ],
            },
          ];

          const contentById = new Map(ED_CONTENT.map(i => [i.id, i]));

          const myTools = ED_CONTENT
            .filter(i => getMeta(i.id).starred)
            .sort((a, b) => getMeta(b.id).lastOpenedAt - getMeta(a.id).lastOpenedAt);

          type FreqItem =
            | { kind: 'content'; item: typeof ED_CONTENT[number] }
            | { kind: 'modal'; id: string; title: string; desc: string; tag: string };

          const allModalItems: Array<{ id: string; title: string; desc: string; tag: string }> = [];
          const seenModalIds = new Set<string>();
          ARTICLE_TOPICS.forEach(topic => {
            topic.items.forEach(ti => {
              if (ti.kind === 'modal' && !seenModalIds.has(ti.id)) {
                seenModalIds.add(ti.id);
                allModalItems.push({ id: ti.id, title: ti.title, desc: ti.desc, tag: ti.tag });
              }
            });
          });

          const frequentlyUsed: FreqItem[] = [
            ...ED_CONTENT
              .filter(i => { const m = getMeta(i.id); return m.openCount > 0 && !m.starred; })
              .map(i => ({ kind: 'content' as const, item: i })),
            ...allModalItems
              .filter(m => getMeta(m.id).openCount > 0)
              .map(m => ({ kind: 'modal' as const, id: m.id, title: m.title, desc: m.desc, tag: m.tag })),
          ]
            .sort((a, b) => {
              const openA = getMeta(a.kind === 'content' ? a.item.id : a.id).openCount;
              const openB = getMeta(b.kind === 'content' ? b.item.id : b.id).openCount;
              return openB - openA;
            })
            .slice(0, 8);

          const secOpen = (id: string) => expandedArticleSections.has(id);
          const toggleSec = (id: string) => setExpandedArticleSections(prev => {
            const s = new Set(prev);
            s.has(id) ? s.delete(id) : s.add(id);
            return s;
          });

          const secHeaderStyle = (open: boolean): React.CSSProperties => ({
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 16px',
            background: 'var(--color-bg-card)',
            border: '1px solid var(--color-border)',
            borderRadius: open ? 'var(--radius-md) var(--radius-md) 0 0' : 'var(--radius-md)',
            cursor: 'pointer',
            fontSize: 'var(--fs-sm-plus)',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            textAlign: 'left',
          });

          const secBodyStyle: React.CSSProperties = {
            border: '1px solid var(--color-border)',
            borderTop: 'none',
            borderRadius: '0 0 12px 12px',
            padding: '12px',
            background: 'var(--color-bg-surface)',
          };

          const chevron = (open: boolean) => (
            <span style={{ fontSize: 'var(--fs-sm)', transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none', display: 'inline-block', color: 'var(--color-text-secondary)' }}>▾</span>
          );


          return (
          <div className="screen">
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                {/* eslint-disable-next-line cw/no-inline-button-style -- articles screen "‹" back chevron; ghost icon-only with 44px tap target inline with avatar + title row, no .btn-icon-only primitive */}
                <button onClick={() => showScreen(articlesBackTarget)} style={{ background: 'none', border: 'none', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-lg-plus)', cursor: 'pointer', padding: '8px 10px 8px 0', lineHeight: 1, flexShrink: 0, minHeight: 44, minWidth: 36, display: 'flex', alignItems: 'center' }} aria-label="Back">‹</button>
                {userAvatarId && <GuideBubble avatarId={userAvatarId} size={32} showGlow={false} customisation={userCustomisation} />}
                <div>
                  <h2 style={{ margin: 0 }}>Articles</h2>
                  <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', margin: 0 }}>Community wisdom, advocacy tools, and grounding practices</p>
                </div>
              </div>
              <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1, height: 6, background: 'var(--color-bg-surface)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', background: 'var(--color-success)', borderRadius: 3, width: `${ED_CONTENT.length > 0 ? Math.round((ED_CONTENT.filter(c => progress[c.id] === 'completed').length / ED_CONTENT.length) * 100) : 0}%`, transition: 'width 0.4s ease' }} />
                </div>
                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', flexShrink: 0 }}>
                  {ED_CONTENT.filter(c => progress[c.id] === 'completed').length} of {ED_CONTENT.length} completed
                </span>
              </div>
            </div>

            {/* ── My tools ──────────────────────── */}
            <div style={{ marginBottom: 10 }}>
              {/* eslint-disable-next-line cw/no-inline-button-style -- articles screen "My tools" accordion header; full-width disclosure trigger using shared secHeaderStyle helper, not a chamfered .btn variant */}
              <button style={secHeaderStyle(secOpen('my-tools'))} onClick={() => toggleSec('my-tools')}>
                <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}><IconSparkle size={16} ariaLabel="" /> My tools{myTools.length > 0 && <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 500, color: 'var(--color-text-secondary)', marginLeft: 6 }}>({myTools.length})</span>}</span>
                {chevron(secOpen('my-tools'))}
              </button>
              {secOpen('my-tools') && (
                <div style={secBodyStyle}>
                  {myTools.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '22px 0', color: 'var(--color-text-secondary)' }}>
                      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'center' }} aria-hidden="true">{(() => { const Icon = CRYSTAL_ICONS['star-outlined']; return <Icon size={28} ariaLabel="" />; })()}</div>
                      <p style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>No favourites yet. Tap the star on any article to keep it close.</p>
                    </div>
                  ) : (
                    <div className="module-grid">
                      {myTools.map(item => (
                        <ArticleCard
                          key={item.id}
                          item={item}
                          status={getStatus(item.id)}
                          starred={getMeta(item.id).starred}
                          onToggleStar={() => toggleStar(item.id)}
                          onOpen={() => openContentItem(item)}
                          onCycleStatus={(e) => handleResourceCycleStatus(e, item)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Frequently used (hidden until ≥1 open, collapsible, default expanded) ── */}
            {frequentlyUsed.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                {/* eslint-disable-next-line cw/no-inline-button-style -- articles screen "Frequently used" accordion header; full-width disclosure trigger using shared secHeaderStyle helper, not a chamfered .btn variant */}
                <button style={secHeaderStyle(secOpen('frequent'))} onClick={() => toggleSec('frequent')}>
                  <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}><IconFire size={16} ariaLabel="" /> Frequently used<span style={{ fontSize: 'var(--fs-xs)', fontWeight: 500, color: 'var(--color-text-secondary)', marginLeft: 6 }}>({frequentlyUsed.length})</span></span>
                  {chevron(secOpen('frequent'))}
                </button>
                {secOpen('frequent') && (
                  <div style={secBodyStyle}>
                    <div className="module-grid">
                      {frequentlyUsed.map(fi => {
                        if (fi.kind === 'content') {
                          const item = fi.item;
                          return (
                            <ArticleCard
                              key={`freq-${item.id}`}
                              item={item}
                              status={getStatus(item.id)}
                              starred={getMeta(item.id).starred}
                              onToggleStar={() => toggleStar(item.id)}
                              onOpen={() => openContentItem(item)}
                              onCycleStatus={(e) => handleResourceCycleStatus(e, item)}
                            />
                          );
                        } else {
                          return (
                            <div key={`freq-modal-${fi.id}`} className="module-card" role="button" tabIndex={0} aria-label={fi.title} onClick={() => { recordOpen(fi.id); openModal(fi.id as Parameters<typeof openModal>[0]); }} onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); recordOpen(fi.id); openModal(fi.id as Parameters<typeof openModal>[0]); } }}>
                              <h4>{fi.title}</h4>
                              <p>{fi.desc}</p>
                              <span className="module-tag">{fi.tag}</span>
                            </div>
                          );
                        }
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── All articles by topic ─────────── */}
            <div style={{ marginBottom: 10 }}>
              {/* eslint-disable-next-line cw/no-inline-button-style -- articles screen "All articles by topic" accordion header; full-width disclosure trigger using shared secHeaderStyle helper, not a chamfered .btn variant */}
              <button style={secHeaderStyle(secOpen('all-topics'))} onClick={() => toggleSec('all-topics')}>
                <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}><IconBook size={16} ariaLabel="" /> All articles by topic</span>
                {chevron(secOpen('all-topics'))}
              </button>
              {secOpen('all-topics') && (
                <div style={secBodyStyle}>
                  {ARTICLE_TOPICS.map(topic => {
                    const isTopicOpen = expandedArticleTopic === topic.id;
                    const itemCount = topic.items.length;
                    return (
                      <div key={topic.id} style={{ marginBottom: 8 }}>
                        {/* eslint-disable-next-line cw/no-inline-button-style -- articles topic-row accordion trigger; full-width disclosure with dynamic per-topic color tint, not a chamfered .btn variant */}
                        <button
                          onClick={() => setExpandedArticleTopic(isTopicOpen ? null : topic.id)}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px 14px',
                            background: isTopicOpen ? `color-mix(in srgb, ${topic.color} 7%, transparent)` : 'var(--color-bg-card)',
                            border: `1px solid ${isTopicOpen ? `color-mix(in srgb, ${topic.color} 31%, transparent)` : 'var(--color-border)'}`,
                            borderRadius: isTopicOpen ? 'var(--radius-md) var(--radius-md) 0 0' : 'var(--radius-md)',
                            cursor: 'pointer',
                            color: 'var(--color-text-primary)',
                            textAlign: 'left',
                          }}
                        >
                          <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}>
                              {(() => { const Icon = CRYSTAL_ICONS[topic.iconKey]; return <Icon size={20} ariaLabel="" />; })()}
                            </span>
                            <span>{topic.label}</span>
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                            <span style={{ fontSize: 'var(--fs-xs)', background: `color-mix(in srgb, ${topic.color} 13%, transparent)`, color: topic.color, borderRadius: 'var(--radius-xl)', padding: '2px 8px', fontWeight: 600 }}>
                              {itemCount}
                            </span>
                            <span style={{ fontSize: 'var(--fs-base)', color: 'var(--color-text-secondary)', transform: isTopicOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', display: 'inline-block' }}>›</span>
                          </span>
                        </button>

                        {isTopicOpen && (
                          <div style={{
                            background: 'var(--color-bg-surface)',
                            border: `1px solid color-mix(in srgb, ${topic.color} 19%, transparent)`,
                            borderTop: 'none',
                            borderRadius: '0 0 10px 10px',
                            padding: '10px',
                          }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                              {topic.items.map(ti => {
                                if (ti.kind === 'content') {
                                  const item = contentById.get(ti.id);
                                  if (!item) return null;
                                  return (
                                    <ArticleCard
                                      key={`${topic.id}-${item.id}`}
                                      item={item}
                                      status={getStatus(item.id)}
                                      starred={getMeta(item.id).starred}
                                      onToggleStar={() => toggleStar(item.id)}
                                      onOpen={() => openContentItem(item)}
                                      onCycleStatus={(e) => handleResourceCycleStatus(e, item)}
                                    />
                                  );
                                } else {
                                  return (
                                    <div key={`${topic.id}-modal-${ti.id}`} className="module-card" role="button" tabIndex={0} aria-label={ti.title} onClick={() => { recordOpen(ti.id); openModal(ti.id as Parameters<typeof openModal>[0]); }} onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); recordOpen(ti.id); openModal(ti.id as Parameters<typeof openModal>[0]); } }}>
                                      <h4>{ti.title}</h4>
                                      <p>{ti.desc}</p>
                                      <span className="module-tag">{ti.tag}</span>
                                    </div>
                                  );
                                }
                              })}
                              {topic.id === 'mental-health-stigma' && (
                                <a href="https://kidshelpphone.ca/live-chat/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'block' }}>
                                  <div style={{ padding: '14px', background: 'color-mix(in srgb, var(--color-warning) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--color-warning) 30%, transparent)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <div style={{ width: 42, height: 42, borderRadius: 'var(--radius-md)', flexShrink: 0, background: 'color-mix(in srgb, var(--color-warning) 15%, transparent)', border: '1.5px solid color-mix(in srgb, var(--color-warning) 35%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IconHeart size={24} ariaLabel="" heartTint="var(--color-warning)" /></div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm)', color: 'var(--color-text-primary)', marginBottom: 2 }}>Kids Help Phone — Live Chat</div>
                                      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', lineHeight: 1.45 }}>Free, 24/7 confidential chat. Available to youth across Canada.</div>
                                    </div>
                                    <div style={{ flexShrink: 0, fontSize: 'var(--fs-sm)', fontWeight: 700, color: 'var(--color-warning)' }}>Open ↗</div>
                                  </div>
                                </a>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── Share your story ─────────────── */}
            <div className="card">
              <h3 style={{ display:'inline-flex', alignItems:'center', gap:8 }}><IconHandshake size={22} ariaLabel="" /> Share your story</h3>
              <p style={{ marginBottom: 16 }}>Submit your story, poem, or tips to help other youth navigating similar experiences</p>
              <button className="btn btn-primary" onClick={() => openModal('youth-submission')}>Submit Your Story</button>
            </div>

            <div style={{ height: 80 }} />

            {/* ── Floating nav buttons ─────────── */}
            {articleScrollY > 200 && (
              // eslint-disable-next-line cw/no-inline-button-style -- articles screen floating scroll-to-top FAB; circular 44px fixed bottom-right, accent-tinted, no .btn-fab primitive
              <button
                onClick={() => containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
                aria-label="Scroll to top"
                style={{
                  position: 'fixed',
                  bottom: 140,
                  right: 16,
                  zIndex: 'var(--z-floating)',
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: 'var(--color-accent)',
                  border: 'none',
                  color: '#fff',
                  fontSize: 'var(--fs-lg)',
                  cursor: 'pointer',
                  boxShadow: 'var(--shadow-raised)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                }}
              >↑</button>
            )}
            {/* eslint-disable-next-line cw/no-inline-button-style -- articles screen floating scroll-to-bottom FAB; circular 44px fixed bottom-right with outline tint, no .btn-fab primitive */}
            <button
              onClick={() => { const el = containerRef.current; if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' }); }}
              aria-label="Scroll to bottom"
              style={{
                position: 'fixed',
                bottom: 88,
                right: 16,
                zIndex: 'var(--z-floating)',
                width: 44,
                height: 44,
                borderRadius: '50%',
                background: 'var(--color-bg-card)',
                border: '1.5px solid var(--color-border)',
                color: 'var(--color-text-secondary)',
                fontSize: 'var(--fs-lg)',
                cursor: 'pointer',
                boxShadow: 'var(--shadow-raised)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
              }}
            >↓</button>
          </div>
          );
        })()}

        {screen === 'crystal-guide' && currentUser && (
          // Gate on currentUser (cookie session valid), not authToken state.
          // authToken is memory-only and stays null on cold tab loads until
          // the user re-authenticates or the silent token refresh kicks in.
          // If the user hits Save before authToken is populated, the inner
          // updateProfile call surfaces a saveError gracefully.
          <Suspense fallback={<LazyFallback />}>
            <GuideCustomizer
              token={authToken ?? ''}
              currentAvatarId={currentUser?.avatarId ?? null}
              currentCustomisation={userCustomisation}
              userLevel={levelInfo.level}
              guideBondProgress={currentUser?.guideBondProgress ?? {}}
              onSave={(newAvatarId, newCustomisation) => {
                setCurrentUser(prev => prev ? { ...prev, avatarId: newAvatarId, avatarCustomisation: newCustomisation } : null);
                showScreen('profile');
              }}
              onClose={() => showScreen('profile')}
            />
          </Suspense>
        )}

        {screen === 'profile' && (
          <div className="screen">
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                {/* eslint-disable-next-line cw/no-inline-button-style -- profile screen "‹" back chevron; ghost icon-only with 44px tap target inline with avatar + title row, no .btn-icon-only primitive */}
                <button onClick={() => showScreen('home')} style={{ background: 'none', border: 'none', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-lg-plus)', cursor: 'pointer', padding: '8px 10px 8px 0', lineHeight: 1, flexShrink: 0, minHeight: 44, minWidth: 36, display: 'flex', alignItems: 'center' }} aria-label="Back">‹</button>
                <div><h2 style={{ margin: 0 }}>Your profile</h2><p style={{ margin: '2px 0 0', fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)' }}>Define who you are and what matters most</p></div>
              </div>
            </div>
            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{ width: 120, height: 120, margin: '16px auto', position: 'relative' }}>
                <GuideBubble
                  avatarId={userAvatarId}
                  size={120}
                  showGlow={true}
                  customisation={userCustomisation}
                 
                  onClick={() => showScreen('crystal-guide')}
                  ariaLabel="Tap to customize your avatar"
                  style={{ margin: '0 auto' }}
                />
                <div style={{
                  position: 'absolute',
                  bottom: 2,
                  right: 2,
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: 'var(--color-accent-light)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'var(--fs-sm)',
                  boxShadow: 'var(--shadow-raised)',
                  pointerEvents: 'none',
                  color: 'var(--color-accent)',
                }}>
                  {(() => { const Icon = CRYSTAL_ICONS['pencil']; return <Icon size={16} ariaLabel="" />; })()}
                </div>
              </div>
              <h3 style={{ marginTop: 12 }}>{userDisplayName}</h3>
              <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', marginBottom: 8 }}>
                {[currentUser?.pronouns, currentUser?.ageRange, currentUser?.careCenter].filter(Boolean).join(' • ') || currentUser?.email || ''}
              </p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 14 }}>
                {/* eslint-disable-next-line cw/no-inline-button-style -- profile sub-action pill "Edit profile"; outlined ghost chip in 3-up centered action row, not a chamfered .btn variant */}
                <button
                  onClick={() => {
                    setEditProfileName(currentUser?.displayName || '');
                    setEditProfilePronouns(currentUser?.pronouns || '');
                    setEditProfileCareCenter(currentUser?.careCenter || '');
                    setShowEditProfile(true);
                  }}
                  style={{ fontSize: 'var(--fs-xs-plus)', color: 'var(--color-accent)', background: 'none', border: '1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)', cursor: 'pointer', padding: '8px 20px', borderRadius: 'var(--radius-sm)', minHeight: 36 }}
                >
                  Edit profile
                </button>
                {(currentUser?.isAdmin || currentUser?.isModerator) && (
                  // eslint-disable-next-line cw/no-inline-button-style -- profile sub-action pill "Switch view" (admin); outlined ghost chip in 3-up centered action row, not a chamfered .btn variant
                  <button
                    onClick={() => { setMenuOpen(false); setAuthFlow('mode-chooser'); }}
                    style={{ fontSize: 'var(--fs-xs-plus)', color: 'var(--color-accent)', background: 'none', border: '1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)', cursor: 'pointer', padding: '8px 20px', borderRadius: 'var(--radius-sm)', minHeight: 36 }}
                  >
                    Switch view
                  </button>
                )}
                {/* eslint-disable-next-line cw/no-inline-button-style -- profile sub-action pill "Log out"; subdued outlined ghost chip in 3-up centered action row, not a chamfered .btn variant */}
                <button
                  onClick={() => { if (isDemoVisit()) { exitDemo(); return; } clearToken(); setCurrentUser(null); setAuthToken(null); setAuthFlow('splash'); }}
                  style={{ fontSize: 'var(--fs-xs-plus)', color: 'var(--color-text-secondary)', background: 'none', border: '1px solid color-mix(in srgb, var(--color-accent) 15%, transparent)', cursor: 'pointer', padding: '8px 20px', borderRadius: 'var(--radius-sm)', minHeight: 36 }}
                >
                  {isDemoVisit() ? 'Exit demo' : 'Log out'}
                </button>
              </div>
              <div style={{ padding: 16, background: 'var(--color-accent-light)', borderRadius: 'var(--radius-md)' }}>
                <div style={{ fontSize: 'var(--fs-lg-plus)', marginBottom: 4, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  {(() => {
                    const LevelBadge = CRYSTAL_ICONS[`level-${Math.min(7, Math.max(1, levelInfo.level))}` as CrystalIconKey];
                    return LevelBadge ? <LevelBadge size={28} ariaLabel="" /> : null;
                  })()}
                  Level {levelInfo.level} — {levelInfo.name}
                </div>
                <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', marginBottom: 12 }}>
                  {levelInfo.toNext > 0 ? `${levelInfo.toNext} XP to ${levelInfo.nextLevelName}` : 'Max level reached'}
                </div>
                <div className="progress-bar"><div className="progress-fill" style={{ width: `${levelInfo.pct}%`, background: 'var(--color-accent)' }} /></div>
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginTop: 6, textAlign: 'right' }}>{totalXP} XP total</div>
              </div>
            </div>
            {activeGuideChar && (
              <div className="card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
                  <h3 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {(() => { const Icon = CRYSTAL_ICONS['gem']; return <Icon size={20} ariaLabel="" />; })()}
                    Crystal Guide Bond
                  </h3>
                  {/* eslint-disable-next-line cw/no-inline-button-style -- profile crystal-guide bond enter pill; pill-shape (999) chip with dynamic per-guide gradient, sits beside h3 in card header, not a chamfered .btn variant */}
                  <button
                    type="button"
                    onClick={() => showScreen('crystal-guide')}
                    style={{
                      background: `linear-gradient(135deg, ${activeGuideChar.bg}, color-mix(in srgb, ${activeGuideChar.bg} 80%, transparent))`,
                      color: '#fff',
                      border: 'none',
                      borderRadius: 'var(--radius-pill)',
                      padding: '6px 14px',
                      fontSize: 'var(--fs-xs-plus)',
                      fontWeight: 700,
                      letterSpacing: '0.02em',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      boxShadow: `0 2px 8px color-mix(in srgb, ${activeGuideChar.bg} 27%, transparent)`,
                      flexShrink: 0,
                      minHeight: 32,
                    }}
                    aria-label={`Enter ${activeGuideChar.crystalName}'s Globe — auras, comfort items, lore`}
                  >
                    Enter {activeGuideChar.crystalName}'s Globe ›
                  </button>
                </div>
                <p style={{ fontSize: 'var(--fs-xs-plus)', color: 'var(--color-text-secondary)', margin: '0 0 4px', fontStyle: 'italic' }}>
                  Aura, comfort items, lore.
                </p>
                <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', marginBottom: 16 }}>
                  Your connection with {activeGuideChar.crystalName} deepens each time you check in while this guide is active.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: '50%',
                    background: `color-mix(in srgb, ${activeGuideChar.bg} 20%, transparent)`,
                    border: `2px solid color-mix(in srgb, ${activeGuideChar.bg} 40%, transparent)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    color: activeGuideChar.bg,
                  }}>
                    {(() => { const Icon = CRYSTAL_ICONS['gem']; return <Icon size={26} ariaLabel="" />; })()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm-plus)', color: activeGuideChar.bg }}>
                      {activeGuideChar.crystalName}
                    </div>
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginTop: 2 }}>
                      Bond Level {guideBondProgress.level} of 5 · {guideBondDays} check-in {guideBondDays === 1 ? 'day' : 'days'}
                    </div>
                  </div>
                  {guideBondProgress.level >= 5 && (
                    <span style={{
                      padding: '4px 10px', borderRadius: 'var(--radius-xl)',
                      background: `color-mix(in srgb, ${activeGuideChar.bg} 33%, transparent)`,
                      border: `1px solid color-mix(in srgb, ${activeGuideChar.bg} 53%, transparent)`,
                      color: activeGuideChar.bg,
                      fontSize: 'var(--fs-xs)', fontWeight: 700,
                    }}>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}><IconSparkle size={12} ariaLabel="" /> Bonded</span>
                    </span>
                  )}
                </div>
                <div style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)' }}>
                      Level {guideBondProgress.level}
                    </span>
                    {guideBondProgress.level < 5 ? (
                      <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)' }}>
                        {guideBondProgress.daysToNext} more {guideBondProgress.daysToNext === 1 ? 'day' : 'days'} to Level {guideBondProgress.nextLevel}
                      </span>
                    ) : (
                      <span style={{ fontSize: 'var(--fs-xs)', color: activeGuideChar.bg }}>Max Bond Reached</span>
                    )}
                  </div>
                  <div className="progress-bar" style={{ height: 8, borderRadius: 'var(--radius-sm)' }}>
                    <div
                      className="progress-fill"
                      style={{
                        width: `${guideBondProgress.pct}%`,
                        background: `linear-gradient(90deg, ${activeGuideChar.bg}, ${activeGuideChar.bg})`,
                        borderRadius: 'var(--radius-sm)',
                        transition: 'width 0.5s ease',
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                    {[1, 2, 3, 4, 5].map(lvl => (
                      <div key={lvl} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <div style={{
                          width: 10, height: 10, borderRadius: '50%',
                          background: lvl <= guideBondProgress.level ? activeGuideChar.bg : 'color-mix(in srgb, var(--color-text-primary) 8%, transparent)',
                          border: `2px solid ${lvl <= guideBondProgress.level ? activeGuideChar.bg : 'color-mix(in srgb, var(--color-text-primary) 10%, transparent)'}`,
                          transition: 'background 0.3s',
                        }} />
                        <span style={{ fontSize: 'var(--fs-xs)', color: lvl <= guideBondProgress.level ? activeGuideChar.bg : 'var(--color-text-secondary)', fontWeight: 600 }}>
                          {lvl}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Bond level 3+ unlocks are now handled through the aura/item progression system */}
                {guideBondProgress.level >= 5 && (
                  <div style={{
                    padding: '10px 12px',
                    background: `color-mix(in srgb, ${activeGuideChar.bg} 13%, transparent)`,
                    border: `1px solid color-mix(in srgb, ${activeGuideChar.bg} 27%, transparent)`,
                    borderRadius: 'var(--radius-md)',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <span style={{ display:'inline-flex' }}><IconSparkle size={18} ariaLabel="" /></span>
                    <div>
                      <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: activeGuideChar.bg }}>Bond Level 5 Unlock</div>
                      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)' }}>
                        "Bonded" badge — visible on your Circle profile
                      </div>
                    </div>
                  </div>
                )}
                {guideBondGreeting && (
                  <div style={{
                    marginTop: 12, padding: '10px 12px',
                    background: 'color-mix(in srgb, var(--color-text-primary) 2%, transparent)', borderRadius: 'var(--radius-md)',
                    border: '1px solid color-mix(in srgb, var(--color-text-primary) 6%, transparent)',
                  }}>
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginBottom: 4 }}>Your guide says:</div>
                    <p style={{ margin: 0, fontSize: 'var(--fs-sm)', fontStyle: 'italic', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
                      "{guideBondGreeting}"
                    </p>
                  </div>
                )}
              </div>
            )}
            <div className="card">
              <h3>Identities that matter to you</h3>
              <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', marginBottom: 12 }}>
                Select the identities you want CareWeave to recognize in your experiences
              </p>
              {(currentUser?.identities ?? []).length === 0 ? (
                <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', fontStyle: 'italic', marginBottom: 12 }}>
                  No identities added yet. Tap Edit identities to add the ones that matter to you.
                </p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                  {(currentUser?.identities ?? []).map(id => (
                    <span key={id} className="identity-pill identity-pill--selected">{id}</span>
                  ))}
                </div>
              )}
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setIdentityPickerDraft(currentUser?.identities ?? []);
                  openModal('identity-picker');
                }}
              >
                Edit identities
              </button>
            </div>
            <div className="card">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 6 }}><IconMedications size={20} ariaLabel="" /> Medications</h3>
              <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', marginBottom: 12 }}>Manage your medication list and log today's doses</p>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                {medications.filter(m => m.active).length === 0 ? (
                  <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>No medications added yet</span>
                ) : (
                  medications.filter(m => m.active).slice(0, 3).map(med => (
                    <span key={med.id} style={{ fontSize: 'var(--fs-xs)', background: 'color-mix(in srgb, var(--color-accent) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--color-accent) 25%, transparent)', borderRadius: 'var(--radius-xl)', padding: '4px 10px' }}>
                      {med.name}
                    </span>
                  ))
                )}
                {medications.filter(m => m.active).length > 3 && (
                  <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)' }}>+{medications.filter(m => m.active).length - 3} more</span>
                )}
              </div>
              <button className="btn btn-secondary" onClick={() => showScreen('medications')}>
                Manage medications
              </button>
            </div>
            <div className="card">
              <h3>Appearance and settings</h3>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid color-mix(in srgb, var(--color-text-primary) 7%, transparent)', marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>Dark mode</div>
                  <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginTop: 2 }}>Deeper dark theme for reduced eye strain</div>
                </div>
                {/* eslint-disable-next-line cw/no-inline-button-style -- profile appearance dark-mode iOS-style switch toggle (48×28 with absolute knob); native-control look, not a chamfered .btn variant */}
                <button
                  onClick={toggleDarkMode}
                  aria-label={darkMode ? 'Dark mode enabled — tap to disable' : 'Dark mode disabled — tap to enable'}
                  style={{
                    flexShrink: 0,
                    width: 48, height: 28,
                    borderRadius: 'var(--radius-pill)',
                    border: 'none',
                    cursor: 'pointer',
                    background: darkMode ? 'var(--color-accent)' : 'color-mix(in srgb, var(--color-text-primary) 8%, transparent)',
                    position: 'relative',
                    transition: 'background 0.2s',
                    padding: 0,
                  }}
                >
                  <span style={{
                    position: 'absolute',
                    top: 3, left: darkMode ? 23 : 3,
                    width: 22, height: 22,
                    borderRadius: '50%',
                    background: '#fff',
                    transition: 'left 0.2s',
                    boxShadow: 'var(--shadow-card)',
                  }} />
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
                <div>
                  <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>Weather report location</div>
                  <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginTop: 2 }}>
                    {flareLocation ? flareLocation.name : 'Not set'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {/* eslint-disable-next-line cw/no-inline-button-style -- profile weather-location update chip; small accent-tinted pill in 2-up sub-action row, not a chamfered .btn variant */}
                  <button
                    onClick={() => {
                      clearFlareLocation();
                      requestFlareLocation();
                      setWeatherBackTarget('tools');
                      showScreen('weather-report');
                    }}
                    style={{ padding: '6px 10px', background: 'color-mix(in srgb, var(--color-accent) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)', borderRadius: 'var(--radius-sm)', color: 'var(--color-accent)', fontSize: 'var(--fs-xs)', cursor: 'pointer' }}
                  >
                    Update
                  </button>
                  {flareLocation && (
                    // eslint-disable-next-line cw/no-inline-button-style -- profile weather-location clear chip; small warm-tinted pill in 2-up sub-action row, not a chamfered .btn variant
                    <button
                      onClick={clearFlareLocation}
                      style={{ padding: '6px 10px', background: 'color-mix(in srgb, var(--color-crisis) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--color-crisis) 30%, transparent)', borderRadius: 'var(--radius-sm)', color: 'var(--color-crisis)', fontSize: 'var(--fs-xs)', cursor: 'pointer' }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="card">
              <h3>Daily check-in questions</h3>
              {essentialsMode && (
                <div style={{ padding: '8px 12px', marginBottom: 12, background: 'color-mix(in srgb, var(--color-accent) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--color-accent) 20%, transparent)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--fs-xs-plus)', color: 'var(--color-text-secondary)', lineHeight: 1.4, display:'flex', alignItems:'flex-start', gap:6 }}>
                  <IconBrain size={16} ariaLabel="" /> <span>Essentials Mode is active — check-in questions are locked to the essentials preset. Turn off Essentials Mode in Settings to customize.</span>
                </div>
              )}
              <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', marginBottom: 14 }}>Choose which questions you want to answer. You can always skip optional ones.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  [1, 'How are you today?'],
                  [2, 'Sleep quality'],
                  [3, 'Stress level'],
                  [4, 'Worry level'],
                  [5, 'Feeling blue'],
                  [6, 'Fatigue level'],
                  [7, 'Pain level'],
                  [8, 'Body map — where is the pain?'],
                  [9, 'How were you treated?'],
                  [10, 'What helped today?'],
                ].map(([num, label]) => (
                  <div key={num} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {/* eslint-disable-next-line cw/no-inline-button-style -- checkin-questions checkbox toggle; 24×24 square native-checkbox look paired with a sibling <label>, not a chamfered .btn variant */}
                    <button
                      onClick={() => toggleCheckinQuestion(num as number)}
                      aria-label={`${label}: ${enabledCheckinQuestions.has(num as number) ? 'enabled' : 'disabled'}`}
                      style={{
                        flexShrink: 0,
                        width: 24, height: 24,
                        borderRadius: 4,
                        border: `2px solid ${enabledCheckinQuestions.has(num as number) ? 'var(--color-accent)' : 'color-mix(in srgb, var(--color-text-primary) 25%, transparent)'}`,
                        background: enabledCheckinQuestions.has(num as number) ? 'var(--color-accent)' : 'transparent',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: 'var(--fs-sm)',
                        fontWeight: 600,
                        transition: 'all 0.2s',
                      }}
                    >
                      {enabledCheckinQuestions.has(num as number) && <IconCheck size={12} ariaLabel="" />}
                    </button>
                    <label style={{ cursor: 'pointer', flex: 1, fontSize: 'var(--fs-sm)', display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => toggleCheckinQuestion(num as number)}>
                      {CHECKIN_STEP_ICONS[num as number]?.({ size: 16, color: 'var(--color-text-secondary)' })}
                      {label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="card">
              <h3>Privacy and safety</h3>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid color-mix(in srgb, var(--color-text-primary) 7%, transparent)', marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>Hide pattern views</div>
                  <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginTop: 2 }}>Blur health data on the Patterns screen</div>
                </div>
                {/* eslint-disable-next-line cw/no-inline-button-style -- profile privacy hide-patterns iOS-style switch toggle (48×28 with absolute knob); native-control look, not a chamfered .btn variant */}
                <button
                  onClick={toggleHidePatterns}
                  role="switch"
                  aria-checked={hidePatterns}
                  aria-label={hidePatterns ? 'Pattern views hidden — tap to show' : 'Pattern views visible — tap to hide'}
                  style={{
                    flexShrink: 0,
                    width: 48, height: 28,
                    borderRadius: 'var(--radius-pill)',
                    border: 'none',
                    cursor: 'pointer',
                    background: hidePatterns ? 'var(--color-accent)' : 'color-mix(in srgb, var(--color-text-primary) 8%, transparent)',
                    position: 'relative',
                    transition: 'background 0.2s',
                    padding: 0,
                  }}
                >
                  <span style={{
                    position: 'absolute',
                    top: 3, left: hidePatterns ? 23 : 3,
                    width: 22, height: 22,
                    borderRadius: '50%',
                    background: '#fff',
                    transition: 'left 0.2s',
                    boxShadow: 'var(--shadow-card)',
                  }} />
                </button>
              </div>
              {/* eslint-disable-next-line cw/no-inline-button-style -- inline .btn-secondary marginBottom override on profile privacy "Privacy policy and terms" link; rule flags any style on button regardless of className */}
              <button className="btn btn-secondary" style={{ marginBottom: 10 }} onClick={() => showScreen('privacy-policy')}>Privacy policy and terms</button>
              <button className="btn btn-secondary" onClick={() => showScreen('crisis-resources')}>Crisis resources</button>
            </div>
          </div>
        )}

        {screen === 'medications' && (
          !isFeatureTutorialSeen('cw_tutorial_medications') ? (
            <FeatureTutorial storageKey="cw_tutorial_medications" steps={MEDICATIONS_TUTORIAL} onComplete={() => forceUpdate(n => n + 1)} xpReward={10} />
          ) :
          <MedicationsScreen
            medications={medications}
            medicationLogs={medicationLogs}
            moduleIntroSeen={moduleIntroSeenMedications}
            dismissModuleIntro={dismissModuleIntroMedications}
            userAvatarId={userAvatarId}
            userCustomisation={userCustomisation}
            showScreen={showScreen}
            openModal={openModal}
            setMedFormName={setMedFormName}
            setMedFormDose={setMedFormDose}
            setMedFormUnit={setMedFormUnit}
            setMedFormFrequency={setMedFormFrequency}
            setMedFormNotes={setMedFormNotes}
            setEditingMed={setEditingMed}
            setMedNameDropdownOpen={setMedNameDropdownOpen}
            saveMedicationLog={saveMedicationLog}
            deleteMedication={deleteMedication}
            updateMedication={updateMedication}
          />
        )}

        {screen === 'growth' && (
          <Suspense fallback={<LazyFallback />}>
            <GrowthScreenInline
              growthTab={growthTab}
              setGrowthTab={setGrowthTab}
              growthExploreOpen={growthExploreOpen}
              setGrowthExploreOpen={setGrowthExploreOpen}
              exploreTheme={exploreTheme}
              setExploreTheme={setExploreTheme}
              writeOwnOpen={writeOwnOpen}
              setWriteOwnOpen={setWriteOwnOpen}
              writeOwnTitle={writeOwnTitle}
              setWriteOwnTitle={setWriteOwnTitle}
              writeOwnDescription={writeOwnDescription}
              setWriteOwnDescription={setWriteOwnDescription}
              customGoalTitle={customGoalTitle}
              setCustomGoalTitle={setCustomGoalTitle}
              customGoalDescription={customGoalDescription}
              setCustomGoalDescription={setCustomGoalDescription}
              customGoalSteps={customGoalSteps}
              setCustomGoalSteps={setCustomGoalSteps}
              smartGuideStep={smartGuideStep}
              setSmartGuideStep={setSmartGuideStep}
              goals={goals}
              activeGoals={activeGoals}
              completedGoals={completedGoals}
              totalAdopted={totalAdopted}
              totalCompleted={totalCompleted}
              activeJourneys={activeJourneys}
              completedJourneyResources={completedJourneyResources}
              phaseResourcesData={phaseResourcesData}
              journeyActivatePrompt={journeyActivatePrompt}
              setJourneyActivatePrompt={setJourneyActivatePrompt}
              journeyGroupExpanded={journeyGroupExpanded}
              setJourneyGroupExpanded={setJourneyGroupExpanded}
              expandedJourneyPhaseResources={expandedJourneyPhaseResources}
              setExpandedJourneyPhaseResources={setExpandedJourneyPhaseResources}
              activeScene={activeScene}
              setActiveScene={setActiveScene}
              levelInfo={levelInfo}
              totalXP={totalXP}
              appStats={appStats}
              unlockedAchievements={unlockedAchievements}
              lockedAchievements={lockedAchievements}
              activeAchievement={activeAchievement}
              setActiveAchievement={setActiveAchievement}
              progress={progress}
              userAvatarId={userAvatarId}
              userCustomisation={userCustomisation}
              moduleIntroSeenGrowth={moduleIntroSeenGrowth}
              dismissModuleIntroGrowth={dismissModuleIntroGrowth}
              moduleIntroSeenJourneys={moduleIntroSeenJourneys}
              dismissModuleIntroJourneys={dismissModuleIntroJourneys}
              setActiveContentItem={setActiveContentItem}
              showScreen={showScreen}
              openModal={openModal}
              adoptGoal={adoptGoal}
              addCustomGoal={addCustomGoal}
              toggleStep={toggleStep}
              markXPAwarded={markXPAwarded}
              earnXP={earnXP}
              earnXPOnce={earnXPOnce}
              activateJourney={activateJourney}
              advanceJourneyPhase={advanceJourneyPhase}
              deactivateJourney={deactivateJourney}
              toggleJourneyResource={toggleJourneyResource}
              accessibilityPrefs={accessibilityPrefs}
              streak={streak}
              eventsLogged={eventsLogged}
              tutorialCompleted={tutorialCompleted}
              forceUpdate={forceUpdate}
            />
          </Suspense>
        )}
        {screen === 'history' && (
          <HistoryScreen
            checkinEntries={checkinEntries}
            edVisitHistory={edVisitHistory}
            eventLogEntries={eventLogEntries}
            medicationLogs={medicationLogs}
            medications={medications}
            historyFilter={historyFilter}
            setHistoryFilter={setHistoryFilter}
            historyCalCollapsed={historyCalCollapsed}
            setHistoryCalCollapsed={setHistoryCalCollapsed}
            historyMonthView={historyMonthView}
            setHistoryMonthView={setHistoryMonthView}
            selectedHistoryDay={selectedHistoryDay}
            setSelectedHistoryDay={setSelectedHistoryDay}
            userAvatarId={userAvatarId}
            userCustomisation={userCustomisation}
            showScreen={showScreen}
          />
        )}

        {screen === 'hospital-prep' && (
          !isFeatureTutorialSeen('cw_tutorial_hospitalprep') ? (
            <FeatureTutorial storageKey="cw_tutorial_hospitalprep" steps={HOSPITALPREP_TUTORIAL} onComplete={() => forceUpdate(n => n + 1)} xpReward={10} />
          ) :
          <HospitalPrepScreen
            showScreen={showScreen}
            userAvatarId={userAvatarId}
            userCustomisation={userCustomisation}
            currentUserDisplayName={currentUser?.displayName ?? null}
            medications={medications}
            prepTab={prepTab}
            setPrepTab={setPrepTab}
            prepProfile={prepProfile}
            setPrepProfile={setPrepProfile}
            prepEditingProfile={prepEditingProfile}
            setPrepEditingProfile={setPrepEditingProfile}
            prepScriptCopied={prepScriptCopied}
            setPrepScriptCopied={setPrepScriptCopied}
            prepPackChecked={prepPackChecked}
            setPrepPackChecked={setPrepPackChecked}
            prepQuestionsExpanded={prepQuestionsExpanded}
            setPrepQuestionsExpanded={setPrepQuestionsExpanded}
            prepContacts={prepContacts}
            setPrepContacts={setPrepContacts}
            prepContactForm={prepContactForm}
            setPrepContactForm={setPrepContactForm}
            SK_PREP_PROFILE={SK_PREP_PROFILE}
            SK_PREP_PACK={SK_PREP_PACK}
            SK_PREP_CONTACTS={SK_PREP_CONTACTS}
          />
        )}

        {screen === 'weather-report' && (
          <OfflineGate isOnline={isOnline} featureName="Weather Report">
          <WeatherReportScreen
            showScreen={showScreen}
            userAvatarId={userAvatarId}
            userCustomisation={userCustomisation}
            checkinEntries={checkinEntries}
            flareWeather={flareWeather}
            flareWeatherLoading={flareWeatherLoading}
            flareLocation={flareLocation}
            flareLocationStatus={flareLocationStatus}
            setFlareLocationStatus={setFlareLocationStatus}
            flareSelectedDay={flareSelectedDay}
            setFlareSelectedDay={setFlareSelectedDay}
            flareTab={flareTab}
            setFlareTab={setFlareTab}
            flareBackTarget={flareBackTarget}
            requestFlareLocation={requestFlareLocation}
            clearFlareLocation={clearFlareLocation}
            fetchFlareWeather={fetchFlareWeather}
            saveFlareLocation={saveFlareLocation}
          />
          </OfflineGate>
        )}

        {screen === 'crisis-plan' && (
          <CrisisPlanScreen
            crisisPlanStages={crisisPlanStages}
            setCrisisPlanStages={setCrisisPlanStages}
            crisisPlanExpanded={crisisPlanExpanded}
            setCrisisPlanExpanded={setCrisisPlanExpanded}
            crisisPlanChecked={crisisPlanChecked}
            setCrisisPlanChecked={setCrisisPlanChecked}
            crisisPlanEditMode={crisisPlanEditMode}
            setCrisisPlanEditMode={setCrisisPlanEditMode}
            saveCrisisPlanToBackend={saveCrisisPlanToBackend}
            userAvatarId={userAvatarId}
            userCustomisation={userCustomisation}
            showScreen={showScreen}
            SK_CRISIS_PLAN_STAGES={SK_CRISIS_PLAN_STAGES}
            SK_CRISIS_CHECKED={SK_CRISIS_CHECKED}
          />
        )}

        {screen === 'care-team' && (
          <OfflineGate isOnline={isOnline} featureName="Care Team">
          <CareTeamScreen
            showScreen={showScreen}
            userAvatarId={userAvatarId}
            userCustomisation={userCustomisation}
            careTeamMembers={careTeamMembers}
            careTeamLoading={careTeamLoading}
            careTeamView={careTeamView}
            setCareTeamView={setCareTeamView}
            careTeamExpanded={careTeamExpanded}
            setCareTeamExpanded={setCareTeamExpanded}
            careTeamDownloading={careTeamDownloading}
            showCareTeamForm={showCareTeamForm}
            setShowCareTeamForm={setShowCareTeamForm}
            careTeamFormMode={careTeamFormMode}
            careTeamFormName={careTeamFormName}
            setCareTeamFormName={setCareTeamFormName}
            careTeamFormRole={careTeamFormRole}
            setCareTeamFormRole={setCareTeamFormRole}
            careTeamFormOrg={careTeamFormOrg}
            setCareTeamFormOrg={setCareTeamFormOrg}
            careTeamFormPhone={careTeamFormPhone}
            setCareTeamFormPhone={setCareTeamFormPhone}
            careTeamFormEmail={careTeamFormEmail}
            setCareTeamFormEmail={setCareTeamFormEmail}
            careTeamFormNotes={careTeamFormNotes}
            setCareTeamFormNotes={setCareTeamFormNotes}
            careTeamFormAppt={careTeamFormAppt}
            setCareTeamFormAppt={setCareTeamFormAppt}
            careTeamFormEmoji={careTeamFormEmoji}
            setCareTeamFormEmoji={setCareTeamFormEmoji}
            careTeamFormColor={careTeamFormColor}
            setCareTeamFormColor={setCareTeamFormColor}
            careTeamFormMemberType={careTeamFormMemberType}
            setCareTeamFormMemberType={setCareTeamFormMemberType}
            careTeamFormSaving={careTeamFormSaving}
            saveCareTeamMember={saveCareTeamMember}
            deleteCareTeamMember={deleteCareTeamMember}
            resetCareTeamForm={resetCareTeamForm}
            openCareTeamEditForm={openCareTeamEditForm}
            downloadHealthSummary={downloadHealthSummary}
            prepProfile={prepProfile}
            medications={medications}
          />
          </OfflineGate>
        )}

        {screen === 'support-network' && (
          <SupportNetworkScreen
            showScreen={showScreen}
            userAvatarId={userAvatarId}
            userCustomisation={userCustomisation}
            networkMembers={networkMembers}
            networkLoading={networkLoading}
            networkView={networkView}
            setNetworkView={setNetworkView}
            networkSelectedId={networkSelectedId}
            setNetworkSelectedId={setNetworkSelectedId}
            showNetworkForm={showNetworkForm}
            setShowNetworkForm={setShowNetworkForm}
            networkFormMode={networkFormMode}
            networkFormName={networkFormName}
            setNetworkFormName={setNetworkFormName}
            networkFormRelationship={networkFormRelationship}
            setNetworkFormRelationship={setNetworkFormRelationship}
            networkFormRing={networkFormRing}
            setNetworkFormRing={setNetworkFormRing}
            networkFormPhone={networkFormPhone}
            setNetworkFormPhone={setNetworkFormPhone}
            networkFormNotes={networkFormNotes}
            setNetworkFormNotes={setNetworkFormNotes}
            networkFormEmoji={networkFormEmoji}
            setNetworkFormEmoji={setNetworkFormEmoji}
            networkFormColor={networkFormColor}
            setNetworkFormColor={setNetworkFormColor}
            networkFormSaving={networkFormSaving}
            saveSupportNetworkMember={saveSupportNetworkMember}
            deleteSupportNetworkMember={deleteSupportNetworkMember}
            resetNetworkForm={resetNetworkForm}
            openNetworkEditForm={openNetworkEditForm}
          />
        )}

        {screen === 'youth-mockups' && (
          <YouthFeatureMockups showScreen={showScreen} />
        )}

        {screen === 'peer-connect' && !isOnline && (
          <OfflineGate isOnline={isOnline} featureName="The Circle" children={null} />
        )}
        {screen === 'peer-connect' && isOnline && (() => {
          if (!isFeatureTutorialSeen('cw_tutorial_circle')) {
            return <FeatureTutorial storageKey="cw_tutorial_circle" steps={CIRCLE_TUTORIAL} onComplete={() => forceUpdate(n => n + 1)} xpReward={10} />;
          }
          // Lock The Circle until the user completes the community agreement quiz
          if (!circleProfile) {
            return (
              <div className="screen">
                <div className="card" style={{ textAlign: 'center', padding: '40px 24px' }}>
                  <div style={{ marginBottom: 16, display:'flex', justifyContent:'center' }}><IconLock size={40} ariaLabel="" /></div>
                  <h2 style={{ marginBottom: 8 }}>The Circle is locked</h2>
                  <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)', lineHeight: 1.6, marginBottom: 20, maxWidth: 320, margin: '0 auto 20px' }}>
                    Before you can join The Circle, you need to complete the Community Agreement quiz and set up your anonymous identity.
                  </p>
                  <button
                    className="btn btn-primary btn-lg btn-auto"
                    onClick={() => setShowCircleWelcome(true)}
                  ><span>Start community agreement</span></button>
                  {/* eslint-disable-next-line cw/no-inline-button-style -- Circle lock-screen tertiary back; ghost text-link below the primary, not a chamfered .btn variant */}
                  <button
                    onClick={() => showScreen('home')}
                    style={{ display: 'block', margin: '12px auto 0', background: 'none', border: 'none', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)', cursor: 'pointer', fontFamily: 'inherit' }}
                  >← Back to home</button>
                </div>
              </div>
            );
          }
          const liveMapped = circleLivePosts.map((p) => ({
            id: p.id,
            avatarEmoji: p.avatarEmoji,
            handle: p.handle,
            age: undefined as number | undefined,
            birthYear: undefined as number | undefined,
            journey: p.journey,
            scdType: undefined as string | undefined,
            isMod: p.isMod,
            identities: [] as string[],
            time: circleTimeAgo(p.createdAt),
            type: p.postType,
            text: p.text,
            isLive: true,
            replyCount: p.replyCount,
            mediaUrl: p.mediaUrl ?? null,
            gifUrl: p.gifUrl ?? null,
            mediaAlt: p.mediaAlt ?? null,
          }));
          const liveMappedWithKey = liveMapped.map(p => ({ ...p, uiKey: `live-${p.id}` }));
          const demoMapped = demoActive
            ? getDemoPosts().map(dp => ({
                id: dp.id,
                avatarEmoji: dp.avatarEmoji,
                handle: dp.handle,
                age: undefined as number | undefined,
                birthYear: dp.birthYear as number | undefined,
                journey: dp.journey,
                scdType: dp.scdType as string | undefined,
                isMod: dp.isMod,
                identities: dp.identities,
                time: circleTimeAgo(dp.createdAt),
                type: dp.type,
                text: dp.text,
                isLive: false,
                replyCount: dp.replyCount,
                uiKey: `demo-${dp.id}`,
              }))
            : [];
          const localMapped = demoLocalPosts.map(lp => ({
            id: lp.id,
            avatarEmoji: (() => { try { return sessionStorage.getItem('cw_demo_circle_emoji'); } catch { return null; } })() || userAvatarEmoji || '🌱',
            handle: circleProfile?.nickname ?? 'you',
            age: undefined as number | undefined,
            birthYear: undefined as number | undefined,
            journey: 'general',
            scdType: undefined as string | undefined,
            isMod: false,
            identities: [] as string[],
            time: circleTimeAgo(lp.createdAt),
            type: lp.type,
            text: lp.text,
            isLive: false,
            replyCount: 0,
            uiKey: `local-${lp.id}`,
          }));
          const POSTS = [...localMapped, ...liveMappedWithKey, ...demoMapped];
          const TYPE_STYLES: Record<string, { label: string; iconKey?: CrystalIconKey; color: string; bg: string }> = {
            tip: { label: 'Tip', iconKey: 'lightbulb', color: 'var(--color-warning)', bg: 'color-mix(in srgb, var(--color-warning) 10%, transparent)' },
            solidarity: { label: 'Solidarity', iconKey: 'solidarity', color: 'var(--color-accent)', bg: 'color-mix(in srgb, var(--color-accent) 10%, transparent)' },
            story: { label: 'Story', iconKey: 'book', color: 'var(--color-accent)', bg: 'var(--color-accent-light)' },
            question: { label: 'Question', iconKey: 'thought-bubble', color: 'var(--color-info)', bg: 'color-mix(in srgb, var(--color-info) 10%, transparent)' },
          };
          const JOURNEY_META: Record<string, { label: string; iconKey: CrystalIconKey; color: string }> = {
            hospital: { label: 'Hospital & Treatment', iconKey: 'journey-clinic-visit', color: 'var(--color-crisis)' },
            school: { label: 'School & Independence', iconKey: 'journey-school', color: 'var(--color-info)' },
            transition: { label: 'Transition to Adult Care', iconKey: 'journey-adult-transition', color: 'var(--color-accent)' },
            'mental-health': { label: 'Mental Health', iconKey: 'journey-mental-health', color: 'var(--color-success)' },
            general: { label: 'General', iconKey: 'journey-disclosure', color: 'var(--color-text-secondary)' },
          };
          const SCD_TYPES = SCD_TYPES_FILTER;
          const AGE_GROUPS = ['all', '12-17', '18-21', '22-25'];
          const getAgeGroup = (birthYear: number) => {
            const age = new Date().getFullYear() - birthYear;
            if (age >= 12 && age <= 17) return '12-17';
            if (age >= 18 && age <= 21) return '18-21';
            if (age >= 22 && age <= 25) return '22-25';
            return null;
          };
          const filtered = POSTS.filter(p => {
            if (peerFilter !== 'all' && p.type !== peerFilter) return false;
            if (peerAgeFilter !== 'all' && (p.birthYear === undefined || getAgeGroup(p.birthYear) !== peerAgeFilter)) return false;
            if (peerScdFilter !== 'all' && p.scdType !== peerScdFilter) return false;
            return true;
          });
          const fchip = (active: boolean, color?: string) => ({
            flexShrink: 0 as const, padding: '4px 10px', borderRadius: 'var(--radius-xl)',
            border: `1px solid ${active ? (color || 'var(--color-accent)') : 'var(--color-accent-light)'}`,
            background: active ? `${color || 'var(--color-accent-light)'}` : 'transparent',
            color: active ? (color || 'var(--color-accent)') : 'var(--color-text-secondary)',
            fontSize: 'var(--fs-xs)', fontWeight: (active ? 700 : 400) as number, cursor: 'pointer' as const,
          });
          const renderPost = (post: typeof POSTS[0]) => {
            if (peerRemovedPosts.has(post.id)) return null;
            const ts = TYPE_STYLES[post.type];
            const jm = JOURNEY_META[post.journey] || JOURNEY_META.general;
            const reactions = peerReactions[post.id] || {};
            const isModerator = (currentUser?.isAdmin || currentUser?.isModerator) || false;
            const handleLower = post.handle.toLowerCase();
            const isBlocked = safetyBlockedMembers.some(m => m.blockedHandle === handleLower);
            const isMutedMember = safetyMutedMembers.some(m => m.mutedHandle === handleLower);
            const matchedWord = safetyMutedWords.find(mw => post.text.toLowerCase().includes(mw.word));
            // Only live (server) posts carry a photo or GIF.
            const postMedia = post as { mediaUrl?: string | null; gifUrl?: string | null; mediaAlt?: string | null };
            const isCollapsed = (isMutedMember || !!matchedWord) && !peerRevealedPosts.has(post.id);
            const isMuted = isMutedMember || !!matchedWord;
            if (isBlocked) return null;
            const collapseReason = matchedWord
              ? `You muted a word in this post — tap to view`
              : `You muted ${post.handle} — tap to view`;
            return (
              <div key={post.uiKey ?? post.id} style={{ background: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', border: '1px solid var(--color-accent-light)', borderLeft: `3px solid ${jm.color}` }}>
                {isCollapsed ? (
                  // eslint-disable-next-line cw/no-inline-button-style -- Circle post collapsed/muted reveal trigger; full-width "Tap to view" row inside the muted post card, not a chamfered .btn variant
                  <button
                    onClick={() => setPeerRevealedPosts(prev => { const next = new Set(prev); next.add(post.id); return next; })}
                    style={{ width: '100%', background: 'none', border: 'none', padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left' }}
                  >
                    <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', flex: 1 }}>{collapseReason}</span>
                    <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-accent)', fontWeight: 600, flexShrink: 0 }}>Tap to view</span>
                  </button>
                ) : (
                  <>
                    <div
                      style={{ padding: '12px 14px 10px', cursor: (post.isLive || isDemoVisit()) ? 'pointer' : 'default' }}
                      onClick={(post.isLive || isDemoVisit()) ? () => openCircleThread({ id: post.id, handle: post.handle, avatarEmoji: post.avatarEmoji, text: post.text, type: post.type, journey: post.journey, time: post.time }) : undefined}
                      role={(post.isLive || isDemoVisit()) ? 'button' : undefined}
                      aria-label={(post.isLive || isDemoVisit()) ? 'Open thread' : undefined}
                      tabIndex={(post.isLive || isDemoVisit()) ? 0 : undefined}
                      onKeyDown={(post.isLive || isDemoVisit()) ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openCircleThread({ id: post.id, handle: post.handle, avatarEmoji: post.avatarEmoji, text: post.text, type: post.type, journey: post.journey, time: post.time }); } } : undefined}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                        <div style={{ width: 44, height: 44, borderRadius: '50%', flexShrink: 0, background: `color-mix(in srgb, ${jm.color} 13%, transparent)`, border: `2.5px solid color-mix(in srgb, ${jm.color} 56%, transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--fs-lg-plus)' }}>
                          {post.avatarEmoji}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 700, fontSize: 'var(--fs-sm)' }}>{post.handle}</span>
                            {post.isMod && <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--color-success)', background: 'color-mix(in srgb, var(--color-success) 15%, transparent)', borderRadius: 'var(--radius-sm)', padding: '1px 6px', letterSpacing: '0.05em' }}>MOD</span>}
                            {post.birthYear && <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)' }}>· {getAgeGroup(post.birthYear)}</span>}
                            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)' }}>· {post.time}</span>
                          </div>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 3 }}>
                            {post.identities.map((id) => <span key={id} style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', background: 'var(--color-accent-light)', borderRadius: 'var(--radius-md)', padding: '1px 7px' }}>{id}</span>)}
                          </div>
                        </div>
                        <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: ts.color, background: ts.bg, borderRadius: 'var(--radius-md)', padding: '2px 8px', flexShrink: 0, display:'inline-flex', alignItems:'center', gap:4 }}>{ts.iconKey ? (() => { const Icon = CRYSTAL_ICONS[ts.iconKey!]; return <Icon size={12} ariaLabel="" />; })() : null} {ts.label}</span>
                      </div>
                      <p style={{ fontSize: 'var(--fs-sm)', lineHeight: 1.6, margin: '0 0 8px' }}>{post.text}</p>
                      {(postMedia.mediaUrl || postMedia.gifUrl) && (
                        <img
                          src={(postMedia.mediaUrl || postMedia.gifUrl) as string}
                          alt={postMedia.mediaAlt || (postMedia.mediaUrl ? 'Photo shared with this post' : 'GIF shared with this post')}
                          loading="lazy"
                          style={{ display: 'block', width: '100%', maxHeight: 240, objectFit: 'cover', borderRadius: 'var(--radius-md)', margin: '0 0 8px' }}
                        />
                      )}
                      <span style={{ fontSize: 'var(--fs-xs)', color: jm.color, background: `color-mix(in srgb, ${jm.color} 9%, transparent)`, borderRadius: 'var(--radius-md)', padding: '2px 8px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>{(() => { const Icon = CRYSTAL_ICONS[jm.iconKey]; return <Icon size={12} ariaLabel="" />; })()} {jm.label}</span>
                    </div>
                    <div style={{ position: 'relative' }}>
                    {peerEmojiPickerPost === post.id && (() => {
                      const EMOJI_GROUPS = [
                        { label: 'Support', emojis: ['🫂', '💜', '🤝', '🌟', '🙏', '💛', '🫶', '❤️', '💙', '🌈'] },
                        { label: 'Feelings', emojis: ['😊', '😢', '😤', '😔', '🥺', '😌', '😅', '🤗', '💪', '😭'] },
                        { label: 'Strength', emojis: ['🔥', '⚡', '💥', '🏆', '🦁', '💎', '🌊', '🎯', '🛡️', '⭐'] },
                        { label: 'Fun', emojis: ['🎉', '🎊', '✨', '🌺', '🍀', '🦋', '🌸', '🌻', '🎶', '🌙'] },
                      ];
                      return (
                        <div
                          style={{ position: 'absolute', bottom: '100%', left: 0, zIndex: 'var(--z-dropdown)', background: 'var(--color-bg-card)', border: '1px solid var(--color-accent-light)', borderRadius: 'var(--radius-md)', padding: '10px 12px', boxShadow: 'var(--shadow-overlay)', minWidth: 240, marginBottom: 4 }}
                          onClick={e => e.stopPropagation()}
                        >
                          {EMOJI_GROUPS.map(group => (
                            <div key={group.label} style={{ marginBottom: 8 }}>
                              <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--color-text-secondary)', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 4 }}>{group.label}</div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                {group.emojis.map(em => (
                                  // eslint-disable-next-line cw/no-inline-button-style -- Circle emoji-picker dropdown emoji button; tiny ghost square in grouped emoji grid inside floating popover, not a chamfered .btn variant
                                  <button
                                    key={em}
                                    onClick={() => {
                                      setPeerEmojiPickerPost(null);
                                      const key = `${post.id}-${em}`;
                                      const alreadyReacted = peerReacted[key];
                                      if (!alreadyReacted) {
                                        setPeerReactions(r => ({ ...r, [post.id]: { ...(r[post.id] || {}), [em]: ((r[post.id] || {})[em] || 0) + 1 } }));
                                        setPeerReacted(r => ({ ...r, [key]: true }));
                                        if (!isDemoVisit()) fetch(`${API_BASE}/circle/reactions`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` }, body: JSON.stringify({ postId: post.id, emoji: em }) }).catch(() => { toast.error("Couldn't save your reaction."); });
                                      }
                                    }}
                                    style={{ background: 'none', border: 'none', fontSize: 'var(--fs-lg)', cursor: 'pointer', padding: '2px 3px', borderRadius: 'var(--radius-sm)', lineHeight: 1 }}
                                  >{em}</button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                    <div style={{ display: 'flex', gap: 6, padding: '8px 14px', borderTop: '1px solid var(--color-accent-light)', background: 'var(--color-accent-light)', flexWrap: 'wrap' }}>
                      {Object.entries(reactions).filter(([, count]) => count > 0).map(([emoji, count]) => {
                        const key = `${post.id}-${emoji}`;
                        const active = !!peerReacted[key];
                        return (
                          // eslint-disable-next-line cw/no-inline-button-style -- Circle post reaction count chip; pill with emoji + count and dynamic active outline, not a chamfered .btn variant
                          <button key={emoji} onClick={() => {
                            if (active) {
                              setPeerReactions(r => {
                                const cur = r[post.id] || {};
                                const newCount = (cur[emoji] || 1) - 1;
                                if (newCount <= 0) { const { [emoji]: _, ...rest } = cur; return { ...r, [post.id]: rest }; }
                                return { ...r, [post.id]: { ...cur, [emoji]: newCount } };
                              });
                              setPeerReacted(r => { const { [key]: _, ...rest } = r; return rest; });
                              if (!isDemoVisit()) fetch(`${API_BASE}/circle/reactions`, { method: 'DELETE', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` }, body: JSON.stringify({ postId: post.id, emoji }) }).catch(() => { toast.error("Couldn't update your reaction."); });
                            } else {
                              setPeerReactions(r => ({ ...r, [post.id]: { ...(r[post.id] || {}), [emoji]: ((r[post.id] || {})[emoji] || 0) + 1 } }));
                              setPeerReacted(r => ({ ...r, [key]: true }));
                              if (!isDemoVisit()) fetch(`${API_BASE}/circle/reactions`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` }, body: JSON.stringify({ postId: post.id, emoji }) }).catch(() => { toast.error("Couldn't save your reaction."); });
                            }
                          }} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 9px', borderRadius: 'var(--radius-xl)', border: `1px solid ${active ? 'var(--color-accent)' : 'var(--color-accent-light)'}`, background: active ? 'var(--color-accent-light)' : 'transparent', cursor: 'pointer', fontSize: 'var(--fs-xs)' }}>
                            <span>{emoji}</span>
                            <span style={{ color: active ? 'var(--color-accent)' : 'var(--color-text-secondary)', fontWeight: active ? 700 : 400 }}>{count}</span>
                          </button>
                        );
                      })}
                      {/* eslint-disable-next-line cw/no-inline-button-style -- Circle post "add reaction" icon-only "+" chip; circular 28×28 toggle in actions row, not a chamfered .btn variant */}
                      <button
                        onClick={e => { e.stopPropagation(); setPeerEmojiPickerPost(prev => prev === post.id ? null : post.id); }}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 'var(--radius-xl)', border: '1px solid var(--color-accent-light)', background: peerEmojiPickerPost === post.id ? 'var(--color-accent-light)' : 'transparent', cursor: 'pointer', fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', flexShrink: 0 }}
                        title="Add reaction"
                      >+</button>
                      {(post.isLive || isDemoVisit()) && (
                        // eslint-disable-next-line cw/no-inline-button-style -- Circle post "view replies" chip; small accent-tinted pill with speech-bubble icon + count, not a chamfered .btn variant
                        <button
                          onClick={e => { e.stopPropagation(); openCircleThread({ id: post.id, handle: post.handle, avatarEmoji: post.avatarEmoji, text: post.text, type: post.type, journey: post.journey, time: post.time }); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '3px 9px', borderRadius: 'var(--radius-xl)', border: '1px solid var(--color-accent-light)', background: 'var(--color-accent-light)', cursor: 'pointer', fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', flexShrink: 0 }}
                          title="View replies"
                        >
                          <span style={{ display:'inline-flex' }}><IconSpeechBubble size={14} ariaLabel="" /></span>
                          <span>{circleThreadReplyCounts[post.id] ?? post.replyCount ?? 0}</span>
                        </button>
                      )}
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        {!isMuted && (
                          // eslint-disable-next-line cw/no-inline-button-style -- Circle post safety mute text-link; ghost-only tiny chip in actions row, not a chamfered .btn variant
                          <button
                            onClick={() => muteMember(post.handle)}
                            style={{ background: 'none', border: 'none', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-xs)', cursor: 'pointer', padding: '2px 6px' }}
                            title={`Mute ${post.handle}`}
                          >Mute</button>
                        )}
                        {isMuted && (
                          // eslint-disable-next-line cw/no-inline-button-style -- Circle post safety unmute text-link; accent-tinted tiny chip in actions row, not a chamfered .btn variant
                          <button
                            onClick={() => { const m = safetyMutedMembers.find(m => m.mutedHandle === handleLower); if (m) unmuteMember(m.id); setPeerRevealedPosts(prev => { const next = new Set(prev); next.delete(post.id); return next; }); }}
                            style={{ background: 'none', border: 'none', color: 'var(--color-accent)', fontSize: 'var(--fs-xs)', cursor: 'pointer', padding: '2px 6px', fontWeight: 600 }}
                          >Unmute</button>
                        )}
                        {/* eslint-disable-next-line cw/no-inline-button-style -- Circle post safety block text-link; warning-tinted tiny chip in actions row, not a chamfered .btn variant */}
                        <button
                          onClick={() => blockMember(post.handle)}
                          style={{ background: 'none', border: 'none', color: 'var(--color-warning)', fontSize: 'var(--fs-xs)', cursor: 'pointer', padding: '2px 6px' }}
                          title={`Block ${post.handle}`}
                        >Block</button>
                          </span>
                      </div>
                      {isModerator && (
                        // eslint-disable-next-line cw/no-inline-button-style -- Circle post moderator-only Remove chip; warning-tinted small pill in actions row, not a chamfered .btn variant
                        <button
                          onClick={() => { setPeerRemovalModal({ postId: post.id, handle: post.handle }); setPeerRemovalReason(null); setPeerRemovalConfirmed(false); }}
                          style={{ background: 'var(--color-accent-light)', border: '1px solid var(--color-accent-light)', borderRadius: 'var(--radius-sm)', color: 'var(--color-warning)', fontSize: 'var(--fs-xs)', fontWeight: 600, padding: '3px 9px', cursor: 'pointer', flexShrink: 0 }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    </div>
                  </>
                )}
              </div>
            );
          };
          const circlePIICheckNickname = (val: string): string => {
            const emailRe = /[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/;
            const phoneRe = /(\+?1[\s\-.]?)?\(?\d{3}\)?[\s\-.]?\d{3}[\s\-.]?\d{4}/;
            const nameRe = /\b[A-Z][a-z]{1,}\s+[A-Z][a-z]{1,}(\s+[A-Z][a-z]{1,})?\b/;
            if (emailRe.test(val) || phoneRe.test(val) || nameRe.test(val)) {
              return "This looks a bit too close to real\u2011life details. Try a nickname that doesn\u2019t include your full name, email, or phone number.";
            }
            return '';
          };
          const circlePIICheckBio = (val: string): string => {
            const emailRe = /[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/;
            const phoneRe = /(\+?1[\s\-.]?)?\(?\d{3}\)?[\s\-.]?\d{3}[\s\-.]?\d{4}/;
            const nameRe = /\b[A-Z][a-z]{1,}\s+[A-Z][a-z]{1,}(\s+[A-Z][a-z]{1,})?\b/;
            if (emailRe.test(val) || phoneRe.test(val) || nameRe.test(val)) {
              return "This bio includes details that could make it easier to identify you outside this app.";
            }
            return '';
          };
          const saveCircleEdit = async () => {
            const nn = circleEditNickname.trim();
            const bio = circleEditBio.trim();
            let hasErr = false;
            if (nn.length < 2) { setCircleEditNicknameError('Nickname must be at least 2 characters'); hasErr = true; }
            else if (nn.length > 40) { setCircleEditNicknameError('Nickname must be 40 characters or less'); hasErr = true; }
            else { const e = circlePIICheckNickname(nn); if (e) { setCircleEditNicknameError(e); hasErr = true; } else setCircleEditNicknameError(''); }
            if (bio) { const e = circlePIICheckBio(bio); if (e) { setCircleEditBioError(e); hasErr = true; } else setCircleEditBioError(''); }
            else setCircleEditBioError('');
            if (hasErr) return;
            setCircleEditSaving(true);
            try {
              const headers: HeadersInit = { 'Content-Type': 'application/json' };
              if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
              const res = await fetchWithRetry(`${API_BASE}/circle/profile`, { method: 'POST', headers, credentials: 'include', body: JSON.stringify({ nickname: nn, bio }) });
              if (res.ok) {
                const data = await res.json() as { success: boolean; profile: { nickname: string; bio: string | null } };
                setCircleProfile(data.profile);
                setCircleEditSaved(true);
                setTimeout(() => setCircleEditSaved(false), 2500);
              } else {
                const err = await res.json() as { error?: string; field?: string };
                if (err.field === 'bio') setCircleEditBioError(err.error || 'Invalid bio');
                else setCircleEditNicknameError(err.error || "Couldn't save your nickname. Try again.");
              }
            } catch { /* request failed */ setCircleEditNicknameError("That didn't go through. Try again."); }
            finally { setCircleEditSaving(false); }
          };

          return (
            <div className="screen" onClick={() => { if (peerEmojiPickerPost !== null) setPeerEmojiPickerPost(null); }}>
              {!moduleIntroSeenCircle && (
                <ModuleIntroBanner
                  icon="🫂"
                  title="The Circle"
                  body="A safe, moderated community just for youth with SCD. Read tips and stories, ask questions, share solidarity — all anonymously, on your terms."
                  onDismiss={dismissModuleIntroCircle}
                />
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                <button className="btn btn-primary btn-sm btn-auto" onClick={() => {
                  setPeerPostError(null); setPeerPostNudge(null);
                  if (!circleProfile) { setShowCircleWelcome(true); }
                  else { setPeerPostModal(true); }
                }}><span>+ Post</span></button>
              </div>
              <div style={{ background: 'var(--color-bg-card)', borderRadius: 'var(--radius-md)', padding: '10px 14px', marginBottom: 10, border: '1px solid var(--color-accent-light)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--color-accent-light)', border: '2px solid var(--color-accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--fs-lg)', flexShrink: 0 }}>
                    {userAvatarEmoji || <IconSparkle size={20} ariaLabel="" />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>{circleProfile?.nickname || userDisplayName}</div>
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)' }}>
                      {circleProfile?.bio || (userAvatarEmoji ? `Circle emoji: ${userAvatarEmoji}` : 'No emoji avatar set yet')}
                    </div>
                  </div>
                  {/* eslint-disable-next-line cw/no-inline-button-style -- Circle header card right-side "Edit" emoji-picker trigger; small accent-tinted chip inline with avatar + nickname, not a chamfered .btn variant */}
                  <button
                    onClick={() => { setCircleEmojiDraft(userAvatarEmoji || ''); setShowCircleEmojiPicker(true); }}
                    style={{ padding: '5px 10px', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-accent-light)', background: 'var(--color-accent-light)', color: 'var(--color-accent)', fontSize: 'var(--fs-xs)', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
                  >
                    Edit
                  </button>
                </div>
                {(circleDisclosure && (circleDisclosure.ageBracket || circleDisclosure.scdType || (circleDisclosure.customTags && circleDisclosure.customTags.length > 0))) || guideBondLevel >= 5 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                    {circleDisclosure?.ageBracket && circleDisclosure.ageBracket !== 'Prefer not to say' && (
                      <span style={{ fontSize: 'var(--fs-xs)', padding: '2px 8px', borderRadius: 'var(--radius-xl)', background: 'var(--color-accent-light)', border: '1px solid var(--color-accent-light)', color: 'var(--color-accent)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>{(() => { const Icon = CRYSTAL_ICONS['cake']; return <Icon size={13} ariaLabel="" />; })()} {circleDisclosure.ageBracket}</span>
                    )}
                    {circleDisclosure?.scdType && circleDisclosure.scdType !== 'Prefer not to say' && (
                      <span style={{ fontSize: 'var(--fs-xs)', padding: '2px 8px', borderRadius: 'var(--radius-xl)', background: 'var(--color-accent-light)', border: '1px solid var(--color-accent-light)', color: 'var(--color-accent)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>{(() => { const Icon = CRYSTAL_ICONS['trait-sickle']; return <Icon size={13} ariaLabel="" />; })()} {circleDisclosure.scdType}</span>
                    )}
                    {(circleDisclosure?.customTags ?? []).map(t => (
                      <span key={t} style={{ fontSize: 'var(--fs-xs)', padding: '2px 8px', borderRadius: 'var(--radius-xl)', background: 'var(--color-secondary-light)', border: '1px solid var(--color-secondary-light)', color: 'var(--color-secondary)' }}>{t}</span>
                    ))}
                    {guideBondLevel >= 5 && activeGuideChar && (
                      <span style={{
                        fontSize: 'var(--fs-xs)', padding: '2px 8px', borderRadius: 'var(--radius-xl)',
                        background: `color-mix(in srgb, ${activeGuideChar.bg} 27%, transparent)`,
                        border: `1px solid color-mix(in srgb, ${activeGuideChar.bg} 40%, transparent)`,
                        color: activeGuideChar.bg,
                        fontWeight: 700,
                      }}><span style={{ display:'inline-flex', alignItems:'center', gap:4 }}><IconSparkle size={12} ariaLabel="" /> Bonded · {activeGuideChar.crystalName}</span></span>
                    )}
                    {circleDisclosure && (circleDisclosure.ageBracket || circleDisclosure.scdType || (circleDisclosure.customTags && circleDisclosure.customTags.length > 0)) && (
                      <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', alignSelf: 'center', marginLeft: 2 }}>· self-reported</span>
                    )}
                  </div>
                ) : null}
              </div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                {([['feed', 'Feed'], ['topics', 'Topics'], ['circle-profile', 'My profile']] as const).map(([v, label]) => (
                  // eslint-disable-next-line cw/no-inline-button-style -- Circle 3-tab segmented control (Feed / Topics / My profile); pill-style toggle with dynamic on/off state, not a chamfered .btn variant
                  <button key={v} onClick={() => {
                    setPeerView(v);
                    setPeerSelectedTopic(null);
                    if (v === 'circle-profile' && circleProfile) {
                      setCircleEditNickname(circleProfile.nickname);
                      setCircleEditBio(circleProfile.bio || '');
                      setCircleEditNicknameError('');
                      setCircleEditBioError('');
                      setCircleEditSaved(false);
                      const PRESET_SCD = SCD_TYPES_CIRCLE;
                      const disc = circleDisclosure;
                      setCircleDisclosureEditAgeBracket(disc?.ageBracket ?? null);
                      if (disc?.scdType && !PRESET_SCD.includes(disc.scdType)) {
                        setCircleDisclosureEditScdType(null);
                        setCircleDisclosureEditScdOther(disc.scdType);
                        setCircleDisclosureEditScdShowOther(true);
                      } else {
                        setCircleDisclosureEditScdType(disc?.scdType ?? null);
                        setCircleDisclosureEditScdOther('');
                        setCircleDisclosureEditScdShowOther(false);
                      }
                      setCircleDisclosureEditTags(disc?.customTags ?? []);
                      setCircleDisclosureEditTagInput('');
                      setCircleDisclosureEditTagError('');
                      setCircleDisclosureEditSaved(false);
                    }
                  }} style={{ flex: 1, padding: '7px', borderRadius: 'var(--radius-md)', border: '1px solid', borderColor: peerView === v ? 'var(--color-accent)' : 'var(--color-accent-light)', background: peerView === v ? 'var(--color-accent-light)' : 'transparent', color: peerView === v ? 'var(--color-accent)' : 'var(--color-text-secondary)', fontSize: 'var(--fs-xs)', fontWeight: peerView === v ? 700 : 400, cursor: 'pointer' }}>
                    {label}
                  </button>
                ))}
              </div>
              {peerView === 'feed' && (<>
              <div style={{ display: 'flex', gap: 5, overflowX: 'auto', marginBottom: 5, paddingBottom: 2, scrollbarWidth: 'none' }}>
                {['all', 'tip', 'solidarity', 'story', 'question'].map(f => (
                  // eslint-disable-next-line cw/no-inline-button-style -- Circle post-type filter chip; horizontal scroll segmented control using shared fchip helper, not a chamfered .btn variant
                  <button key={f} onClick={() => setPeerFilter(f)} style={fchip(peerFilter === f)}>{f === 'all' ? 'All posts' : TYPE_STYLES[f]?.label}</button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 5, overflowX: 'auto', marginBottom: 5, paddingBottom: 2, scrollbarWidth: 'none' }}>
                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', alignSelf: 'center', flexShrink: 0 }}>Age:</span>
                {/* eslint-disable-next-line cw/no-inline-button-style -- Circle age-bracket filter chip; horizontal scroll segmented control using shared fchip helper, not a chamfered .btn variant */}
                {AGE_GROUPS.map(g => <button key={g} onClick={() => setPeerAgeFilter(g)} style={fchip(peerAgeFilter === g)}>{g === 'all' ? 'All' : g}</button>)}
              </div>
              <div style={{ display: 'flex', gap: 5, overflowX: 'auto', marginBottom: 14, paddingBottom: 4, scrollbarWidth: 'none' }}>
                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', alignSelf: 'center', flexShrink: 0 }}>SCD:</span>
                {/* eslint-disable-next-line cw/no-inline-button-style -- Circle SCD-type filter chip; horizontal scroll segmented control using shared fchip helper, not a chamfered .btn variant */}
                {SCD_TYPES.map(t => <button key={t} onClick={() => setPeerScdFilter(t)} style={fchip(peerScdFilter === t)}>{t === 'all' ? 'All types' : t}</button>)}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {filtered.length === 0 ? <div style={{ textAlign: 'center', padding: '24px', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)' }}>{POSTS.length === 0 ? 'No posts yet — be the first to share.' : 'No posts match your filters.'}</div> : filtered.map(post => renderPost(post))}
              </div>
              </>)}
              {peerView === 'topics' && !peerSelectedTopic && (
                <>
                  <div style={{ height: 4 }} />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 4 }}>
                    {Object.entries(JOURNEY_META).map(([key, meta]) => {
                      const count = POSTS.filter(p => p.journey === key).length;
                      return (
                        <div key={key} role="button" tabIndex={0} aria-label={`${meta.label}, ${count} post${count !== 1 ? 's' : ''}`} onClick={() => setPeerSelectedTopic(key)} onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPeerSelectedTopic(key); } }} style={{ background: `color-mix(in srgb, ${meta.color} 7%, transparent)`, border: `1.5px solid color-mix(in srgb, ${meta.color} 25%, transparent)`, borderRadius: 'var(--radius-lg)', padding: '16px 12px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {(() => { const Icon = CRYSTAL_ICONS[meta.iconKey]; return <Icon size={28} ariaLabel="" />; })()}
                          <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm)', color: meta.color, lineHeight: 1.3 }}>{meta.label}</div>
                          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)' }}>{count} post{count !== 1 ? 's' : ''}</div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
              {peerView === 'topics' && peerSelectedTopic && (() => {
                const meta = JOURNEY_META[peerSelectedTopic];
                const topicPosts = POSTS.filter(p => p.journey === peerSelectedTopic);
                return (
                  <div>
                    {/* eslint-disable-next-line cw/no-inline-button-style -- Circle topic-detail "← Back to topics" link; ghost text-link above topic header chip, not a chamfered .btn variant */}
                    <button onClick={() => setPeerSelectedTopic(null)} style={{ background: 'none', border: 'none', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-xs)', cursor: 'pointer', padding: '0 0 10px', display: 'flex', alignItems: 'center', gap: 4 }}>← Back to topics</button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: `color-mix(in srgb, ${meta.color} 9%, transparent)`, borderRadius: 'var(--radius-md)', border: `1px solid color-mix(in srgb, ${meta.color} 20%, transparent)`, marginBottom: 12 }}>
                      {(() => { const Icon = CRYSTAL_ICONS[meta.iconKey]; return <Icon size={18} ariaLabel="" />; })()}
                      <span style={{ fontWeight: 700, fontSize: 'var(--fs-sm)', color: meta.color }}>{meta.label}</span>
                      <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginLeft: 'auto' }}>{topicPosts.length} post{topicPosts.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {topicPosts.length === 0 ? <div style={{ textAlign: 'center', padding: '24px', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)' }}>No posts in this topic yet. Be the first if you're ready.</div> : topicPosts.map(post => renderPost(post))}
                    </div>
                  </div>
                );
              })()}
              {peerView === 'circle-profile' && (
                <div>
                  {!circleProfile ? (
                    <div style={{ textAlign: 'center', padding: '32px 20px', background: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-accent-light)' }}>
                      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }} aria-hidden="true">{(() => { const Icon = CRYSTAL_ICONS['handshake']; return <Icon size={48} ariaLabel="" />; })()}</div>
                      <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm-plus)', marginBottom: 8 }}>You don't have a Circle identity yet</div>
                      <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', marginBottom: 20, lineHeight: 1.6 }}>Choose a nickname that keeps you safe — no real name, email, or phone number.</div>
                      <button className="btn btn-primary btn-auto" onClick={() => setShowCircleWelcome(true)}><span>Set up your Circle identity</span></button>
                    </div>
                  ) : (
                    <div style={{ background: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)', padding: '20px', border: '1px solid var(--color-accent-light)' }}>
                      <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm-plus)', marginBottom: 4 }}>Your Circle identity</div>
                      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginBottom: 16, lineHeight: 1.5 }}>This is how you appear to other members. Your real name and details are never shown.</div>

                      {/* Disclosure badges preview */}
                      {circleDisclosure && (circleDisclosure.ageBracket || circleDisclosure.scdType || (circleDisclosure.customTags && circleDisclosure.customTags.length > 0)) && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 16, padding: '10px 12px', background: 'var(--color-accent-light)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-accent-light)' }}>
                          <div style={{ width: '100%', fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 5, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Currently visible on your profile</div>
                          {circleDisclosure.ageBracket && circleDisclosure.ageBracket !== 'Prefer not to say' && (
                            <span style={{ fontSize: 'var(--fs-xs)', padding: '3px 9px', borderRadius: 'var(--radius-xl)', background: 'var(--color-accent-light)', border: '1px solid var(--color-accent-light)', color: 'var(--color-accent)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>{(() => { const Icon = CRYSTAL_ICONS['cake']; return <Icon size={14} ariaLabel="" />; })()} {circleDisclosure.ageBracket}</span>
                          )}
                          {circleDisclosure.scdType && circleDisclosure.scdType !== 'Prefer not to say' && (
                            <span style={{ fontSize: 'var(--fs-xs)', padding: '3px 9px', borderRadius: 'var(--radius-xl)', background: 'var(--color-accent-light)', border: '1px solid var(--color-accent-light)', color: 'var(--color-accent)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>{(() => { const Icon = CRYSTAL_ICONS['trait-sickle']; return <Icon size={14} ariaLabel="" />; })()} {circleDisclosure.scdType}</span>
                          )}
                          {(circleDisclosure.customTags ?? []).map(t => (
                            <span key={t} style={{ fontSize: 'var(--fs-xs)', padding: '3px 9px', borderRadius: 'var(--radius-xl)', background: 'var(--color-secondary-light)', border: '1px solid var(--color-secondary-light)', color: 'var(--color-secondary)' }}>{t}</span>
                          ))}
                        </div>
                      )}

                      <label style={{ display: 'block', fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Nickname <span style={{ color: 'var(--color-crisis)' }}>*</span></label>
                      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginBottom: 6, lineHeight: 1.5 }}>Choose something creative that isn't your real name — no full names, email addresses, or phone numbers.</div>
                      <input
                        type="text"
                        value={circleEditNickname}
                        onChange={e => { setCircleEditNickname(e.target.value); setCircleEditNicknameError(''); setCircleEditSaved(false); }}
                        maxLength={40}
                        placeholder="e.g. sunflower.storm, ayo_k, quietthread"
                        aria-label="Nickname"
                        style={{ width: '100%', boxSizing: 'border-box', background: 'var(--color-bg-surface)', border: `1px solid ${circleEditNicknameError ? 'var(--color-crisis)' : 'var(--color-accent-light)'}`, borderRadius: 'var(--radius-md)', padding: '10px 12px', color: 'var(--color-text-primary)', fontSize: 'var(--fs-sm)', outline: 'none', marginBottom: 4, fontFamily: 'inherit' }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: circleEditNicknameError ? 0 : 16 }}>
                        <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-crisis)', flex: 1 }}>{circleEditNicknameError}</span>
                        <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)' }}>{circleEditNickname.length}/40</span>
                      </div>
                      {circleEditNicknameError && <div style={{ height: 12 }} />}

                      <label style={{ display: 'block', fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 6, marginTop: 4 }}>Bio <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 400 }}>(optional)</span></label>
                      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginBottom: 6, lineHeight: 1.5 }}>Share a little about your experience — without identifiable details like your school, city, or grade.</div>
                      <textarea
                        value={circleEditBio}
                        onChange={e => { setCircleEditBio(e.target.value.slice(0, 120)); setCircleEditBioError(''); setCircleEditSaved(false); }}
                        maxLength={120}
                        rows={3}
                        placeholder="e.g. Living with HbSS since I was little. Here to connect and not feel alone in this."
                        aria-label="Bio"
                        style={{ width: '100%', boxSizing: 'border-box', background: 'var(--color-bg-surface)', border: `1px solid ${circleEditBioError ? 'var(--color-crisis)' : 'var(--color-accent-light)'}`, borderRadius: 'var(--radius-md)', padding: '10px 12px', color: 'var(--color-text-primary)', fontSize: 'var(--fs-sm)', resize: 'none', outline: 'none', fontFamily: 'inherit', lineHeight: 1.5 }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: circleEditBioError ? 0 : 20 }}>
                        <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-crisis)', flex: 1 }}>{circleEditBioError}</span>
                        <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)' }}>{circleEditBio.length}/120</span>
                      </div>
                      {circleEditBioError && <div style={{ height: 16 }} />}

                      {circleEditSaved && (
                        <div style={{ padding: '8px 12px', background: 'color-mix(in srgb, var(--color-success) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--color-success) 30%, transparent)', borderRadius: 'var(--radius-md)', fontSize: 'var(--fs-xs)', color: 'var(--color-success)', marginBottom: 12, textAlign: 'center' }}>
                          Circle identity saved!
                        </div>
                      )}

                      {/* eslint-disable-next-line cw/no-inline-button-style -- inline .btn-primary opacity override on Circle "Save changes" CTA when disabled; rule flags any style on button regardless of className */}
                      <button
                        disabled={circleEditSaving || circleEditNickname.trim().length < 2}
                        onClick={saveCircleEdit}
                        className="btn btn-primary"
                        style={{ opacity: (circleEditSaving || circleEditNickname.trim().length < 2) ? 0.4 : 1 }}
                      >
                        {circleEditSaving ? 'Saving…' : 'Save changes'}
                      </button>

                      <div style={{ marginTop: 20, padding: '12px', background: 'var(--color-accent-light)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-accent-light)' }}>
                        <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 6 }}>What's kept private</div>
                        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>Your real name, email, age, grade, school, postal code, and account details are never visible to other Circle members.</div>
                      </div>

                      {/* ── Voluntary Disclosure ──────────────────────────────────── */}
                      {(() => {
                        const PRESET_SCD_EDIT = SCD_TYPES_CIRCLE;
                        const AGE_BRACKETS_EDIT = ["14–16", "17–19", "20–22", "23–25", "Prefer not to say"];
                        const MAX_TAGS_EDIT = 3;
                        const MAX_TAG_LEN_EDIT = 20;
                        const emailRe2 = /[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/;
                        const phoneRe2 = /(\+?1[\s\-.]?)?\(?\d{3}\)?[\s\-.]?\d{3}[\s\-.]?\d{4}/;
                        const nameRe2 = /\b[A-Z][a-z]{1,}\s+[A-Z][a-z]{1,}(\s+[A-Z][a-z]{1,})?\b/;
                        const discChip = (label: string, active: boolean, onClick: () => void) => (
                          // eslint-disable-next-line cw/no-inline-button-style -- Circle voluntary-disclosure chip helper (age-bracket / SCD-type / "Other" toggle); pill-style chip with dynamic active outline, not a chamfered .btn variant
                          <button key={label} onClick={onClick} style={{ padding: '5px 12px', borderRadius: 'var(--radius-xl)', border: `1.5px solid ${active ? 'var(--color-accent)' : 'var(--color-accent-light)'}`, background: active ? 'var(--color-accent-light)' : 'var(--color-accent-light)', color: active ? 'var(--color-accent)' : 'var(--color-text-secondary)', fontSize: 'var(--fs-xs)', fontWeight: active ? 700 : 400, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>{label}</button>
                        );
                        const saveDisclosure = async () => {
                          const finalScd = circleDisclosureEditScdShowOther ? circleDisclosureEditScdOther.trim() || null : circleDisclosureEditScdType;
                          setCircleDisclosureEditSaving(true);
                          try {
                            const headers: HeadersInit = { 'Content-Type': 'application/json' };
                            if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
                            const res = await fetchWithRetry(`${API_BASE}/circle/disclosure`, {
                              method: 'PUT', headers,
                              body: JSON.stringify({ ageBracket: circleDisclosureEditAgeBracket, scdType: finalScd, customTags: circleDisclosureEditTags }),
                            });
                            if (res.ok) {
                              const data = await res.json() as { disclosure: { ageBracket: string | null; scdType: string | null; customTags: string[] } };
                              setCircleDisclosure(data.disclosure);
                              setCircleDisclosureEditSaved(true);
                              setTimeout(() => setCircleDisclosureEditSaved(false), 2500);
                            }
                          } catch { /* error handled silently */ }
                          finally { setCircleDisclosureEditSaving(false); }
                        };
                        const addDiscTag = () => {
                          const t = circleDisclosureEditTagInput.trim();
                          if (!t) return;
                          if (t.length > MAX_TAG_LEN_EDIT) { setCircleDisclosureEditTagError(`Tags can be up to ${MAX_TAG_LEN_EDIT} characters`); return; }
                          if (emailRe2.test(t) || phoneRe2.test(t) || nameRe2.test(t)) { setCircleDisclosureEditTagError('This tag looks too close to real-life details'); return; }
                          if (circleDisclosureEditTags.includes(t)) { setCircleDisclosureEditTagError('Already added'); return; }
                          if (circleDisclosureEditTags.length >= MAX_TAGS_EDIT) { setCircleDisclosureEditTagError(`Up to ${MAX_TAGS_EDIT} tags only`); return; }
                          setCircleDisclosureEditTags(prev => [...prev, t]);
                          setCircleDisclosureEditTagInput('');
                          setCircleDisclosureEditTagError('');
                        };
                        return (
                          <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--color-accent-light)' }}>
                            <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, letterSpacing: '0.08em', color: 'var(--color-accent)', textTransform: 'uppercase', marginBottom: 6 }}>Optional · You choose what to share</div>
                            <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm)', marginBottom: 4 }}>Voluntary disclosure</div>
                            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginBottom: 16, lineHeight: 1.5 }}>
                              These details appear as small badges on your profile — only to other Circle members. Nothing here is required, and fields you leave blank stay invisible.
                            </div>

                            <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Age range</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                              {AGE_BRACKETS_EDIT.map(b => discChip(b, circleDisclosureEditAgeBracket === b, () => setCircleDisclosureEditAgeBracket(prev => prev === b ? null : b)))}
                            </div>

                            <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 6 }}>SCD type <span style={{ fontWeight: 400, fontSize: 'var(--fs-xs)' }}>(self-reported)</span></div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: circleDisclosureEditScdShowOther ? 8 : 16 }}>
                              {PRESET_SCD_EDIT.map(t => discChip(t, !circleDisclosureEditScdShowOther && circleDisclosureEditScdType === t, () => { setCircleDisclosureEditScdShowOther(false); setCircleDisclosureEditScdType(prev => prev === t ? null : t); }))}
                              {discChip('Other', circleDisclosureEditScdShowOther, () => { setCircleDisclosureEditScdShowOther(prev => !prev); setCircleDisclosureEditScdType(null); })}
                            </div>
                            {circleDisclosureEditScdShowOther && (
                              <input
                                type="text"
                                value={circleDisclosureEditScdOther}
                                onChange={e => setCircleDisclosureEditScdOther(e.target.value.slice(0, 40))}
                                placeholder="e.g. HbSF, compound heterozygous…"
                                aria-label="Other SCD type"
                                style={{ width: '100%', boxSizing: 'border-box', background: 'var(--color-bg-surface)', border: '1px solid var(--color-accent-light)', borderRadius: 'var(--radius-md)', padding: '8px 12px', color: 'var(--color-text-primary)', fontSize: 'var(--fs-sm)', outline: 'none', fontFamily: 'inherit', marginBottom: 16 }}
                              />
                            )}

                            <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Labels & tags <span style={{ fontWeight: 400, fontSize: 'var(--fs-xs)' }}>— up to {MAX_TAGS_EDIT}</span></div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: circleDisclosureEditTags.length ? 8 : 0 }}>
                              {circleDisclosureEditTags.map(t => (
                                <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 'var(--radius-xl)', background: 'var(--color-accent-light)', border: '1.5px solid var(--color-accent-light)', color: 'var(--color-accent)', fontSize: 'var(--fs-xs)', fontWeight: 600 }}>
                                  {t}
                                  {/* eslint-disable-next-line cw/no-inline-button-style -- Circle voluntary-disclosure tag pill remove X; ghost icon-only inside pill chip, no .btn-icon-only primitive */}
                                  <button onClick={() => setCircleDisclosureEditTags(prev => prev.filter(x => x !== t))} style={{ background: 'none', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', padding: 0, lineHeight: 1, display: 'flex', alignItems: 'center' }} aria-label="Remove tag"><IconClose size={12} ariaLabel="" /></button>
                                </span>
                              ))}
                            </div>
                            {circleDisclosureEditTags.length < MAX_TAGS_EDIT && (
                              <div style={{ display: 'flex', gap: 6, marginTop: 8, marginBottom: 4 }}>
                                <input
                                  type="text"
                                  value={circleDisclosureEditTagInput}
                                  onChange={e => { setCircleDisclosureEditTagInput(e.target.value.slice(0, MAX_TAG_LEN_EDIT)); setCircleDisclosureEditTagError(''); }}
                                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addDiscTag(); } }}
                                  placeholder="e.g. warrior, caregiver…"
                                  aria-label="Add a label or tag"
                                  style={{ flex: 1, background: 'var(--color-bg-surface)', border: `1px solid ${circleDisclosureEditTagError ? 'var(--color-crisis)' : 'var(--color-accent-light)'}`, borderRadius: 'var(--radius-md)', padding: '7px 10px', color: 'var(--color-text-primary)', fontSize: 'var(--fs-xs)', outline: 'none', fontFamily: 'inherit' }}
                                />
                                {/* eslint-disable-next-line cw/no-inline-button-style -- Circle voluntary-disclosure inline "Add" tag-input button; small accent-tinted chip paired with text input, not a chamfered .btn variant */}
                                <button onClick={addDiscTag} disabled={!circleDisclosureEditTagInput.trim()} style={{ background: 'var(--color-accent-light)', border: '1px solid var(--color-accent-light)', borderRadius: 'var(--radius-md)', color: 'var(--color-accent)', fontSize: 'var(--fs-xs)', fontWeight: 700, padding: '7px 12px', cursor: circleDisclosureEditTagInput.trim() ? 'pointer' : 'default', opacity: circleDisclosureEditTagInput.trim() ? 1 : 0.4, fontFamily: 'inherit' }}>Add</button>
                              </div>
                            )}
                            {circleDisclosureEditTagError && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-crisis)', marginBottom: 8 }}>{circleDisclosureEditTagError}</div>}
                            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginBottom: 14, marginTop: 6 }}>Suggested: warrior · caregiver · newly diagnosed · in remission · advocate</div>

                            {circleDisclosureEditSaved && (
                              <div style={{ padding: '8px 12px', background: 'color-mix(in srgb, var(--color-success) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--color-success) 30%, transparent)', borderRadius: 'var(--radius-md)', fontSize: 'var(--fs-xs)', color: 'var(--color-success)', marginBottom: 12, textAlign: 'center' }}>
                                Disclosure preferences saved!
                              </div>
                            )}
                            {/* eslint-disable-next-line cw/no-inline-button-style -- Circle voluntary-disclosure "Save disclosure preferences" CTA; full-width accent-tinted secondary in disclosure-section card, not a chamfered .btn variant */}
                            <button
                              onClick={saveDisclosure}
                              disabled={circleDisclosureEditSaving}
                              style={{ width: '100%', padding: '11px', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-accent-light)', cursor: 'pointer', background: 'var(--color-accent-light)', color: 'var(--color-accent)', fontWeight: 700, fontSize: 'var(--fs-sm)', opacity: circleDisclosureEditSaving ? 0.5 : 1, fontFamily: 'inherit' }}
                            >
                              {circleDisclosureEditSaving ? 'Saving…' : 'Save disclosure preferences'}
                            </button>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {screen === 'circle-safety' && (
          <div className="screen">
            <div style={{ background: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)', padding: '14px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 38, height: 38, borderRadius: 'var(--radius-md)', background: 'var(--color-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IconShield size={22} ariaLabel="" /></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm-plus)' }}>Filters & safety</div>
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)' }}>Your personal Circle controls</div>
              </div>
              {/* eslint-disable-next-line cw/no-inline-button-style -- circle-safety screen header "← Back" text-link; ghost-only sibling to title row, not a chamfered .btn variant */}
              <button onClick={() => showScreen('peer-connect')} style={{ background: 'none', border: 'none', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)', cursor: 'pointer' }}>← Back</button>
            </div>

            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', background: 'var(--color-accent-light)', borderRadius: 'var(--radius-md)', padding: '10px 14px', marginBottom: 18, lineHeight: 1.6 }}>
              These controls are personal to you and separate from platform-level moderation. Muted and blocked people cannot tell that you've muted or blocked them.
            </div>

            {!safetyLoaded ? (
              <div style={{ textAlign: 'center', padding: 32, color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)' }}>Pulling your safety settings…</div>
            ) : (
              <>
                {/* ── Muted Words ───────────────────────────────────────────── */}
                <div style={{ background: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)', padding: '14px 16px', marginBottom: 14 }}>
                  <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm)', marginBottom: 4 }}>Muted words & phrases</div>
                  <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginBottom: 14, lineHeight: 1.5 }}>
                    Posts containing these words will be collapsed. Tap to reveal them on a post-by-post basis.
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    <input
                      value={safetyNewWord}
                      onChange={e => { setSafetyNewWord(e.target.value); setSafetyWordError(''); }}
                      onKeyDown={e => { if (e.key === 'Enter') addMutedWord(safetyNewWord); }}
                      placeholder="Add a word or phrase…"
                      aria-label="Add a muted word or phrase"
                      style={{ flex: 1, background: 'var(--color-bg-surface)', border: '1px solid var(--color-accent-light)', borderRadius: 'var(--radius-md)', padding: '8px 12px', color: 'var(--color-text-primary)', fontSize: 'var(--fs-sm)', outline: 'none' }}
                    />
                    {/* eslint-disable-next-line cw/no-inline-button-style -- circle-safety muted-words inline "Add" button paired with text input; accent-filled small chip, not a chamfered .btn variant */}
                    <button
                      onClick={() => addMutedWord(safetyNewWord)}
                      disabled={!safetyNewWord.trim()}
                      style={{ background: 'var(--color-accent)', border: 'none', borderRadius: 'var(--radius-md)', color: '#fff', fontWeight: 700, fontSize: 'var(--fs-sm)', padding: '8px 16px', cursor: safetyNewWord.trim() ? 'pointer' : 'default', opacity: safetyNewWord.trim() ? 1 : 0.4 }}
                    >Add</button>
                  </div>
                  {safetyWordError && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-crisis)', marginBottom: 8 }}>{safetyWordError}</div>}
                  {safetyMutedWords.length === 0 ? (
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', textAlign: 'center', padding: '10px 0' }}>No muted words yet</div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {safetyMutedWords.map(mw => (
                        <div key={mw.id} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--color-accent-light)', border: '1px solid var(--color-accent-light)', borderRadius: 'var(--radius-xl)', padding: '4px 10px 4px 12px' }}>
                          <span style={{ fontSize: 'var(--fs-sm)' }}>{mw.word}</span>
                          {/* eslint-disable-next-line cw/no-inline-button-style -- circle-safety muted-word pill remove X; ghost icon-only inside pill chip, no .btn-icon-only primitive */}
                          <button onClick={() => removeMutedWord(mw.id)} aria-label={`Remove muted word ${mw.word}`} style={{ background: 'none', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', fontSize: 'var(--fs-sm)', lineHeight: 1, padding: 0 }}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* ── Muted People ─────────────────────────────────────────── */}
                <div style={{ background: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)', padding: '14px 16px', marginBottom: 14 }}>
                  <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm)', marginBottom: 4 }}>Muted people</div>
                  <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginBottom: 14, lineHeight: 1.5 }}>
                    Posts from muted members are collapsed. You can still tap to reveal them. Mute from individual posts via the "Mute" button.
                  </div>
                  {safetyMutedMembers.length === 0 ? (
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', textAlign: 'center', padding: '10px 0' }}>No one muted yet</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {safetyMutedMembers.map(m => (
                        <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--color-accent-light)' }}>
                          <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 500 }}>@{m.mutedHandle}</span>
                          {/* eslint-disable-next-line cw/no-inline-button-style -- circle-safety muted-members list "Unmute" row chip; accent-tinted pill in 2-col list row, not a chamfered .btn variant */}
                          <button
                            onClick={() => unmuteMember(m.id)}
                            style={{ background: 'var(--color-accent-light)', border: '1px solid var(--color-accent-light)', borderRadius: 'var(--radius-xl)', color: 'var(--color-accent)', fontSize: 'var(--fs-xs)', fontWeight: 600, padding: '4px 12px', cursor: 'pointer' }}
                          >Unmute</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* ── Blocked People ───────────────────────────────────────── */}
                <div style={{ background: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)', padding: '14px 16px', marginBottom: 14 }}>
                  <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm)', marginBottom: 4 }}>Blocked people</div>
                  <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginBottom: 14, lineHeight: 1.5 }}>
                    Posts from blocked members are hidden entirely. Block from individual posts via the "Block" button.
                  </div>
                  {safetyBlockedMembers.length === 0 ? (
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', textAlign: 'center', padding: '10px 0' }}>No one blocked yet</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {safetyBlockedMembers.map(m => (
                        <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--color-accent-light)' }}>
                          <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 500 }}>@{m.blockedHandle}</span>
                          {/* eslint-disable-next-line cw/no-inline-button-style -- circle-safety blocked-members list "Unblock" row chip; warning-tinted pill in 2-col list row, not a chamfered .btn variant */}
                          <button
                            onClick={() => unblockMember(m.id)}
                            style={{ background: 'var(--color-accent-light)', border: '1px solid var(--color-accent-light)', borderRadius: 'var(--radius-xl)', color: 'var(--color-warning)', fontSize: 'var(--fs-xs)', fontWeight: 600, padding: '4px 12px', cursor: 'pointer' }}
                          >Unblock</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

      </main>

      {/* Circle Welcome / Setup Screen */}
      {showCircleWelcome && (
        <CircleWelcomeScreen
          authToken={authToken}
          apiBase={API_BASE}
          onComplete={(profile, disclosure) => { setCircleProfile(profile); if (disclosure !== undefined) setCircleDisclosure(disclosure); setShowCircleWelcome(false); }}
          onSkip={() => { setShowCircleWelcome(false); if (screen === 'peer-connect' && !circleProfile) showScreen('home'); }}
        />
      )}

      {/* Circle Setup Modal (legacy — kept for profile edit trigger) */}
      {circleSetupModal && (
        <>
          <div role="presentation" onClick={() => setCircleSetupModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 'var(--z-dialog-backdrop)', backdropFilter: 'blur(4px)' }} />
          <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 'var(--z-dialog)', background: 'var(--color-bg-card)', borderRadius: '20px 20px 0 0', padding: '20px 20px 32px', paddingBottom: 'max(32px, env(safe-area-inset-bottom))', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: 'color-mix(in srgb, var(--color-text-primary) 8%, transparent)' }} />
              {/* eslint-disable-next-line cw/no-inline-button-style -- Circle setup modal close X; absolute icon-only inside drag-handle header row, no .btn-icon-only primitive */}
              <button onClick={() => setCircleSetupModal(false)} style={{ position: 'absolute', right: 0, top: -8, background: 'none', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', lineHeight: 1, padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IconClose size={18} ariaLabel="Close" /></button>
            </div>
            <div style={{ fontWeight: 700, fontSize: 'var(--fs-base-plus)', marginBottom: 4 }}>Create your Circle identity</div>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginBottom: 20, lineHeight: 1.6 }}>The Circle uses a separate nickname so your real-life details stay private. Choose something that doesn't include your name, email, or phone number.</div>

            <label style={{ display: 'block', fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Nickname <span style={{ color: 'var(--color-crisis)' }}>*</span></label>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginBottom: 6, lineHeight: 1.5 }}>No full names, email addresses, or phone numbers — choose something creative that feels like you.</div>
            <input
              type="text"
              value={circleSetupNickname}
              onChange={e => { setCircleSetupNickname(e.target.value); setCircleSetupNicknameError(''); }}
              maxLength={40}
              placeholder="e.g. sunflower.storm, ayo_k, quietthread"
              aria-label="Nickname"
              style={{ width: '100%', boxSizing: 'border-box', background: 'var(--color-bg-surface)', border: `1px solid ${circleSetupNicknameError ? 'var(--color-crisis)' : 'color-mix(in srgb, var(--color-accent) 25%, transparent)'}`, borderRadius: 'var(--radius-md)', padding: '10px 12px', color: 'var(--color-text-primary)', fontSize: 'var(--fs-sm)', outline: 'none', marginBottom: 4, fontFamily: 'inherit' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: circleSetupNicknameError ? 4 : 16 }}>
              <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-crisis)', flex: 1 }}>{circleSetupNicknameError}</span>
              <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)' }}>{circleSetupNickname.length}/40</span>
            </div>

            <label style={{ display: 'block', fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Bio <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 400 }}>(optional)</span></label>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginBottom: 6, lineHeight: 1.5 }}>Share a little about your experience — no school, city, grade, or other identifying details.</div>
            <textarea
              value={circleSetupBio}
              onChange={e => { setCircleSetupBio(e.target.value.slice(0, 120)); setCircleSetupBioError(''); }}
              maxLength={120}
              rows={3}
              placeholder="e.g. Living with HbSS since I was little. Here to connect and not feel alone in this."
              aria-label="Bio"
              style={{ width: '100%', boxSizing: 'border-box', background: 'var(--color-bg-surface)', border: `1px solid ${circleSetupBioError ? 'var(--color-crisis)' : 'color-mix(in srgb, var(--color-accent) 25%, transparent)'}`, borderRadius: 'var(--radius-md)', padding: '10px 12px', color: 'var(--color-text-primary)', fontSize: 'var(--fs-sm)', resize: 'none', outline: 'none', fontFamily: 'inherit', lineHeight: 1.5 }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: circleSetupBioError ? 4 : 20 }}>
              <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-crisis)', flex: 1 }}>{circleSetupBioError}</span>
              <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)' }}>{circleSetupBio.length}/120</span>
            </div>

            {/* eslint-disable-next-line cw/no-inline-button-style -- inline .btn-primary opacity override on Circle setup modal "Join the Circle" CTA when disabled; rule flags any style on button regardless of className */}
            <button
              disabled={circleSetupSaving || circleSetupNickname.trim().length < 2}
              onClick={async () => {
                const nn = circleSetupNickname.trim();
                const bio = circleSetupBio.trim();
                let hasErr = false;
                const emailRe = /[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/;
                const phoneRe = /(\+?1[\s\-.]?)?\(?\d{3}\)?[\s\-.]?\d{3}[\s\-.]?\d{4}/;
                const nameRe = /\b[A-Z][a-z]{1,}\s+[A-Z][a-z]{1,}(\s+[A-Z][a-z]{1,})?\b/;
                if (nn.length < 2) { setCircleSetupNicknameError('Nickname must be at least 2 characters'); hasErr = true; }
                else if (nn.length > 40) { setCircleSetupNicknameError('Nickname must be 40 characters or less'); hasErr = true; }
                else if (emailRe.test(nn) || phoneRe.test(nn) || nameRe.test(nn)) { setCircleSetupNicknameError("This looks a bit too close to real\u2011life details. Try a nickname that doesn\u2019t include your full name, email, or phone number."); hasErr = true; }
                if (bio && (emailRe.test(bio) || phoneRe.test(bio) || nameRe.test(bio))) { setCircleSetupBioError("This bio includes details that could make it easier to identify you outside this app."); hasErr = true; }
                if (hasErr) return;
                setCircleSetupSaving(true);
                try {
                  const headers: HeadersInit = { 'Content-Type': 'application/json' };
                  if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
                  const res = await fetchWithRetry(`${API_BASE}/circle/profile`, { method: 'POST', headers, credentials: 'include', body: JSON.stringify({ nickname: nn, bio }) });
                  if (res.ok) {
                    const data = await res.json() as { success: boolean; profile: { nickname: string; bio: string | null } };
                    setCircleProfile(data.profile);
                    setCircleSetupModal(false);
                    setCircleSetupNickname('');
                    setCircleSetupBio('');
                  } else {
                    const err = await res.json() as { error?: string; field?: string };
                    if (err.field === 'bio') setCircleSetupBioError(err.error || 'Invalid bio');
                    else setCircleSetupNicknameError(err.error || "Couldn't save your nickname. Try again.");
                  }
                } catch { /* request failed */ setCircleSetupNicknameError("That didn't go through. Try again."); }
                finally { setCircleSetupSaving(false); }
              }}
              className="btn btn-primary"
              style={{ opacity: (circleSetupSaving || circleSetupNickname.trim().length < 2) ? 0.4 : 1 }}
            >
              {circleSetupSaving ? 'Saving…' : 'Join the Circle'}
            </button>

            <div style={{ marginTop: 16, padding: '10px 12px', background: 'color-mix(in srgb, var(--color-accent) 6%, transparent)', borderRadius: 'var(--radius-md)', border: '1px solid color-mix(in srgb, var(--color-accent) 15%, transparent)' }}>
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>Your real name, email, age, grade, school, postal code, and account details are never visible to other Circle members.</div>
            </div>

            {/* eslint-disable-next-line cw/no-inline-button-style -- Circle setup modal tertiary "Not now — go back"; full-width ghost text-link below primary, not a chamfered .btn variant */}
            <button
              onClick={() => { setCircleSetupModal(false); showScreen('home'); }}
              style={{ width: '100%', marginTop: 12, background: 'none', border: 'none', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)', cursor: 'pointer', padding: '8px 0' }}
            >
              Not now — go back
            </button>
          </div>
        </>
      )}

      {/* Circle Media Consent Modal (first-time safety prompt) */}
      {circleMediaConsentModal && (
        <>
          <div role="presentation" onClick={() => { setCircleMediaConsentModal(false); setCircleMediaConsentPendingAction(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 'var(--z-dialog-backdrop)', backdropFilter: 'blur(4px)' }} />
          <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 'var(--z-dialog)', background: 'var(--color-bg-card)', borderRadius: '20px 20px 0 0', padding: '24px 20px 32px', paddingBottom: 'max(32px, env(safe-area-inset-bottom))' }}>
            <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: 'color-mix(in srgb, var(--color-text-primary) 8%, transparent)' }} />
              {/* eslint-disable-next-line cw/no-inline-button-style -- Circle media-consent modal close X; absolute icon-only inside drag-handle header row, no .btn-icon-only primitive */}
              <button onClick={() => { setCircleMediaConsentModal(false); setCircleMediaConsentPendingAction(null); }} style={{ position: 'absolute', right: 0, top: -8, background: 'none', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', lineHeight: 1, padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IconClose size={18} ariaLabel="Close" /></button>
            </div>
            <div style={{ textAlign: 'center', marginBottom: 12, display:'flex', justifyContent:'center' }}><IconShield size={28} ariaLabel="" /></div>
            <div style={{ fontWeight: 700, fontSize: 'var(--fs-base-plus)', marginBottom: 10, textAlign: 'center' }}>Sharing gifs, memes, and photos</div>
            <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.7, marginBottom: 20 }}>
              <p style={{ margin: '0 0 10px' }}>Before you share an image or GIF, a few things to keep you and others safe:</p>
              <ul style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <li>Don't share photos that could identify you (faces, school uniforms, locations)</li>
                <li>Don't share images of others without their permission</li>
                <li>Images are reviewed — content that harms or sexualises is always blocked</li>
                <li>Sensitive content may receive an automatic content note visible to other members</li>
                <li>The Circle is youth-only and moderated — be kind to yourself and others</li>
              </ul>
            </div>
            {/* eslint-disable-next-line cw/no-inline-button-style -- inline .btn-primary marginBottom override on Circle media-consent "Got it" CTA; rule flags any style on button regardless of className */}
            <button
              onClick={() => {
                localStorage.setItem(CIRCLE_MEDIA_CONSENT_KEY, '1');
                setCircleMediaConsentModal(false);
                if (circleMediaConsentPendingAction === 'gif') {
                  setShowGifPicker(true);
                  if (authToken) {
                    fetch(`${API_BASE}/circle/gifs`, { headers: { Authorization: `Bearer ${authToken}` } })
                      .then(r => r.json() as Promise<{ gifs: typeof gifItems; categories: typeof gifCategories }>)
                      .then(data => { setGifItems(data.gifs || []); setGifCategories(data.categories || []); })
                      .catch(() => { toast.error("Couldn't load GIFs. Try again."); });
                  }
                }
                setCircleMediaConsentPendingAction(null);
              }}
              className="btn btn-primary"
              style={{ marginBottom: 10 }}
            >
              Got it
            </button>
            {/* eslint-disable-next-line cw/no-inline-button-style -- Circle media-consent tertiary "Cancel upload"; full-width ghost text-link below primary, not a chamfered .btn variant */}
            <button
              onClick={() => { setCircleMediaConsentModal(false); setCircleMediaConsentPendingAction(null); }}
              style={{ width: '100%', background: 'none', border: 'none', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)', cursor: 'pointer', padding: '8px 0' }}
            >
              Cancel upload
            </button>
          </div>
        </>
      )}

      {/* GIF Picker Modal */}
      {showGifPicker && (
        <>
          <div role="presentation" onClick={() => setShowGifPicker(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 'var(--z-dialog-backdrop)', backdropFilter: 'blur(4px)' }} />
          <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 'var(--z-dialog)', background: 'var(--color-bg-card)', borderRadius: '20px 20px 0 0', padding: '20px 20px 32px', paddingBottom: 'max(32px, env(safe-area-inset-bottom))', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: 'color-mix(in srgb, var(--color-text-primary) 8%, transparent)' }} />
              {/* eslint-disable-next-line cw/no-inline-button-style -- GIF picker modal close X; absolute icon-only inside drag-handle header row, no .btn-icon-only primitive */}
              <button onClick={() => setShowGifPicker(false)} style={{ position: 'absolute', right: 0, top: -8, background: 'none', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', lineHeight: 1, padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IconClose size={18} ariaLabel="Close" /></button>
            </div>
            <div style={{ fontWeight: 700, fontSize: 'var(--fs-base)', marginBottom: 12 }}>Choose a GIF</div>
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 14, paddingBottom: 4, scrollbarWidth: 'none' }}>
              {/* eslint-disable-next-line cw/no-inline-button-style -- GIF picker "All" filter chip; horizontal scroll segmented control, not a chamfered .btn variant */}
              <button onClick={() => setGifFilter('all')} style={{ flexShrink: 0, padding: '4px 12px', borderRadius: 'var(--radius-xl)', border: `1px solid ${gifFilter === 'all' ? 'var(--color-accent)' : 'color-mix(in srgb, var(--color-text-primary) 10%, transparent)'}`, background: gifFilter === 'all' ? 'color-mix(in srgb, var(--color-accent) 15%, transparent)' : 'transparent', color: gifFilter === 'all' ? 'var(--color-accent)' : 'var(--color-text-secondary)', fontSize: 'var(--fs-xs)', fontWeight: gifFilter === 'all' ? 700 : 400, cursor: 'pointer' }}>All</button>
              {gifCategories.filter(c => c.enabled).map(cat => (
                // eslint-disable-next-line cw/no-inline-button-style -- GIF picker category filter chip; horizontal scroll segmented control, not a chamfered .btn variant
                <button key={cat.slug} onClick={() => setGifFilter(cat.slug)} style={{ flexShrink: 0, padding: '4px 12px', borderRadius: 'var(--radius-xl)', border: `1px solid ${gifFilter === cat.slug ? 'var(--color-accent)' : 'color-mix(in srgb, var(--color-text-primary) 10%, transparent)'}`, background: gifFilter === cat.slug ? 'color-mix(in srgb, var(--color-accent) 15%, transparent)' : 'transparent', color: gifFilter === cat.slug ? 'var(--color-accent)' : 'var(--color-text-secondary)', fontSize: 'var(--fs-xs)', fontWeight: gifFilter === cat.slug ? 700 : 400, cursor: 'pointer' }}>{cat.label}</button>
              ))}
            </div>
            {gifItems.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)' }}>No GIFs available right now.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                {gifItems.filter(g => gifFilter === 'all' || g.category === gifFilter).map(gif => (
                  // eslint-disable-next-line cw/no-inline-button-style -- GIF picker image tile; <button> wraps <img> for 2-col grid tap target, padding-0 to let img fill, not a chamfered .btn variant
                  <button key={gif.id} onClick={() => { setPeerPostGif({ url: gif.url, alt: gif.alt }); setPeerPostImage(null); setPeerPostImageName(''); setPeerPostImageContentNote(null); setShowGifPicker(false); }} style={{ padding: 0, border: '2px solid transparent', borderRadius: 'var(--radius-md)', overflow: 'hidden', cursor: 'pointer', background: 'none' }}>
                    <img src={gif.url} alt={gif.alt} loading="lazy" style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Peer Post Modal */}
      {peerPostModal && (
        <>
          <div role="presentation" onClick={() => { setPeerPostModal(false); setShowGifPicker(false); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 'var(--z-dialog-backdrop)', backdropFilter: 'blur(4px)' }} />
          <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 'var(--z-dialog)', background: 'var(--color-bg-card)', borderRadius: '20px 20px 0 0', padding: '20px 20px 32px', paddingBottom: 'max(32px, env(safe-area-inset-bottom))', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: 'color-mix(in srgb, var(--color-text-primary) 8%, transparent)' }} />
              {/* eslint-disable-next-line cw/no-inline-button-style -- peer-post modal close X; absolute icon-only inside drag-handle header row, no .btn-icon-only primitive */}
              <button onClick={() => { setPeerPostModal(false); setShowGifPicker(false); }} style={{ position: 'absolute', right: 0, top: -8, background: 'none', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', lineHeight: 1, padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IconClose size={18} ariaLabel="Close" /></button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'color-mix(in srgb, var(--color-accent) 15%, transparent)', border: '2px solid color-mix(in srgb, var(--color-accent) 40%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--fs-lg)', flexShrink: 0 }}>
                {userAvatarEmoji || <IconSparkle size={20} ariaLabel="" />}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm-plus)' }}>Share with the Circle</div>
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)' }}>Youth-only · moderated · you choose what you share</div>
              </div>
            </div>

            {/* Post type selector */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, overflowX: 'auto', paddingBottom: 4, scrollbarWidth: 'none' }}>
              {([['tip', 'Tip', 'var(--color-warning)', 'lightbulb'], ['solidarity', 'Solidarity', 'var(--color-accent)', 'solidarity'], ['story', 'Story', 'var(--color-accent)', 'book'], ['question', 'Question', 'var(--color-info)', 'thought-bubble']] as [typeof peerPostType, string, string, CrystalIconKey][]).map(([type, label, color, iconKey]) => {
                const Icon = CRYSTAL_ICONS[iconKey];
                return (
                  // eslint-disable-next-line cw/no-inline-button-style -- peer-post modal post-type segmented chip (Tip / Solidarity / Story / Question); horizontal scroll pill toggle with per-type tint, not a chamfered .btn variant
                  <button
                    key={type}
                    onClick={() => setPeerPostType(type)}
                    style={{ flexShrink: 0, padding: '6px 14px', borderRadius: 'var(--radius-xl)', border: `1px solid ${peerPostType === type ? color : 'color-mix(in srgb, var(--color-text-primary) 12%, transparent)'}`, background: peerPostType === type ? `color-mix(in srgb, ${color} 13%, transparent)` : 'transparent', color: peerPostType === type ? color : 'var(--color-text-secondary)', fontSize: 'var(--fs-xs)', fontWeight: peerPostType === type ? 700 : 400, cursor: 'pointer', display:'inline-flex', alignItems:'center', gap:6 }}
                  >
                    <Icon size={14} ariaLabel="" /> {label}
                  </button>
                );
              })}
            </div>

            {/* Prompt */}
            <div style={{ padding: '10px 12px', background: 'color-mix(in srgb, var(--color-accent) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--color-accent) 20%, transparent)', borderRadius: 'var(--radius-md)', marginBottom: 12, fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', lineHeight: 1.5, display:'flex', alignItems:'flex-start', gap:6 }}>
              {peerPostType === 'tip' && <><IconLightbulb size={14} ariaLabel="" /> <span>Share a tip that has helped you navigate care, school, crisis visits, or daily life with SCD.</span></>}
              {peerPostType === 'solidarity' && <><IconSolidarity size={14} ariaLabel="" /> <span>No advice needed. Just say what's true for you right now. This space holds it.</span></>}
              {peerPostType === 'story' && <><IconBook size={14} ariaLabel="" /> <span>Share a moment — with your care team, during a crisis, at school, or anywhere — that others might recognize.</span></>}
              {peerPostType === 'question' && <><IconThoughtBubble size={14} ariaLabel="" /> <span>Ask something you've wondered about. SCD-specific, care-related, or just lived experience.</span></>}
            </div>

            {/* Text area */}
            <textarea
              value={peerPostText}
              onChange={e => setPeerPostText(e.target.value.slice(0, 280))}
              placeholder="Write your post..."
              aria-label="Write your post"
              rows={3}
              style={{ width: '100%', background: 'var(--color-bg-surface)', border: '1px solid color-mix(in srgb, var(--color-accent) 25%, transparent)', borderRadius: 'var(--radius-md)', padding: '12px', color: 'var(--color-text-primary)', fontSize: 'var(--fs-sm)', resize: 'none', outline: 'none', boxSizing: 'border-box', lineHeight: 1.5, fontFamily: 'inherit' }}
            />
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginBottom: 12, textAlign: 'right' }}>{peerPostText.length}/280</div>

            {/* Media attach row */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
              <label
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 'var(--radius-xl)', border: '1px solid color-mix(in srgb, var(--color-text-primary) 12%, transparent)', background: 'transparent', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-xs)', cursor: peerPostImageUploading ? 'not-allowed' : 'pointer' }}
              >
                <span>Photo</span>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  style={{ display: 'none' }}
                  aria-label="Upload photo"
                  disabled={peerPostImageUploading}
                  onChange={async e => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const hasConsent = localStorage.getItem(CIRCLE_MEDIA_CONSENT_KEY);
                    if (!hasConsent) {
                      setCircleMediaConsentPendingAction('image');
                      setCircleMediaConsentModal(true);
                      e.target.value = '';
                      return;
                    }
                    setPeerPostImageError('');
                    setPeerPostImageUploading(true);
                    try {
                      const fd = new FormData();
                      fd.append('image', file);
                      if (peerPostAltText) fd.append('altText', peerPostAltText);
                      const headers: HeadersInit = {};
                      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
                      const res = await fetch(`${API_BASE}/circle/media/upload`, { method: 'POST', headers, body: fd });
                      const data = await res.json() as { success?: boolean; imageUrl?: string; contentNote?: string | null; flagged?: boolean; error?: string; blocked?: boolean };
                      if (!res.ok || data.blocked) {
                        setPeerPostImageError(data.error || 'This image could not be uploaded.');
                      } else {
                        setPeerPostImage(data.imageUrl || null);
                        setPeerPostImageName(file.name);
                        setPeerPostImageContentNote(data.contentNote || null);
                        setPeerPostGif(null);
                      }
                    } catch { /* request failed */
                      setPeerPostImageError('Upload failed. Please try again.');
                    } finally {
                      setPeerPostImageUploading(false);
                      e.target.value = '';
                    }
                  }}
                />
              </label>
              {/* eslint-disable-next-line cw/no-inline-button-style -- peer-post modal media-attach "GIF" chip; outlined pill in 3-up media-attach row (Photo / GIF / Remove), not a chamfered .btn variant */}
              <button
                onClick={() => {
                  const hasConsent = localStorage.getItem(CIRCLE_MEDIA_CONSENT_KEY);
                  if (!hasConsent) {
                    setCircleMediaConsentPendingAction('gif');
                    setCircleMediaConsentModal(true);
                    return;
                  }
                  if (gifItems.length === 0 && authToken) {
                    fetch(`${API_BASE}/circle/gifs`, { headers: { Authorization: `Bearer ${authToken}` } })
                      .then(r => r.json() as Promise<{ gifs: typeof gifItems; categories: typeof gifCategories }>)
                      .then(data => { setGifItems(data.gifs || []); setGifCategories(data.categories || []); })
                      .catch(() => { toast.error("Couldn't load GIFs. Try again."); });
                  }
                  setShowGifPicker(true);
                }}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 'var(--radius-xl)', border: '1px solid color-mix(in srgb, var(--color-text-primary) 12%, transparent)', background: 'transparent', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-xs)', cursor: 'pointer' }}
              >
                <span>GIF</span>
              </button>
              {(peerPostImage || peerPostGif) && (
                // eslint-disable-next-line cw/no-inline-button-style -- peer-post modal "Remove media" text-link; ghost-only chip pushed to row end with marginLeft auto, not a chamfered .btn variant
                <button
                  onClick={() => { setPeerPostImage(null); setPeerPostImageName(''); setPeerPostGif(null); setPeerPostImageContentNote(null); setPeerPostImageError(''); }}
                  style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-xs)', cursor: 'pointer' }}
                >
                  Remove media
                </button>
              )}
            </div>

            {peerPostImageUploading && (
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginBottom: 10, textAlign: 'center' }}>Uploading and checking image…</div>
            )}
            {peerPostImageError && (
              <div style={{ padding: '8px 12px', background: 'color-mix(in srgb, var(--color-crisis) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--color-crisis) 30%, transparent)', borderRadius: 'var(--radius-md)', fontSize: 'var(--fs-xs)', color: 'var(--color-crisis)', marginBottom: 10 }}>{peerPostImageError}</div>
            )}

            {peerPostImage && (
              <div style={{ marginBottom: 12, borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '1px solid color-mix(in srgb, var(--color-text-primary) 8%, transparent)' }}>
                <img src={peerPostImage} alt={peerPostAltText || peerPostImageName} loading="lazy" style={{ width: '100%', maxHeight: 160, objectFit: 'cover', display: 'block' }} />
                {peerPostImageContentNote && (
                  <div style={{ padding: '6px 10px', background: 'color-mix(in srgb, var(--color-warning) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--color-warning) 30%, transparent)', borderTop: 'none', fontSize: 'var(--fs-xs)', color: 'var(--color-warning)', display:'flex', alignItems:'center', gap:6 }}>
                    <IconWarning size={14} ariaLabel="" /> <span>Content note: {peerPostImageContentNote} — this label will be visible to other members</span>
                  </div>
                )}
              </div>
            )}

            {peerPostGif && (
              <div style={{ marginBottom: 12, borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '1px solid color-mix(in srgb, var(--color-text-primary) 8%, transparent)' }}>
                <img src={peerPostGif.url} alt={peerPostGif.alt} loading="lazy" style={{ width: '100%', maxHeight: 140, objectFit: 'cover', display: 'block' }} />
              </div>
            )}

            {peerPostImage && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 6 }}>What's in this image? <span style={{ fontWeight: 400 }}>(optional)</span></label>
                <input
                  type="text"
                  value={peerPostAltText}
                  onChange={e => setPeerPostAltText(e.target.value.slice(0, 200))}
                  placeholder="Describe the image for people who can't see it"
                  aria-label="Image description"
                  style={{ width: '100%', boxSizing: 'border-box', background: 'var(--color-bg-surface)', border: '1px solid color-mix(in srgb, var(--color-accent) 25%, transparent)', borderRadius: 'var(--radius-md)', padding: '9px 12px', color: 'var(--color-text-primary)', fontSize: 'var(--fs-sm)', outline: 'none', fontFamily: 'inherit' }}
                />
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginTop: 4, textAlign: 'right' }}>{peerPostAltText.length}/200</div>
              </div>
            )}

            {peerPostError && (
              <div style={{ color: 'var(--color-crisis)', fontSize: 'var(--fs-sm)', marginBottom: 10, padding: '10px 12px', background: 'color-mix(in srgb, var(--color-crisis) 8%, transparent)', borderRadius: 'var(--radius-md)', border: '1px solid color-mix(in srgb, var(--color-crisis) 25%, transparent)', lineHeight: 1.5 }}>
                {peerPostError}
              </div>
            )}
            {peerPostNudge && (
              <div style={{ color: 'var(--color-warning)', fontSize: 'var(--fs-sm)', marginBottom: 10, padding: '10px 12px', background: 'color-mix(in srgb, var(--color-warning) 8%, transparent)', borderRadius: 'var(--radius-md)', border: '1px solid color-mix(in srgb, var(--color-warning) 25%, transparent)', lineHeight: 1.5 }}>
                {peerPostNudge}
              </div>
            )}
            {/* eslint-disable-next-line cw/no-inline-button-style -- inline .btn-primary opacity override on peer-post modal "Post to the Circle +25 XP" CTA when disabled; rule flags any style on button regardless of className */}
            <button
              disabled={peerPostText.trim().length < 10 || peerPostLoading}
              onClick={async () => {
                setPeerPostError(null);
                setPeerPostNudge(null);
                setPeerPostLoading(true);
                if (isDemoVisit()) {
                  setDemoLocalPosts(prev => [{ id: -Date.now(), type: peerPostType, text: peerPostText.trim(), createdAt: new Date().toISOString() }, ...prev]);
                  setPeerPostModal(false);
                  setPeerPostText('');
                  setPeerPostLoading(false);
                  toast('Demo: your post is only visible to you and is not sent anywhere.');
                  return;
                }
                try {
                  const res = await fetchWithRetry(`${API_BASE}/circle/posts`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
                    body: JSON.stringify({
                      type: peerPostType,
                      text: peerPostText,
                      journey: 'general',
                      // Photos come back from /circle/media/upload as data URLs; GIFs are from the curated list.
                      ...(peerPostImage ? { mediaUrl: peerPostImage, altText: peerPostAltText || undefined } : {}),
                      ...(peerPostGif ? { gifUrl: peerPostGif.url } : {}),
                    }),
                  });
                  const data = await res.json() as { error?: string; nudge?: string };
                  if (!res.ok) {
                    setPeerPostError(data.error ?? "Your post didn't go through. Try again.");
                  } else {
                    // The modal closes on success, so show the server's note as a toast.
                    if (data.nudge) toast(data.nudge);
                    setPeerPostModal(false);
                    setPeerPostText('');
                    setPeerPostImage(null);
                    setPeerPostImageName('');
                    setPeerPostAltText('');
                    setPeerPostGif(null);
                    setPeerPostImageContentNote(null);
                    setPeerPostImageError('');
                    earnXP(25);
                    earnXPOnce('first_circle_post', 25);
                    loadCirclePosts();
                  }
                } catch { /* request failed */
                  setPeerPostError("Couldn't reach the server. Check your connection and try again.");
                } finally {
                  setPeerPostLoading(false);
                }
              }}
              className="btn btn-primary"
              style={{ opacity: peerPostText.trim().length < 10 || peerPostLoading ? 0.4 : 1 }}
            >
              {peerPostLoading ? 'Posting…' : 'Post to the Circle +25 XP'}
            </button>
          </div>
        </>
      )}

      {/* Post Removal Modal */}
      {peerRemovalModal && (
        <>
          <div role="presentation" onClick={() => { setPeerRemovalModal(null); setPeerRemovalReason(null); setPeerRemovalConfirmed(false); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 'var(--z-dialog-backdrop)', backdropFilter: 'blur(4px)' }} />
          <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 'var(--z-dialog)', background: 'var(--color-bg-card)', borderRadius: '20px 20px 0 0', padding: '20px 20px 32px', paddingBottom: 'max(32px, env(safe-area-inset-bottom))' }}>
            <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: 'color-mix(in srgb, var(--color-text-primary) 8%, transparent)' }} />
              {/* eslint-disable-next-line cw/no-inline-button-style -- post-removal modal close X; absolute icon-only inside drag-handle header row, no .btn-icon-only primitive */}
              <button onClick={() => { setPeerRemovalModal(null); setPeerRemovalReason(null); setPeerRemovalConfirmed(false); }} style={{ position: 'absolute', right: 0, top: -8, background: 'none', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', lineHeight: 1, padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IconClose size={18} ariaLabel="Close" /></button>
            </div>
            {!peerRemovalConfirmed ? (
              <>
                <div style={{ fontWeight: 700, fontSize: 'var(--fs-base-plus)', marginBottom: 4 }}>Remove post by {peerRemovalModal.handle}</div>
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginBottom: 20 }}>Select the reason this post is being removed. The author will receive a message explaining why.</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                  {([
                    { value: 'HATE_SLUR' as const, label: 'Direct hate / slurs', desc: 'Language that targets people\'s identity' },
                    { value: 'STRUCTURAL_HARM_SLUR' as const, label: 'Structural harm naming + slur', desc: 'Real critique using words that land as slurs' },
                    { value: 'PRIVACY_RISK' as const, label: 'Serious privacy risk', desc: 'Details that could identify someone' },
                  ]).map(option => (
                    // eslint-disable-next-line cw/no-inline-button-style -- post-removal moderator option tile (HATE_SLUR / STRUCTURAL_HARM_SLUR / PRIVACY_RISK); large warm-tinted tile with label + desc, dynamic active border, not a chamfered .btn variant
                    <button
                      key={option.value}
                      onClick={() => setPeerRemovalReason(option.value)}
                      style={{ textAlign: 'left', padding: '14px 16px', borderRadius: 'var(--radius-md)', border: `2px solid ${peerRemovalReason === option.value ? 'var(--color-crisis)' : 'color-mix(in srgb, var(--color-text-primary) 10%, transparent)'}`, background: peerRemovalReason === option.value ? 'color-mix(in srgb, var(--color-crisis) 10%, transparent)' : 'var(--color-bg-surface)', cursor: 'pointer', transition: 'all 0.15s' }}
                    >
                      <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm)', color: peerRemovalReason === option.value ? 'var(--color-crisis)' : 'var(--color-text-primary)', marginBottom: 3 }}>{option.label}</div>
                      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)' }}>{option.desc}</div>
                    </button>
                  ))}
                </div>
                {peerRemovalReason && (
                  <div style={{ padding: '12px 14px', background: 'color-mix(in srgb, var(--color-crisis) 7%, transparent)', border: '1px solid color-mix(in srgb, var(--color-crisis) 20%, transparent)', borderRadius: 'var(--radius-md)', marginBottom: 16 }}>
                    <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--color-crisis)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Message that will be sent to author</div>
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-primary)', lineHeight: 1.6 }}>
                      {peerRemovalReason === 'HATE_SLUR' && "This community doesn't allow language that targets people's race, Indigeneity, gender, sexuality, disability, or other identities. Your post was removed because the words used harm people in this space."}
                      {peerRemovalReason === 'STRUCTURAL_HARM_SLUR' && "You're naming something real about how power and stigma show up in care. This version of your post used words that land as slurs here, so it was removed. If you want to, you can share the experience again in your own words without using that language."}
                      {peerRemovalReason === 'PRIVACY_RISK' && "This post included details that could make it easier to identify you or someone else outside this app. It was removed to reduce that risk. You're welcome to post again without names, photos, or details that could pinpoint where you live, study, or get care."}
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 10 }}>
                  {/* eslint-disable-next-line cw/no-inline-button-style -- post-removal modal Cancel chip; ghost outlined 2-col flex pair, not a chamfered .btn variant */}
                  <button onClick={() => { setPeerRemovalModal(null); setPeerRemovalReason(null); }} style={{ flex: 1, padding: '12px', borderRadius: 'var(--radius-md)', border: '1px solid color-mix(in srgb, var(--color-text-primary) 15%, transparent)', background: 'transparent', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                  {/* eslint-disable-next-line cw/no-inline-button-style -- post-removal modal Confirm CTA; warm-toned filled primary in 2-col flex pair, not a chamfered .btn variant */}
                  <button
                    disabled={!peerRemovalReason}
                    onClick={async () => {
                      if (!peerRemovalReason || !authToken) return;
                      try {
                        await fetch(`${API_BASE}/admin/circle/posts/${peerRemovalModal.postId}/remove`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
                          body: JSON.stringify({ reason: peerRemovalReason, authorParticipantId: currentUser?.participantId || '' }),
                        });
                      } catch { /* error handled silently */ }
                      setPeerRemovedPosts(prev => { const next = new Set(prev); next.add(peerRemovalModal.postId); return next; });
                      setPeerRemovalConfirmed(true);
                    }}
                    style={{ flex: 2, padding: '12px', borderRadius: 'var(--radius-md)', border: 'none', background: peerRemovalReason ? 'var(--color-crisis)' : 'color-mix(in srgb, var(--color-crisis) 30%, transparent)', color: '#fff', fontSize: 'var(--fs-sm)', fontWeight: 700, cursor: peerRemovalReason ? 'pointer' : 'not-allowed', transition: 'all 0.2s' }}
                  >
                    Confirm removal
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ textAlign: 'center', padding: '16px 0 20px' }}>
                  <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}><IconCheck size={56} ariaLabel="" /></div>
                  <div style={{ fontWeight: 700, fontSize: 'var(--fs-base-plus)', marginBottom: 8 }}>Post removed</div>
                  <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>The post has been removed and the author has been notified with the reason.</div>
                </div>
                <button onClick={() => { setPeerRemovalModal(null); setPeerRemovalReason(null); setPeerRemovalConfirmed(false); }} className="btn btn-primary">Done</button>
              </>
            )}
          </div>
        </>
      )}

      {/* Circle Thread Panel */}
      {circleThreadPost && (
        <>
          <div role="presentation" onClick={() => setCircleThreadPost(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 'var(--z-dialog-backdrop)', backdropFilter: 'blur(4px)' }} />
          <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 'var(--z-dialog)', background: 'var(--color-bg-card)', borderRadius: '20px 20px 0 0', maxHeight: '90dvh', display: 'flex', flexDirection: 'column', paddingBottom: 'max(0px, env(safe-area-inset-bottom))' }}>
            <div style={{ padding: '16px 20px 0', flexShrink: 0 }}>
              <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
                <div style={{ width: 36, height: 4, borderRadius: 2, background: 'color-mix(in srgb, var(--color-text-primary) 8%, transparent)' }} />
                {/* eslint-disable-next-line cw/no-inline-button-style -- Circle thread panel close X; absolute icon-only inside drag-handle header row, no .btn-icon-only primitive */}
                <button onClick={() => setCircleThreadPost(null)} style={{ position: 'absolute', right: 0, top: -8, background: 'none', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', lineHeight: 1, padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IconClose size={18} ariaLabel="Close" /></button>
              </div>
              <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--color-text-secondary)', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 10 }}>Thread</div>
              {/* Original post */}
              <div style={{ background: 'var(--color-bg-surface)', borderRadius: 'var(--radius-md)', padding: '12px 14px', marginBottom: 12, border: '1px solid color-mix(in srgb, var(--color-accent) 20%, transparent)', borderLeft: '3px solid var(--color-accent)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 'var(--fs-lg)' }}>{circleThreadPost.avatarEmoji}</span>
                  <span style={{ fontWeight: 700, fontSize: 'var(--fs-sm)' }}>{circleThreadPost.handle}</span>
                  <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginLeft: 'auto' }}>{circleThreadPost.time}</span>
                </div>
                <p style={{ fontSize: 'var(--fs-sm)', lineHeight: 1.6, margin: 0, color: 'var(--color-text-primary)' }}>{circleThreadPost.text}</p>
              </div>
              <div style={{ borderTop: '1px solid color-mix(in srgb, var(--color-text-primary) 6%, transparent)', marginBottom: 10 }} />
            </div>
            {/* Replies list */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px' }}>
              {circleThreadLoading ? (
                <div style={{ textAlign: 'center', padding: '20px', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)' }}>Pulling replies…</div>
              ) : circleThreadReplies.filter(r => !circleThreadRemovedReplies.has(r.id)).length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)' }}>No replies yet — be the first to respond.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: 8 }}>
                  {circleThreadReplies.filter(r => !circleThreadRemovedReplies.has(r.id)).map(reply => (
                    <div key={reply.id} style={{ background: 'var(--color-bg-surface)', borderRadius: 'var(--radius-md)', padding: '10px 12px', border: '1px solid color-mix(in srgb, var(--color-text-primary) 6%, transparent)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
                        <span style={{ fontSize: 'var(--fs-md)' }}>{reply.avatarEmoji}</span>
                        <span style={{ fontWeight: 600, fontSize: 'var(--fs-xs)' }}>{reply.handle}</span>
                        {reply.isMod && <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--color-success)', background: 'color-mix(in srgb, var(--color-success) 15%, transparent)', borderRadius: 'var(--radius-sm)', padding: '1px 5px', letterSpacing: '0.05em' }}>MOD</span>}
                        <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginLeft: 'auto' }}>{circleTimeAgo(reply.createdAt)}</span>
                        {!(currentUser?.isAdmin || currentUser?.isModerator) && (
                          circleThreadReportedReplies.has(reply.id) ? (
                            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', padding: '2px 7px', flexShrink: 0 }}>Reported</span>
                          ) : (
                            // eslint-disable-next-line cw/no-inline-button-style -- Circle thread reply Report text-link; ghost-only tiny chip in reply header row, not a chamfered .btn variant
                            <button
                              onClick={async () => {
                                if (!authToken) return;
                                try {
                                  const res = await fetch(`${API_BASE}/circle/replies/${reply.id}/report`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
                                    body: JSON.stringify({ reason: 'INAPPROPRIATE' }),
                                  });
                                  if (res.ok) {
                                    setCircleThreadReportedReplies(prev => { const next = new Set(prev); next.add(reply.id); return next; });
                                  }
                                } catch { /* error handled silently */ }
                              }}
                              style={{ background: 'none', border: 'none', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-xs)', cursor: 'pointer', padding: '2px 7px', flexShrink: 0 }}
                              title="Report this reply"
                            >Report</button>
                          )
                        )}
                        {(currentUser?.isAdmin || currentUser?.isModerator) && (
                          // eslint-disable-next-line cw/no-inline-button-style -- Circle thread reply moderator-only Remove chip; warm-tinted small chip in reply header row, not a chamfered .btn variant
                          <button
                            onClick={async () => {
                              if (!authToken) return;
                              try {
                                const res = await fetch(`${API_BASE}/admin/circle/replies/${reply.id}/remove`, {
                                  method: 'DELETE',
                                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
                                  body: JSON.stringify({ reason: 'PRIVACY_RISK' }),
                                });
                                if (res.ok) {
                                  setCircleThreadRemovedReplies(prev => { const next = new Set(prev); next.add(reply.id); return next; });
                                  setCircleThreadReplyCounts(prev => ({ ...prev, [circleThreadPost.id]: Math.max(0, (prev[circleThreadPost.id] ?? 1) - 1) }));
                                }
                              } catch { /* error handled silently */ }
                            }}
                            style={{ background: 'color-mix(in srgb, var(--color-crisis) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--color-crisis) 25%, transparent)', borderRadius: 'var(--radius-sm)', color: 'var(--color-crisis)', fontSize: 'var(--fs-xs)', fontWeight: 600, padding: '2px 7px', cursor: 'pointer', flexShrink: 0 }}
                          >Remove</button>
                        )}
                      </div>
                      <p style={{ fontSize: 'var(--fs-sm)', lineHeight: 1.55, margin: 0, color: 'var(--color-text-primary)' }}>{reply.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Reply composer */}
            <div style={{ flexShrink: 0, padding: '12px 20px 20px', borderTop: '1px solid color-mix(in srgb, var(--color-text-primary) 6%, transparent)', paddingBottom: 'max(20px, env(safe-area-inset-bottom))' }}>
              {circleThreadReplyError && (
                <div style={{ padding: '8px 12px', background: 'color-mix(in srgb, var(--color-crisis) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--color-crisis) 20%, transparent)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--fs-xs)', color: 'var(--color-crisis)', marginBottom: 8 }}>{circleThreadReplyError}</div>
              )}
              {!circleProfile ? (
                <div style={{ textAlign: 'center', padding: '10px', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-xs)' }}>Set up a Circle identity to reply.</div>
              ) : (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <textarea
                    value={circleThreadReplyText}
                    onChange={e => { setCircleThreadReplyText(e.target.value); setCircleThreadReplyError(null); }}
                    placeholder="Add a reply…"
                    aria-label="Add a reply"
                    rows={2}
                    maxLength={500}
                    style={{ flex: 1, background: 'var(--color-bg-surface)', border: '1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)', borderRadius: 'var(--radius-md)', padding: '10px 12px', color: 'var(--color-text-primary)', fontSize: 'var(--fs-sm)', resize: 'none', outline: 'none', fontFamily: 'inherit', lineHeight: 1.5 }}
                  />
                  {/* eslint-disable-next-line cw/no-inline-button-style -- Circle thread Reply send chip paired with textarea; solid accent when enabled, muted when empty, not a chamfered .btn variant */}
                  <button
                    disabled={circleThreadReplyLoading || !circleThreadReplyText.trim()}
                    onClick={submitCircleReply}
                    style={{ padding: '10px 14px', borderRadius: 'var(--radius-md)', border: 'none', background: circleThreadReplyText.trim() ? 'var(--color-accent)' : 'color-mix(in srgb, var(--color-accent) 20%, transparent)', color: 'var(--color-on-accent)', fontSize: 'var(--fs-sm)', fontWeight: 700, cursor: circleThreadReplyText.trim() && !circleThreadReplyLoading ? 'pointer' : 'not-allowed', opacity: circleThreadReplyLoading ? 0.6 : 1, flexShrink: 0 }}
                  >{circleThreadReplyLoading ? '…' : 'Reply'}</button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Circle Emoji Avatar Picker Modal */}
      {showCircleEmojiPicker && (
        <>
          <div role="presentation" onClick={() => setShowCircleEmojiPicker(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 'var(--z-dialog-backdrop)', backdropFilter: 'blur(4px)' }} />
          <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 'var(--z-dialog)', background: 'var(--color-bg-card)', borderRadius: '20px 20px 0 0', padding: '20px 20px 32px', paddingBottom: 'max(32px, env(safe-area-inset-bottom))', maxHeight: '85dvh', overflowY: 'auto' }}>
            <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: 'color-mix(in srgb, var(--color-text-primary) 8%, transparent)' }} />
              {/* eslint-disable-next-line cw/no-inline-button-style -- Circle emoji-avatar picker modal close X; absolute icon-only inside drag-handle header row, no .btn-icon-only primitive */}
              <button onClick={() => setShowCircleEmojiPicker(false)} style={{ position: 'absolute', right: 0, top: -8, background: 'none', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', lineHeight: 1, padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IconClose size={18} ariaLabel="Close" /></button>
            </div>
            <div style={{ fontWeight: 700, fontSize: 'var(--fs-base-plus)', marginBottom: 4 }}>Your Circle emoji avatar</div>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginBottom: 16 }}>Pick an emoji that feels like you here. No faces, no flags.</div>
            <CircleAvatarPicker
              selected={circleEmojiDraft}
              onChange={setCircleEmojiDraft}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              {/* eslint-disable-next-line cw/no-inline-button-style -- emoji-picker modal Cancel; ghost outlined in 2-col flex pair, not a chamfered .btn variant */}
              <button
                onClick={() => setShowCircleEmojiPicker(false)}
                style={{ flex: 1, padding: '12px', borderRadius: 'var(--radius-md)', border: '1.5px solid color-mix(in srgb, var(--color-text-primary) 15%, transparent)', background: 'var(--color-bg-surface)', color: 'var(--color-text-secondary)', fontWeight: 600, fontSize: 'var(--fs-sm)', cursor: 'pointer' }}
              >
                Cancel
              </button>
              {/* eslint-disable-next-line cw/no-inline-button-style -- emoji-picker modal Save CTA; brand-gradient primary in 2-col flex pair, parked older gradient family pre-dating chamfer */}
              <button
                onClick={async () => {
                  if (!authToken) return;
                  await updateProfile(authToken, { avatarEmoji: circleEmojiDraft || null });
                  setCurrentUser(prev => prev ? { ...prev, avatarEmoji: circleEmojiDraft || null } : null);
                  setShowCircleEmojiPicker(false);
                }}
                style={{ flex: 2, padding: '12px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--color-accent)', color: '#fff', fontWeight: 700, fontSize: 'var(--fs-sm)', cursor: 'pointer' }}
              >
                Save emoji avatar
              </button>
            </div>
          </div>
        </>
      )}

      {/* Researcher Dashboard — access denied */}
      {screen === 'researcher' && authToken && !currentUser?.isAdmin && (
        <div className="screen" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <div style={{ textAlign: 'center', maxWidth: 360, padding: '0 24px' }}>
            <img src="/careweave-net-icon.svg" alt="" width="48" height="48" style={{ display: 'inline-block', marginBottom: 16, opacity: 0.5 }} />
            <h2 style={{ fontSize: 'var(--fs-lg)', fontWeight: 700, marginBottom: 8 }}>Access required</h2>
            <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', marginBottom: 20 }}>
              The Researcher Dashboard is restricted. Please contact the development team for access.
            </p>
            <button className="btn btn-primary btn-auto" onClick={() => showScreen('home')}>
              <span>Back to app</span>
            </button>
          </div>
        </div>
      )}

      {/* Researcher Dashboard */}
      {screen === 'researcher' && authToken && currentUser?.isAdmin && (
        <div className="admin-theme">
          <AdminHeader title="Researcher Dashboard" email={currentUser?.email ?? ''} onSwitchView={() => setAuthFlow('mode-chooser')} onSignOut={() => { clearToken(); setAuthToken(null); setCurrentUser(null); setAuthFlow('login'); }} />
          <ResearcherDashboard token={authToken} />
        </div>
      )}

      {/* Moderation Dashboard — now redirects to unified ModeratorDashboard */}
      {screen === 'moderation' && authToken && (currentUser?.isAdmin || currentUser?.isModerator) && (
        <div className="admin-theme">
          <AdminHeader title="Moderation" email={currentUser?.email ?? ''} onSwitchView={() => setAuthFlow('mode-chooser')} onSignOut={() => { clearToken(); setAuthToken(null); setCurrentUser(null); setAuthFlow('login'); }} />
          <ModeratorDashboard
            token={authToken}
            userName={currentUser?.displayName ?? currentUser?.email ?? 'Moderator'}
            isAdmin={currentUser?.isAdmin ?? false}
            onSwitchView={() => setAuthFlow('mode-chooser')}
            onSignOut={() => { clearToken(); setAuthToken(null); setCurrentUser(null); setAuthFlow('login'); }}
          />
        </div>
      )}

      {/* Moderator Dashboard */}
      {screen === 'moderator' && authToken && (currentUser?.isAdmin || currentUser?.isModerator) && (
        <div className="admin-theme">
          <AdminHeader title="Moderator Dashboard" email={currentUser?.email ?? ''} onSwitchView={() => setAuthFlow('mode-chooser')} onSignOut={() => { clearToken(); setAuthToken(null); setCurrentUser(null); setAuthFlow('login'); }} />
          <ModeratorDashboard
            token={authToken}
            userName={currentUser?.displayName ?? currentUser?.email ?? 'Moderator'}
            isAdmin={currentUser?.isAdmin ?? false}
            onSwitchView={() => setAuthFlow('mode-chooser')}
            onSignOut={() => {
              clearToken();
              setAuthToken(null);
              setCurrentUser(null);
              setAuthFlow('login');
            }}
          />
        </div>
      )}

      {/* Content Manager */}
      {screen === 'content-manager' && authToken && currentUser?.isAdmin && (
        <div className="admin-theme">
          <Suspense fallback={<div style={{ padding: 32, textAlign: 'center' }}>Loading...</div>}>
            <ContentManager onBack={() => showScreen('home')} />
          </Suspense>
        </div>
      )}

      {/* Left-side drawer overlay */}
      <div
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 'var(--z-overlay)',
          backdropFilter: 'blur(2px)',
          opacity: menuOpen ? 1 : 0,
          pointerEvents: menuOpen ? 'auto' : 'none',
          transition: 'opacity 0.3s ease',
        }}
      />

      {/* Left-side drawer panel */}
      <nav aria-label="Main menu" style={{
        position: 'fixed', top: 0, bottom: 0, left: 0, zIndex: 'var(--z-drawer)',
        width: '82vw', maxWidth: 340,
        background: 'var(--color-bg-card)',
        boxShadow: menuOpen ? '4px 0 32px rgba(0,0,0,0.5)' : 'none',
        transform: menuOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.32s cubic-bezier(0.32,0.72,0,1)',
        display: 'flex', flexDirection: 'column',
        overflowY: 'auto',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}>

        {/* Profile header */}
        <div style={{ padding: '24px 20px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <div
            style={{ position: 'relative', cursor: 'pointer', display: 'inline-block' }}
            onClick={() => { showScreen('profile'); setMenuOpen(false); }}
            role="button"
            aria-label="Edit profile"
          >
            <GuideBubble
              avatarId={userAvatarId}
              size={72}
              showGlow={false}
              customisation={userCustomisation}
              ariaLabel="Your Crystal Guide"
            />
            <div style={{
              position: 'absolute', bottom: 0, right: 0,
              width: 24, height: 24, borderRadius: '50%',
              background: 'var(--color-accent)',
              border: '2px solid var(--color-bg-card)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 'var(--fs-2xs)', color: '#fff',
            }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: 'var(--fs-base)', color: 'var(--color-text-primary)' }}>{userDisplayName}</div>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginTop: 2, display: 'inline-flex', alignItems: 'center', gap: 4 }}>{(() => { const LB = CRYSTAL_ICONS[`level-${Math.min(7, Math.max(1, levelInfo.level))}` as CrystalIconKey]; return LB ? <LB size={14} ariaLabel="" /> : null; })()} Level {levelInfo.level} · {levelInfo.name}</div>
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--color-border)', margin: '0 16px 4px' }} />

        {/* Settings & support */}
        <div style={{ padding: '4px 0', flex: 1 }}>
          {[
            { label: 'My profile', action: () => { showScreen('profile'); setMenuOpen(false); } },
            { label: 'Crystal Guide', action: () => { showScreen('crystal-guide'); setMenuOpen(false); } },
            { label: 'Notifications', action: () => { openNotificationPanel(); setMenuOpen(false); } },
            { label: 'Accessibility', action: () => { setSettingsPanelOpen(true); setMenuOpen(false); } },
            { label: 'Privacy policy', action: () => { showScreen('privacy-policy'); setMenuOpen(false); } },
          ].map(item => (
            // eslint-disable-next-line cw/no-inline-button-style -- side-drawer settings menu row; full-width disclosure with chevron + borderBottom divider, not a chamfered .btn variant
            <button
              key={item.label}
              onClick={item.action}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', cursor: 'pointer', width: '100%', background: 'none', border: 'none', borderBottom: '1px solid var(--color-border)', textAlign: 'left', fontFamily: 'inherit', color: 'var(--color-text-primary)', fontSize: 'var(--fs-sm-plus)', fontWeight: 500 }}
            >
              {item.label}
              <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)', opacity: 0.5 }} aria-hidden="true">›</span>
            </button>
          ))}
        </div>

        <div style={{ borderTop: '1px solid var(--color-border)', margin: '0 16px' }} />

        {/* Support */}
        <div style={{ padding: '4px 0' }}>
          {[
            { label: 'FAQ', action: () => { setFaqPanelOpen(true); setMenuOpen(false); } },
            { label: 'Contact support', action: () => { setContactSupportPanelOpen(true); setMenuOpen(false); } },
          ].filter(item => !(isDemoVisit() && item.label === 'Contact support')).map(item => (
            // eslint-disable-next-line cw/no-inline-button-style -- side-drawer support menu row; full-width disclosure with chevron + borderBottom divider, not a chamfered .btn variant
            <button
              key={item.label}
              onClick={item.action}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', cursor: 'pointer', width: '100%', background: 'none', border: 'none', borderBottom: '1px solid var(--color-border)', textAlign: 'left', fontFamily: 'inherit', color: 'var(--color-text-primary)', fontSize: 'var(--fs-sm-plus)', fontWeight: 500 }}
            >
              {item.label}
              <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)', opacity: 0.5 }} aria-hidden="true">›</span>
            </button>
          ))}
        </div>

        {/* Log out */}
        <div style={{ padding: '4px 20px 8px' }}>
          {/* eslint-disable-next-line cw/no-inline-button-style -- side-drawer log-out row; subdued ghost text inside drawer footer, not a chamfered .btn variant */}
          <button
            onClick={() => { if (isDemoVisit()) { exitDemo(); return; } clearToken(); setCurrentUser(null); setAuthToken(null); setAuthFlow('splash'); setMenuOpen(false); }}
            style={{ display: 'flex', alignItems: 'center', padding: '14px 0', cursor: 'pointer', width: '100%', background: 'none', border: 'none', textAlign: 'left', fontFamily: 'inherit', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)', fontWeight: 500 }}
          >
            {isDemoVisit() ? 'Exit demo' : 'Log out'}
          </button>
        </div>

        {/* Crisis lines — always pinned at bottom */}
        <div style={{ flexShrink: 0, margin: '0 16px 12px', borderRadius: 'var(--radius-md)', background: 'var(--color-accent-light)', border: '1px solid var(--color-border)', overflow: 'hidden' }}>
          {/* eslint-disable-next-line cw/no-inline-button-style -- side-drawer crisis-lines pinned trigger; full-width composite row inside accent-tinted card pinned to drawer bottom, not a chamfered .btn variant */}
          <button
            onClick={() => { setShowCrisisBanner(true); setMenuOpen(false); }}
            style={{ width: '100%', background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', cursor: 'pointer', textAlign: 'left' }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm)', color: 'var(--color-text-primary)' }}>Crisis and support lines</div>
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginTop: 2 }}>911 · 988 · Kids Help Phone</div>
            </div>
            <span style={{ color: 'var(--color-accent)', fontSize: 'var(--fs-base-plus)', fontWeight: 600 }} aria-hidden="true">›</span>
          </button>
        </div>

      </nav>

      <InstallPrompt />

      <div className={`crisis-banner ${showCrisisBanner ? 'crisis-banner--visible' : ''}`}>
        <div className="crisis-banner-header">
          <span className="crisis-banner-title" style={{ display:'inline-flex', alignItems:'center', gap:6 }}><IconPhone size={16} ariaLabel="" /> Crisis Lines</span>
          <button className="crisis-banner-close" onClick={() => setShowCrisisBanner(false)} aria-label="Close crisis lines"><IconClose size={16} ariaLabel="" /></button>
        </div>
        <p className="crisis-banner-msg">Call 911 if you or someone you know is in immediate danger or needs urgent medical care.</p>
        <a href="tel:911" className="crisis-banner-btn crisis-btn-911" style={{ display:'inline-flex', alignItems:'center', gap:6 }}><IconAlertSiren size={16} ariaLabel="" /> Call 911 — Emergency</a>
        <p className="crisis-banner-msg">Call 988 if you or someone you know is thinking about suicide.</p>
        <a href="tel:988" className="crisis-banner-btn crisis-btn-988" style={{ display:'inline-flex', alignItems:'center', gap:6 }}><IconHeart size={16} ariaLabel="" heartTint="var(--color-info)" /> Call 988 — Suicide Crisis</a>
        <p className="crisis-banner-msg" style={{ marginTop: 10 }}>Kids Help Phone — free, 24/7 confidential support for youth.</p>
        <a href="tel:18006686868" className="crisis-banner-btn crisis-btn-khp" style={{ display:'inline-flex', alignItems:'center', gap:6 }}><IconHeart size={16} ariaLabel="" heartTint="var(--color-warning)" /> Call 1-800-668-6868 — Kids Help Phone</a>
        <a href="sms:686868?body=CONNECT" className="crisis-banner-btn crisis-btn-khp" style={{ display:'inline-flex', alignItems:'center', gap:6 }}><IconSpeechBubble size={16} ariaLabel="" /> Text CONNECT to 686868</a>
        <p className="crisis-banner-msg" style={{ marginTop: 10 }}>Hope for Wellness Help Line — free, 24/7 Indigenous crisis support.</p>
        <a href="tel:18552423310" className="crisis-banner-btn crisis-btn-indigenous" style={{ display:'inline-flex', alignItems:'center', gap:6 }}><IconGrowthSprout size={16} ariaLabel="" /> Call 1-855-242-3310 — Indigenous Healing</a>
      </div>



      {/* Hide bottom nav on admin screens */}
      {screen !== 'researcher' && screen !== 'moderator' && screen !== 'moderation' && screen !== 'content-manager' && (
        <nav className="bottom-nav" aria-label="Main navigation">
          {!crisisNoteDismissed && (
            <div className="crisis-safety-note" role="note">
              If you or someone you know is in danger or harm, call 911
              <button
                type="button"
                onClick={dismissCrisisNote}
                aria-label="Hide this note"
                className="crisis-safety-close"
              >×</button>
            </div>
          )}
          <div className="bottom-nav-items">
            {([
              ['home', 'home', 'Home'],
              ['tools', 'tools', 'Tools'],
              ['patterns', 'chart', 'Patterns'],
              ['growth', 'growth-sprout', 'Growth'],
              ['peer-connect', 'circle', 'Circle'],
            ] as [Screen, CrystalIconKey, string][]).map(([s, iconKey, label]) => {
              const Icon = CRYSTAL_ICONS[iconKey];
              return (
                <button
                  key={s}
                  className={`bottom-nav-item ${screen === s ? 'active' : ''}`}
                  aria-current={screen === s ? 'page' : undefined}
                  aria-label={label}
                  onClick={() => showScreen(s as Screen)}
                >
                  <span className="bottom-nav-icon" aria-hidden="true"><Icon size={24} ariaLabel="" /></span>
                </button>
              );
            })}
          </div>
        </nav>
      )}

      {activeModal && (
        <div className="modal-overlay active" role="dialog" aria-modal="true" aria-label="Dialog" onClick={(e) => { if ((e.target as HTMLElement).classList.contains('modal-overlay')) closeModal(); }}>
          <div className="modal">
            {activeModal === 'ed-script' && (
              <>
                <div className="modal-header"><h3 style={{ display:'inline-flex', alignItems:'center', gap:8 }}><IconAdvocacy size={22} ariaLabel="" /> Crisis advocacy script</h3><button className="modal-close" onClick={closeModal} aria-label="Close dialog">×</button></div>
                <div className="modal-body">
                  <p>This script helps you communicate clearly during crisis visits when you feel your pain isn't being believed.</p>
                  <h4>Template Script</h4>
                  <div className="script-template"><p>"I have sickle cell disease and I'm having a pain crisis. My baseline pain management plan includes [medication and dose]. My pain is currently [X/10]. I've tried [home measures] and they haven't helped. I need appropriate pain management as outlined in my care plan."</p></div>
                  <h4>Customize Your Version</h4>
                  <div className="script-editor">
                    <textarea value={edScript} onChange={e => setEdScript(e.target.value)} rows={6} aria-label="Customize your crisis script" style={{ width: '100%', background: 'var(--color-bg-elevated)', border: '1px solid var(--color-bg-surface)', borderRadius: 'var(--radius-sm)', color: 'var(--color-text-primary)', fontSize: 'var(--fs-sm)', padding: 12, resize: 'vertical', fontFamily: 'inherit' }} />
                  </div>
                  <h4>Key Phrases to Remember</h4>
                  <ul>
                    <li>"This is my baseline treatment that works"</li>
                    <li>"I'm not asking for your judgment, I'm asking for care"</li>
                    <li>"I'd like to speak with the charge nurse/patient advocate"</li>
                    <li>"Please document that I requested this in my chart"</li>
                  </ul>
                  <div className="btn-group">
                    <button className="btn btn-secondary" onClick={closeModal}>Close</button>
                    <button className="btn btn-secondary" onClick={() => { closeModal(); openModal('ed-report'); }}>View Report</button>
                    {/* eslint-disable-next-line cw/no-inline-button-style -- inline .btn-primary display:inline-flex override on ed-script modal "Copy script" CTA (for icon + label); rule flags any style on button regardless of className */}
                    <button className="btn btn-primary" onClick={() => copyScript('ed')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{copySuccess ? <><IconCheck size={14} ariaLabel="" /> Copied!</> : 'Copy script'}</button>
                  </div>
                </div>
              </>
            )}

            {activeModal === 'ed-report' && (
              <>
                <div className="modal-header"><h3 style={{ display:'inline-flex', alignItems:'center', gap:8 }}><IconAlertSiren size={22} ariaLabel="" /> Crisis report preview</h3><button className="modal-close" onClick={closeModal} aria-label="Close dialog">×</button></div>
                <div className="modal-body">
                  <h4>Your Crisis Advocacy Script</h4>
                  <div className="script-template"><p style={{ fontFamily: 'inherit', fontSize: 'var(--fs-sm)' }}>{edScript || 'No script saved yet. Create one from Articles.'}</p></div>

                  <h4 style={{ display:'inline-flex', alignItems:'center', gap:6 }}><IconMedications size={18} ariaLabel="" /> Current Medications</h4>
                  {medications.filter(m => m.active).length === 0 ? (
                    <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', fontStyle: 'italic', marginBottom: 12 }}>
                      No medications added yet. <span style={{ color: 'var(--color-accent)', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => { closeModal(); showScreen('medications'); }}>Add your medications</span> so providers can see them in this report.
                    </p>
                  ) : (
                    <div style={{ marginBottom: 14 }}>
                      {medications.filter(m => m.active).map(med => (
                        <div key={med.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'var(--color-bg-surface)', borderRadius: 'var(--radius-sm)', marginBottom: 6, borderLeft: '3px solid var(--color-accent)' }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }}>{med.name}</div>
                            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)' }}>{med.dose} · {med.frequency}</div>
                          </div>
                          {med.notes && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', maxWidth: 120, textAlign: 'right' }}>{med.notes}</div>}
                        </div>
                      ))}
                    </div>
                  )}

                  <h4>Today's Crisis Snapshots</h4>
                  {edCheckins.length === 0 ? (
                    <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                      {edModeState === 'active' ? 'No check-ins logged yet this visit.' : 'Crisis Mode is not active. No check-ins recorded.'}
                    </p>
                  ) : (
                    <div>
                      {edCheckins.map((c, i) => {
                        const t = new Date(c.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                        return (
                          <div key={c.id} style={{ background: 'var(--color-bg-surface)', borderRadius: 'var(--radius-sm)', padding: 12, marginBottom: 10, borderLeft: '3px solid var(--color-danger)' }}>
                            <div style={{ fontWeight: 600, fontSize: 'var(--fs-sm)', marginBottom: 6 }}>Check-in {i + 1} — {t}</div>
                            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                              <div>VOC: <span style={{ color: 'var(--color-text-primary)' }}>{c.vocStatus || 'Not specified'}</span></div>
                              <div>Pain: <span style={{ color: 'var(--color-danger)', fontWeight: 600 }}>{c.painLevel}/10</span></div>
                              {c.bodyAreas.length > 0 && <div>Areas: {c.bodyAreas.map(a => `${BODY_AREA_NAMES[a.area] || a.area} (${INTENSITY_LABELS[a.intensity]})`).join(', ')}</div>}
                              {c.safetyFlags.length > 0 && <div style={{ color: 'var(--color-warning)', display:'inline-flex', alignItems:'center', gap:6 }}><IconWarning size={14} ariaLabel="" /> Flags: {c.safetyFlags.join(', ')}</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <h4>Pattern Highlights</h4>
                  <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)' }}>Recent stigma scores: 8.2/10 average during crisis visits</p>
                  <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)' }}>Common tags: Drug-seeking, Security called, Race/Racism</p>

                  <h4>What I want this team to know</h4>
                  <textarea value={edReportNote} onChange={e => setEdReportNote(e.target.value)} placeholder="Add any notes for your care team..." aria-label="Notes for your care team" rows={4} style={{ width: '100%', background: 'var(--color-bg-elevated)', border: '1px solid var(--color-bg-surface)', borderRadius: 'var(--radius-sm)', color: 'var(--color-text-primary)', fontSize: 'var(--fs-sm)', padding: 12, resize: 'vertical', fontFamily: 'inherit' }} />
                  <div className="btn-group" style={{ marginTop: 16 }}>
                    <button className="btn btn-secondary" onClick={closeModal}>Close</button>
                    {/* eslint-disable-next-line cw/no-inline-button-style -- inline .btn-secondary display:inline-flex override on ed-report modal "Copy full report" CTA (for icon + label); rule flags any style on button regardless of className */}
                    <button className="btn btn-secondary" onClick={copyEDReport} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{copySuccess ? <><IconCheck size={14} ariaLabel="" /> Copied!</> : 'Copy full report'}</button>
                    <button className="btn btn-primary" onClick={downloadEDReportPDF} disabled={pdfLoading}>{pdfLoading ? 'Generating your report…' : 'Download PDF'}</button>
                  </div>
                </div>
              </>
            )}

            {activeModal === 'grounding-54321' && (
              <>
                <div className="modal-header"><h3 style={{ display:'inline-flex', alignItems:'center', gap:8 }}><IconGrounding size={22} ariaLabel="" /> 5-4-3-2-1 grounding</h3><button className="modal-close" onClick={closeModal} aria-label="Close dialog">×</button></div>
                <div className="modal-body">
                  <p>This sensory technique helps bring you back to the present moment when feeling overwhelmed.</p>
                  {[['5 things you can SEE', 'Look around and name 5 things. Colors, objects, textures.'], ['4 things you can TOUCH', 'Notice textures. Your clothes, chair, floor, a nearby surface.'], ['3 things you can HEAR', 'Focus on sounds. Background noise, breathing, distant voices.'], ['2 things you can SMELL', 'What scents are present? Or imagine comforting smells.'], ['1 thing you can TASTE', 'Notice any taste in your mouth, or take a sip of water.']].map(([t, d]) => (
                    <div key={t} className="script-template"><p><strong>{t}</strong><br />{d}</p></div>
                  ))}
                  <button className="btn btn-primary" onClick={closeModal}>Got It</button>
                </div>
              </>
            )}

            {activeModal === 'breathing' && (
              <>
                <div className="modal-header"><h3 style={{ display:'inline-flex', alignItems:'center', gap:8 }}><IconGrounding size={22} ariaLabel="" /> Box breathing</h3><button className="modal-close" onClick={closeModal} aria-label="Close dialog">×</button></div>
                <div className="modal-body">
                  <p>Calm your nervous system with structured breathing. Breathe in for 4, hold for 4, out for 4, hold for 4.</p>
                  <div className="script-template"><p><strong>Inhale</strong> — slowly breathe in through your nose, counting to 4.</p></div>
                  <div className="script-template"><p><strong>Hold</strong> — hold your breath for a count of 4.</p></div>
                  <div className="script-template"><p><strong>Exhale</strong> — slowly breathe out through your mouth for 4 counts.</p></div>
                  <div className="script-template"><p><strong>Hold</strong> — hold for 4 counts before the next breath.</p></div>
                  <button className="btn btn-primary" onClick={closeModal}>Got It</button>
                </div>
              </>
            )}

            {activeModal === 'story-maya' && (
              <>
                <div className="modal-header"><h3 style={{ display:'inline-flex', alignItems:'center', gap:8 }}><IconBook size={22} ariaLabel="" /> Maya's crisis story</h3><button className="modal-close" onClick={closeModal} aria-label="Close dialog">×</button></div>
                <div className="modal-body">
                  <p><em>Maya is 22, lives in Toronto, and has HbSS sickle cell disease. She's had over 30 crisis visits in the past 5 years.</em></p>
                  <h4>The Pattern I Saw</h4>
                  <p>For years, I thought the problem was me. I'd show up in crisis pain—8, 9, sometimes 10 out of 10—and I'd get treated like I was drug-seeking. Security watching me. Long waits. Nurses who wouldn't make eye contact.</p>
                  <p>Then I started tracking my crisis visits. I logged every interaction, every wait time, every comment. And I saw it: the pattern wasn't about me. It was about <em>them</em>. It was racism and stigma, not my behaviour.</p>
                  <h4>What Changed</h4>
                  <p>Once I had data, I had power. I brought my logs to my hematologist. We wrote a crisis plan together. I stopped apologizing. I started saying "I need appropriate care for my sickle cell pain."</p>
                  <h4>What I'd Tell You</h4>
                  <ul><li>Track everything. Dates, times, who said what, how long you waited.</li><li>Bring someone with you if you can. Witnesses change behaviour.</li><li>Know your rights. You have the right to pain management.</li><li>It's not your fault. Stigma is a system problem, not a you problem.</li></ul>
                  <p><strong>You deserve care. Full stop.</strong></p>
                  <button className="btn btn-primary" onClick={closeModal}>Close</button>
                </div>
              </>
            )}

            {activeModal === 'story-jamal' && (
              <>
                <div className="modal-header"><h3 style={{ display:'inline-flex', alignItems:'center', gap:8 }}><IconBook size={22} ariaLabel="" /> Jamal's transition journey</h3><button className="modal-close" onClick={closeModal} aria-label="Close dialog">×</button></div>
                <div className="modal-body">
                  <p><em>Jamal is 24, recently moved from pediatric to adult sickle cell care. The transition was one of the hardest things he's navigated.</em></p>
                  <h4>The Gap</h4>
                  <p>When I turned 18, my pediatric hematologist basically handed me a referral letter and said goodbye. The adult care system felt completely different—less hand-holding, more paperwork, harder to get appointments.</p>
                  <h4>What Helped</h4>
                  <p>I asked my pediatric team to write a full transition summary. I requested copies of all my records. I brought a parent to my first adult appointment. And I found a community of young adults with SCD online who shared what to expect.</p>
                  <button className="btn btn-primary" onClick={closeModal}>Close</button>
                </div>
              </>
            )}

            {activeModal === 'racial-justice' && (
              <>
                <div className="modal-header"><h3 style={{ display:'inline-flex', alignItems:'center', gap:8 }}><IconSolidarity size={22} ariaLabel="" /> Racial justice & SCD</h3><button className="modal-close" onClick={closeModal} aria-label="Close dialog">×</button></div>
                <div className="modal-body">
                  <p>Sickle cell disease predominantly affects Black people, and its history is deeply intertwined with racism.</p>
                  <h4>The History</h4>
                  <p>SCD was first described in a medical journal in 1910, but for decades it received minimal research funding compared to diseases affecting white populations. SCD research receives a fraction of the funding that cystic fibrosis receives, despite SCD being more common.</p>
                  <h4>Stigma in Healthcare</h4>
                  <ul><li>Longer wait times for pain medication</li><li>Lower doses of pain medication</li><li>Assumptions of drug-seeking behaviour</li><li>More frequent security presence</li><li>Documented racial bias in pain assessment</li></ul>
                  <p><strong>Your experiences matter. Your advocacy matters. You're part of a movement for justice.</strong></p>
                  <button className="btn btn-primary" onClick={closeModal}>Close</button>
                </div>
              </>
            )}

            {activeModal === 'stigma-101' && (
              <>
                <div className="modal-header"><h3 style={{ display:'inline-flex', alignItems:'center', gap:8 }}><IconBook size={22} ariaLabel="" /> Stigma 101</h3><button className="modal-close" onClick={closeModal} aria-label="Close dialog">×</button></div>
                <div className="modal-body">
                  <h4>What Is Stigma?</h4>
                  <p>Stigma happens when someone is labeled, judged, or discriminated against because of a characteristic or condition. For people with SCD, stigma can come from healthcare providers, workplaces, schools, and even family.</p>
                  <h4>Intersectional Stigma</h4>
                  <p>Intersectional stigma means experiencing multiple overlapping forms of bias at the same time—race, age, gender, disability, and disease status all at once. This is what many young Black people with SCD face in the healthcare system.</p>
                  <h4>Why It Matters</h4>
                  <p>Stigma has real medical consequences: delayed care, undertreated pain, trauma responses, and avoidance of the healthcare system. Naming and tracking stigma is an act of resistance.</p>
                  <button className="btn btn-primary" onClick={closeModal}>Close</button>
                </div>
              </>
            )}

            {activeModal === 'transition-script' && (
              <>
                <div className="modal-header"><h3 style={{ display:'inline-flex', alignItems:'center', gap:8 }}><IconJourneyAdultTransition size={22} ariaLabel="" /> Transition conversation starter</h3><button className="modal-close" onClick={closeModal} aria-label="Close dialog">×</button></div>
                <div className="modal-body">
                  <p>These questions help you advocate during the move from pediatric to adult sickle cell care.</p>
                  <h4>Questions to Ask Your Pediatric Team</h4>
                  <ul><li>Can you write a full medical summary I can share with adult providers?</li><li>Who will you connect me to in adult care?</li><li>What should I know about my care that I don't already?</li></ul>
                  <h4>Questions to Ask Your New Adult Team</h4>
                  <ul><li>What's the best way to reach you during a crisis?</li><li>Do you have experience with SCD in young adults?</li><li>Can we write a crisis plan together?</li></ul>
                  <button className="btn btn-primary" onClick={closeModal}>Close</button>
                </div>
              </>
            )}

            {activeModal === 'rights-script' && (
              <>
                <div className="modal-header"><h3 style={{ display:'inline-flex', alignItems:'center', gap:8 }}><IconJourneyEmploymentRights size={22} ariaLabel="" /> Know your rights</h3><button className="modal-close" onClick={closeModal} aria-label="Close dialog">×</button></div>
                <div className="modal-body">
                  <h4>As a Patient, You Have the Right To:</h4>
                  <ul>
                    <li>Receive care without discrimination based on race, age, gender, or disability</li>
                    <li>Have your pain taken seriously and treated appropriately</li>
                    <li>Access your medical records</li>
                    <li>Request a patient advocate</li>
                    <li>Refuse treatment and ask about alternatives</li>
                    <li>File a complaint without fear of retaliation</li>
                  </ul>
                  <h4>Useful Phrases</h4>
                  <div className="script-template"><p>"I'd like to speak with a patient advocate, please."</p></div>
                  <div className="script-template"><p>"Please document in my chart that I requested [X] and it was denied."</p></div>
                  <div className="script-template"><p>"I am refusing to leave without receiving appropriate pain management."</p></div>
                  <button className="btn btn-primary" onClick={closeModal}>Close</button>
                </div>
              </>
            )}

            {activeModal === 'content-item' && activeContentItem && (() => {
              const renderInline = (text: string): React.ReactNode[] => {
                const parts: React.ReactNode[] = [];
                const re = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;
                let last = 0; let m: RegExpExecArray | null;
                while ((m = re.exec(text)) !== null) {
                  if (m.index > last) parts.push(text.slice(last, m.index));
                  if (m[2] !== undefined) parts.push(<strong key={m.index} style={{ color: 'var(--color-text-primary)', fontWeight: 700 }}>{m[2]}</strong>);
                  else if (m[3] !== undefined) parts.push(<em key={m.index} style={{ color: 'var(--color-accent)', fontStyle: 'italic' }}>{m[3]}</em>);
                  last = m.index + m[0].length;
                }
                if (last < text.length) parts.push(text.slice(last));
                return parts;
              };

              const renderBody = (body: string) => {
                const lines = body.split('\n');
                const nodes: React.ReactNode[] = [];
                let i = 0;
                while (i < lines.length) {
                  const line = lines[i];
                  if (line.trim() === '') { i++; continue; }
                  if (line.trim() === '---') {
                    nodes.push(<hr key={i} style={{ border: 'none', borderTop: '1px solid color-mix(in srgb, var(--color-text-primary) 10%, transparent)', margin: '14px 0' }} />);
                    i++; continue;
                  }
                  if (line.trim().startsWith('•')) {
                    const bullets: string[] = [];
                    while (i < lines.length && lines[i].trim().startsWith('•')) {
                      bullets.push(lines[i].trim().slice(1).trim());
                      i++;
                    }
                    nodes.push(
                      <ul key={`ul-${i}`} style={{ margin: '6px 0 10px', paddingLeft: 0, listStyle: 'none' }}>
                        {bullets.map((b, bi) => (
                          <li key={bi} style={{ display: 'flex', gap: 10, marginBottom: 6, alignItems: 'flex-start' }}>
                            <span style={{ color: 'var(--color-accent)', fontSize: 'var(--fs-base)', lineHeight: 1.4, flexShrink: 0 }}>·</span>
                            <span style={{ fontSize: 'var(--fs-sm)', lineHeight: 1.6, color: 'var(--color-text-primary)' }}>{renderInline(b)}</span>
                          </li>
                        ))}
                      </ul>
                    );
                    continue;
                  }
                  const isBold = /^\*\*[^*]/.test(line.trim());
                  nodes.push(
                    <p key={i} style={{ margin: '0 0 10px', fontSize: 'var(--fs-sm)', lineHeight: 1.7, color: isBold ? 'var(--color-text-primary)' : 'var(--color-text-secondary)' }}>
                      {renderInline(line.trim())}
                    </p>
                  );
                  i++;
                }
                return nodes;
              };

              const BreathingGif = () => (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '18px 0 8px' }}>
                  <style>{`
                    @keyframes cw-breathe {
                      0%,100%{transform:scale(1);opacity:0.55}
                      50%{transform:scale(1.45);opacity:1}
                    }
                    @keyframes cw-breathe-ring {
                      0%,100%{transform:scale(1);opacity:0.2}
                      50%{transform:scale(1.6);opacity:0.5}
                    }
                    @keyframes cw-breathe-label {
                      0%,100%{opacity:0.5;letter-spacing:0.05em}
                      50%{opacity:1;letter-spacing:0.18em}
                    }
                  `}</style>
                  <div style={{ position: 'relative', width: 120, height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ position: 'absolute', width: 110, height: 110, borderRadius: '50%', background: 'color-mix(in srgb, var(--color-accent) 12%, transparent)', border: '1.5px solid color-mix(in srgb, var(--color-accent) 25%, transparent)', animation: 'cw-breathe-ring 4s ease-in-out infinite' }} />
                    <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'radial-gradient(circle at 38% 38%, color-mix(in srgb, var(--color-accent) 90%, transparent), color-mix(in srgb, var(--color-accent) 60%, transparent))', boxShadow: '0 0 28px color-mix(in srgb, var(--color-accent) 45%, transparent)', animation: 'cw-breathe 4s ease-in-out infinite' }} />
                  </div>
                  <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-accent)', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', animation: 'cw-breathe-label 4s ease-in-out infinite' }}>
                    breathe
                  </span>
                  <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', textAlign: 'center', maxWidth: 220 }}>
                    Breathe in as the circle expands · out as it shrinks · repeat at your own pace
                  </span>
                </div>
              );

              const EquityGif = () => (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '14px 0 8px' }}>
                  <style>{`
                    @keyframes cw-eq-appear { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
                    @keyframes cw-eq-fence { 0%{clip-path:inset(0 100% 0 0)} 100%{clip-path:inset(0 0% 0 0)} }
                  `}</style>
                  {[
                    { label: 'Equality', boxes: [1,1,1], heights: [140, 115, 90], colors: ['var(--color-accent)','var(--color-secondary)','var(--color-crisis)'] },
                    { label: 'Equity', boxes: [1,2,3], heights: [140, 115, 90], colors: ['var(--color-accent)','var(--color-secondary)','var(--color-crisis)'] },
                  ].map((scene, si) => (
                    <div key={si} style={{ textAlign: 'center', animation: `cw-eq-appear 0.4s ease ${si * 0.2}s both` }}>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, justifyContent: 'center', marginBottom: 6 }}>
                        {scene.heights.map((h, pi) => {
                          const stackCount = scene.boxes[pi];
                          const personH = 32;
                          const boxH = 14;
                          const totalBoxH = stackCount * boxH;
                          const visible = (h + totalBoxH) >= 140;
                          return (
                            <div key={pi} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
                              <div style={{ width: 22, height: personH, borderRadius: '50% 50% 0 0', background: visible ? scene.colors[pi] : `color-mix(in srgb, ${scene.colors[pi]} 33%, transparent)`, transition: 'background 0.5s', position: 'relative' }}>
                                <div style={{ width: 14, height: 14, borderRadius: '50%', background: visible ? scene.colors[pi] : `color-mix(in srgb, ${scene.colors[pi]} 33%, transparent)`, position: 'absolute', top: -10, left: 4, border: '2px solid color-mix(in srgb, var(--color-text-primary) 30%, transparent)' }} />
                              </div>
                              {Array.from({ length: stackCount }).map((_, bi) => (
                                <div key={bi} style={{ width: 28, height: boxH - 1, background: `color-mix(in srgb, ${scene.colors[pi]} 53%, transparent)`, borderRadius: 2, border: `1px solid ${scene.colors[pi]}`, marginTop: 1 }} />
                              ))}
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--color-text-secondary)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{scene.label}</div>
                    </div>
                  ))}
                  <p style={{ margin: '4px 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', textAlign: 'center', maxWidth: 240, lineHeight: 1.5 }}>
                    Same boxes = equal. Right-sized boxes = equitable.
                  </p>
                </div>
              );

              return (
                <>
                  <div className="modal-header">
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <span style={{ display:'inline-flex' }}>{activeContentItem.type === 'script' ? <IconAdvocacy size={18} ariaLabel="" /> : activeContentItem.type === 'story' ? <IconBook size={18} ariaLabel="" /> : <IconLightbulb size={18} ariaLabel="" />}</span>
                      <span style={{ fontSize: 'var(--fs-base)', lineHeight: 1.3 }}>{activeContentItem.title}</span>
                    </h3>
                    <button className="modal-close" onClick={() => { closeModal(); setActiveContentItem(null); }} aria-label="Close dialog">×</button>
                  </div>
                  <div className="modal-body">
                    {/* Story: enhanced quote header with identity badges */}
                    {activeContentItem.type === 'story' && (
                      <StoryQuoteHeader
                        summary={activeContentItem.summary}
                        age={activeContentItem.age_at_event}
                        identities={activeContentItem.self_described_identities}
                        contentWarnings={activeContentItem.content_warnings}
                      />
                    )}

                    {/* Non-story content warnings */}
                    {activeContentItem.type !== 'story' && activeContentItem.content_warnings && activeContentItem.content_warnings.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '9px 12px', background: 'color-mix(in srgb, var(--color-warning) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--color-warning) 25%, transparent)', borderRadius: 'var(--radius-md)', marginBottom: 14, fontSize: 'var(--fs-xs)', color: 'var(--color-warning)' }}>
                        <IconWarning size={14} ariaLabel="" />
                        <span><strong>Content notes:</strong> {activeContentItem.content_warnings.join(', ')}</span>
                      </div>
                    )}

                    {activeContentItem.notes_for_youth && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '9px 12px', background: 'color-mix(in srgb, var(--color-accent) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--color-accent) 20%, transparent)', borderLeft: '3px solid var(--color-accent)', borderRadius: '0 10px 10px 0', marginBottom: 14, fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                        <span style={{ display:'inline-flex' }}><IconSpeechBubble size={14} ariaLabel="" /></span>
                        <span>{activeContentItem.notes_for_youth}</span>
                      </div>
                    )}

                    {/* Gifs / built-in animations */}
                    {activeContentItem.gif === '__breathing__' && <BreathingGif />}
                    {activeContentItem.gif === '__equity__' && <EquityGif />}
                    {activeContentItem.gif && activeContentItem.gif !== '__breathing__' && activeContentItem.gif !== '__equity__' && (
                      <img src={activeContentItem.gif} alt="Animated illustration" style={{ width: '100%', borderRadius: 'var(--radius-md)', marginBottom: 14, maxHeight: 200, objectFit: 'cover' }} />
                    )}

                    {/* ── Content-specific enhancements ── */}
                    {activeContentItem.id === 'ed_before_is_it_bad_enough_v1' && <DecisionTreeDiagram />}
                    {activeContentItem.id === 'ed_before_triage_overview_v1' && <TriageTimeline />}
                    {activeContentItem.id === 'ed_before_emergency_profile_intro_v1' && <EmergencyProfileCard />}
                    {activeContentItem.id === 'ed_during_security_safety_check_v1' && <SafetyAssessment />}
                    {activeContentItem.id === 'ed_after_one_slider_checkin_v1' && <HeavinessSlider />}
                    {activeContentItem.id === 'ed_after_debrief_normalize_v1' && <SystemVsYouVisual />}
                    {activeContentItem.id === 'newcomer_rights_canadian_healthcare_v1' && <RightsGrid />}
                    {activeContentItem.id === 'newcomer_3sentence_health_summary_v1' && <ThreeSentenceBuilder />}
                    {activeContentItem.id === 'newcomer_pain_looks_different_v1' && <PainSpectrum />}
                    {activeContentItem.id === 'newcomer_your_pain_is_real_v1' && <NewcomerAffirmation />}

                    {/* ── Grounding items get 5-4-3-2-1 ── */}
                    {activeContentItem.delivery_mode.includes('grounding') && activeContentItem.id !== 'ed_during_security_safety_check_v1' && (
                      <Grounding54321 />
                    )}

                    {/* ── Script items: practice mode strip ── */}
                    {activeContentItem.type === 'script' && activeContentItem.script_lines && (
                      <ScriptPracticeMode lines={activeContentItem.script_lines} />
                    )}

                    {/* ── Main content ── */}
                    {activeContentItem.type === 'script' && activeContentItem.script_lines ? (
                      <div>
                        {activeContentItem.script_lines.map((line, i) => (
                          <div key={i} style={{ background: 'color-mix(in srgb, var(--color-accent) 7%, transparent)', border: '1px solid color-mix(in srgb, var(--color-accent) 18%, transparent)', borderLeft: '3px solid var(--color-accent)', borderRadius: '0 10px 10px 0', padding: '10px 14px', marginBottom: 10 }}>
                            <p style={{ margin: 0, fontSize: 'var(--fs-sm)', lineHeight: 1.65, color: 'var(--color-text-primary)', fontStyle: 'italic' }}>{line}</p>
                          </div>
                        ))}
                        {/* eslint-disable-next-line cw/no-inline-button-style -- inline .btn-primary style overrides (marginTop, width, inline-flex icon) on content-item script "Copy all phrases" CTA; rule flags any style on button regardless of className */}
                        <button className="btn btn-primary" style={{ marginTop: 6, width: '100%', display:'inline-flex', alignItems:'center', justifyContent:'center', gap:6 }} onClick={() => {
                          const text = activeContentItem.script_lines!.join('\n\n');
                          navigator.clipboard.writeText(text);
                        }}><IconChecklist size={14} ariaLabel="" /> Copy all phrases</button>
                      </div>
                    ) : (
                      <div style={{ marginTop: activeContentItem.gif ? 14 : 0 }}>
                        {renderBody(activeContentItem.body)}
                      </div>
                    )}

                    <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {activeContentItem.delivery_mode.map(m => (
                        <span key={m} className="module-tag" style={{ fontSize: 'var(--fs-xs)' }}>{DELIVERY_ICONS[m]} {m.replace(/_/g, ' ')}</span>
                      ))}
                      <span className="module-tag" style={{ fontSize: 'var(--fs-xs)' }}>{INTENSITY_ICONS[activeContentItem.intensity]} intensity</span>
                      <span className="module-tag" style={{ fontSize: 'var(--fs-xs)' }}>{activeContentItem.estimated_time_minutes} min</span>
                    </div>

                    <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: '1fr auto', gap: 10 }}>
                      {(() => {
                        const status = getStatus(activeContentItem.id);
                        return (
                          <button
                            className={status === 'completed' ? 'btn btn-secondary' : 'btn btn-primary'}
                            onClick={() => {
                              if (status === 'completed') {
                                cycleStatus(activeContentItem.id);
                              } else {
                                const { isFirstCompletion } = markCompleted(activeContentItem.id);
                                if (isFirstCompletion) earnXP(25);
                              }
                            }}
                          >
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCheck size={14} ariaLabel="" /> {status === 'completed' ? 'Completed — undo?' : 'Mark complete (+25 XP)'}</span>
                          </button>
                        );
                      })()}
                      <button className="btn btn-secondary" onClick={() => { closeModal(); setActiveContentItem(null); }}>Close</button>
                    </div>
                  </div>
                </>
              );
            })()}

            {activeModal === 'youth-submission' && (
              <>
                <div className="modal-header"><h3 style={{ display:'inline-flex', alignItems:'center', gap:8 }}><IconLogEvent size={22} ariaLabel="" /> Share your story</h3><button className="modal-close" onClick={closeModal} aria-label="Close dialog">×</button></div>
                <div className="modal-body">
                  <p>Your experiences, tips, poems, or reflections can help other youth navigating similar challenges. Submissions are reviewed by youth advisors before being shared.</p>
                  <h4>Your Submission</h4>
                  <textarea value={youthSubmissionText} onChange={e => setYouthSubmissionText(e.target.value)} placeholder="Share your story, poem, tips, or reflections here..." aria-label="Your submission" rows={8} style={{ width: '100%', background: 'var(--color-bg-elevated)', border: '1px solid var(--color-bg-surface)', borderRadius: 'var(--radius-sm)', color: 'var(--color-text-primary)', fontSize: 'var(--fs-sm)', padding: 12, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
                  {submissionSuccess && (
                    <div style={{ background: 'color-mix(in srgb, var(--color-success) 15%, transparent)', border: '1px solid color-mix(in srgb, var(--color-success) 30%, transparent)', color: 'var(--color-success)', padding: 12, borderRadius: 'var(--radius-sm)', marginTop: 12 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCheck size={14} ariaLabel="" /> Thank you for sharing! Your submission will be reviewed by youth advisors.</span>
                    </div>
                  )}
                  <div className="btn-group" style={{ marginTop: 16 }}>
                    <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                    <button className="btn btn-primary" onClick={submitYouthStory}>Submit</button>
                  </div>
                </div>
              </>
            )}

            {(activeModal === 'add-medication' || activeModal === 'edit-medication') && (() => {
              const MED_UNITS = ['mg', 'mL', 'mcg', 'tablet', 'capsule', 'patch', 'puff', 'injection'];
              const FREQ_OPTIONS: { value: string; iconKey: CrystalIconKey; label: string }[] = [
                { value: 'Once daily', iconKey: 'med-freq-once-daily', label: 'Once daily' },
                { value: 'Twice daily', iconKey: 'med-freq-twice-daily', label: 'Twice daily' },
                { value: 'Three times daily', iconKey: 'med-freq-thrice-daily', label: '3× daily' },
                { value: 'As needed', iconKey: 'med-freq-as-needed', label: 'As needed' },
                { value: 'Weekly', iconKey: 'med-freq-weekly', label: 'Weekly' },
                { value: 'Every other day', iconKey: 'med-freq-alt-days', label: 'Alt. days' },
              ];
              const doseStr = medFormDose.trim();
              const doseIsNumeric = doseStr !== '' && /^[\d.,/\s]+$/.test(doseStr);
              const finalDose = doseStr
                ? (doseIsNumeric ? `${doseStr} ${medFormUnit}` : doseStr)
                : '';
              const canSave = medFormName.trim() && medFormDose.trim() && medFormFrequency;
              return (
                <>
                  <div className="modal-header">
                    <h3 style={{ display:'inline-flex', alignItems:'center', gap:8 }}><IconMedications size={22} ariaLabel="" /> {activeModal === 'add-medication' ? 'Add a medication' : 'Edit medication'}</h3>
                    <button className="modal-close" onClick={closeModal} aria-label="Close dialog">×</button>
                  </div>
                  <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                    <div style={{ position: 'relative' }}>
                      <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Medication name *</div>
                      <input
                        type="text"
                        value={medFormName}
                        onChange={e => { setMedFormName(e.target.value); setMedNameDropdownOpen(true); }}
                        onFocus={() => setMedNameDropdownOpen(true)}
                        onBlur={() => setTimeout(() => setMedNameDropdownOpen(false), 150)}
                        placeholder="Search or type a medication…"
                        aria-label="Medication name"
                        autoComplete="off"
                        style={{ width: '100%', padding: '14px 16px', borderRadius: 'var(--radius-lg)', border: '1.5px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)', fontSize: 'var(--fs-sm-plus)', boxSizing: 'border-box', outline: 'none' }}
                      />
                      {medNameDropdownOpen && (() => {
                        const q = medFormName.trim().toLowerCase();
                        const filtered = COMMON_MEDICATIONS.filter(m => m.toLowerCase().includes(q));
                        return filtered.length > 0 ? (
                          <ul style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 'var(--z-dropdown)', margin: '4px 0 0', padding: 0, listStyle: 'none', background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-overlay)', maxHeight: 200, overflowY: 'auto' }}>
                            {filtered.map(med => (
                              <li
                                key={med}
                                onMouseDown={() => { setMedFormName(med); setMedNameDropdownOpen(false); }}
                                style={{ padding: '11px 16px', cursor: 'pointer', fontSize: 'var(--fs-sm)', color: 'var(--color-text-primary)', borderBottom: '1px solid var(--color-border)' }}
                                onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-bg-surface)')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                              >
                                {med}
                              </li>
                            ))}
                          </ul>
                        ) : null;
                      })()}
                    </div>

                    <div>
                      <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>How much each time? *</div>
                      {/* Dose display */}
                      <div style={{
                        padding: '14px 16px',
                        borderRadius: 'var(--radius-lg)',
                        border: '1.5px solid var(--color-border)',
                        background: 'var(--color-bg-elevated)',
                        color: medFormDose ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                        fontSize: 'var(--fs-lg)',
                        fontWeight: 600,
                        textAlign: 'center',
                        minHeight: 24,
                        letterSpacing: '0.02em',
                      }}>
                        {medFormDose || 'Tap to enter dose'}
                      </div>
                      {/* Quick-select common doses */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                        {['0.5', '1', '2', '5', '10', '25', '50', '100', '250', '500'].map(d => (
                          // eslint-disable-next-line cw/no-inline-button-style -- medication dose quick-pick chip; flex-wrap pill toggle with dynamic active accent, not a chamfered .btn variant
                          <button
                            key={d}
                            onClick={() => setMedFormDose(d)}
                            style={{
                              padding: '7px 12px',
                              borderRadius: 'var(--radius-xl)',
                              border: medFormDose === d ? '1.5px solid var(--color-accent)' : '1.5px solid var(--color-border)',
                              background: medFormDose === d ? 'color-mix(in srgb, var(--color-accent) 12%, transparent)' : 'var(--color-bg-surface)',
                              color: medFormDose === d ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                              fontSize: 'var(--fs-sm)',
                              fontWeight: medFormDose === d ? 700 : 500,
                              cursor: 'pointer',
                              transition: 'all 0.15s',
                            }}
                          >
                            {d}
                          </button>
                        ))}
                      </div>
                      {/* Number pad */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: 8,
                        marginTop: 12,
                      }}>
                        {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'del'].map(key => (
                          // eslint-disable-next-line cw/no-inline-button-style -- medication dose number-pad key (digit / . / del); 3-col grid with tactile press transform handlers, native-keypad look, not a chamfered .btn variant
                          <button
                            key={key}
                            onClick={() => {
                              if (key === 'del') {
                                setMedFormDose(prev => prev.slice(0, -1));
                              } else if (key === '.') {
                                setMedFormDose(prev => prev.includes('.') ? prev : prev + '.');
                              } else {
                                setMedFormDose(prev => prev + key);
                              }
                            }}
                            style={{
                              padding: '14px 0',
                              borderRadius: 'var(--radius-md)',
                              border: '1.5px solid var(--color-border)',
                              background: key === 'del' ? 'color-mix(in srgb, var(--color-crisis) 8%, transparent)' : 'var(--color-bg-surface)',
                              color: key === 'del' ? 'var(--color-crisis)' : 'var(--color-text-primary)',
                              fontSize: key === 'del' ? 'var(--fs-base)' : 'var(--fs-md)',
                              fontWeight: 600,
                              cursor: 'pointer',
                              transition: 'all 0.12s',
                              userSelect: 'none',
                              WebkitTapHighlightColor: 'transparent',
                            }}
                            onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.95)'; e.currentTarget.style.background = key === 'del' ? 'color-mix(in srgb, var(--color-crisis) 15%, transparent)' : 'var(--color-bg-elevated)'; }}
                            onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.background = key === 'del' ? 'color-mix(in srgb, var(--color-crisis) 8%, transparent)' : 'var(--color-bg-surface)'; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.background = key === 'del' ? 'color-mix(in srgb, var(--color-crisis) 8%, transparent)' : 'var(--color-bg-surface)'; }}
                            onTouchStart={e => { e.currentTarget.style.transform = 'scale(0.95)'; e.currentTarget.style.background = key === 'del' ? 'color-mix(in srgb, var(--color-crisis) 15%, transparent)' : 'var(--color-bg-elevated)'; }}
                            onTouchEnd={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.background = key === 'del' ? 'color-mix(in srgb, var(--color-crisis) 8%, transparent)' : 'var(--color-bg-surface)'; }}
                          >
                            {key === 'del' ? '⌫' : key}
                          </button>
                        ))}
                      </div>
                      {/* Clear button */}
                      {medFormDose && (
                        // eslint-disable-next-line cw/no-inline-button-style -- medication dose "Clear" ghost text-link below number pad; underlined sub-action, not a chamfered .btn variant
                        <button
                          onClick={() => setMedFormDose('')}
                          style={{
                            marginTop: 8,
                            padding: '8px 0',
                            width: '100%',
                            borderRadius: 'var(--radius-md)',
                            border: 'none',
                            background: 'transparent',
                            color: 'var(--color-text-secondary)',
                            fontSize: 'var(--fs-sm)',
                            fontWeight: 500,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                          }}
                        >
                          Clear
                        </button>
                      )}
                      {/* Unit selector */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                        {MED_UNITS.map(u => (
                          // eslint-disable-next-line cw/no-inline-button-style -- medication unit chip (mg / mL / mcg / tablet / etc.); flex-wrap pill toggle with dynamic active accent fill, not a chamfered .btn variant
                          <button
                            key={u}
                            onClick={() => setMedFormUnit(u)}
                            style={{
                              padding: '7px 14px',
                              borderRadius: 'var(--radius-xl)',
                              border: medFormUnit === u ? '1.5px solid var(--color-accent)' : '1.5px solid var(--color-border)',
                              background: medFormUnit === u ? 'var(--color-accent)' : 'var(--color-bg-surface)',
                              color: medFormUnit === u ? '#fff' : 'var(--color-text-secondary)',
                              fontSize: 'var(--fs-sm)',
                              fontWeight: medFormUnit === u ? 700 : 400,
                              cursor: 'pointer',
                              transition: 'all 0.15s',
                            }}
                          >
                            {u}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>How often? *</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                        {FREQ_OPTIONS.map(opt => {
                          const isSelected = medFormFrequency === opt.value;
                          const IconComp = CRYSTAL_ICONS[opt.iconKey];
                          return (
                            // eslint-disable-next-line cw/no-inline-button-style -- medication frequency tile (Once / Twice / 3× / As needed / Weekly / Alt. days); 3-col grid icon + 2-line label, Phase 3 Track E shipped frequency icons, not a chamfered .btn variant
                            <button
                              key={opt.value}
                              onClick={() => setMedFormFrequency(opt.value)}
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 6,
                                padding: '12px 8px',
                                borderRadius: 'var(--radius-lg)',
                                border: isSelected ? '2px solid var(--color-accent)' : '1.5px solid var(--color-border)',
                                background: isSelected ? 'color-mix(in srgb, var(--color-accent) 12%, transparent)' : 'var(--color-bg-surface)',
                                cursor: 'pointer',
                                transition: 'all 0.15s',
                                color: isSelected ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                              }}
                            >
                              <IconComp size={26} ariaLabel="" />
                              <span style={{ fontSize: 'var(--fs-xs)', fontWeight: isSelected ? 700 : 500, color: isSelected ? 'var(--color-accent)' : 'var(--color-text-secondary)', textAlign: 'center', lineHeight: 1.2 }}>{opt.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Any notes? (optional)</div>
                      <input
                        type="text"
                        value={medFormNotes}
                        onChange={e => setMedFormNotes(e.target.value)}
                        placeholder="e.g. Take with food, before bed…"
                        aria-label="Medication notes"
                        style={{ width: '100%', padding: '14px 16px', borderRadius: 'var(--radius-lg)', border: '1.5px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)', fontSize: 'var(--fs-sm-plus)', boxSizing: 'border-box', outline: 'none' }}
                      />
                    </div>

                    <div className="btn-group" style={{ marginTop: 4 }}>
                      <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                      <button
                        className="btn btn-primary"
                        disabled={!canSave}
                        onClick={async () => {
                          if (!canSave) return;
                          if (activeModal === 'edit-medication' && editingMed) {
                            await updateMedication(editingMed.id, {
                              name: medFormName.trim(),
                              dose: finalDose,
                              frequency: medFormFrequency,
                              notes: medFormNotes.trim() || null,
                            });
                          } else {
                            await saveMedication(medFormName.trim(), finalDose, medFormFrequency, medFormNotes.trim());
                          }
                          closeModal();
                        }}
                      >
                        {activeModal === 'add-medication' ? 'Add Medication' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                </>
              );
            })()}

            {activeModal === 'create-custom-goal' && (
              <>
                <div className="modal-header">
                  <h3 style={{ display:'inline-flex', alignItems:'center', gap:8 }}><IconAdvocacy size={22} ariaLabel="" /> Create a SMART goal</h3>
                  <button className="modal-close" onClick={closeModal} aria-label="Close dialog">×</button>
                </div>
                <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {/* Progress dots */}
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 2 }}>
                    {['Learn', 'Name it', 'Plan it', 'Review'].map((label, i) => (
                      <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: smartGuideStep >= i ? 'var(--color-accent)' : 'var(--color-border-subtle)', transition: 'background 0.2s' }} />
                        <div style={{ fontSize: 'var(--fs-xs)', color: smartGuideStep >= i ? 'var(--color-accent)' : 'var(--color-text-secondary)', fontWeight: smartGuideStep === i ? 700 : 500, transition: 'color 0.2s' }}>{label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Step 0: SMART Overview */}
                  {smartGuideStep === 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.5, margin: 0 }}>
                        A <strong>SMART</strong> goal helps you turn a wish into a plan. Let's build one together.
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {[
                          { letter: 'S', word: 'Specific', desc: 'What exactly will you do?', color: 'var(--color-accent)' },
                          { letter: 'M', word: 'Measurable', desc: 'How will you know you did it?', color: 'var(--color-info)' },
                          { letter: 'A', word: 'Achievable', desc: 'Is this something you can actually do right now?', color: 'var(--color-success)' },
                          { letter: 'R', word: 'Relevant', desc: 'Does this matter to you and your life?', color: 'var(--color-warning)' },
                          { letter: 'T', word: 'Time-bound', desc: 'When will you do this by?', color: 'var(--color-danger)' },
                        ].map(item => (
                          <div key={item.letter} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 'var(--radius-md)', background: `color-mix(in srgb, ${item.color} 7%, transparent)`, border: `1px solid color-mix(in srgb, ${item.color} 19%, transparent)` }}>
                            <div style={{ width: 28, height: 28, borderRadius: '50%', background: item.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 'var(--fs-sm)', flexShrink: 0 }}>{item.letter}</div>
                            <div>
                              <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 700, color: 'var(--color-text-primary)' }}>{item.word}</div>
                              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginTop: 1 }}>{item.desc}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="hint-box">
                        <div className="hint-text">
                          <strong style={{ color: 'var(--color-text-primary)' }}>Example:</strong> Instead of "I want to feel better," a SMART goal might be: <em>"I will journal for 5 minutes every evening this week to understand my feelings."</em>
                        </div>
                      </div>
                      <div className="btn-group mt-4">
                        <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                        <button className="btn btn-primary" onClick={() => setSmartGuideStep(1)}>Got it, let's start</button>
                      </div>
                    </div>
                  )}

                  {/* Step 1: Name your goal (Specific + Relevant) */}
                  {smartGuideStep === 1 && (
                    <div className="flex-col gap-14">
                      <div className="hint-box">
                        <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 700, color: 'var(--color-accent)', marginBottom: 4 }}>S + R: Specific &amp; Relevant</div>
                        <div className="hint-text">
                          Think about something specific that matters to <em>you</em>. Not what others want — what would make <em>your</em> life better?
                        </div>
                      </div>
                      <div>
                        <div className="section-label mb-4" style={{ marginBottom: 6 }}>What's your goal? *</div>
                        <input
                          type="text"
                          className="input-field"
                          value={customGoalTitle}
                          onChange={e => setCustomGoalTitle(e.target.value)}
                          placeholder="e.g. Talk to my worker about changing schools"
                          aria-label="What's your goal?"
                          maxLength={100}
                        />
                      </div>
                      <div>
                        <div className="section-label" style={{ marginBottom: 6 }}>Why does this matter to you? (optional)</div>
                        <textarea
                          className="textarea-field"
                          value={customGoalDescription}
                          onChange={e => setCustomGoalDescription(e.target.value)}
                          placeholder="e.g. I'm not happy at my current school and I want to feel safer"
                          aria-label="Why does this matter to you?"
                          rows={2}
                          maxLength={300}
                        />
                      </div>
                      <div className="card-elevated rounded-10 hint-text">
                        <strong>Quick check:</strong> Can you picture yourself doing this? If your goal feels too big, try narrowing it down to one specific action.
                      </div>
                      <div className="btn-group mt-4">
                        <button className="btn btn-secondary" onClick={() => setSmartGuideStep(0)}>Back</button>
                        <button className="btn btn-primary" disabled={!customGoalTitle.trim()} onClick={() => setSmartGuideStep(2)}>Next: Plan your steps</button>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Plan steps (Measurable + Achievable + Time-bound) */}
                  {smartGuideStep === 2 && (
                    <div className="flex-col gap-14">
                      <div className="rounded-12" style={{ padding: '10px 14px', background: 'color-mix(in srgb, var(--color-info) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--color-info) 30%, transparent)' }}>
                        <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 700, color: 'var(--color-info)', marginBottom: 4 }}>M + A + T: Measurable, Achievable &amp; Time-bound</div>
                        <div className="hint-text">
                          Break your goal into small, doable steps. Each step should be something you can check off — something you'll <em>know</em> when it's done.
                        </div>
                      </div>
                      <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 700, color: 'var(--color-text-primary)', padding: '8px 0 0 0' }}>
                        Steps for: <span style={{ color: 'var(--color-accent)' }}>{customGoalTitle}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {customGoalSteps.map((step, idx) => (
                          <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <div style={{ width: 22, height: 22, borderRadius: '50%', background: step.trim() ? 'var(--color-accent)' : 'transparent', border: step.trim() ? 'none' : '2px solid var(--color-text-secondary)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--fs-xs)', color: step.trim() ? '#fff' : 'var(--color-text-secondary)', fontWeight: 700, transition: 'all 0.2s' }}>{idx + 1}</div>
                            <input
                              type="text"
                              value={step}
                              onChange={e => setCustomGoalSteps(prev => prev.map((s, i) => i === idx ? e.target.value : s))}
                              aria-label={`Goal step ${idx + 1}`}
                              placeholder={
                                idx === 0 ? 'First small step… e.g. "Write down what I want to say"' :
                                idx === 1 ? 'Next step… e.g. "Ask my carer to set up a meeting"' :
                                idx === 2 ? 'Then… e.g. "Attend the meeting and share my thoughts"' :
                                `Step ${idx + 1}…`
                              }
                              maxLength={150}
                              style={{ flex: 1, padding: '11px 14px', borderRadius: 'var(--radius-md)', border: `1.5px solid ${step.trim() ? 'color-mix(in srgb, var(--color-accent) 40%, transparent)' : 'var(--color-border)'}`, background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)', fontSize: 'var(--fs-sm)', boxSizing: 'border-box', outline: 'none', transition: 'border-color 0.2s' }}
                            />
                            {customGoalSteps.length > 1 && (
                              // eslint-disable-next-line cw/no-inline-button-style -- custom-goal step input "×" remove glyph; ghost icon-only paired with text input, no .btn-icon-only primitive
                              <button
                                onClick={() => setCustomGoalSteps(prev => prev.filter((_, i) => i !== idx))}
                                style={{ background: 'none', border: 'none', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-base)', cursor: 'pointer', padding: '0 4px', flexShrink: 0 }}
                                aria-label="Remove step"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      {customGoalSteps.length < 5 && (
                        // eslint-disable-next-line cw/no-inline-button-style -- custom-goal "+ Add another step" button; dashed-outline ghost-add chip in vertical step stack, not a chamfered .btn variant
                        <button
                          onClick={() => setCustomGoalSteps(prev => [...prev, ''])}
                          style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-accent)', background: 'none', border: '1.5px dashed color-mix(in srgb, var(--color-accent) 30%, transparent)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontWeight: 600, padding: '10px 14px', textAlign: 'center' }}
                        >
                          + Add another step
                        </button>
                      )}
                      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', padding: '8px 12px', borderRadius: 'var(--radius-md)', background: 'var(--color-bg-elevated)', lineHeight: 1.5 }}>
                        <strong>Tips:</strong> Start each step with an action word (write, ask, go, practice). Keep steps small enough that you could do one this week.
                      </div>
                      <div className="btn-group" style={{ marginTop: 4 }}>
                        <button className="btn btn-secondary" onClick={() => setSmartGuideStep(1)}>Back</button>
                        <button className="btn btn-primary" disabled={!customGoalSteps.some(s => s.trim())} onClick={() => setSmartGuideStep(3)}>Next: Review</button>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Review */}
                  {smartGuideStep === 3 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      <div style={{ padding: '14px 16px', borderRadius: 'var(--radius-lg)', background: 'color-mix(in srgb, var(--color-success) 8%, transparent)', border: '1.5px solid color-mix(in srgb, var(--color-success) 30%, transparent)' }}>
                        <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 700, color: 'var(--color-success)', marginBottom: 8 }}>Your SMART Goal</div>
                        <div style={{ fontSize: 'var(--fs-sm-plus)', fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: customGoalDescription.trim() ? 4 : 0 }}>{customGoalTitle}</div>
                        {customGoalDescription.trim() && (
                          <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.5, fontStyle: 'italic' }}>{customGoalDescription}</div>
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Your steps</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {customGoalSteps.filter(s => s.trim()).map((step, idx) => (
                            <div key={idx} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 14px', borderRadius: 'var(--radius-md)', background: 'var(--color-bg-elevated)' }}>
                              <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--color-accent)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--fs-xs)', color: '#fff', fontWeight: 700 }}>{idx + 1}</div>
                              <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-primary)' }}>{step}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                      {/* Reflective prompts — no validation */}
                      <div style={{ padding: '10px 14px', borderRadius: 'var(--radius-md)', background: 'color-mix(in srgb, var(--color-accent) 6%, transparent)', border: '1px solid color-mix(in srgb, var(--color-accent) 15%, transparent)' }}>
                        <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Things to think about</div>
                        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', lineHeight: 1.7 }}>
                          You know your life best. If this goal feels right to you, go for it. Here are some questions you might find helpful:
                        </div>
                        <ul style={{ margin: '6px 0 0 0', paddingLeft: 18, fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', lineHeight: 1.7 }}>
                          <li>Do you feel clear on what step 1 looks like?</li>
                          <li>Is there anyone who could support you with this?</li>
                          <li>What would it feel like to finish this goal?</li>
                        </ul>
                      </div>
                      <div className="btn-group" style={{ marginTop: 4 }}>
                        <button className="btn btn-secondary" onClick={() => setSmartGuideStep(2)}>Edit steps</button>
                        <button
                          className="btn btn-primary"
                          onClick={() => {
                            const steps = customGoalSteps
                              .filter(s => s.trim())
                              .map((s, i) => ({ id: `custom-step-${i}-${Date.now()}`, label: s.trim() }));
                            if (steps.length === 0) return;
                            addCustomGoal(customGoalTitle, customGoalDescription, steps);
                            earnXP(20);
                            earnXPOnce('first_goal_adopted', 30);
                            closeModal();
                            showScreen('growth');
                          }}
                        >
                          Save my goal
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {activeModal === 'identity-picker' && (
              <>
                <div className="modal-header">
                  <h3>Edit identities</h3>
                  <button className="modal-close" onClick={closeModal} aria-label="Close dialog">×</button>
                </div>
                <div className="modal-body">
                  <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', marginBottom: 16 }}>
                    Select all that apply. Your selections are private and saved to your profile. You can also add your own identities below.
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
                    {IDENTITY_OPTIONS.map(option => {
                      const selected = identityPickerDraft.includes(option);
                      return (
                        // eslint-disable-next-line cw/no-inline-button-style -- identity-picker chip; flex-wrap pill toggle with dynamic active accent fill (multi-select), not a chamfered .btn variant
                        <button
                          key={option}
                          onClick={() => {
                            setIdentityPickerDraft(prev =>
                              prev.includes(option)
                                ? prev.filter(x => x !== option)
                                : [...prev, option]
                            );
                          }}
                          style={{
                            padding: '6px 14px',
                            borderRadius: 'var(--radius-xl)',
                            border: selected ? '1.5px solid var(--color-accent)' : '1.5px solid color-mix(in srgb, var(--color-accent) 30%, transparent)',
                            background: selected ? 'var(--color-accent)' : 'var(--color-bg-surface)',
                            color: selected ? 'white' : 'var(--color-text-secondary)',
                            fontSize: 'var(--fs-sm)',
                            fontWeight: selected ? 600 : 400,
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                          }}
                        >
                          {option}
                        </button>
                      );
                    })}
                  </div>
                  <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', marginBottom: 10, marginTop: 20 }}>Add your own identity:</p>
                  <div style={{ marginBottom: 20 }}>
                    <AddCustomTag onAdd={tag => { if (!identityPickerDraft.includes(tag)) setIdentityPickerDraft(prev => [...prev, tag]); }} />
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    {/* eslint-disable-next-line cw/no-inline-button-style -- inline .btn-secondary flex:1 override on identity-picker Cancel in 2-col flex pair; rule flags any style on button regardless of className */}
                    <button
                      className="btn btn-secondary"
                      style={{ flex: 1 }}
                      onClick={closeModal}
                    >
                      Cancel
                    </button>
                    {/* eslint-disable-next-line cw/no-inline-button-style -- inline .btn-primary flex:1 override on identity-picker Save in 2-col flex pair; rule flags any style on button regardless of className */}
                    <button
                      className="btn btn-primary"
                      style={{ flex: 1 }}
                      onClick={() => {
                        saveIdentities(identityPickerDraft);
                        closeModal();
                      }}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </>
            )}

            {activeModal === 'achievement-detail' && activeAchievement && (() => {
              const isEarned = unlockedAchievements.some(a => a.id === activeAchievement.id);
              return (
                <>
                  <div className="modal-header">
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{activeAchievement.iconKey ? (() => { const Icon = CRYSTAL_ICONS[activeAchievement.iconKey as CrystalIconKey]; return Icon ? <Icon size={22} ariaLabel="" /> : null; })() : activeAchievement.icon} {activeAchievement.label}</h3>
                    <button className="modal-close" onClick={closeModal} aria-label="Close dialog">×</button>
                  </div>
                  <div className="modal-body">
                    <div className="flex-col items-center gap-12" style={{ paddingTop: 8, paddingBottom: 16 }}>
                      <div style={{ fontSize: 'var(--fs-6xl)' }}>{activeAchievement.iconKey ? (() => { const Icon = CRYSTAL_ICONS[activeAchievement.iconKey as CrystalIconKey]; return Icon ? <Icon size={96} ariaLabel="" /> : null; })() : activeAchievement.icon}</div>
                      {isEarned && (
                        <span className="status-pill earned" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><IconCheck size={12} ariaLabel="" /> Earned</span>
                      )}
                      {!isEarned && (
                        <span className="status-pill pending">Not yet earned</span>
                      )}
                    </div>
                    <p className="text-center mb-12" style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-primary)', fontWeight: 500 }}>{activeAchievement.desc}</p>
                    <div className="card-surface text-center" style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)' }}>
                      {isEarned ? activeAchievement.earnedBy : activeAchievement.earnedBy.replace(/^Earned by/, 'You can earn this by')}
                    </div>
                    <div className="flex-center mt-16">
                      {/* eslint-disable-next-line cw/no-inline-button-style -- inline .btn-secondary padding override on achievement-detail "Close" CTA; rule flags any style on button regardless of className */}
                      <button className="btn btn-secondary" style={{ padding: '8px 24px' }} onClick={closeModal}>Close</button>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* FAQ Panel */}
      <FAQPanel open={faqPanelOpen} onClose={() => setFaqPanelOpen(false)} />

      {/* Contact Support Panel */}
      <ContactSupportPanel
        open={contactSupportPanelOpen}
        onClose={() => setContactSupportPanelOpen(false)}
        userName={currentUser?.displayName ?? null}
        userEmail={currentUser?.email ?? null}
        authToken={authToken}
      />

      {journeyActivatePrompt && (
        <div
          className="modal-overlay"
          role="presentation"
          onClick={() => setJourneyActivatePrompt(null)}
          style={{ alignItems: 'flex-end' }}
        >
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="flex-between mb-16" style={{ marginBottom: 18 }}>
              <h3 style={{ margin: 0, fontSize: 'var(--fs-md)', fontWeight: 700 }}>Activate journey</h3>
              {/* eslint-disable-next-line cw/no-inline-button-style -- journey-activate-prompt close X; circular 32×32 icon-only inside modal header, no .btn-icon-only primitive */}
              <button
                onClick={() => setJourneyActivatePrompt(null)}
                className="flex-center rounded-full"
                style={{ background: 'color-mix(in srgb, var(--color-text-primary) 4%, transparent)', border: 'none', width: 32, height: 32, cursor: 'pointer', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-md)' }}
              >
                ×
              </button>
            </div>
            <p style={{ margin: '0 0 20px', fontSize: 'var(--fs-sm-plus)', fontWeight: 600 }}>{journeyActivatePrompt.title}</p>
            <p style={{ margin: '0 0 20px', fontSize: 'var(--fs-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
              Activating a journey unlocks guided phases with articles, reflections, and actions tailored to this topic. You can leave at any time.
            </p>
            <div className="flex-col gap-8 rounded-12" style={{ background: 'color-mix(in srgb, var(--color-text-primary) 2%, transparent)', padding: '14px 16px', marginBottom: 24 }}>
              <div className="flex-between" style={{ fontSize: 'var(--fs-sm)' }}>
                <span style={{ color: 'var(--color-text-secondary)' }}>Activate journey</span>
                <span className="xp-badge">+30 XP</span>
              </div>
              <div className="flex-between" style={{ fontSize: 'var(--fs-sm)' }}>
                <span style={{ color: 'var(--color-text-secondary)' }}>Advance a phase</span>
                <span className="xp-badge">+50 XP</span>
              </div>
              <div className="flex-between" style={{ fontSize: 'var(--fs-sm)' }}>
                <span style={{ color: 'var(--color-text-secondary)' }}>Complete journey</span>
                <span className="xp-badge">+150 XP</span>
              </div>
            </div>
            <div className="flex-row gap-10">
              {/* eslint-disable-next-line cw/no-inline-button-style -- journey-activate-prompt Cancel; ghost outlined in 2-col flex pair with non-standard 1:1.5 ratio, not a chamfered .btn variant */}
              <button
                onClick={() => setJourneyActivatePrompt(null)}
                className="rounded-12"
                style={{ flex: 1, padding: '13px', background: 'var(--color-bg-surface)', border: '1px solid color-mix(in srgb, var(--color-text-primary) 12%, transparent)', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-sm)', fontWeight: 600, cursor: 'pointer' }}
              >
                Cancel
              </button>
              {/* eslint-disable-next-line cw/no-inline-button-style -- journey-activate-prompt confirm CTA; brand-filled primary in 2-col flex pair with non-standard 1:1.5 ratio, not a chamfered .btn variant */}
              <button
                onClick={() => activateJourney(journeyActivatePrompt.id, journeyActivatePrompt.firstPhase)}
                className="rounded-12"
                style={{ flex: 1.5, padding: '13px', background: 'var(--color-primary)', border: 'none', color: '#fff', fontSize: 'var(--fs-sm)', fontWeight: 700, cursor: 'pointer' }}
              >
                Yes, activate this journey
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Panel */}
      <SettingsPanel
        open={settingsPanelOpen}
        onClose={() => setSettingsPanelOpen(false)}
        darkMode={darkMode}
        onToggleDarkMode={toggleDarkMode}
        accessibilityPrefs={accessibilityPrefs}
        onAccessibilityChange={handleAccessibilityChange}
        reminderTime={reminderTime}
        onSaveReminderTime={saveReminderTime}
        tutorialCompleted={tutorialCompleted}
        onReplayTour={handleReplayTour}
        essentialsMode={essentialsMode}
        onToggleEssentialsMode={toggleEssentialsMode}
        essentialsTools={essentialsTools}
        onUpdateEssentialsTools={updateEssentialsTools}
        demoProfiles={isDevMode ? demoProfilesOn : undefined}
        onToggleDemoProfiles={isDevMode ? toggleDemoProfiles : undefined}
      />
    </div>
    </Suspense>
  );
}
